import React from 'react'
import UserIcon from 'components/users/UserIcon'
import UserName from 'components/users/UserName'
import CommonEditor from 'components/shared/CommonEditor'
import AddImageButton from 'components/answers/AnswerControls/AddImageButton'
import AddFileButton from 'components/answers/AnswerControls/AddFileButton'
import { BarChart3, Globe, Lock, Maximize2, Users } from 'lucide-react'
import WithTooltip from 'components/shared/WithTooltip'
import FileEmbed from './FileEmbed'
import Connector from '../state/Connector'
import PostEmbed from './PostEmbed'
import StreamEmbed from '../shared/StreamEmbed'
import LeaderboardMessageEmbed from 'components/leaderboards/LeaderboardMessageEmbed'
import TdpMessageEmbed from 'components/tdp/TdpMessageEmbed'
import api, { captureUploadFailure } from '../../api/api'
import UploadProgress from 'components/shared/UploadProgress'
import PreviewCancelButton from 'components/shared/PreviewCancelButton'
import ReliableButton from 'components/shared/ReliableButton'
import PayloadContainer from './Answer/PayloadContainer'
import {
    LinkPreview,
    extractUrls,
    extractKnovEmbedTarget,
    extractLeaderboardEmbedConfig,
    extractStreamEmbedConfig,
    extractStreamEmbedConfigs,
    extractTdpEmbedConfig,
    getLeaderboardEmbedPreviewKey,
    getKnovLinkRegexp,
    getTdpEmbedPreviewKey,
} from 'components/shared/LinkPreview'
import { extractOrdinalOutpointFromUrl } from 'components/shared/ordinalUrlUtils'
import { getText, getImages } from '../../lib/value'
import {
    addSuppressedPreviewUrl,
    areSamePreviewUrl,
    filterSuppressedPreviewUrls,
    pruneSuppressedPreviewUrls,
} from './payloadRemoval'
import { isMobile, isIOS, isAndroid } from 'lib/device'
import answerModel from 'components/answers/answerModel'
import {
    answerMatchesKnovEmbedTarget,
    filterEmbedsForKnovEmbedLinks,
    getKnovEmbedTargets,
    getMatchedKnovEmbedTargetKeys,
} from 'components/answers/knovEmbedLinks'
import styles from './new-answer.module.scss'
import cn from 'classnames'
import * as cache from 'state/cache'
import { draftAnswerStore } from 'state/draftAnswerStore'
import AnswerUploadManager from './answerUploadManager'
import { resolveComposerVideoPreviewSource } from './videoPreviewSource'
import {
    hasPendingLocalMediaForSubmit,
    hasUnresolvedLocalVideoForSubmit,
    mergeCompletedAnswerMediaForSubmit,
    prepareAnswerImagesForSubmit,
} from './answerSubmitMedia'
import {
    MAX_COMPOSER_IMAGES,
    composerImagePayloadItems,
    composerImagePreviewUrls,
    composerImageValues,
    removeComposerImage,
} from './composerImages'
import {
    mergeNbComposerMediaInputAttrs,
    partitionNbComposerMediaInputs,
} from './nbComposerMediaInputs'
import AiInputUploadManager from './aiInputUploadManager'
import { resolveCompletedDraftUploads } from './aiActionDraftUploadResolution'
import { focusEditorAtEndSafely } from './editorFocusSafety'
import {
    PICKED_MEDIA_SELECTORS,
    shouldExpandCompactComposerOnPointerDown,
    shouldPreventComposerControlFocusTransfer,
    resolveKeyboardDismissFold,
    shouldProgrammaticallyFocusInlineComposer,
    shouldRequestScrollToBottomOnKeyboardOpen,
    shouldTakeComposerTapFocus,
} from './inlineComposerFocusBehavior'
import { holdKeyboardAcrossHandoff, shouldHoldKeyboardAcrossHandoff } from 'lib/keyboardKeeper'
import { debugCopy } from 'utils'
import { isEqual, debounce, defer } from 'lodash'
import KnovAgentButtonUIMain from '../quests/KnovAgentButtonUIMain'
import {
    getAgentModelMentionDetails,
    getDefaultAgentModel,
    resolveAgentModelSelectionId,
} from 'lib/agentModels'
import { cacheAnswer, getCachedAnswer, cacheQuest } from 'state/cache'
import { quoteMainEnabled } from 'lib/quoteMain'
import { revealBottomEditorCaret } from 'lib/bottomEditor'
import useCachedAnswer from 'refactor/hooks/api/useCachedAnswer'
import useGetAnswer from 'refactor/hooks/api/useGetAnswer'
import ErrorBoundary from 'components/shared/ErrorBoundary'
import CableApp from '../../actioncable'
import { withChannelAuth } from 'state/channels/channelAuth'
import { isPanelLeft, isPanelCenter, isPanelRight } from 'state/imperativeApis/swiperApi'
import usePanelContext from 'refactor/hooks/usePanelContext'
import StreamIcon from '../quests/StreamIcon'
import questModel from 'components/quests/questModel'
import { clearPublishedNewThreadContext } from 'components/quests/newThreadDraftRecovery'
import { openCenter, withDepth } from 'components/filters/useStreamFilters'
import { takePendingComposerSeed } from 'state/pendingComposerSeed'
import { v4 as uuid } from 'uuid'
import useOpenRight from 'refactor/hooks/useOpenRight'
import useStore from 'state/knovStore'
import { hasPendingAnswerOutboxItem } from 'state/pendingAnswerOutbox'
import { initWallet } from 'wallets/wallets'
import {
    subscribePaymentUpdate,
    isTerminalPaymentStatus,
    isSuccessfulPaymentStatus,
} from 'state/channels/useUserChannel'
import { toast } from 'utils/toast'
import { shortTxid } from 'lib/bsv-util'
import { TextSelection } from '@tiptap/pm/state'
import { focusActionTokenPromptNowAndAfterRender } from 'components/shared/tiptap/actionTokenPromptFocus'
import { commitPendingExactMention } from 'components/shared/tiptap/MentionSuggestion'
import { commitPendingAutolinks } from 'components/shared/tiptap/pendingAutolinks'
import Quill from 'quill'
import { confirmAsync } from 'components/shared/confirmUtils'
import { confirmBsvPaymentTray } from 'utils/bsvPaymentConfirmation'
import { generateFingerprint } from '../../utils'
import {
    actionDisplayNameForModel,
    canonicalActionTagFor,
    findEffectiveModelForAction,
    findModelByActionTag,
    getActionPricingContext,
    getPublicModelCostWithDuration,
    getPrivateModelCostWithDuration,
    normalizeActionTag,
    usdToSats,
} from 'state/aiModels'
import {
    buildActionDefinitions,
    toolActionDispatchMetadataForCommandTag,
} from 'state/actionDefinitions'
import { DEFAULT_VIDEO_MODEL } from 'state/defaultAiModels'
import { COMMAND_TAG_PREVIEW_CONFIG_OVERRIDES } from 'components/answers/knownActionTags'
import {
    inferActionCommandCanRemixImage,
    inferActionCommandModelType,
} from 'components/shared/tiptap/actionCommandIconFlow'
import {
    deserializePromptReferenceMentions,
    promptReferenceLabelsForAttrs,
    serializePromptReferenceMentions,
} from 'utils/aiPromptReferences'
import {
    confirmAiPaymentTray as confirmSharedAiPaymentTray,
    parseDurationFromContent,
    validateDurationForModel,
    confirmInvalidDuration,
} from 'utils/actionCommandFeeConfirmation'
import { serializeAiActionInputs } from 'utils/aiActionInputTransport'
import { modelInputKinds } from 'utils/aiActionInputCapabilities'
import { getDefaultAspectRatioForActionCommand } from 'utils/actionCommandMetadata'
import { actionProducesGeneratedOutput } from 'utils/actionOutputPolicy'
import {
    explicitVideoKeyframePayload,
    videoKeyframeInputs,
    videoKeyframeInputsFromFormState,
} from 'utils/aiVideoKeyframes'
import { bsvAmountToSats } from 'utils/bsvAmountInput'
import { isPublicAiPricingContext } from 'utils/questPricingContext'
import AiMagicWandMenuButton from 'components/quests/AiMagicWandMenuButton'
import {
    POST_MEDIA_ACTION_TOOLTIP,
    applyPostMediaOverridesToAction,
    buildPostMediaAiAction,
    buildPostMediaActionUsageAnalytics,
    canLaunchExistingPostMediaAction,
    canUsePostMediaActions,
    enableMediaRemixPromptInput,
    finalizePostMediaActionsForSubmit,
    isSimplePostMediaAction,
    postMediaActionKindForTag,
    postMediaPromptCraftFeeForAction,
    postMediaSourceReference,
    postMediaTargetActionTag,
} from 'components/answers/postMediaActions'
import { getAnswerDisplayText } from 'components/answers/answerDisplayText'
import openSourceAnswerThread from 'utils/openSourceAnswerThread'
import PostMediaActionMenuButton from 'components/answers/PostMediaActionMenuButton'
import PostMediaSettingsDialog from 'components/answers/PostMediaSettingsDialog'
import DiceButton from 'components/quests/DiceButton'
import Raffle from './Raffle'
import { userHasFeature } from 'refactor/hooks/useUserHasFeature'
import AiActionPreview from 'components/shared/tiptap/AiActionPreview'
import { aiActionPromptValidationMessage } from 'components/shared/aiActionPromptValidation'
import PayActionCard from 'components/shared/tiptap/PayActionCard'
import {
    getComposerPaymentIntent,
    restoreComposerPaymentIntents,
} from 'utils/composerPaymentIntent'
import {
    parsePayGroup,
    paySubmissionInputs,
    formatPayGroup,
    normalizePay,
} from 'utils/payActionPrompt'
import queryClient from 'api/queryClient'
import ReplyTargetQuote from 'components/answers/ReplyTargetQuote'
import ReplyModalCrumb, {
    quoteRepeatsCrumb,
    threadHeadPost,
} from 'components/answers/ReplyModalCrumb'
import ComposerAutoQuote from 'components/answers/ComposerAutoQuote'
import TitleDraftManager from 'lib/titleDraftManager'
import WithSelection from 'components/shared/WithSelection'
import { PEOPLE_CHATS_QUERY_KEY } from 'refactor/hooks/api/useGetPeopleChats'
import { operationDiagnostics } from 'utils/operationDiagnostics'
import { replySubmitDiagnostics } from 'utils/replySubmitDiagnostics'
import { describeTarget } from 'utils/replySubmitDiagnosticsCore'
import { currentUserSnapshot } from 'lib/currentUserSnapshot'

const ACTION_PROMPT_SENTINEL = '\u00a0'
const Delta = Quill.import('delta')
const POLL_DURATION_PRESETS = [
    { label: '1h', minutes: 60 },
    { label: '6h', minutes: 360 },
    { label: '12h', minutes: 720 },
    { label: '1d', minutes: 1440 },
    { label: '3d', minutes: 4320 },
    { label: '7d', minutes: 10080 },
]
const DEFAULT_POLL_DURATION_MINUTES = 1440
const MIN_POLL_OPTIONS = 2
const MAX_POLL_OPTIONS = 4
const MAX_POLL_OPTION_LENGTH = 50
const AI_ACTION_INPUT_VALIDATION_CLEAR_MS = 2600
// How long after a press on a composer control the blur it causes is excused
// from folding a compact composer (see armInlineCollapseSkip). Long enough for
// a phone's synthesized mousedown to arrive after its touchstart.
const INLINE_COLLAPSE_SKIP_WINDOW_MS = 600
// How long a phone's composer pill takes to open out of its folded line, or to
// fold back (animateCompactComposerResize).
const COMPACT_COMPOSER_RESIZE_MS = 240
const PROMPT_OPTIONAL_COMMAND_TAGS = new Set(['gif', 'screenshot', 'resize', 'extend', 'stitch'])
const IMAGE_REQUIRED_COMMAND_TAGS = new Set(['grokvideo1.5'])
const COMMAND_TAG_DISPLAY_NAME_OVERRIDES = {
    extend: 'Extend',
    gif: 'GIF',
    nb: 'Nano Banana 2',
    nbpro: 'Nano Banana Pro',
    screenshot: 'Screenshot',
    stitch: 'Smart Stitch',
}
const AI_ACTION_USAGE_SOURCE_UNKNOWN = 'unknown'

const modelActionPrice = (model, isPublic, durationSeconds = null, pricingContext = null) => {
    if (!model) return 0
    return isPublic
        ? getPublicModelCostWithDuration(model, durationSeconds, pricingContext)
        : getPrivateModelCostWithDuration(model, durationSeconds, pricingContext)
}

const formatAiActionPrice = price => {
    const numericPrice = Number(price)
    if (!Number.isFinite(numericPrice) || numericPrice <= 0) return 'FREE'
    return `$${numericPrice.toFixed(2)}`
}

const aiActionModelDisplayName = (model, fallbackTag) => {
    const tag = normalizeActionTag(fallbackTag)
    return COMMAND_TAG_DISPLAY_NAME_OVERRIDES[tag] || actionDisplayNameForModel(model, tag) || tag
}

const aiActionTokenDisplayName = (model, fallbackTag) => {
    const tag = normalizeActionTag(fallbackTag)
    return actionDisplayNameForModel(model, tag)
}

const actionPreviewConfig = (tag, model = null) => {
    const normalizedTag = normalizeActionTag(tag)
    const override = COMMAND_TAG_PREVIEW_CONFIG_OVERRIDES[normalizedTag] || {}

    if (model && Array.isArray(model.supportedInputs)) {
        return {
            ...override,
            supportedInputs: new Set(modelInputKinds(model)),
            supportsReferences: modelSupportsReferences(model),
        }
    }
    if (override.supportedInputs) {
        return { ...override, supportedInputs: new Set(override.supportedInputs) }
    }
    return { ...override, supportedInputs: new Set(modelInputKinds(model)) }
}

const modelSupportsReferences = model => {
    if (!model) return false
    return (
        Number(model.referenceImageLimit || 0) > 0 ||
        Number(model.referenceVideoLimit || 0) > 0 ||
        Number(model.referenceAudioLimit || 0) > 0
    )
}

const stateSelector = (state, props) => ({
    // Override answer from cache.
    //newAnswer: props.newAnswer,
    aiModels: state.aiModels,
    actionDefinitions: state.actionDefinitions,
    activeSpaceId: state.activeSpaceId,
    globalAgentModel: resolveAgentModelSelectionId(
        state.questAgentModels?.[props.quest?.id],
        state.agentModelConfig,
        state.aiModels,
    ),
    agentReplyPendingStatus: state.agentReplyPendingStatus?.[props.quest?.id],
    DEBUG: state.DEBUG,
    currentUserBsvWalletType: state.currentUserBsvWalletType,
    exchangeRate: state.exchangeRate,
})

const INTERRUPT_POSTING = Symbol('INTERRUPT_POST') // sentinel value for interrupting post action

const activeStorageBlobPreviewUrl = (signedId, filename = null) => {
    if (!signedId) return null
    const baseUrl = typeof window !== 'undefined' && window.knovApiUrl ? window.knovApiUrl : ''
    const safeFilename = encodeURIComponent((filename || 'upload').split('/').pop() || 'upload')
    return `${baseUrl}/rails/active_storage/blobs/redirect/${encodeURIComponent(
        signedId,
    )}/${safeFilename}`
}

class NewAnswer extends React.PureComponent {
    constructor(props) {
        super(props)

        this.CHAR_LIMIT = 280
        this.ORIG_COLOR = 'rgb(102, 102, 102)'
        this.LIMIT_COLOR = 'red'

        // debounced version of the save method to prevent excessive saving
        // Use shorter debounce in modal (closeModal prop) so save fires before quick close
        const debounceMs = props.closeModal ? 150 : 500
        this.debouncedSaveDraft = debounce(this.saveDraftToLocalStorage, debounceMs)
        // debounced version for title draft saving - very short to prevent data loss
        this.debouncedSaveTitleDraft = debounce(this.saveToTitleDrafts, 200)
        const initialDraftActionRequests = this.ensureAiActionClientIds(
            this.getActionRequestsFromSource(props.newAnswer),
        )
        const initialTitleDraftId = props.isTitle ? uuid() : null
        this.state = {
            // Which offered auto-quote the reader has refused, by answer id.
            dismissedAutoQuoteId: null,
            // Bumped when the reader asks for a quote, so the pill runs its
            // attention pass. Only an explicit ask animates: the offer that
            // simply appears under your own trailing message is not news.
            quoteAttention: { key: 0, kind: 'enter' },
            hover: false,
            linkPreviews: [],
            streamEmbeds: [],
            streamEmbedsLoading: false,
            isValidContent: initialDraftActionRequests.length > 0,
            embedsLoading: false,
            embedLoadingPromise: null,
            waitingForEmbeds: false,
            modelPreviewProgress: 0,
            currentDraftId: initialTitleDraftId, // ID of the draft being edited
            isDraftLoaded: false, // Whether we loaded an existing draft
            titleDraftCount: props.isTitle ? TitleDraftManager.getCount() : 0, // Number of saved title drafts
            uploadProgress: { recording: null, files: {} },
            inlineComposerExpanded: !(props.inlineCompactComposer || props.collapsibleComposer),
            hiddenPreviewUrls: [],
            suppressedPreviewUrls: [],
            draftActionRequests: initialDraftActionRequests,
            aiActionInputValidation: null,
            aiInputMediaTree: { questId: null, texts: [], images: [], videos: [], audios: [] },
            aiInputMediaTreeLoading: false,
            isPosting: false,
            isUpdatingThreadReplyPolicy: false,
        }

        this.urlPreviewChannel = null
        this._urlPreviewTimeout = null
        this._lastPreviewUrls = []
        this._draftActionRequestsSnapshot = this.state.draftActionRequests || []
        this._draftActionRequestsRevision = 0
        this._pendingActionRequestsByClientId = {}
        this._draftActionRequestsSnapshot.forEach(action => {
            const actionId = action?.id || action?.client_id
            if (actionId) {
                this._pendingActionRequestsByClientId[actionId] = action
            }
        })
        this._pendingProgrammaticSuppressedPreviewUrls = []
        this._nbComposerMediaFingerprint = null
        this._postingDraftId = null
        this._isUnmounted = false
        this._composerDiagnosticId = operationDiagnostics.composerId()
        this._replySubmitDiagnostics = replySubmitDiagnostics.createComposer()
        this.aiInputUploadManager = new AiInputUploadManager({
            getAnswerId: () => this.props.newAnswer?.id,
            onChange: () => this.handleAiInputUploadChange(),
            onError: () => {
                this.showNotice('Unable to upload AI input. Please try again.', {
                    variant: 'error',
                    durationMs: 6500,
                })
            },
        })
        this.uploadManager = new AnswerUploadManager({
            getAnswerId: () => this.getComposeAnswerId(),
            setUploadProgress: updater =>
                this.setState(state => ({
                    uploadProgress: updater(
                        state?.uploadProgress || { recording: null, files: {} },
                    ),
                })),
            setWaitingForEmbeds: waiting => this.setState({ waitingForEmbeds: waiting }),
            setModelPreviewProgress: pct => this.setState({ modelPreviewProgress: pct }),
            onMediaUploaded: (patch, answerId) => {
                if (answerId !== this.getComposeAnswerId()) return
                this.syncNbActionRequestsWithComposerMedia()
                this.scheduleDraftPersistence()
            },
            onUploadError: message => {
                this.showNotice(message, { variant: 'error' })
            },
            startUploadDiagnostic: mediaKind =>
                this.startComposerDiagnostic('composer_media_upload', {
                    media_kind: mediaKind,
                }),
        })
        this._currentTitleDraftId = initialTitleDraftId
    }

    showNotice = (message, options = {}) => {
        useStore.getState().showGlobalNotice(message, options)
    }

    diagnosticMediaKind = () => {
        try {
            const draftId =
                this.props.newAnswer?.id || this._currentTitleDraftId || this.state.currentDraftId
            const answer = (draftId && getCachedAnswer(draftId)) || this.props.newAnswer || {}
            const image = composerImageValues(answer).length > 0
            const video = !!(answer.recording || answer.recording_url)
            const file = !!(answer.files?.length || answer.file_urls?.length)
            if ([image, video, file].filter(Boolean).length > 1) return 'mixed'
            if (video) return 'video'
            if (image) return 'image'
            if (file) return 'file'
        } catch {
            // Diagnostics must not affect composing when a draft has unexpected media state.
        }
        return 'none'
    }

    diagnosticLinkCount = delta => {
        try {
            return extractUrls(delta || {}).knovEmbedLinks.length
        } catch {
            return 0
        }
    }

    startComposerDiagnostic = (operation, context = {}) => {
        if (!this._composerDiagnosticId) return null
        return operationDiagnostics.start(
            this.props.closeModal ? 'modal' : 'inline',
            this._composerDiagnosticId,
            operation,
            context,
        )
    }

    // The phone pill's height before it opens or folds, so componentDidUpdate
    // can run the change as a motion rather than a jump.
    getSnapshotBeforeUpdate(prevProps, prevState) {
        if (
            !this.props.collapsibleComposer ||
            prevState?.inlineComposerExpanded === this.state.inlineComposerExpanded
        ) {
            return null
        }
        const root = this.containerRef?.current
        if (!root) return null
        return { compactComposerHeight: root.getBoundingClientRect().height }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (snapshot?.compactComposerHeight != null) {
            // An unfold aims its motion at the open pill's size, and a long
            // draft's field is capped by the room the open pill leaves it: the
            // panel measures that first (PanelContainer).
            if (this.state.inlineComposerExpanded) {
                this.props.panelContext?.measureBottomEditorField?.()
            }
            this.animateCompactComposerResize(snapshot.compactComposerHeight)
        }
        // bottom_editor_dock: the pill folds when its own panel's keyboard goes.
        // The fold the panel controller sends at that moment reaches only the
        // composer last registered for the page (editorApi), and every thread
        // panel the swiper keeps mounted registers its own as it re-renders.
        if (
            this.props.collapsibleComposer &&
            this.props.panelContext?.bottomEditorDocked &&
            prevProps.panelContext?.keyboardOpen &&
            !this.props.panelContext?.keyboardOpen
        ) {
            this.foldCompactComposerForKeyboardDismiss()
        }
        // A reply pill pressed on the message above is an explicit ask for
        // this quote, so it overrides an earlier refusal of the same message.
        if (
            this.props.autoQuoteRequestSeq &&
            this.props.autoQuoteRequestSeq !== prevProps?.autoQuoteRequestSeq
        ) {
            // Whether the pill has to arrive or is already sitting there decides
            // which pass it runs. Judged by what was showing BEFORE this update:
            // the props already carry the new pin, so asking the current offer
            // would say "showing" for every fresh press and run the pulse where
            // the entrance belongs. It was there only if the same message was
            // pinned before and the reader had not refused it.
            const prevId = prevProps?.autoQuoteAnswer?.id
                ? String(prevProps.autoQuoteAnswer.id)
                : null
            const nextId = this.props.autoQuoteAnswer?.id
                ? String(this.props.autoQuoteAnswer.id)
                : null
            const wasShowing =
                !!prevId && prevId === nextId && this.state.dismissedAutoQuoteId !== prevId
            this.setState(
                {
                    dismissedAutoQuoteId: null,
                    quoteAttention: {
                        key: this.state.quoteAttention.key + 1,
                        kind: wasShowing ? 'pulse' : 'enter',
                    },
                    // A quote sent into a collapsed composer opens it: the quote
                    // row only renders in the open state, and the press was an
                    // ask to start typing.
                    ...(this.isCompactComposer() ? { inlineComposerExpanded: true } : null),
                },
                this.reseatCaretAfterLayout,
            )
        }
        // The quote removed: the field moves up under the caret.
        if (
            this.state.dismissedAutoQuoteId &&
            this.state.dismissedAutoQuoteId !== prevState?.dismissedAutoQuoteId
        ) {
            this.reseatCaretAfterLayout()
        }
        // A composer that stops being collapsible (the pill unmounts, the
        // thread leaves centre) must not stay folded with no way to open.
        if (
            prevProps?.collapsibleComposer &&
            !this.props.collapsibleComposer &&
            !this.isCompactComposer() &&
            !this.state.inlineComposerExpanded
        ) {
            this.setState({ inlineComposerExpanded: true })
        }
        this.reportQuoteTarget()

        try {
            const prevUrl = prevProps?.newAnswer?.recording_url
            const curUrl = this.props?.newAnswer?.recording_url
            if (curUrl && curUrl !== prevUrl) {
                const vid = this.videoPreviewRef?.current
                if (vid && typeof vid.load === 'function') {
                    try {
                        vid.load()
                    } catch (e) {}
                }
            }
        } catch (e) {}

        if (
            this._pendingInlineFocusAfterExpand &&
            this.isCompactComposer() &&
            isMobile &&
            !prevState?.inlineComposerExpanded &&
            this.state.inlineComposerExpanded
        ) {
            this._pendingInlineFocusAfterExpand = false
            this.refocusInlineComposerAfterExpand()
        }
    }

    // NOTE: Selection swipe guard is registered via <WithSelection/>

    _lastInlineBoundaryPointerDownInside = false
    _pendingInlineFocusAfterExpand = false

    componentWillUnmount = () => {
        this._isUnmounted = true
        this._replySubmitDiagnostics.crumb('composer_unmount', {
            composer: this.diagnosticComposerKind(),
        })
        this._replySubmitDiagnostics.closed({
            how: 'unmount',
            composer: this.diagnosticComposerKind(),
            has_draft: !!this.state.isValidContent,
        })
        if (this.keyboardDismissFoldRetryTimer) {
            clearTimeout(this.keyboardDismissFoldRetryTimer)
            this.keyboardDismissFoldRetryTimer = null
        }
        if (this.props.isTitle) this.debouncedSaveTitleDraft?.flush?.()
        else this.debouncedSaveDraft?.flush?.()
        window.removeEventListener('beforeunload', this.flushDraftPersistence)
        this.uploadManager?.dispose?.()
        this.unsubscribeUrlPreviewChannel()
        window.removeEventListener('knov:insert-ai-action-from-media', this.insertAiActionFromMedia)
        if (this.inlineCompactAutoCollapseTimer) {
            clearTimeout(this.inlineCompactAutoCollapseTimer)
            this.inlineCompactAutoCollapseTimer = null
        }
        if (this.compactComposerResizeAnimation) {
            this.compactComposerResizeAnimation.oncancel = null
            this.compactComposerResizeAnimation.cancel()
            this.compactComposerResizeAnimation = null
        }
        if (this.aiActionInputValidationClearTimer) {
            clearTimeout(this.aiActionInputValidationClearTimer)
            this.aiActionInputValidationClearTimer = null
        }
        if (this.inlineComposerCollapseCheckTimer) {
            clearTimeout(this.inlineComposerCollapseCheckTimer)
            this.inlineComposerCollapseCheckTimer = null
        }
        if (this.inlineComposerMenuOpenSkipClearTimer) {
            clearTimeout(this.inlineComposerMenuOpenSkipClearTimer)
            this.inlineComposerMenuOpenSkipClearTimer = null
        }
        if (this._onDraftModalClosed) {
            window.removeEventListener('draft_modal_closed', this._onDraftModalClosed)
        }
        document.removeEventListener('mousedown', this.onDocumentInlineBoundaryPointerDown, true)
        document.removeEventListener('touchstart', this.onDocumentInlineBoundaryPointerDown, true)
        document.removeEventListener('keydown', this.onQuoteEscape, true)
    }

    isPollMode = (answer = this.props.newAnswer) => answer?.message_type === 'poll'

    getPollOptions = (answer = this.props.newAnswer) => {
        const draftOptions = Array.isArray(answer?.poll_options)
            ? answer.poll_options.slice(0, MAX_POLL_OPTIONS)
            : []
        return draftOptions.length > 0 ? draftOptions : ['', '']
    }

    getPollDurationMinutes = (answer = this.props.newAnswer) =>
        answer?.poll_duration_minutes || DEFAULT_POLL_DURATION_MINUTES

    getPollValidationErrors = (answer = this.props.newAnswer) => {
        if (!this.isPollMode(answer)) return []

        const pollOptions = this.getPollOptions(answer).map(option => option.toString().trim())
        const errors = []

        if (pollOptions.length < MIN_POLL_OPTIONS || pollOptions.length > MAX_POLL_OPTIONS) {
            errors.push(`Add between ${MIN_POLL_OPTIONS} and ${MAX_POLL_OPTIONS} options.`)
        }

        if (pollOptions.some(option => option.length === 0)) {
            errors.push('Fill in every poll option.')
        }

        if (pollOptions.some(option => option.length > MAX_POLL_OPTION_LENGTH)) {
            errors.push(`Keep each option at or below ${MAX_POLL_OPTION_LENGTH} characters.`)
        }

        const normalizedOptions = pollOptions.filter(Boolean).map(option => option.toLowerCase())
        if (normalizedOptions.length !== new Set(normalizedOptions).size) {
            errors.push('Each poll option must be unique.')
        }

        if (
            !POLL_DURATION_PRESETS.some(
                preset => preset.minutes === this.getPollDurationMinutes(answer),
            )
        ) {
            errors.push('Choose one of the preset poll durations.')
        }

        return errors
    }

    updatePollDraft = patch => {
        if (!this.props.newAnswer?.id) return

        const nextAnswer = {
            ...(this.props.newAnswer || {}),
            ...patch,
        }
        cache.updateCachedAnswer(this.props.newAnswer.id, patch)
        this.setState({
            isValidContent: answerModel.hasContent(this.getNewAnswer(null, nextAnswer)),
        })
    }

    // Thread reply policy (title composer): who may post into this thread's main
    // thread once published. Stored on the draft quest so publish carries it into
    // api.createQuest. Mirrors the PermsOptions draft pattern: mutate the quest
    // object in place (the inline stream composer holds it in local state) and
    // recache; forceUpdate repaints since the prop reference doesn't change.
    //
    // On a published thread (the thread-bottom composer, owner only) the same
    // toggle persists through quests#update instead, optimistically so the row
    // and the rest of the thread chrome flip without waiting on the round trip.
    //
    // Channel threads add a middle setting, members-only (members_only_replies),
    // which the author can opt into. A new channel thread starts open to replies;
    // only posting new threads needs membership.
    mainThreadReplyPolicy = quest => {
        if (quest?.replies_enabled === false) return 'author'
        if (!quest?.team_id) return 'anyone'
        const membersOnly = !!quest.members_only_replies
        return membersOnly ? 'members' : 'anyone'
    }

    nextMainThreadReplyPolicy = quest => {
        const current = this.mainThreadReplyPolicy(quest)
        if (!quest?.team_id) return current === 'author' ? 'anyone' : 'author'
        return { members: 'anyone', anyone: 'author', author: 'members' }[current]
    }

    setMainThreadReplyPolicy = async policy => {
        const quest = this.props.quest
        if (!quest?.id) return
        if (!quest.is_draft && this.state.isUpdatingThreadReplyPolicy) return
        const current = this.mainThreadReplyPolicy(quest)
        if (current === policy) return
        const previousFields = {
            replies_enabled: quest.replies_enabled,
            members_only_replies: quest.members_only_replies,
        }
        const nextFields =
            policy === 'author'
                ? { replies_enabled: false }
                : { replies_enabled: true, members_only_replies: policy === 'members' }
        const updatedQuest = { ...quest, ...nextFields }
        Object.assign(quest, updatedQuest)
        if (quest.is_draft) {
            useStore.getState().updateQuest(updatedQuest)
            this.forceUpdate()
            return
        }

        // Paint immediately, but serialize saves and roll back if persistence
        // fails so the label never claims a policy the server did not accept.
        useStore.getState().updateQuest(updatedQuest)
        this.setState({ isUpdatingThreadReplyPolicy: true })

        try {
            const persistedQuest = await api.updateQuest(quest.id, nextFields)
            if (!persistedQuest?.id) throw new Error('Thread reply policy update failed')
            Object.assign(quest, persistedQuest)
        } catch (error) {
            const rolledBackQuest = { ...quest, ...previousFields }
            Object.assign(quest, rolledBackQuest)
            useStore.getState().updateQuest(rolledBackQuest)
            this.showNotice('Could not change who can reply. Please try again.', {
                variant: 'error',
            })
        } finally {
            if (!this._isUnmounted) this.setState({ isUpdatingThreadReplyPolicy: false })
        }
    }

    togglePollMode = () => {
        if (this.isPollMode()) {
            this.updatePollDraft({
                message_type: null,
                poll_options: [],
                poll_duration_minutes: null,
            })
            return
        }

        this.updatePollDraft({
            message_type: 'poll',
            poll_options: this.getPollOptions(),
            poll_duration_minutes: this.getPollDurationMinutes(),
        })
    }

    updatePollOption = (index, value) => {
        const nextOptions = this.getPollOptions()
        nextOptions[index] = value.slice(0, MAX_POLL_OPTION_LENGTH)
        this.updatePollDraft({ poll_options: nextOptions })
    }

    addPollOption = () => {
        const nextOptions = this.getPollOptions()
        if (nextOptions.length >= MAX_POLL_OPTIONS) return
        nextOptions.push('')
        this.updatePollDraft({ poll_options: nextOptions })
    }

    removePollOption = index => {
        const nextOptions = this.getPollOptions()
        if (nextOptions.length <= MIN_POLL_OPTIONS) return
        this.updatePollDraft({
            poll_options: nextOptions.filter((_, optionIndex) => optionIndex !== index),
        })
    }

    updatePollDuration = pollDurationMinutes => {
        this.updatePollDraft({ poll_duration_minutes: pollDurationMinutes })
    }

    subscribeUrlPreviewChannel = () => {
        const answerId = this.props.newAnswer?.id
        if (!answerId) return
        try {
            this.urlPreviewChannel = CableApp.cable.subscriptions.create(
                withChannelAuth({
                    channel: 'AnswerChannel',
                    stream_from: `answer_${answerId}`,
                    answer_id: answerId,
                }),
                {
                    received: data => {
                        try {
                            if (!data || data.message !== 'url_screenshot') return
                            // Worker signals it's safe to enable Post once preview work is queued/attached
                            if (data.status === 'ready' || data.status === 'error') {
                                this.setState({ waitingForEmbeds: false })
                            }
                        } catch (e) {}
                    },
                },
            )
        } catch (e) {}
    }

    unsubscribeUrlPreviewChannel = () => {
        try {
            if (this.urlPreviewChannel) {
                this.urlPreviewChannel.unsubscribe()
                this.urlPreviewChannel = null
            }
        } catch (e) {}
    }

    addRaffleEmbed = () => {
        const parent = this.props.quest?.parent

        if (!parent || !parent.bsv_upvalues) {
            this.showNotice('No upvalues found on the title message', { variant: 'error' })
            return
        }

        // Check if there's already a dice/raffle
        const currentDice = this.props.newAnswer?.dice
        if (currentDice) {
            this.showNotice('You can only have one raffle per message', { variant: 'error' })
            return
        }

        // Filter out upvalues from the title author
        const validUpvalues = parent.bsv_upvalues.filter(upvalue => {
            return upvalue.from_user_id && parent.user_id && upvalue.from_user_id !== parent.user_id
        })

        if (validUpvalues.length === 0) {
            this.showNotice('No eligible participants found for the raffle', { variant: 'error' })
            return
        }

        // Create dice data
        const diceData = {
            type: 'title_upvalue_raffle',
            status: 'preview',
            title_answer_id: parent.id,
            title_user_id: parent.user_id,
            upvalues: validUpvalues, // Store snapshot of valid upvalues only
        }

        // Update answer cache with dice field
        cache.updateCachedAnswer(this.props.newAnswer?.id, { dice: diceData })
    }

    removeRaffle = () => {
        // Clear dice field from answer cache
        cache.updateCachedAnswer(this.props.newAnswer?.id, { dice: null })
    }

    hasFiles = () => !!this.props.newAnswer?.file_urls?.length
    hasVideo = () => !!this.props.newAnswer?.recording_url
    hasImage = deltaJson => !!this.props.newAnswer?.answer_image_url || getImages(deltaJson)

    ensureReplyDraftForQuest = quest => {
        if (!quest?.id) return this.props.newAnswer
        if (this.props.newAnswer?.id) return this.props.newAnswer

        const existingDraftId = draftAnswerStore.getDraftId(quest.id)
        const existingDraft = existingDraftId ? getCachedAnswer(existingDraftId) : null
        if (existingDraft) return existingDraft

        const freshDraft = answerModel.newAnswer({
            quest_id: quest.id,
            space_id: quest.space_id || this.props.activeSpaceId,
            is_draft: true,
        })
        cacheAnswer(freshDraft)
        draftAnswerStore.setDraft(quest.id, freshDraft.id)
        return freshDraft
    }

    getComposeAnswerId = () => {
        return (
            this.props.newAnswer?.id || this.props.quest?.parent?.id || this.props.quest?.parent_id
        )
    }

    rememberComposerPaymentReceipt = receipt => {
        const draftId = this.getComposeAnswerId()
        if (!this._composerPaymentReceipts) this._composerPaymentReceipts = new Map()
        const cachedReceipts = getCachedAnswer(draftId)?.payment_receipts || []
        const receipts =
            this._composerPaymentReceipts.get(draftId) ||
            new Map(cachedReceipts.map(item => [item.payment_id, item]))
        receipts.set(receipt.payment_id, receipt)
        this._composerPaymentReceipts.set(draftId, receipts)
        cache.updateCachedAnswer(draftId, {
            payment_ids: [...receipts.keys()],
            payment_receipts: [...receipts.values()],
            composer_payment_intents: [...(this._composerPaymentIntents || new Map()).entries()],
        })
    }

    getNewAnswer = (
        preFetchedDeltaJson = null,
        baseAnswer = null,
        actionRequestsOverride = null,
    ) => {
        const now = new Date()

        // Set flag to trim whitespace during submission
        this._preparingForSubmission = true
        // Use the pre-fetched delta if available; otherwise, get current from editor
        const rawDeltaJson = preFetchedDeltaJson || this.getDeltaJson()
        const content = getText(rawDeltaJson || {}, {}).trim() // Derive text from the chosen delta
        const actionRequests = this.sanitizeActionRequestsForTransport(
            actionRequestsOverride || this.getActionRequestsForSubmit(rawDeltaJson),
        )
        const deltaJson = this.sanitizeDeltaJsonForTransport(rawDeltaJson)
        this._preparingForSubmission = false

        const composeAnswerId = this.getComposeAnswerId()
        const sourceAnswer =
            baseAnswer ||
            getCachedAnswer(composeAnswerId) ||
            this.props.newAnswer ||
            this.props.quest?.parent ||
            {}
        const {
            suppressed_preview_urls: _suppressedPreviewUrls,
            ...sourceAnswerWithoutPreviewState
        } = sourceAnswer

        const answer = {
            ...sourceAnswerWithoutPreviewState,
            is_draft: false,
            space_id: this.props.activeSpaceId,
            content,
            delta_json: deltaJson,
            action_requests: actionRequests,
            created_at: now,
            updated_at: now,
            position: new Date().getTime(),
            isLocal: true,
            ...(this.props.globalAgentModel ? { agent_model: this.props.globalAgentModel } : {}),
            ...(sourceAnswer?.dice ? { dice: sourceAnswer.dice } : {}),
        }

        const paymentReceipts = this._composerPaymentReceipts?.get(composeAnswerId)
        if (paymentReceipts?.size) {
            answer.payment_ids = [...paymentReceipts.keys()]
            answer.payment_receipts = [...paymentReceipts.values()]
        }

        if (actionRequests.length) {
            answer.ai_runs = this.buildOptimisticAiRunsForActions(actionRequests, answer)
        }

        return answer
    }

    hasCurrentDraftContent = actionRequestsOverride => {
        const answer = this.getNewAnswer()
        if (actionRequestsOverride !== undefined) {
            answer.action_requests = this.ensureAiActionClientIds(actionRequestsOverride)
        }
        return answerModel.hasContent(answer)
    }

    buildOptimisticAiRunsForActions = (actionRequests, answer) => {
        if (!Array.isArray(actionRequests)) return []

        return actionRequests.flatMap((action, index) => {
            const tag = normalizeActionTag(action?.tag)
            if (!actionProducesGeneratedOutput(tag)) return []

            const modelTag =
                tag === 'extend' ? this.extendModelCommandTagForAction(action) || tag : tag
            const model = findModelByActionTag(this.props.aiModels || [], tag)
            const effectiveModel =
                findEffectiveModelForAction(this.props.aiModels || [], modelTag, action) ||
                findModelByActionTag(this.props.aiModels || [], modelTag) ||
                model
            const generationCount = this.generationCountForAction(action)
            const aiRunIds = this.aiRunIdsForAction(action)

            return Array.from({ length: generationCount }, (_, generationIndex) => ({
                id:
                    aiRunIds[generationIndex] ||
                    `local-ai-run-${answer?.id || 'answer'}-${index}-${generationIndex}-${
                        tag || 'action'
                    }`,
                answer_id: answer?.id,
                action_index: index,
                generation_index: generationIndex,
                action_tag: tag || action?.tag || '',
                model_type: action?.model_type || effectiveModel?.modelType || null,
                provider: effectiveModel?.provider || null,
                provider_model: effectiveModel?.name || null,
                provider_job_id: null,
                action_payload: {
                    ...action,
                    ai_run_id: aiRunIds[generationIndex],
                    generation_index: generationIndex,
                    generation_count: generationCount,
                },
                status: 'running',
                output_urls: [],
                ai_model: effectiveModel
                    ? {
                          id: effectiveModel.id,
                          name: effectiveModel.name,
                          human_name: effectiveModel.humanName,
                          model_type: effectiveModel.modelType,
                          provider: effectiveModel.provider,
                          action_tag_name: effectiveModel.actionTagName,
                      }
                    : null,
            }))
        })
    }

    getCurrentDraftAnswerForPersistence = () => {
        const composeAnswerId = this.getComposeAnswerId()
        return (
            getCachedAnswer(composeAnswerId) ||
            this.props.newAnswer ||
            this.props.quest?.parent ||
            {}
        )
    }

    isPersistentDraftPayloadUrl = url => {
        if (!url || typeof url !== 'string') return false
        return !/^(blob|mediastream|data):/i.test(url)
    }

    createAiActionClientId = () => {
        const randomId =
            typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : `${Date.now()}-${Math.random().toString(36).slice(2)}`
        return `ai-action-${randomId}`
    }

    createAiRunId = () => {
        return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : uuid()
    }

    generationCountForAction = action => {
        const rawCount =
            action?.generationCount ??
            action?.generation_count ??
            action?.settings?.generation_count
        const count = Number(rawCount || 1)
        if (!Number.isFinite(count)) return 1
        return Math.min(Math.max(Math.trunc(count), 1), 4)
    }

    aiRunIdsForAction = action => {
        const generationCount = this.generationCountForAction(action)
        const existingIds = [
            ...(Array.isArray(action?.ai_run_ids) ? action.ai_run_ids : []),
            ...(Array.isArray(action?.aiRunIds) ? action.aiRunIds : []),
        ].filter(Boolean)
        const firstRunId = action?.ai_run_id || action?.aiRunId
        if (firstRunId && !existingIds.includes(firstRunId)) {
            existingIds.unshift(firstRunId)
        }

        return Array.from(
            { length: generationCount },
            (_, index) => existingIds[index] || this.createAiRunId(),
        )
    }

    ensureAiActionClientId = action => {
        if (!action || typeof action !== 'object') return action
        const aiRunIds = this.aiRunIdsForAction(action)
        const actionId = action.id || action.client_id || this.createAiActionClientId()
        return {
            ...action,
            id: actionId,
            client_id: actionId,
            ai_run_id: aiRunIds[0],
            ai_run_ids: aiRunIds,
        }
    }

    ensureAiActionClientIds = actionRequests => {
        if (!Array.isArray(actionRequests)) return []
        return actionRequests.map(action => this.ensureAiActionClientId(action))
    }

    getActionRequestsFromSource = source => {
        const actionRequests = source?.action_requests
        return Array.isArray(actionRequests) ? actionRequests : []
    }

    actionTokenReferenceAttrs = (
        attrs,
        { includePromptContent = false, includeDisplayName = false } = {},
    ) => {
        if (!attrs || typeof attrs !== 'object') return attrs

        const tag = normalizeActionTag(attrs.tag)
        const displayName = includeDisplayName
            ? attrs.displayName ||
              attrs.display_name ||
              aiActionTokenDisplayName(findModelByActionTag(this.props.aiModels || [], tag), tag)
            : null
        const serializedPromptContent =
            attrs?.inputs?.text?.value || attrs?.prompt || attrs?.promptContent || null
        const promptContent =
            includePromptContent && (attrs.promptContent || attrs.prompt_content)
                ? attrs.promptContent || attrs.prompt_content
                : includePromptContent && serializedPromptContent
                ? deserializePromptReferenceMentions(serializedPromptContent, {
                      tag,
                      settings: attrs.settings || {},
                      inputs: attrs.inputs || {},
                  })?.trim() || null
                : null
        const supportsPromptInput =
            typeof attrs.supportsPromptInput === 'boolean'
                ? attrs.supportsPromptInput
                : typeof attrs.supports_prompt_input === 'boolean'
                ? attrs.supports_prompt_input
                : null
        const promptPlaceholder =
            attrs.promptPlaceholder === true ||
            (attrs.prompt_is_default === true &&
                (attrs.promptPlacement === 'before' || attrs.prompt_placement === 'before'))
        return this.compactObject({
            tag,
            ai_action_id: this.getAiActionRefId(attrs),
            craftStatus:
                attrs.craftStatus ||
                attrs.craft_status ||
                attrs.craft_context?.craft_status ||
                null,
            promptContent,
            promptPlacement: attrs.promptPlacement || attrs.prompt_placement || null,
            promptPlaceholder: promptPlaceholder || null,
            supportsPromptInput,
            displayName,
            actionDefinitionId: attrs.actionDefinitionId || attrs.action_definition_id || null,
            kind: attrs.kind || attrs.type || null,
            aiModelId: attrs.aiModelId || attrs.ai_model_id || null,
            toolKey: attrs.toolKey || attrs.tool_key || null,
            type: attrs.type || attrs.kind || null,
            modelType: attrs.modelType || attrs.model_type || null,
            defaultDurationSeconds:
                attrs.defaultDurationSeconds || attrs.default_duration_seconds || null,
            defaultAspectRatio: attrs.defaultAspectRatio || attrs.default_aspect_ratio || null,
            durationSeconds: attrs.durationSeconds || attrs.duration_seconds || null,
            aspectRatio: attrs.aspectRatio || attrs.aspect_ratio || null,
            generationCount: attrs.generationCount || attrs.generation_count || null,
            gifFrameRate: attrs.gifFrameRate || attrs.gif_frame_rate || null,
            gifMaxWidth: attrs.gifMaxWidth || attrs.gif_max_width || null,
            screenshotTimestamp: attrs.screenshotTimestamp || attrs.screenshot_timestamp || null,
            extendActionTag: attrs.extendActionTag || attrs.extend_action_tag || null,
            extendModelDisplayName:
                attrs.extendModelDisplayName || attrs.extend_model_display_name || null,
            extendTimestamp: attrs.extendTimestamp || attrs.extend_timestamp || null,
            smartStitch: attrs.smartStitch ?? attrs.smart_stitch ?? null,
            previewTitle: attrs.previewTitle || attrs.preview_title || null,
            remixKind: attrs.remixKind || attrs.remix_kind || null,
            analyticsSource:
                attrs.analyticsSource ||
                attrs.analytics_source ||
                attrs.usageSource ||
                attrs.usage_source ||
                null,
            videoKeyframeMode:
                attrs.videoKeyframeMode === true || attrs.video_keyframe_mode === true
                    ? true
                    : null,
        })
    }

    getActionTokenReferenceMentions = attrs => {
        if (!attrs || typeof attrs !== 'object') return []

        const actionId = this.getAiActionRefId(attrs)
        const currentActions =
            this._draftActionRequestsSnapshot || this.state.draftActionRequests || []
        const action =
            (actionId &&
                currentActions.find(candidate => this.getAiActionRefId(candidate) === actionId)) ||
            null
        const sourceAttrs = action ? this.aiActionToActionTokenAttrs(action) : attrs
        const mentionItems = []
        const seenInputs = new Set()
        const addReferenceMentions = (kind, inputs = []) => {
            inputs.forEach((input, index) => {
                const url = input?.url || null
                const identity = input?.id || url
                if (!identity || seenInputs.has(`${kind}:${identity}`)) return

                seenInputs.add(`${kind}:${identity}`)
                const kindCount = mentionItems.filter(item => item.kind === kind).length + 1
                const label = `${kind}${kindCount}`
                const sourceLabel =
                    String(input?.label || '')
                        .replace(/^\[|\]$/g, '')
                        .trim() || `${label} reference`
                const previewUrl = input?.previewImageUrl || input?.preview_image_url || url

                mentionItems.push({
                    id: `ai-action-reference-${kind}-${identity || index}`,
                    content: label,
                    value: `@${label}`,
                    type: 'reference',
                    denotationChar: '@',
                    link: url || previewUrl || `#${label}`,
                    meta: sourceLabel,
                    kind,
                    previewUrl,
                    url,
                    contentType: input?.contentType || input?.content_type || null,
                })
            })
        }

        addReferenceMentions('image', sourceAttrs?.referenceImageInputs || [])
        addReferenceMentions('video', sourceAttrs?.referenceVideoInputs || [])
        addReferenceMentions('audio', sourceAttrs?.referenceAudioInputs || [])

        return mentionItems
    }

    actionTokenPromptContent = attrs => {
        if (!attrs || typeof attrs !== 'object') return ''
        const promptContent =
            typeof attrs.promptContent === 'string'
                ? attrs.promptContent
                : typeof attrs.prompt_content === 'string'
                ? attrs.prompt_content
                : ''
        return promptContent
            .replace(new RegExp(ACTION_PROMPT_SENTINEL, 'g'), '')
            .replace(/\u200b/g, '')
            .replace(/\u00a0/g, ' ')
            .replace(/[\r\n]+/g, ' ')
            .trim()
    }

    actionTokenSupportsPromptInput = attrs => {
        if (!attrs || typeof attrs !== 'object') return true
        return attrs.supportsPromptInput !== false && attrs.supports_prompt_input !== false
    }

    actionTokenEditorPromptContent = attrs => {
        if (attrs?.promptPlaceholder === true) return ACTION_PROMPT_SENTINEL
        const promptContent = this.actionTokenPromptContent(attrs)
        if (promptContent) return `${promptContent}${ACTION_PROMPT_SENTINEL}`
        return this.actionTokenSupportsPromptInput(attrs) ? ACTION_PROMPT_SENTINEL : ''
    }

    actionTokenInlineContentForAttrs = attrs => {
        return [this.actionTokenNodeForAttrs(attrs)]
    }

    actionTokenNodeForAttrs = attrs => {
        const promptContent = this.actionTokenPromptContent(attrs)
        const editorPromptContent = this.actionTokenEditorPromptContent(attrs)
        const nodeAttrs = {
            ...attrs,
            ...(promptContent ? { promptContent } : {}),
        }
        if (!promptContent) {
            delete nodeAttrs.promptContent
            delete nodeAttrs.prompt_content
        }
        return {
            type: 'actionTag',
            attrs: nodeAttrs,
            ...(editorPromptContent
                ? { content: [{ type: 'text', text: editorPromptContent }] }
                : {}),
        }
    }

    actionTokenContentNodeForAttrs = (schema, attrs) => {
        const editorPromptContent = this.actionTokenEditorPromptContent(attrs)
        return editorPromptContent ? schema.text(editorPromptContent) : null
    }

    actionPromptContentFromRange = range => {
        return this.actionTokenPromptContent({ promptContent: range?.text || '' })
    }

    findActionPromptRangeAfterToken = (instance, pos, node) => {
        if (!instance?.state || typeof pos !== 'number' || node?.type?.name !== 'actionTag') {
            return null
        }

        return {
            from: pos + 1,
            to: pos + node.nodeSize - 1,
            text: node.textContent || '',
            markAttrs: node.attrs || {},
        }
    }

    findLastActionTokenNodeByAttrs = attrs => {
        const instance = this.getEditorInstance()
        const state = instance?.state
        if (!state?.doc) return null

        const actionId = this.getAiActionRefId(attrs)
        let target = null
        state.doc.descendants((node, pos) => {
            if (node.type?.name !== 'actionTag') return true
            const nodeActionId = this.getAiActionRefId(node.attrs)
            if (!actionId || nodeActionId === actionId) {
                target = { node, pos, instance }
            }
            return
        })
        return target
    }

    focusActionTokenPrompt = attrs => {
        if (!this.actionTokenSupportsPromptInput(attrs)) return

        const focusPrompt = () => {
            const target = this.findLastActionTokenNodeByAttrs(attrs)
            const instance = target?.instance
            if (!target || !instance?.state) return false

            const promptContent = this.actionTokenPromptContent({
                promptContent: target.node?.textContent || target.node?.attrs?.promptContent || '',
            })
            const targetPos = Math.min(
                Math.max(target.pos + 1 + promptContent.length, 0),
                instance.state.doc.content.size,
            )
            if (typeof instance.chain === 'function') {
                instance
                    .chain()
                    .focus(undefined, { scrollIntoView: false })
                    .setTextSelection(targetPos)
                    .run()
                return true
            }

            if (instance.view?.dom) {
                try {
                    instance.view.dom.focus({ preventScroll: true })
                } catch (e) {
                    instance.view.dom.focus()
                }
                const { selection } = instance.state
                if (!selection?.empty || selection?.from !== targetPos) {
                    instance.view.dispatch(
                        instance.state.tr.setSelection(
                            TextSelection.create(instance.state.doc, targetPos),
                        ),
                    )
                }
                return true
            }

            if (instance.commands?.setTextSelection) {
                instance.commands.focus?.(undefined, { scrollIntoView: false })
                instance.commands.setTextSelection(targetPos)
                return true
            }

            return
        }

        focusActionTokenPromptNowAndAfterRender(focusPrompt)
    }

    actionTokenAttrsTransaction = (target, attrs) => {
        const { pos, instance, node } = target || {}
        if (!instance?.state || typeof pos !== 'number' || !node) return null

        const promptContent = this.actionTokenPromptContent(attrs)
        const nextAttrs = { ...(attrs || {}) }
        if (promptContent) {
            nextAttrs.promptContent = promptContent
        } else {
            delete nextAttrs.promptContent
            delete nextAttrs.prompt_content
        }

        let tr = instance.state.tr.setNodeMarkup(pos, undefined, nextAttrs)
        const promptRange = this.findActionPromptRangeAfterToken(instance, pos, node)
        const currentPromptContent = this.actionPromptContentFromRange(promptRange)
        const currentEditorPromptContent =
            typeof promptRange?.text === 'string' ? promptRange.text : ''
        const nextEditorPromptContent = this.actionTokenEditorPromptContent(nextAttrs)
        const nextPromptNode = this.actionTokenContentNodeForAttrs(instance.state.schema, nextAttrs)
        const selectionInsidePrompt =
            promptRange &&
            instance.state.selection.from >= promptRange.from &&
            instance.state.selection.to <= promptRange.to

        if (
            promptRange &&
            (currentPromptContent !== promptContent ||
                (!selectionInsidePrompt && currentEditorPromptContent !== nextEditorPromptContent))
        ) {
            const from = tr.mapping.map(promptRange.from)
            const to = tr.mapping.map(promptRange.to)
            tr = nextPromptNode ? tr.replaceWith(from, to, nextPromptNode) : tr.delete(from, to)
        }

        return tr
    }

    replaceActionTokenNodeTransaction = (target, attrs) => {
        const { pos, instance, node } = target || {}
        if (!instance?.state || typeof pos !== 'number' || !node) return null

        const from = pos
        const to = pos + node.nodeSize
        const contentNode = this.actionTokenNodeForAttrs(attrs)
        const content = this.actionTokenContentNodeForAttrs(
            instance.state.schema,
            contentNode.attrs,
        )
        const nextNode = node.type.create(contentNode.attrs, content, node.marks)

        return instance.state.tr.replaceWith(from, to, nextNode)
    }

    deleteActionTokenRangeTransaction = target => {
        const { pos, instance, node } = target || {}
        if (!instance?.state || typeof pos !== 'number' || !node) return null
        const from = pos
        let to = pos + node.nodeSize
        const nextChar = instance.state.doc.textBetween(
            to,
            Math.min(to + 1, instance.state.doc.content.size),
            '',
            '',
        )
        if (nextChar === ' ') to += 1
        return instance.state.tr.delete(from, to)
    }

    getAiActionRefId = attrsOrAction => {
        if (!attrsOrAction || typeof attrsOrAction !== 'object') return null
        return (
            attrsOrAction.ai_action_id ||
            attrsOrAction.actionId ||
            attrsOrAction.id ||
            attrsOrAction.client_id ||
            null
        )
    }

    sanitizeActionTokenAttrsForTransport = attrs => {
        if (!attrs || typeof attrs !== 'object') return attrs

        return this.actionTokenReferenceAttrs(attrs)
    }

    sanitizeDeltaJsonForTransport = deltaJson => {
        if (!deltaJson?.ops || !Array.isArray(deltaJson.ops)) return deltaJson

        return {
            ...deltaJson,
            ops: deltaJson.ops.map(op => {
                const attrs = op?.attributes?.actionTagAttrs
                if (!attrs) return op

                return {
                    ...op,
                    attributes: {
                        ...op.attributes,
                        actionTagAttrs: this.sanitizeActionTokenAttrsForTransport(attrs),
                    },
                }
            }),
        }
    }

    sanitizeActionRequestsForTransport = actionRequests => {
        if (!Array.isArray(actionRequests)) return []
        return this.ensureAiActionClientIds(finalizePostMediaActionsForSubmit(actionRequests)).map(
            action => ({
                ...action,
                inputs: serializeAiActionInputs(
                    action?.inputs || {},
                    input => this.aiMediaInputPayload(input),
                    url => this.isPersistentDraftPayloadUrl(url),
                    !!this.getCurrentDraftAnswerForPersistence()?.answer_image,
                ),
            }),
        )
    }

    withPaymentModeForActionRequests = (actionRequests, paymentMode) => {
        if (!paymentMode || !Array.isArray(actionRequests)) return actionRequests

        return actionRequests.map(action => ({
            ...action,
            payment_mode: paymentMode,
        }))
    }

    sanitizeAiMediaDescriptor = (media, { fallbackUrl = null, fallbackPreviewUrl = null } = {}) => {
        if (!media || typeof media !== 'object') return null
        const url = this.isPersistentDraftPayloadUrl(media.url) ? media.url : fallbackUrl
        const previewUrl = this.isPersistentDraftPayloadUrl(
            media.preview_image_url || media.previewImageUrl,
        )
            ? media.preview_image_url || media.previewImageUrl
            : fallbackPreviewUrl

        return this.aiMediaInputPayload({
            ...media,
            url,
            preview_image_url: previewUrl,
        })
    }

    isPersistableAiMediaDescriptor = media =>
        !!media?.signed_id || this.isPersistentDraftPayloadUrl(media?.url)

    sanitizeAiMediaList = (items, options = {}) =>
        (Array.isArray(items) ? items : []).map(item => {
            const payload = this.sanitizeAiMediaDescriptor(item, options)
            if (
                payload?.signed_id ||
                payload?.attachment_id ||
                payload?.blob_id ||
                this.isPersistentDraftPayloadUrl(payload?.url)
            )
                return payload
            return { ...payload, pending_upload: true }
        })

    sanitizeAiReferences = (references, options = {}) => {
        if (!references || typeof references !== 'object') return null
        const sanitized = this.compactObject({
            images: this.sanitizeAiMediaList(references.images, options.image || {}),
            videos: this.sanitizeAiMediaList(references.videos, options.video || {}),
            audios: this.sanitizeAiMediaList(references.audios, options.audio || {}),
        })
        return Object.keys(sanitized).length ? sanitized : null
    }

    compactObject = object =>
        Object.fromEntries(
            Object.entries(object || {}).filter(([, value]) => {
                if (value == null) return false
                if (Array.isArray(value)) return value.length > 0
                if (typeof value === 'object') return Object.keys(value).length > 0
                return value !== ''
            }),
        )

    aiMediaInputPayload = media => {
        if (!media || typeof media !== 'object') return null
        const signedId = media.signed_id || media.signedId || null
        const url =
            media.url ||
            activeStorageBlobPreviewUrl(
                signedId,
                media.label || media.source_label || media.sourceLabel || null,
            )

        return this.compactObject({
            id: media.id || null,
            url: url || null,
            preview_image_url: media.preview_image_url || media.previewImageUrl || null,
            signed_id: signedId,
            upload_id: media.upload_id || media.uploadId || null,
            label: media.label || null,
            source_label: media.source_label || media.sourceLabel || null,
            source: media.source || null,
            content_type: media.content_type || media.contentType || media.mime || null,
            duration_seconds:
                media.duration_seconds ?? media.durationSeconds ?? media.duration ?? null,
            action_tag: media.action_tag || media.actionTag || null,
            answer_id: media.answer_id || media.answerId || media.source_answer_id || null,
            attachment_id: media.attachment_id || media.attachmentId || null,
            blob_id: media.blob_id || media.blobId || null,
            run_id: media.run_id || media.runId || media.source_run_id || null,
            root_remix_ai_run_id: media.root_remix_ai_run_id || media.rootRemixAiRunId || null,
            action_id: media.action_id || media.actionId || media.source_action_id || null,
            index: media.index ?? null,
        })
    }

    getSerializableDraftPayloads = (answer = this.getCurrentDraftAnswerForPersistence()) => {
        const persistentUrl = url => (this.isPersistentDraftPayloadUrl(url) ? url : null)
        const answerImageUrl =
            persistentUrl(answer.answer_image_url) ||
            activeStorageBlobPreviewUrl(
                typeof answer.answer_image === 'string' ? answer.answer_image : null,
                'image',
            )
        const localAnswerImageUrl =
            persistentUrl(answer.local_answer_image_url) ||
            (answer.answer_image_url === answer.local_answer_image_url ? answerImageUrl : null)
        const signedFileIds = (answer.files || []).filter(file => typeof file === 'string')
        const fileUrls = [
            ...(answer.file_urls || []).map((file, index) =>
                this.compactObject({
                    ...file,
                    url:
                        persistentUrl(file?.url || file?.file_url) ||
                        activeStorageBlobPreviewUrl(signedFileIds[index] || null, file?.name),
                    file_url:
                        persistentUrl(file?.file_url || file?.url) ||
                        activeStorageBlobPreviewUrl(signedFileIds[index] || null, file?.name),
                }),
            ),
            ...signedFileIds.slice(answer.file_urls?.length || 0).map((signedId, index) => {
                const url = activeStorageBlobPreviewUrl(signedId, `file-${index + 1}`)
                return this.compactObject({
                    url,
                    file_url: url,
                    name: `file-${index + 1}`,
                })
            }),
        ].filter(file => file.url || file.file_url)
        const recordingUrl =
            persistentUrl(answer.recording_url) ||
            activeStorageBlobPreviewUrl(
                typeof answer.recording === 'string' ? answer.recording : null,
                'recording',
            )
        const mp4RecordingUrl =
            persistentUrl(answer.mp4_recording_url) ||
            activeStorageBlobPreviewUrl(
                typeof answer.mp4_recording === 'string' ? answer.mp4_recording : null,
                'recording.mp4',
            )

        return this.compactObject({
            answer_image_url: answerImageUrl || localAnswerImageUrl,
            local_answer_image_url:
                localAnswerImageUrl && localAnswerImageUrl !== answerImageUrl
                    ? localAnswerImageUrl
                    : null,
            image_urls: answer.image_urls || [],
            images_v2: answer.images_v2 || [],
            recording: typeof answer.recording === 'string' ? answer.recording : null,
            recording_url: recordingUrl,
            mp4_recording: typeof answer.mp4_recording === 'string' ? answer.mp4_recording : null,
            mp4_recording_url: mp4RecordingUrl,
            recording_content_type: answer.recording_content_type || null,
            recording_thumbnail_url: persistentUrl(answer.recording_thumbnail_url),
            original_recording_type: answer.original_recording_type || null,
            files: signedFileIds,
            file_urls: fileUrls,
            file_previews: (answer.file_previews || []).filter(
                preview => typeof preview === 'string',
            ),
            embeds: answer.embeds || [],
            url: answer.url || null,
            url_id: answer.url_id ?? null,
        })
    }

    readDraftImageFileAsDataUrl = file =>
        new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result)
            reader.onerror = reject
            reader.readAsDataURL(file)
        })

    persistLocalDraftImageFile = file => {
        const composeAnswerId = this.getComposeAnswerId()
        if (!file || !composeAnswerId || !file.type?.startsWith?.('image/')) return

        this.readDraftImageFileAsDataUrl(file)
            .then(dataUrl => {
                if (!dataUrl || typeof dataUrl !== 'string') return

                const currentAnswer = getCachedAnswer(composeAnswerId) || this.props.newAnswer || {}
                const images = composerImageValues(currentAnswer)
                const imageIndex = images.findIndex(image => image === file)
                if (imageIndex < 0) return

                const localImageUrls = composerImagePreviewUrls(currentAnswer)
                localImageUrls[imageIndex] = dataUrl

                const patch = {
                    local_image_urls: localImageUrls,
                    ...(imageIndex === 0
                        ? {
                              answer_image_url: dataUrl,
                              local_answer_image_url: dataUrl,
                          }
                        : {}),
                }
                cache.updateCachedAnswer(composeAnswerId, patch)
                this._nbComposerMediaFingerprint = this.nbComposerMediaFingerprint(
                    this.nbComposerMediaAttrs(),
                )
                this.scheduleDraftPersistence()
            })
            .catch(error => {
                console.warn('Failed to persist draft image payload:', error)
            })
    }

    getFirstDraftPayloadImageUrl = payloads => {
        if (payloads?.local_answer_image_url) return payloads.local_answer_image_url
        if (payloads?.answer_image_url) return payloads.answer_image_url
        const imageUrl = (payloads?.image_urls || [])
            .map(image => image?.image_url || image?.url)
            .find(url => this.isPersistentDraftPayloadUrl(url))
        if (imageUrl) return imageUrl
        return (payloads?.images_v2 || [])
            .map(image => {
                const urls = image?.urls || {}
                return urls.medium || urls.small || urls.original || urls.large
            })
            .find(url => this.isPersistentDraftPayloadUrl(url))
    }

    getFirstDraftPayloadVideoUrl = payloads =>
        [
            payloads?.recording_url,
            payloads?.mp4_recording_url,
            ...(payloads?.file_urls || [])
                .filter(file =>
                    (file?.content_type || file?.contentType || '').startsWith('video/'),
                )
                .map(file => file?.url || file?.file_url),
        ].find(url => this.isPersistentDraftPayloadUrl(url))

    sanitizeActionRequestsForDraftPersistence = (actionRequests, payloads = {}) => {
        if (!Array.isArray(actionRequests)) return []
        const fallbackImageUrl = this.getFirstDraftPayloadImageUrl(payloads)
        const fallbackVideoUrl = this.getFirstDraftPayloadVideoUrl(payloads)
        const fallbackVideoPreviewUrl = this.isPersistentDraftPayloadUrl(
            payloads?.recording_thumbnail_url,
        )
            ? payloads.recording_thumbnail_url
            : null

        return this.ensureAiActionClientIds(actionRequests).map(action => {
            const inputs = action?.inputs || {}
            const startImagePayload = inputs.start_image
                ? this.sanitizeAiMediaDescriptor(inputs.start_image, {
                      fallbackUrl: fallbackImageUrl,
                  })
                : null
            const endImagePayload = inputs.end_image
                ? this.sanitizeAiMediaDescriptor(inputs.end_image, {
                      fallbackUrl: fallbackImageUrl,
                  })
                : null
            const imagePayload = inputs.image
                ? this.sanitizeAiMediaDescriptor(inputs.image, { fallbackUrl: fallbackImageUrl })
                : null
            const videoPayload = inputs.video
                ? this.sanitizeAiMediaDescriptor(inputs.video, {
                      fallbackUrl: fallbackVideoUrl,
                      fallbackPreviewUrl: fallbackVideoPreviewUrl,
                  })
                : null
            const videoInputs = Array.isArray(inputs.videos)
                ? inputs.videos
                      .map(video =>
                          this.sanitizeAiMediaDescriptor(video, {
                              fallbackUrl: fallbackVideoUrl,
                              fallbackPreviewUrl: fallbackVideoPreviewUrl,
                          }),
                      )
                      .filter(this.isPersistableAiMediaDescriptor)
                : []
            const audioPayload = inputs.audio ? this.sanitizeAiMediaDescriptor(inputs.audio) : null
            const referencesPayload = inputs.references
                ? this.sanitizeAiReferences(inputs.references, {
                      image: { fallbackUrl: fallbackImageUrl },
                      video: {
                          fallbackUrl: fallbackVideoUrl,
                          fallbackPreviewUrl: fallbackVideoPreviewUrl,
                      },
                  })
                : null

            return {
                ...action,
                inputs: {
                    ...(inputs.text ? { text: inputs.text } : {}),
                    ...(this.isPersistableAiMediaDescriptor(startImagePayload)
                        ? { start_image: startImagePayload }
                        : {}),
                    ...(this.isPersistableAiMediaDescriptor(endImagePayload)
                        ? { end_image: endImagePayload }
                        : {}),
                    ...(this.isPersistableAiMediaDescriptor(imagePayload)
                        ? { image: imagePayload }
                        : {}),
                    ...(this.isPersistableAiMediaDescriptor(videoPayload)
                        ? { video: videoPayload }
                        : {}),
                    ...(videoInputs.length ? { videos: videoInputs } : {}),
                    ...(this.isPersistableAiMediaDescriptor(audioPayload)
                        ? { audio: audioPayload }
                        : {}),
                    ...(referencesPayload ? { references: referencesPayload } : {}),
                },
            }
        })
    }

    draftPayloadCachePatch = savedDraft => savedDraft?.payloads || {}

    aiRunOutputKind = output => {
        const contentType = output?.content_type || output?.contentType || ''
        const url = output?.url || ''
        if (contentType.startsWith('image/') || /\.(png|jpe?g|webp|gif|avif)(\?|$)/i.test(url)) {
            return 'image'
        }
        if (contentType.startsWith('video/') || /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url)) {
            return 'video'
        }
        if (contentType.startsWith('audio/') || /\.(mp3|wav|ogg|flac|m4a|aac)(\?|$)/i.test(url)) {
            return 'audio'
        }
        return 'file'
    }

    getAnswerAiRunOutputCandidates = (answer, kind, labelPrefix) => {
        if (!Array.isArray(answer?.ai_runs)) return []

        return answer.ai_runs.flatMap((run, runIndex) => {
            if (run?.status !== 'succeeded' && run?.status !== 'finished') return []

            const label =
                labelPrefix ||
                run?.action_payload?.display_name ||
                run?.ai_model?.human_name ||
                run?.provider_model ||
                run?.action_tag ||
                'AI output'
            const outputs = Array.isArray(run?.output_urls) ? run.output_urls : []

            return outputs
                .filter(output => output?.url && this.aiRunOutputKind(output) === kind)
                .map((output, outputIndex) => ({
                    id: output?.id || `ai-run-output-${run?.id || runIndex}-${outputIndex}`,
                    url: output.url,
                    label,
                    file: null,
                    contentType: output?.content_type || output?.contentType || null,
                    durationSeconds:
                        output?.duration_seconds ??
                        output?.durationSeconds ??
                        output?.duration ??
                        null,
                    previewImageUrl:
                        output?.preview_image_url ||
                        output?.previewImageUrl ||
                        output?.preview_url ||
                        output?.urls?.medium ||
                        output?.urls?.small ||
                        output?.thumbnail_url ||
                        output?.thumbnailUrl ||
                        null,
                    source: 'ai_run',
                    answerId: output?.source_answer_id || answer?.id || null,
                    attachmentId: output?.id || null,
                    blobId: output?.blob_id || output?.blobId || null,
                    aiRunId: run?.id || null,
                    actionId:
                        output?.source_action_id ||
                        run?.action_payload?.id ||
                        run?.actionPayload?.id ||
                        run?.action_payload?.client_id ||
                        run?.actionPayload?.client_id ||
                        null,
                    actionTag: run?.action_tag || null,
                }))
        })
    }

    ensureAiActionAudioInputsForSubmit = async answer => {
        const stateActionRequests = this.getCurrentDraftActionRequests()
        const answerActionRequests = this.getActionRequestsFromSource(answer)
        const actionHasUploadInput = action => {
            const uploadInput = input =>
                ['upload', undefined].includes(input?.source) &&
                (input?.upload_id || input?.uploadId || input?.url?.startsWith?.('blob:'))
            const references = action?.inputs?.references || {}
            return (
                ['image', 'video', 'audio', 'file'].some(kind =>
                    uploadInput(action?.inputs?.[kind]),
                ) ||
                ['images', 'videos', 'audios'].some(referenceKey =>
                    (Array.isArray(references?.[referenceKey])
                        ? references[referenceKey]
                        : []
                    ).some(uploadInput),
                )
            )
        }
        const stateHasUploadInput = stateActionRequests.some(actionHasUploadInput)
        const actionRequests = stateHasUploadInput ? stateActionRequests : answerActionRequests
        if (!actionRequests.length) return undefined

        try {
            const nextActionRequests = await this.aiInputUploadManager.resolveActionRequests(
                actionRequests,
            )
            if (nextActionRequests === actionRequests) return undefined
            this.setDraftActionRequests(nextActionRequests)
            return { actionRequests: nextActionRequests }
        } catch (error) {
            console.error('Failed to upload action request input:', error)
            this.showNotice('Unable to upload AI input. Please try again.', {
                variant: 'error',
                durationMs: 6500,
            })
            return null
        }
    }

    handleAiInputUploadChange = () => {
        this.forceUpdate()
        this.resolveCompletedAiInputUploadsForDraft()
    }

    resolveCompletedAiInputUploadsForDraft = async () => {
        try {
            const didResolveUploads = await resolveCompletedDraftUploads({
                getCurrentRevision: () => this._draftActionRequestsRevision || 0,
                getCurrentActionRequests: this.getCurrentDraftActionRequests,
                resolve: requests =>
                    this.aiInputUploadManager.resolveActionRequests(requests, {
                        waitForUploads: false,
                    }),
                commit: nextActionRequests =>
                    this.setDraftActionRequests(nextActionRequests, {
                        resolveCompletedUploads: false,
                    }),
            })
            if (!didResolveUploads) return
            this.flushDraftPersistence()
        } catch (error) {
            console.warn('Failed to resolve completed AI input uploads for draft:', error)
        }
    }

    // A draft composer can be pointed at one of the user's recent threads via the
    // publish modal's Threads tab (share/clip flow). Publishing then appends the
    // composed post to that thread instead of creating a new one, and the panel
    // swaps from the draft to the destination thread.
    postDraftToDestinationThread = async (
        quest,
        newAnswer,
        activeDraftAnswerId,
        diagnostic,
        replyDiagnostic,
    ) => {
        const destinationQuestId = quest.destination_quest_id

        const replyAnswer = { ...newAnswer, quest_id: destinationQuestId }
        // Let the server assign the append position to avoid client clock discrepancy.
        delete replyAnswer.position

        // Re-key the reply's branch quest: the composed title answer arrives carrying
        // the draft quest as its child, but that id must not leak into the destination
        // thread (the panel still shows the draft while we publish).
        const cachedDestQuest = cache.getCachedQuest(destinationQuestId)
        const childPerms = questModel.getPerms(cachedDestQuest || quest)
        const childQuest = questModel.newQuest({
            ...childPerms,
            parent_id: replyAnswer.id,
            parent: replyAnswer,
            is_draft: false,
            isLocalChildQuest: true,
            space_id: quest.space_id || replyAnswer.space_id,
            sorted_answers: [],
        })
        replyAnswer.child_quests = [{ id: childQuest.id }]
        cacheAnswer(replyAnswer)
        cacheQuest(childQuest)

        let serverAnswer
        try {
            serverAnswer = await api.createAnswer(replyAnswer, replyDiagnostic)
            diagnostic?.mark('request_end')
        } catch (error) {
            diagnostic?.mark('failed')
            console.error('Failed to post into destination thread:', error)
            useStore
                .getState()
                .showGlobalNotice(
                    api.getReadableApiErrorMessage(
                        error,
                        'Unable to post into the selected thread. Please try again.',
                    ),
                    { variant: 'error', durationMs: 6500 },
                )
            return false
        }

        // The server accepted the post, so it is now safe to discard the composer draft.
        // Keep this after createAnswer: a rejected request must remain retryable with its
        // original draft identity and metadata intact.
        this.clearDraftFromLocalStorage()
        this.handleSuccessfulPost()
        this.clearPostedDraftActionRequests(activeDraftAnswerId, { cacheAnswerState: false })
        draftAnswerStore.clearDraft(quest.id)

        logEv('POST_ANSWER')

        const postedAnswer = serverAnswer || replyAnswer
        if (serverAnswer) cacheAnswer(serverAnswer)

        // Keep any cached copy of the destination thread consistent so the new post
        // shows immediately when the panel swaps over.
        try {
            const destQuest = cache.getCachedQuest(destinationQuestId)
            if (destQuest && postedAnswer?.id) {
                const ids = destQuest.sorted_answer_ids || []
                if (!ids.includes(postedAnswer.id)) {
                    cache.updateCachedQuest(destinationQuestId, {
                        updated_at: postedAnswer.updated_at || destQuest.updated_at,
                        sorted_answer_ids: [...ids, postedAnswer.id],
                        sorted_answers: [...(destQuest.sorted_answers || []), postedAnswer],
                    })
                }
            }
        } catch (e) {}

        clearPublishedNewThreadContext(
            this.props.panelContext?.panel,
            useStore.getState().panels.editPanel,
        )

        if (this.props.onAnswerPosted) this.props.onAnswerPosted(postedAnswer)

        if (this.props.keepInlineAfterPublish) {
            // Inline composers stay put; just reset them.
            this.clearInlineTitleComposerImmediately()
            this.blurEditor()
            this.setState({ inlineComposerExpanded: false })
            return true
        }

        // Swap this panel from the draft to the destination thread, focused on the new post.
        const destinationFilter = withDepth({
            questId: destinationQuestId,
            answerId: postedAnswer?.id,
        })
        const panelId = this.props.panelContext?.panel?.panelId
        if (panelId) {
            this.holdKeyboardForComposerHandoff()
            useStore.getState().panels.editPanel(panelId, {
                filter: destinationFilter,
                empty: false,
            })
        } else {
            openCenter(destinationFilter)
        }

        const destPath =
            cache.getCachedQuest(destinationQuestId)?.path || `/quest/${destinationQuestId}`
        history.pushState({}, null, destPath)
        return true
    }

    postAnswer = async (
        skipValidation = false,
        preFetchedDeltaJson = null,
        mediaOverride = null,
        actionRequestsOverride = null,
        diagnostic = null,
        replyDiagnostic = null,
    ) => {
        replyDiagnostic?.startWork()
        if (!this.ensureCurrentUser()) {
            diagnostic?.mark('blocked')
            return
        }

        // Get current content to check for URLs
        const currentDelta = preFetchedDeltaJson || this.getDeltaJson()
        if (this.hasUnresolvedStreamEmbeds(currentDelta)) {
            this.setStreamEmbedsLoading(true)
            diagnostic?.mark('blocked')
            return
        }
        const hiddenPreviewUrls = answerModel.mergeHiddenPreviewUrls(
            this.getHiddenPreviewUrls(),
            this.getPersistedSuppressedPreviewUrls(),
        )
        diagnostic?.mark('embed_wait_begin')
        replyDiagnostic?.crumb('embed_wait_begin')
        const visibleKnovEmbedLinks = await answerModel.waitForVisibleEmbeds({
            deltaJson: currentDelta || {},
            hiddenPreviewUrls,
            isEmbedsLoading: () => this.state.embedsLoading,
            getEmbedLoadingPromise: () => this.state.embedLoadingPromise,
            setWaitingForEmbeds: waiting => this.setState({ waitingForEmbeds: waiting }),
        })

        // Ensure embed data is present before posting
        replyDiagnostic?.crumb('embeds_resolve_begin')
        const ensuredEmbeds = await this.ensureEmbedsForLinks(visibleKnovEmbedLinks)
        diagnostic?.mark('embed_wait_end')
        if (ensuredEmbeds == null) {
            diagnostic?.mark('blocked')
            return
        }

        const quest = this.props.quest
        const ensuredDraftAnswer = !quest?.is_draft ? this.ensureReplyDraftForQuest(quest) : null
        const activeDraftAnswer = ensuredDraftAnswer || this.getCurrentDraftAnswerForPersistence()
        const attachmentState = answerModel.getComposeAttachmentState({
            answer: activeDraftAnswer,
            isEditing: true,
            extractedUrls: extractUrls(currentDelta || {}),
            hiddenPreviewUrls,
            resolveEmbedAnswer: embed => getCachedAnswer(embed?.id) || embed,
        })
        replyDiagnostic?.crumb('url_attachment_begin')
        const resolvedUrlAttachment = await answerModel.ensureUrlAttachmentForSubmit(
            attachmentState,
        )
        if (resolvedUrlAttachment === null) {
            diagnostic?.mark('blocked')
            this.showNotice('Unable to attach the URL right now. Please try again.', {
                variant: 'error',
            })
            return
        }

        replyDiagnostic?.crumb('ai_inputs_begin')
        const aiActionAudioMedia = await this.ensureAiActionAudioInputsForSubmit(activeDraftAnswer)
        if (aiActionAudioMedia === null) {
            diagnostic?.mark('blocked')
            return
        }

        // Check if we have a dice/raffle that needs processing
        const dice = activeDraftAnswer?.dice
        if (dice && dice.status === 'new') {
            // Update dice status to ready before posting
            cache.updateCachedAnswer(activeDraftAnswer?.id, {
                dice: { ...dice, status: 'ready' },
            })
        }

        // TODO is this getting the local child_quest? for selected answers?
        let newAnswer = {
            ...this.getNewAnswer(preFetchedDeltaJson, activeDraftAnswer, actionRequestsOverride),
            embeds: ensuredEmbeds || [],
            url: resolvedUrlAttachment.url,
            url_id: resolvedUrlAttachment.url_id,
        }

        // quote_main: this composer was opened by the reply pill quoting a
        // room message — the reply lands in the main thread carrying the
        // aloud edge (server drops invalid targets, degrading to a plain
        // room reply).
        const replyEdgeAnswerId = this.effectiveReplyToAnswerId()
        if (replyEdgeAnswerId && quoteMainEnabled()) {
            newAnswer.reply_to_answer_id = replyEdgeAnswerId
        }

        // Keep the upload manager's completed references as the source of truth for submit.
        // Cache/prop refreshes can otherwise replace the draft snapshot after the background
        // upload succeeds and silently publish the text without its media.
        const completedMedia = this.uploadManager.getCompletedMedia(newAnswer.id)
        newAnswer = mergeCompletedAnswerMediaForSubmit(newAnswer, {
            ...(completedMedia || {}),
            ...(mediaOverride || {}),
        })
        if (hasUnresolvedLocalVideoForSubmit(newAnswer)) {
            diagnostic?.mark('blocked')
            this.showNotice(
                'Video upload is not finished. Your draft is still here. Please wait and try again.',
                { variant: 'error', durationMs: 6500 },
            )
            return
        }
        if (hasPendingLocalMediaForSubmit(newAnswer)) {
            diagnostic?.mark('blocked')
            this.showNotice(
                'Attachment upload failed or is not finished. Your draft is still here. Remove it or attach it again.',
                { variant: 'error', durationMs: 6500 },
            )
            return
        }
        if (aiActionAudioMedia?.actionRequests) {
            const selectedPaymentMode = actionRequestsOverride?.find?.(
                action => action?.payment_mode,
            )?.payment_mode
            newAnswer.action_requests = selectedPaymentMode
                ? this.withPaymentModeForActionRequests(
                      aiActionAudioMedia.actionRequests,
                      selectedPaymentMode,
                  )
                : aiActionAudioMedia.actionRequests
        }

        if (answerModel.hasContent(newAnswer) || skipValidation) {
            const hasImagesToPreupload = composerImageValues(newAnswer).length > 0
            if (hasImagesToPreupload) diagnostic?.mark('preupload_begin')
            try {
                replyDiagnostic?.crumb('image_preupload_begin')
                newAnswer = await prepareAnswerImagesForSubmit(newAnswer, answer =>
                    api.preuploadAnswerImages(answer, {
                        answerId: answer?.id,
                        uploadKind: 'image',
                        source: 'NewAnswer.postAnswer',
                    }),
                )
                if (hasImagesToPreupload) diagnostic?.mark('preupload_end')
            } catch (error) {
                diagnostic?.mark('failed')
                captureUploadFailure(error, {
                    answer_id: newAnswer?.id,
                    upload_kind: 'image',
                    upload_source: 'NewAnswer.postAnswer',
                })
                this.showNotice(
                    'Image upload failed. Your draft is still here. Please try again.',
                    {
                        variant: 'error',
                        durationMs: 6500,
                    },
                )
                return
            }
        }

        // Create child quest for the new answer (for future replies)
        if (!quest.is_draft) {
            const perms = questModel.getPerms(quest)
            const childQuest = questModel.newQuest({
                ...perms,
                parent_id: newAnswer.id,
                parent: newAnswer,
                is_draft: false,
                isLocalChildQuest: true,
                space_id: quest.space_id || newAnswer.space_id,
                sorted_answers: [], // Ensure sorted_answers is initialized
            })

            // Add child quest reference to the answer before caching
            newAnswer.child_quests = [{ id: childQuest.id }]

            // Cache the answer first, then the child quest
            // This ensures the answer exists when the quest is cached
            cacheAnswer(newAnswer)
            cacheQuest(childQuest)
        }

        if (answerModel.hasContent(newAnswer) || skipValidation) {
            this.logAiActionUsageSubmitted(newAnswer)

            // Publishing a draft into an existing thread has its own server write. Do not
            // clear the draft until that write succeeds; the helper performs cleanup after
            // createAnswer resolves and leaves the draft untouched on failure.
            if (quest.is_draft && quest.destination_quest_id) {
                diagnostic?.mark('request_begin')
                const posted = await this.postDraftToDestinationThread(
                    quest,
                    newAnswer,
                    activeDraftAnswer?.id,
                    diagnostic,
                    replyDiagnostic,
                )
                if (posted) diagnostic?.mark('complete')
                return
            }

            // remove the draft from local storage since we're posting the answer
            this.clearDraftFromLocalStorage()
            // Clear title draft if this was a new quest
            this.handleSuccessfulPost()
            this.clearPostedDraftActionRequests(activeDraftAnswer?.id, { cacheAnswerState: false })

            // Only cache the answer if we didn't already cache it above
            if (quest.is_draft) {
                cacheAnswer(newAnswer)
            }
            let draftQuest
            if (quest.is_draft) {
                // Clear the draft from dedicated store
                draftAnswerStore.clearDraft(quest.id)

                draftQuest = {
                    ...quest,
                    // A channel thread takes replies from anyone unless the author chose members-only.
                    members_only_replies: this.mainThreadReplyPolicy(quest) === 'members',
                    is_draft: false, // This publishes the quest in the panel and make react-query update from server on next useGetQuest().
                    created_at: newAnswer.created_at,
                    updated_at: newAnswer.updated_at,
                    parent: newAnswer,
                    isLocalRootQuest: true,
                }
                // The panel is about to become the thread and this composer to
                // give way to its reply composer: keep the keyboard up across
                // the handoff (keyboardKeeper).
                // A thread that shouldn't open its reply box after the first post (the
                // new-wallet intro) lets the keyboard close, so the post stays in view.
                const stayAfterPublish = this.props.noReplyFocusAfterPublish
                if (!this.props.keepInlineAfterPublish && !stayAfterPublish)
                    this.holdKeyboardForComposerHandoff()
                cacheQuest(draftQuest)
                if (stayAfterPublish) {
                    this.blurEditor()
                } else if (this.props.keepInlineAfterPublish) {
                    this.clearInlineTitleComposerImmediately()
                    this.blurEditor()
                    this.setState({ inlineComposerExpanded: false })
                } else if (this.props.focusFirstReplyAfterPublish) {
                    this.props.focusFirstReplyAfterPublish()
                } else {
                    this.props.focusNewAnswer()
                }
                const inlineOptimisticPublish =
                    !!this.props.keepInlineAfterPublish && !!this.props.onQuestPublished
                if (inlineOptimisticPublish) {
                    // Inline stream mode should insert instantly without waiting for server roundtrip.
                    this.props.onQuestPublished(draftQuest, newAnswer?.id)
                }

                // TODO useMutate?
                let apiQuest
                try {
                    diagnostic?.mark('optimistic_commit')
                    diagnostic?.mark('request_begin')
                    apiQuest = await api.createQuest(draftQuest, replyDiagnostic)
                    diagnostic?.mark('request_end')
                } catch (error) {
                    diagnostic?.mark('failed')
                    console.error('Failed to create thread on server:', error)
                    const rejectedQuest = {
                        ...draftQuest,
                        localPublishErrorStatus: error?.status || 422,
                        localPublishErrorMessage: api.getReadableApiErrorMessage(
                            error,
                            'Unable to post thread. Please try again.',
                        ),
                    }
                    cacheQuest(rejectedQuest)
                    if (inlineOptimisticPublish) {
                        this.props.onQuestPublished(rejectedQuest, newAnswer?.id)
                    } else {
                        useStore.getState().updateQuest(rejectedQuest)
                    }
                    useStore.getState().showGlobalNotice(rejectedQuest.localPublishErrorMessage, {
                        variant: 'error',
                        durationMs: 6500,
                    })
                    return
                }
                const publishedQuest = apiQuest || draftQuest
                clearPublishedNewThreadContext(
                    this.props.panelContext?.panel,
                    useStore.getState().panels.editPanel,
                )
                if (!this.props.keepInlineAfterPublish && apiQuest?.path) {
                    history.pushState({}, null, apiQuest.path)
                }
                logEv('POST_ROOT')

                // Ensure the agent status is preserved on the server
                if (this.props.globalAgentModel) {
                    // Set the agent model for the new quest ID
                    const questId = apiQuest?.id
                    if (questId) {
                        const newQuestAgentModels = {
                            ...useStore.getState().questAgentModels,
                            [questId]: this.props.globalAgentModel,
                        }

                        // Update server-side
                        api.updateUserSpaceOptions({
                            quest_agent_models: newQuestAgentModels,
                        })

                        // Update local state
                        useStore.getState().set({
                            questAgentModels: newQuestAgentModels,
                        })
                    }
                }

                // We have to do this one here bc we can't rely on onMount in QuestContainer since we mount a local is_draft queest and it doesn't exist yet on the backend so the view action call will 404.
                apiQuest && (await api.userViewsQuest(apiQuest.id))
                const actionsQueryKey = ['actions']
                queryClient.invalidateQueries({ queryKey: actionsQueryKey })
                queryClient.invalidateQueries({ queryKey: PEOPLE_CHATS_QUERY_KEY })
                queryClient.refetchQueries({ queryKey: actionsQueryKey, exact: true })

                if (this.props.onQuestPublished && !inlineOptimisticPublish) {
                    this.props.onQuestPublished(
                        publishedQuest,
                        publishedQuest?.parent_id || publishedQuest?.parent?.id || newAnswer?.id,
                    )
                }
                diagnostic?.mark('complete')
            } else {
                // Clear the draft from dedicated store
                draftAnswerStore.clearDraft(quest.id)

                try {
                    if (quest?.id && newAnswer?.id) {
                        useStore
                            .getState()
                            .trackPendingOptimisticAnswer(String(quest.id), String(newAnswer.id))
                    }
                } catch {}
                // Make sure newQuest is cached first bc it has newAnswers with child_quests.
                // newAnswer.child_quests is needed for instant local branching until server roundtrip.
                // skip caching parent to avoid clobbering it's lock amt with newAnswer's (empty) lock amt.
                cache.appendOptimisticReplyToQuest(quest, newAnswer)
                diagnostic?.mark('optimistic_commit')
                // addAnswer handles quest answer collapse.
                if (this.props.addAnswer) {
                    this.props.addAnswer(newAnswer)
                }

                try {
                    // The posted answer reuses the current draft id. Do not clear that record,
                    // or the optimistic answer briefly renders as empty until socket refresh.
                    const freshDraft = answerModel.newAnswer({
                        quest_id: quest.id,
                        space_id: quest.space_id,
                        is_draft: true,
                    })
                    cacheAnswer(freshDraft)
                    cache.updateCachedQuest(quest.id, {
                        new_answer: freshDraft,
                        new_answer_id: freshDraft.id,
                    })
                    this.refocusComposerAfterPost()
                } catch (e) {}

                // bottom_editor: posting does not take the reader to the bottom.
                // The panel flashes a reply they can see, brings one they see
                // only in part the rest of the way into view, and offers one
                // they cannot see with the reply modal's pill.
                if (
                    this.props.bottomEditor &&
                    this.props.panelContext?.offerAnswerJump &&
                    newAnswer?.id
                ) {
                    this.props.panelContext.offerAnswerJump(newAnswer.id, quest?.id, {
                        fromBottomEditor: true,
                    })
                } else if (this.props.panelContext?.panel && newAnswer?.id) {
                    // If typing inside a quest (not modal), shake panel and highlight the new local answer
                    const { panel } = this.props.panelContext
                    let attempts = 0
                    const maxAttempts = 25
                    const pollAndHighlight = () => {
                        const sc = panel.getScrollContainer?.()
                        const el = sc?.querySelector?.(`.answer-in-view-${newAnswer.id}`)
                        if (el) {
                            if (isAndroid) {
                                // On Android, avoid scroll-to-answer (which scrolls up then down).
                                // Instead, ensure we stay at bottom and just highlight.
                                panel.scrollToBottom?.()
                                panel.shakeQuestByAnswer?.(newAnswer.id)
                                panel.highlightAnswer?.(newAnswer.id)
                            } else if (isIOS) {
                                // iOS: also stick to bottom to avoid up-then-down and caret drift
                                panel.scrollToBottom?.()
                                //panel.shake?.()
                                panel.highlightAnswer?.(newAnswer.id)
                                // Re-affirm focus a moment later to keep caret in the input
                                try {
                                    if (this.richEditor.current?.focus) {
                                        setTimeout(() => this.richEditor.current?.focus?.(), 100)
                                    }
                                } catch (e) {}
                            } else {
                                // Posting is not a navigation intent. Keep the writer with the
                                // bottom composer and highlight the optimistic post in place;
                                // addressed top placement belongs only to route/panel opens.
                                panel.scrollToBottom?.()
                                panel.shakeQuestByAnswer?.(newAnswer.id)
                                panel.highlightAnswer?.(newAnswer.id)
                            }
                        } else if (attempts++ < maxAttempts) {
                            setTimeout(pollAndHighlight, 80)
                        }
                    }
                    // Start polling after the render pass
                    requestAnimationFrame(pollAndHighlight)
                }

                if (newAnswer?.id) {
                    // Call the callback immediately with local answer (for modal context)
                    if (this.props.onAnswerPosted) {
                        this.props.onAnswerPosted(newAnswer)
                    }

                    // Close modal immediately if we're in modal context
                    if (this.props.closeModal && !this.props.addAnswer) {
                        replyDiagnostic?.crumb('close_modal', { has_id: true })
                        this.props.closeModal()
                    }

                    // Create the answer on the server (don't await)
                    // Remove position to avoid client clock discrepancy, and generate a new one on the server.
                    delete newAnswer.position
                    diagnostic?.mark('request_begin')
                    replyDiagnostic?.crumb('create_answer_call')
                    api.createAnswer(newAnswer, replyDiagnostic)
                        .then(serverAnswer => {
                            diagnostic?.mark('request_end')
                            logEv('POST_ANSWER')

                            // Update the cached answer with server response
                            if (serverAnswer) {
                                const optimisticAnswer = getCachedAnswer(serverAnswer.id)
                                if (
                                    !serverAnswer.ai_runs?.length &&
                                    optimisticAnswer?.ai_runs?.length
                                ) {
                                    serverAnswer.ai_runs = optimisticAnswer.ai_runs
                                }
                                cacheAnswer(serverAnswer)
                                try {
                                    // If we have a local blob for this draft in cache, attach it to the newly cached server answer for immediate playback
                                    const draftId = newAnswer?.id
                                    const draft = getCachedAnswer(draftId)
                                    const blobUrl = draft?.recording_url
                                    const blobType = draft?.original_recording_type || 'video/mp4'
                                    if (
                                        blobUrl &&
                                        !serverAnswer.mp4_recording_url &&
                                        !serverAnswer.recording_url
                                    ) {
                                        cache.updateCachedAnswer(serverAnswer.id, {
                                            recording_url: blobUrl,
                                            original_recording_type: blobType,
                                        })
                                    }

                                    // Same idea for images: keep the local blob URL around so the UI doesn't go empty
                                    // while S3/ActiveStorage are briefly unavailable right after upload.
                                    const localImgUrl =
                                        draft?.local_answer_image_url || draft?.answer_image_url
                                    if (
                                        typeof localImgUrl === 'string' &&
                                        (localImgUrl.startsWith('blob:') ||
                                            localImgUrl.startsWith('data:'))
                                    ) {
                                        cache.updateCachedAnswer(serverAnswer.id, {
                                            // Keep a dedicated copy so cache merges can preserve it
                                            local_answer_image_url: localImgUrl,
                                            // Also keep the visible field populated so Answer.jsx keeps rendering AnswerImage
                                            // even if a server/socket update temporarily sends answer_image_url as null/blank.
                                            answer_image_url: localImgUrl,
                                        })
                                    }
                                } catch (e) {}
                                // Clear any lingering upload UI once the answer is posted
                                this.uploadManager.cancelRecordingUpload({ skipCancelFns: true })
                                this.uploadManager.cancelFileUpload(undefined, {
                                    skipCancelFns: true,
                                })
                                this.setState({ uploadProgress: { recording: null, files: {} } })
                            }
                            diagnostic?.mark('complete')
                        })
                        .catch(error => {
                            diagnostic?.mark('failed')
                            console.error('Failed to create answer on server:', error)
                            if (hasPendingAnswerOutboxItem(newAnswer.id)) {
                                useStore
                                    .getState()
                                    .showGlobalNotice(
                                        'Answer saved locally. It will retry when this thread reloads.',
                                        { variant: 'info', durationMs: 6500 },
                                    )
                                return
                            }

                            cache.removeOptimisticAnswer(newAnswer.id, {
                                questId: quest.id,
                                childQuestIds: newAnswer.child_quests?.map(
                                    childQuest => childQuest.id,
                                ),
                            })
                            this.props.removeAnswer?.(newAnswer.id)
                            useStore
                                .getState()
                                .showGlobalNotice(
                                    api.getReadableApiErrorMessage(
                                        error,
                                        'Unable to post answer. Please try again.',
                                    ),
                                    { variant: 'error', durationMs: 6500 },
                                )
                        })
                } else {
                    // For drafts or other non-persisted answers
                    diagnostic?.mark('complete')
                    if (this.props.onAnswerPosted) {
                        this.props.onAnswerPosted(newAnswer)
                    }

                    if (this.props.closeModal && !this.props.addAnswer) {
                        replyDiagnostic?.crumb('close_modal', { has_id: false })
                        this.props.closeModal()
                    }
                }

                if (answerModel.hasLLMRequest(newAnswer) && !questModel.hasLLMReply(quest)) {
                    const rightPanelId = uuid()
                    const childQuestId = newAnswer.child_quests[0].id

                    // If this is an AI quest, set up the agent model for the child quest
                    if (this.props.globalAgentModel) {
                        useStore.getState().set(state => {
                            //console.log('setting questAgentModels', state.questAgentModels)
                            // DON'T delete the old quest model immediately - keep it until new branch is loaded
                            // delete state.questAgentModels[quest.id]
                            state.questAgentModels[childQuestId] = this.props.globalAgentModel
                            state.selectedAnswers[rightPanelId] = {
                                answerId: newAnswer.id,
                            }
                        })

                        // Update user space options on the server. Read the committed
                        // state: a draft would be a revoked proxy once set() returns,
                        // and updateUserSpaceOptions keeps what it's given in gon.
                        api.updateUserSpaceOptions({
                            quest_agent_models: useStore.getState().questAgentModels,
                        })

                        // Move insertRight outside of set callback
                        //console.log('inserting right panel')
                        this.props.openRight({ questId: childQuestId, answerId: newAnswer.id })

                        // Delay the deletion of the old quest model to prevent race condition
                        // This ensures the new branch UI is loaded before clearing the old model
                        setTimeout(() => {
                            useStore.getState().set(state => {
                                delete state.questAgentModels[quest.id]
                            })
                            // Update server again to remove the old mapping
                            api.updateUserSpaceOptions({
                                quest_agent_models: useStore.getState().questAgentModels,
                            })
                        }, 2000) // Extended time to allow new branch to fully initialize
                    }
                }
            }

            // Inline title composer is now stable (no remount), so clear it explicitly after post.
            const shouldClearInlineTitle = this.props.isTitle && this.props.keepInlineAfterPublish
            if ((!this.props.isTitle || shouldClearInlineTitle) && this.richEditor.current) {
                const editor = this.richEditor.current.getEditor()

                // Instead of completely clearing, we need to preserve model mention if agent model is enabled
                if (
                    this.props.globalAgentModel &&
                    questModel.hasLLMReply(quest) &&
                    !questModel.usesOpenclawStreamRouting(quest, this.props.aiModels || [])
                ) {
                    const agentModelConfig = useStore.getState().agentModelConfig
                    const selectedModel = getAgentModelMentionDetails(
                        this.props.globalAgentModel,
                        agentModelConfig,
                        this.props.aiModels || useStore.getState().aiModels,
                    )
                    if (selectedModel) {
                        const mentionId = String(selectedModel.id)
                        const mentionContent = String(selectedModel.mentionHandle)

                        // Create a model mention delta
                        const modelMention = {
                            id: mentionId,
                            value: mentionContent,
                            denotationChar: '@@',
                            type: 'model',
                            link: selectedModel.link,
                            content: mentionContent,
                        }

                        const newDelta = new Delta().insert({ mention: modelMention }).insert(' ') // Add space after mention

                        // Update the editor content using setContents with USER source
                        editor.setContents(newDelta, Quill.sources.USER)

                        // Use defer to ensure proper timing of selection
                        defer(() => {
                            // Position cursor at end-of-content (after mention + trailing space).
                            // Mention embeds can have variable internal length, so avoid hardcoded index.
                            const endIndex = Math.max((editor.getLength?.() || 1) - 1, 0)
                            editor.setSelection(endIndex, 0, Quill.sources.USER)
                            editor.focus()
                        })
                    } else if (
                        this.richEditor.current &&
                        this.richEditor.current.clearContentProgrammatically
                    ) {
                        this.richEditor.current.clearContentProgrammatically()
                        this.refocusComposerAfterPost()
                    }

                    // Update our state to reflect empty linkPreviews and streamEmbeds
                    this.setState({
                        linkPreviews: [],
                        streamEmbeds: [],
                    })
                } else {
                    // If an agent model was active for this quest, but we're not in an LLM reply context,
                    // just clear the editor normally
                    const currentQuestId = this.props.quest?.id

                    // Clear editor content programmatically via CommonEditor's ref.
                    // This uses the programmaticChangeFlagSetter to signal an intentional change,
                    // preventing Android IME fixes in QuillEditor from reverting the clear.
                    if (
                        this.richEditor.current &&
                        this.richEditor.current.clearContentProgrammatically
                    ) {
                        this.richEditor.current.clearContentProgrammatically()
                        this.refocusComposerAfterPost()
                    }

                    // Update local state to reflect empty linkPreviews and streamEmbeds after clearing.
                    // Defer this to ensure it happens after the editor clearing logic.
                    defer(() => {
                        this.setState({
                            linkPreviews: [],
                            streamEmbeds: [],
                        })
                    })
                }
            }
        } else {
            diagnostic?.mark('blocked')
        }
    }

    getEditor = () => this.richEditor.current?.getEditor()

    getEditorInstance = () => {
        let editor = this.getEditor()
        if (editor?.getEditor) editor = editor.getEditor()
        if (editor?.getEditor) editor = editor.getEditor()
        return editor
    }

    getDeltaJson = () => {
        const contents = this.getEditor()?.getContents()
        if (!contents) return contents

        // Only remove trailing whitespace when specifically preparing for submission
        if (this._preparingForSubmission) {
            const ops = contents.ops
            if (ops && ops.length > 0) {
                const lastOp = ops[ops.length - 1]
                if (typeof lastOp.insert === 'string') {
                    lastOp.insert = lastOp.insert.trimEnd()
                    // Remove the last operation if it's now empty
                    if (lastOp.insert === '') {
                        ops.pop()
                    }
                }
            }
        }
        return { ops: contents.ops }
    }

    /**
     * Extracts text content from the delta JSON, with optional username wrapping.
     * @param {Object} [options] - Optional configuration.
     * @param {boolean} [options.wrapUsernames=false] - Whether to wrap usernames in square brackets.
     * @param {boolean} [options.excludeActionTokens=false] - Whether to remove action tokens from the extracted text.
     * @returns {string} The extracted text content.
     */
    getContent = (options = {}) => getText(this.getDeltaJson() || {}, options).trim()

    scheduleDraftPersistence = () => {
        if (this.props.isTitle) this.debouncedSaveTitleDraft()
        else if (this.props.quest?.id) this.debouncedSaveDraft()
    }

    flushDraftPersistence = () => {
        if (this.props.isTitle) this.saveToTitleDrafts()
        else if (this.props.quest?.id) this.saveDraftToLocalStorage()
    }

    setDraftActionRequests = (
        actionRequests,
        { cacheAnswerState = true, resolveCompletedUploads = true } = {},
    ) => {
        const nextActionRequests = this.ensureAiActionClientIds(actionRequests)
        this.rememberDraftActionRequests(nextActionRequests)
        const nextIsValidContent = this.hasCurrentDraftContent(nextActionRequests)
        if (!isEqual(this.state.draftActionRequests, nextActionRequests)) {
            this.setState({
                draftActionRequests: nextActionRequests,
                isValidContent: nextIsValidContent,
                aiActionInputValidation: null,
            })
        } else if (this.state.isValidContent !== nextIsValidContent) {
            this.setState({ isValidContent: nextIsValidContent })
        }

        if (cacheAnswerState && this.props.newAnswer?.id) {
            cache.updateCachedAnswer(this.props.newAnswer.id, {
                action_requests: nextActionRequests,
            })
        }

        if (this.props.isTitle) this.saveToTitleDrafts()
        else this.scheduleDraftPersistence()

        if (resolveCompletedUploads) {
            this.resolveCompletedAiInputUploadsForDraft()
        }
    }

    rememberDraftActionRequests = actionRequests => {
        const nextActionRequests = this.ensureAiActionClientIds(actionRequests)
        this._draftActionRequestsRevision = (this._draftActionRequestsRevision || 0) + 1
        this._draftActionRequestsSnapshot = nextActionRequests
        this._pendingActionRequestsByClientId ||= {}
        nextActionRequests.forEach(action => {
            const actionId = this.getAiActionRefId(action)
            if (actionId) {
                this._pendingActionRequestsByClientId[actionId] = action
            }
        })
        return nextActionRequests
    }

    getCurrentDraftActionRequests = () => {
        const draftActionRequests = Array.isArray(this._draftActionRequestsSnapshot)
            ? this._draftActionRequestsSnapshot
            : this.state.draftActionRequests

        return this.ensureAiActionClientIds(draftActionRequests || [])
    }

    hasCurrentDraftActionRequests = () => {
        const draftActionRequests = Array.isArray(this._draftActionRequestsSnapshot)
            ? this._draftActionRequestsSnapshot
            : this.state.draftActionRequests

        return Array.isArray(draftActionRequests) && draftActionRequests.length > 0
    }

    commitDraftAiActionAtIndex = (index, action) => {
        const nextAction = this.ensureAiActionClientId(action)
        if (!nextAction) return null

        this._pendingActionRequestsByClientId ||= {}
        const actionId = this.getAiActionRefId(nextAction)
        if (actionId) {
            this._pendingActionRequestsByClientId[actionId] = nextAction
        }

        const nextActionRequests = [
            ...(this._draftActionRequestsSnapshot || this.state.draftActionRequests || []),
        ]
        nextActionRequests[index] = nextAction
        this.setDraftActionRequests(nextActionRequests)
        return nextAction
    }

    normalizeDraftAiAction = (action, deltaJson = null, options = {}) => {
        if (!action || typeof action !== 'object') return null

        const attrs = this.aiActionToActionTokenAttrs(action)
        const rebuiltAction = attrs
            ? this.buildAiActionFromAttrs(attrs, deltaJson, {
                  syncDraftInputs: false,
                  ...options,
              })
            : null

        return this.ensureAiActionClientId({
            ...action,
            ...(rebuiltAction || {}),
            id: action.id || action.client_id || rebuiltAction?.id || rebuiltAction?.client_id,
            client_id:
                action.client_id || action.id || rebuiltAction?.client_id || rebuiltAction?.id,
            ai_run_id: action.ai_run_id || rebuiltAction?.ai_run_id,
            ai_run_ids: action.ai_run_ids || rebuiltAction?.ai_run_ids,
        })
    }

    cloneAiActionForRemix = action => {
        if (!action || typeof action !== 'object') return null

        let clonedAction = null
        try {
            clonedAction =
                typeof structuredClone === 'function'
                    ? structuredClone(action)
                    : JSON.parse(JSON.stringify(action))
        } catch (e) {
            clonedAction = JSON.parse(JSON.stringify(action))
        }
        clonedAction = enableMediaRemixPromptInput(clonedAction)

        const sourceAiRunId =
            clonedAction.ai_run_id ||
            clonedAction.aiRunId ||
            (Array.isArray(clonedAction.ai_run_ids) ? clonedAction.ai_run_ids[0] : null) ||
            (Array.isArray(clonedAction.aiRunIds) ? clonedAction.aiRunIds[0] : null) ||
            clonedAction.run_id ||
            clonedAction.runId ||
            clonedAction.source_run_id ||
            clonedAction.sourceRunId ||
            null
        const sourceRootRemixAiRunId =
            clonedAction.root_remix_ai_run_id ||
            clonedAction.rootRemixAiRunId ||
            clonedAction.remix_source?.root_remix_ai_run_id ||
            clonedAction.remixSource?.rootRemixAiRunId ||
            null
        const sourceParentRemixAiRunId =
            clonedAction.remix_ai_run_id ||
            clonedAction.remixAiRunId ||
            clonedAction.remix_source?.remix_ai_run_id ||
            clonedAction.remixSource?.remixAiRunId ||
            null
        const rootRemixAiRunId =
            sourceRootRemixAiRunId || sourceParentRemixAiRunId || sourceAiRunId || null

        delete clonedAction.client_id
        delete clonedAction.ai_action_id
        delete clonedAction.actionId
        delete clonedAction.ai_run_id
        delete clonedAction.aiRunId
        delete clonedAction.ai_run_ids
        delete clonedAction.aiRunIds
        delete clonedAction.id
        delete clonedAction.idempotency_key
        delete clonedAction.idempotencyKey
        delete clonedAction.provider_job_id
        delete clonedAction.providerJobId
        delete clonedAction.generation_index
        delete clonedAction.generationIndex
        delete clonedAction.status
        delete clonedAction.result_payload
        delete clonedAction.resultPayload
        delete clonedAction.error_payload
        delete clonedAction.errorPayload
        clonedAction.sync_with_draft = false

        const generationCount = this.generationCountForAction(clonedAction)
        const aiRunIds = Array.from({ length: generationCount }, () => this.createAiRunId())
        const actionId = this.createAiActionClientId()
        return {
            ...clonedAction,
            id: actionId,
            client_id: actionId,
            ai_run_id: aiRunIds[0],
            ai_run_ids: aiRunIds,
            remix_ai_run_id: sourceAiRunId,
            root_remix_ai_run_id: rootRemixAiRunId,
            remix_kind: 'remix',
            remix_source: this.compactObject({
                ai_run_id: sourceAiRunId,
                root_remix_ai_run_id: rootRemixAiRunId,
                answer_id:
                    clonedAction.answer_id ||
                    clonedAction.answerId ||
                    clonedAction.remix_source?.answer_id ||
                    clonedAction.remixSource?.answerId ||
                    null,
                attachment_id:
                    clonedAction.attachment_id ||
                    clonedAction.attachmentId ||
                    clonedAction.remix_source?.attachment_id ||
                    clonedAction.remixSource?.attachmentId ||
                    null,
            }),
        }
    }

    derivationKindForMediaCommandTag = (rawTag, { remix = false, previewTitle = null } = {}) => {
        if (remix) return 'remix'

        const tag = normalizeActionTag(rawTag)
        if (tag === 'extend') return 'extend'
        if (/^(animate|kling|kling3)(_|$)/.test(tag)) return 'animate'
        if (/^(edit|nb|nbpro|nanobanana|qwen)(_|$)/.test(tag)) return 'edit'

        const title = String(previewTitle || '').toLowerCase()
        if (title.includes('edit')) return 'edit'
        if (title.includes('animate')) return 'animate'
        if (title.includes('extend')) return 'extend'

        return null
    }

    rootRemixAiRunIdFromMediaInput = input =>
        input?.rootRemixAiRunId ||
        input?.root_remix_ai_run_id ||
        input?.remixAiRunId ||
        input?.remix_ai_run_id ||
        input?.runId ||
        input?.run_id ||
        null

    canonicalRemixCommandTag = rawTag => {
        return canonicalActionTagFor(rawTag) || null
    }

    clearPostedDraftActionRequests = (answerId, { cacheAnswerState = true } = {}) => {
        this._preserveAiActionInputsOnNextDraftSave = false
        this.rememberDraftActionRequests([])
        if (this.state.draftActionRequests?.length) {
            this.setState({ draftActionRequests: [] })
        }

        const draftAnswerId = answerId || this.props.newAnswer?.id
        if (cacheAnswerState && draftAnswerId) {
            cache.updateCachedAnswer(draftAnswerId, {
                action_requests: [],
            })
        }
    }

    isArchivedAiInputSource = answer => {
        const cachedAnswer = answer?.id ? getCachedAnswer(answer.id) || answer : answer
        return !!cachedAnswer?.archive
    }

    isGeneratedAnswerImage = (answer, image) => {
        const filename = image?.image_filename || image?.imageFilename || ''
        return !!answer?.id && filename.startsWith(`stable_diffusion_${answer.id}`)
    }

    hasCompletedAiRunImageOutput = answer => {
        return (answer?.ai_runs || []).some(run => {
            if (run?.status !== 'succeeded' && run?.status !== 'finished') return false
            return (run?.output_urls || []).some(output => this.aiRunOutputKind(output) === 'image')
        })
    }

    getMostRecentThreadAiImageInput = () => {
        const answers = [
            this.props.quest?.parent
                ? {
                      answer:
                          this.props.hydratedParentAnswer ||
                          this.props.parentAnswer ||
                          getCachedAnswer(this.props.quest.parent?.id) ||
                          this.props.quest.parent,
                      index: 0,
                  }
                : null,
            ...(this.props.quest?.sorted_answers || []).map((answer, index) => ({
                answer: getCachedAnswer(answer?.id) || answer,
                index: index + 1,
            })),
        ].filter(item => item?.answer && !this.isArchivedAiInputSource(item.answer))

        const imageCandidates = answers.flatMap(({ answer, index }) => {
            const answerTimestamp =
                Date.parse(answer?.updated_at || answer?.created_at || '') ||
                Number(answer?.position) ||
                index
            return this.getAnswerAiImageCandidates(answer, null).map((image, imageIndex) => ({
                ...image,
                answerTimestamp,
                answerIndex: index,
                imageIndex,
            }))
        })

        return (
            imageCandidates
                .filter(image => image.url)
                .sort((a, b) => {
                    if (a.answerTimestamp !== b.answerTimestamp) {
                        return b.answerTimestamp - a.answerTimestamp
                    }
                    if (a.answerIndex !== b.answerIndex) return b.answerIndex - a.answerIndex
                    return a.imageIndex - b.imageIndex
                })[0] || null
        )
    }

    currentAnswerInputAttrsForActionCommand = (
        tag,
        answerOverride = null,
        deltaJsonOverride = null,
        { includeThreadImageFallback = true } = {},
    ) => {
        const normalizedTag = normalizeActionTag(tag)
        if (!normalizedTag) return {}

        const model = findModelByActionTag(this.props.aiModels || [], normalizedTag)
        const inferredModelType = inferActionCommandModelType(normalizedTag)
        const sourceAnswer =
            answerOverride ||
            getCachedAnswer(this.getComposeAnswerId()) ||
            this.props.newAnswer ||
            this.props.quest?.parent ||
            {}
        const answer = deltaJsonOverride
            ? {
                  ...sourceAnswer,
                  delta_json: deltaJsonOverride,
                  deltaJson: deltaJsonOverride,
              }
            : sourceAnswer
        const escapedTag = normalizedTag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        const textValue = (this.getCurrentAiTextInput() || '')
            .replace(new RegExp(`(^|[\\s\\n])!${escapedTag}(?=[\\s\\n.,;:!?]|$)`, 'gi'), '$1')
            .trim()
        const currentDraftMediaOptions = {
            includeAiActionInputs: false,
            includeAiRunOutputs: false,
        }
        const draftImages =
            this.getAnswerAiImageCandidates(answer, 'Attached image', currentDraftMediaOptions) ||
            []
        const draftVideos =
            this.getAnswerAiVideoCandidates(answer, 'Attached video', currentDraftMediaOptions) ||
            []
        const draftAudios =
            this.getAnswerAiAudioCandidates(answer, 'Attached audio', currentDraftMediaOptions) ||
            []
        const nbMediaInputs =
            normalizedTag === 'nb'
                ? partitionNbComposerMediaInputs({
                      images: draftImages,
                      videos: draftVideos,
                      audios: draftAudios,
                  })
                : null
        const draftImage = nbMediaInputs?.imageInput || draftImages[0] || null
        const video = normalizedTag === 'nb' ? null : draftVideos[0] || null
        const audio = nbMediaInputs?.audioInput || draftAudios[0] || null
        const image =
            draftImage ||
            (includeThreadImageFallback && inferActionCommandCanRemixImage(normalizedTag)
                ? this.getMostRecentThreadAiImageInput()
                : null)
        const config = actionPreviewConfig(normalizedTag, model)

        return {
            tag: normalizedTag,
            type: model || inferredModelType || config.modelType ? 'model' : 'tool',
            modelType: model?.modelType || inferredModelType || config.modelType || null,
            defaultDurationSeconds: model?.defaultDurationSeconds || null,
            defaultAspectRatio: getDefaultAspectRatioForActionCommand(normalizedTag),
            hasTextInput: !!textValue,
            textInputValue: textValue || null,
            hasImageInput: !!image,
            imageInputUrl: image?.url || null,
            imageInput: image || null,
            hasVideoInput: !!video,
            videoInputUrl: video?.url || null,
            videoPreviewImageUrl: video?.previewImageUrl || null,
            videoInputActionTag: video?.actionTag || null,
            videoInput: video || null,
            hasAudioInput: !!audio,
            audioInputUrl: audio?.url || null,
            audioInputSource: audio?.source || null,
            audioInputUploadId: audio?.uploadId || null,
            audioInputSignedId: audio?.signedId || null,
            audioInputLabel: audio?.label || null,
            audioInputContentType: audio?.contentType || null,
            audioInputFile: audio?.file || null,
            audioInput: audio || null,
            referenceImageInputs: nbMediaInputs?.referenceImageInputs || [],
            referenceVideoInputs: nbMediaInputs?.referenceVideoInputs || [],
            referenceAudioInputs: nbMediaInputs?.referenceAudioInputs || [],
        }
    }

    supportedInputKindsForActionCommand = tag => {
        const normalizedTag = normalizeActionTag(tag)
        const model = findModelByActionTag(this.props.aiModels || [], normalizedTag)
        return actionPreviewConfig(normalizedTag, model).supportedInputs
    }

    supportsVideoListInputForActionCommand = tag => {
        const normalizedTag = normalizeActionTag(tag)
        return normalizedTag === 'stitch'
    }

    supportsReferenceInputsForActionAttrs = attrs => {
        const tag = normalizeActionTag(attrs?.tag)
        if (!tag) return false

        const model = findModelByActionTag(this.props.aiModels || [], tag)
        const config = actionPreviewConfig(tag, model)
        if (config.supportsReferences === false) return false
        if (config.supportsReferences === true) return true

        const extendAiModel =
            tag === 'extend' && attrs?.extendAiModelId
                ? (this.props.aiModels || []).find(
                      candidate => candidate?.id === attrs.extendAiModelId,
                  )
                : null
        const pricedModel =
            extendAiModel ||
            (tag === 'extend' && attrs?.extendActionTag
                ? findModelByActionTag(this.props.aiModels || [], attrs.extendActionTag) || model
                : model)

        return modelSupportsReferences(pricedModel)
    }

    filterActionTokenAttrsForSupportedInputs = attrs => {
        const tag = normalizeActionTag(attrs?.tag)
        if (!tag) return attrs

        const supportedInputs = this.supportedInputKindsForActionCommand(tag)
        const supportsTextInput = supportedInputs.has('text')
        const supportsImageInput = supportedInputs.has('image')
        const supportsVideoInput = supportedInputs.has('video')
        const supportsAudioInput = supportedInputs.has('audio')
        const supportsVideoListInput = this.supportsVideoListInputForActionCommand(tag)
        const supportsReferenceInputs = this.supportsReferenceInputsForActionAttrs({
            ...attrs,
            tag,
        })
        const inputOrder = Array.isArray(attrs?.inputOrder)
            ? attrs.inputOrder.filter(inputKind => {
                  if (inputKind === 'text') return supportsTextInput
                  if (inputKind === 'image') return supportsImageInput
                  if (inputKind === 'video') return supportsVideoInput
                  if (inputKind === 'videos') return supportsVideoListInput
                  if (inputKind === 'audio') return supportsAudioInput
                  if (inputKind === 'references') return supportsReferenceInputs
                  return true
              })
            : attrs?.inputOrder

        return {
            ...attrs,
            tag,
            hasTextInput: supportsTextInput && attrs?.hasTextInput,
            textInputValue: supportsTextInput ? attrs?.textInputValue : null,
            promptContent: supportsTextInput ? attrs?.promptContent : null,
            prompt_content: supportsTextInput ? attrs?.prompt_content : null,
            supportsPromptInput: supportsTextInput ? attrs?.supportsPromptInput : false,
            supports_prompt_input: supportsTextInput ? attrs?.supports_prompt_input : false,
            hasImageInput: supportsImageInput && attrs?.hasImageInput,
            imageInputUrl: supportsImageInput ? attrs?.imageInputUrl : null,
            imageInputLabel: supportsImageInput ? attrs?.imageInputLabel : null,
            imageInput: supportsImageInput ? attrs?.imageInput : null,
            hasVideoInput: supportsVideoInput && attrs?.hasVideoInput,
            videoInputUrl: supportsVideoInput ? attrs?.videoInputUrl : null,
            videoPreviewImageUrl: supportsVideoInput ? attrs?.videoPreviewImageUrl : null,
            videoInputActionTag: supportsVideoInput ? attrs?.videoInputActionTag : null,
            videoInputLabel: supportsVideoInput ? attrs?.videoInputLabel : null,
            videoInput: supportsVideoInput ? attrs?.videoInput : null,
            videoInputs: supportsVideoListInput ? attrs?.videoInputs || [] : [],
            hasAudioInput: supportsAudioInput && attrs?.hasAudioInput,
            audioInputUrl: supportsAudioInput ? attrs?.audioInputUrl : null,
            audioInputSource: supportsAudioInput ? attrs?.audioInputSource : null,
            audioInputUploadId: supportsAudioInput ? attrs?.audioInputUploadId : null,
            audioInputSignedId: supportsAudioInput ? attrs?.audioInputSignedId : null,
            audioInputLabel: supportsAudioInput ? attrs?.audioInputLabel : null,
            audioInputContentType: supportsAudioInput ? attrs?.audioInputContentType : null,
            audioInputFile: supportsAudioInput ? attrs?.audioInputFile : null,
            audioInput: supportsAudioInput ? attrs?.audioInput : null,
            referenceImageInputs: supportsReferenceInputs ? attrs?.referenceImageInputs || [] : [],
            referenceVideoInputs: supportsReferenceInputs ? attrs?.referenceVideoInputs || [] : [],
            referenceAudioInputs: supportsReferenceInputs ? attrs?.referenceAudioInputs || [] : [],
            inputOrder,
        }
    }

    actionTokenAttrsWithPreviewDefaults = attrs => {
        const tag = normalizeActionTag(attrs?.tag)
        if (!tag) return null

        const model = findModelByActionTag(this.props.aiModels || [], tag)
        const config = actionPreviewConfig(tag, model)
        const defaults = config.defaultSettings || {}

        return this.filterActionTokenAttrsForSupportedInputs({
            ...attrs,
            tag,
            type:
                model || inferActionCommandModelType(tag) || config.modelType
                    ? 'model'
                    : attrs?.type,
            modelType: attrs?.modelType || model?.modelType || config.modelType || null,
            defaultDurationSeconds:
                attrs?.defaultDurationSeconds ?? model?.defaultDurationSeconds ?? null,
            defaultAspectRatio:
                attrs?.defaultAspectRatio ?? getDefaultAspectRatioForActionCommand(tag),
            durationSeconds: attrs?.durationSeconds ?? defaults.durationSeconds ?? null,
            aspectRatio: attrs?.aspectRatio ?? defaults.aspectRatio ?? null,
            gifFrameRate: attrs?.gifFrameRate ?? defaults.gifFrameRate ?? null,
            gifMaxWidth: attrs?.gifMaxWidth ?? defaults.gifMaxWidth ?? null,
            screenshotTimestamp: attrs?.screenshotTimestamp ?? defaults.screenshotTimestamp ?? null,
        })
    }

    attrsWithSyncedDraftInputs = (attrs, answerOverride = null) => {
        if (!attrs?.syncWithDraft) return attrs

        const supportedInputs = this.supportedInputKindsForActionCommand(attrs.tag)
        const syncedAttrs = this.currentAnswerInputAttrsForActionCommand(attrs.tag, answerOverride)

        return this.filterActionTokenAttrsForSupportedInputs({
            ...attrs,
            ...syncedAttrs,
            hasTextInput: supportedInputs.has('text') && syncedAttrs.hasTextInput,
            textInputValue: supportedInputs.has('text') ? syncedAttrs.textInputValue : null,
            hasImageInput: supportedInputs.has('image') && syncedAttrs.hasImageInput,
            imageInputUrl: supportedInputs.has('image') ? syncedAttrs.imageInputUrl : null,
            imageInput: supportedInputs.has('image') ? syncedAttrs.imageInput : null,
            hasVideoInput: supportedInputs.has('video') && syncedAttrs.hasVideoInput,
            videoInputUrl: supportedInputs.has('video') ? syncedAttrs.videoInputUrl : null,
            videoPreviewImageUrl: supportedInputs.has('video')
                ? syncedAttrs.videoPreviewImageUrl
                : null,
            videoInput: supportedInputs.has('video') ? syncedAttrs.videoInput : null,
            hasAudioInput: supportedInputs.has('audio') && syncedAttrs.hasAudioInput,
            audioInputUrl: supportedInputs.has('audio') ? syncedAttrs.audioInputUrl : null,
            audioInputSource: supportedInputs.has('audio') ? syncedAttrs.audioInputSource : null,
            audioInputUploadId: supportedInputs.has('audio')
                ? syncedAttrs.audioInputUploadId
                : null,
            audioInputSignedId: supportedInputs.has('audio')
                ? syncedAttrs.audioInputSignedId
                : null,
            audioInputLabel: supportedInputs.has('audio') ? syncedAttrs.audioInputLabel : null,
            audioInputContentType: supportedInputs.has('audio')
                ? syncedAttrs.audioInputContentType
                : null,
            audioInputFile: supportedInputs.has('audio') ? syncedAttrs.audioInputFile : null,
            audioInput: supportedInputs.has('audio') ? syncedAttrs.audioInput : null,
            syncWithDraft: true,
        })
    }

    actionInputSnapshotFromAttrs = attrs => {
        const supportedInputs = this.supportedInputKindsForActionCommand(attrs?.tag)
        return {
            text:
                supportedInputs.has('text') && attrs?.hasTextInput
                    ? (attrs.textInputValue || '').trim()
                    : null,
            image:
                supportedInputs.has('image') && attrs?.hasImageInput
                    ? this.aiMediaInputPayload(
                          attrs.imageInput || {
                              url: attrs.imageInputUrl || null,
                          },
                      )
                    : null,
            video:
                supportedInputs.has('video') && attrs?.hasVideoInput
                    ? this.aiMediaInputPayload(
                          attrs.videoInput || {
                              url: attrs.videoInputUrl || null,
                              preview_image_url: attrs.videoPreviewImageUrl || null,
                              action_tag: attrs.videoInputActionTag || null,
                          },
                      )
                    : null,
            audio:
                supportedInputs.has('audio') && attrs?.hasAudioInput
                    ? this.aiMediaInputPayload(
                          attrs.audioInput || {
                              url: attrs.audioInputUrl || null,
                              source: attrs.audioInputSource || null,
                              upload_id: attrs.audioInputUploadId || null,
                              signed_id: attrs.audioInputSignedId || null,
                              label: attrs.audioInputLabel || null,
                              content_type: attrs.audioInputContentType || null,
                              file: attrs.audioInputFile || null,
                          },
                      )
                    : null,
            referenceImages: (attrs?.referenceImageInputs || []).map(input =>
                this.aiMediaInputPayload(input),
            ),
            referenceVideos: (attrs?.referenceVideoInputs || []).map(input =>
                this.aiMediaInputPayload(input),
            ),
            referenceAudios: (attrs?.referenceAudioInputs || []).map(input =>
                this.aiMediaInputPayload(input),
            ),
        }
    }

    wouldSyncAlterActionInputs = attrs => {
        if (!attrs || attrs.syncWithDraft) return false
        const syncedAttrs = this.attrsWithSyncedDraftInputs({ ...attrs, syncWithDraft: true })
        return !isEqual(
            this.actionInputSnapshotFromAttrs(attrs),
            this.actionInputSnapshotFromAttrs(syncedAttrs),
        )
    }

    displayMetadataForActionAttrs = attrs => {
        const attrsWithDefaults = this.actionTokenAttrsWithPreviewDefaults(attrs)
        const tag = normalizeActionTag(attrsWithDefaults?.tag)
        const model =
            findEffectiveModelForAction(this.props.aiModels || [], tag, attrsWithDefaults) ||
            findModelByActionTag(this.props.aiModels || [], tag)
        const extendAiModel =
            tag === 'extend' && attrsWithDefaults?.extendAiModelId
                ? (this.props.aiModels || []).find(
                      candidate => candidate?.id === attrsWithDefaults.extendAiModelId,
                  )
                : null
        const pricedModel =
            extendAiModel ||
            (tag === 'extend' && attrsWithDefaults?.extendActionTag
                ? findModelByActionTag(
                      this.props.aiModels || [],
                      attrsWithDefaults.extendActionTag,
                  ) || model
                : model)
        const durationSeconds =
            attrsWithDefaults?.durationSeconds ?? pricedModel?.defaultDurationSeconds ?? null
        const generationCount = this.generationCountForAction(attrsWithDefaults)
        const displayName =
            attrsWithDefaults?.displayName ||
            attrsWithDefaults?.display_name ||
            aiActionTokenDisplayName(model, tag)
        const singleGenerationPrice = Number(
            modelActionPrice(
                pricedModel,
                isPublicAiPricingContext(this.props.quest),
                durationSeconds,
                getActionPricingContext(pricedModel, attrsWithDefaults),
            ),
        )
        const priceLabel = formatAiActionPrice(
            Number.isFinite(singleGenerationPrice)
                ? singleGenerationPrice * generationCount
                : singleGenerationPrice,
        )

        return {
            display_name: displayName,
            extend_model_display_name:
                tag === 'extend' &&
                pricedModel &&
                (attrsWithDefaults?.extendAiModelId || attrsWithDefaults?.extendActionTag)
                    ? aiActionModelDisplayName(pricedModel, attrsWithDefaults.extendActionTag)
                    : null,
            price_label: priceLabel,
            default_duration_seconds: pricedModel?.defaultDurationSeconds ?? null,
            default_aspect_ratio: getDefaultAspectRatioForActionCommand(
                pricedModel?.actionTagName || model?.actionTagName || tag,
            ),
            gif_frame_rate: attrsWithDefaults?.gifFrameRate ?? null,
            gif_max_width: attrsWithDefaults?.gifMaxWidth ?? null,
            screenshot_timestamp: attrsWithDefaults?.screenshotTimestamp ?? null,
        }
    }

    defaultPromptForActionCommand = tag => {
        const normalizedTag = normalizeActionTag(tag)
        if (/^animate(?:_|$)/.test(normalizedTag)) return 'animate'
        if (/^edit(?:_|$)/.test(normalizedTag)) return 'edit'
        return null
    }

    actionCommandRequiresPrompt = tag => {
        const normalizedTag = normalizeActionTag(tag)
        if (!normalizedTag) return false
        if (PROMPT_OPTIONAL_COMMAND_TAGS.has(normalizedTag)) return false
        if (this.defaultPromptForActionCommand(normalizedTag)) return false

        const model = findModelByActionTag(this.props.aiModels || [], normalizedTag)
        return !!model || !!inferActionCommandModelType(normalizedTag)
    }

    aiActionValidationKey = (action, index) => this.getAiActionRefId(action) || `${index}`

    requiredInputKindsForAiAction = action => {
        const tag = normalizeActionTag(action?.tag)
        if (!tag) return []

        if (tag === 'extend') return ['extend-model', 'video']
        if (tag === 'stitch') return ['video-0', 'video-1']
        if (tag === 'gif' || tag === 'screenshot') return ['video']

        const requiredInputKinds = []
        if (this.actionCommandRequiresPrompt(tag)) requiredInputKinds.push('text')
        if (IMAGE_REQUIRED_COMMAND_TAGS.has(tag)) requiredInputKinds.push('image')

        return requiredInputKinds
    }

    aiActionHasInputKind = (action, inputKind) => {
        const inputs = action?.inputs || {}
        const settings = action?.settings || {}
        const videos = Array.isArray(inputs?.videos)
            ? inputs.videos
            : Array.isArray(inputs?.references?.videos)
            ? inputs.references.videos
            : []

        if (inputKind === 'text') {
            return !!(inputs?.text?.value || action?.prompt || '').trim()
        }
        if (inputKind === 'image') {
            const referenceImages = Array.isArray(inputs?.references?.images)
                ? inputs.references.images
                : []
            return (
                !!inputs?.image?.url ||
                !!inputs?.start_image?.url ||
                !!inputs?.end_image?.url ||
                referenceImages.some(image => !!image?.url)
            )
        }
        if (inputKind === 'video') return !!inputs?.video?.url
        if (inputKind === 'audio') return !!inputs?.audio?.url
        if (inputKind === 'lyrics') return !!settings?.lyrics?.trim()
        if (inputKind === 'extend-model') {
            return !!(settings?.extend_ai_model_id || settings?.extend_action_tag)
        }
        if (inputKind.startsWith('video-')) {
            const index = Number(inputKind.split('-')[1])
            return Number.isInteger(index) && !!videos[index]?.url
        }

        return false
    }

    missingRequiredInputsForAiAction = action =>
        this.requiredInputKindsForAiAction(action).filter(
            inputKind => !this.aiActionHasInputKind(action, inputKind),
        )

    pulseMissingAiActionInputs = missingByActionKey => {
        const token = Date.now()
        if (this.aiActionInputValidationClearTimer) {
            clearTimeout(this.aiActionInputValidationClearTimer)
        }

        this.setState(
            {
                aiActionInputValidation: {
                    token,
                    missingByActionKey,
                },
            },
            () => {
                const firstKey = Object.keys(missingByActionKey)[0]
                if (!firstKey) return
                const escapedKey = window.CSS?.escape?.(firstKey) || firstKey.replace(/"/g, '\\"')
                const selector = `[data-ai-action-validation-key="${escapedKey}"]`
                document
                    .querySelector(selector)
                    ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
            },
        )

        this.aiActionInputValidationClearTimer = setTimeout(() => {
            this.setState(state =>
                state.aiActionInputValidation?.token === token
                    ? { aiActionInputValidation: null }
                    : null,
            )
            this.aiActionInputValidationClearTimer = null
        }, AI_ACTION_INPUT_VALIDATION_CLEAR_MS)
    }

    validateAiActionInputsForPost = deltaJson => {
        const actions = this.getActionRequestsForSubmit(deltaJson)
        let promptValidationMessage = null
        const invalidByActionKey = actions.reduce((acc, action, index) => {
            const invalidInputKinds = this.missingRequiredInputsForAiAction(action)
            const currentPromptValidationMessage = aiActionPromptValidationMessage(action)
            if (currentPromptValidationMessage) {
                if (!invalidInputKinds.includes('text')) invalidInputKinds.push('text')
                promptValidationMessage ||= currentPromptValidationMessage
            }
            if (invalidInputKinds.length) {
                acc[this.aiActionValidationKey(action, index)] = invalidInputKinds
            }
            return acc
        }, {})

        if (Object.keys(invalidByActionKey).length === 0) {
            if (this.aiActionInputValidationClearTimer) {
                clearTimeout(this.aiActionInputValidationClearTimer)
                this.aiActionInputValidationClearTimer = null
            }
            if (this.state.aiActionInputValidation) {
                this.setState({ aiActionInputValidation: null })
            }
            return true
        }

        this.pulseMissingAiActionInputs(invalidByActionKey)
        if (promptValidationMessage) {
            this.showNotice(promptValidationMessage, { variant: 'error', durationMs: 9000 })
        }
        return false
    }

    buildAiActionFromAttrs = (rawAttrs, deltaJson = null, options = {}) => {
        const syncedAttrs =
            options.syncDraftInputs === false ? rawAttrs : this.attrsWithSyncedDraftInputs(rawAttrs)
        const attrs = this.actionTokenAttrsWithPreviewDefaults(syncedAttrs)
        const tag = attrs?.tag
        if (!tag) return null

        const prompt =
            options.useComposerPromptFallback === false
                ? ''
                : getText(deltaJson || this.getDeltaJson() || {}, {
                      excludeActionTokens: true,
                  }).trim()
        const promptContentValue =
            typeof attrs.promptContent === 'string'
                ? attrs.promptContent.trim()
                : typeof attrs.prompt_content === 'string'
                ? attrs.prompt_content.trim()
                : ''
        const promptIsPlaceholder = attrs.promptPlaceholder === true
        const textInputValue =
            attrs.textInputValue?.trim() || (promptIsPlaceholder ? '' : promptContentValue)
        const defaultPrompt = promptIsPlaceholder
            ? promptContentValue
            : this.defaultPromptForActionCommand(tag)
        const displayActionPrompt =
            textInputValue || (promptIsPlaceholder ? defaultPrompt : prompt) || defaultPrompt || ''
        const actionPrompt = serializePromptReferenceMentions(
            displayActionPrompt,
            attrs,
            promptReferenceLabelsForAttrs(attrs),
        )
        const usesDefaultPrompt = promptIsPlaceholder
            ? !textInputValue && !!defaultPrompt
            : !textInputValue && !prompt && !!defaultPrompt
        const displayMetadata = this.displayMetadataForActionAttrs(attrs)
        const generationCount = this.generationCountForAction(attrs)
        const aiRunIds = this.aiRunIdsForAction({
            ...attrs,
            generation_count: generationCount,
        })
        const imageInput = attrs.imageInput || {
            url: attrs.imageInputUrl || null,
            label: attrs.imageInputLabel || null,
        }
        const videoInput = attrs.videoInput || {
            url: attrs.videoInputUrl || null,
            preview_image_url: attrs.videoPreviewImageUrl || null,
            action_tag: attrs.videoInputActionTag || null,
        }
        const audioInput = attrs.audioInput || {
            url: attrs.audioInputUrl || null,
            source: attrs.audioInputSource || null,
            upload_id: attrs.audioInputUploadId || null,
            signed_id: attrs.audioInputSignedId || null,
            label: attrs.audioInputLabel || null,
            content_type: attrs.audioInputContentType || null,
            file: attrs.audioInputFile || null,
        }

        const settings = {
            duration_seconds: attrs.durationSeconds ?? null,
            aspect_ratio: attrs.aspectRatio ?? null,
            generation_count: generationCount,
            gif_frame_rate: attrs.gifFrameRate ?? null,
            gif_max_width: attrs.gifMaxWidth ?? null,
            screenshot_timestamp: attrs.screenshotTimestamp ?? null,
            extend_ai_model_id: attrs.extendAiModelId ?? null,
            extend_action_tag: attrs.extendAiModelId ? null : attrs.extendActionTag ?? null,
            extend_timestamp: attrs.extendTimestamp ?? null,
            smart_stitch: attrs.smartStitch === true,
            lyrics: attrs.lyrics ?? null,
        }
        // Pay is a structured tool: parse the pill prompt ("@user 2000 sats") into settings.pay so
        // the synced pay card reads recipient/amount from it, and submit sends from it. We keep the
        // PARTIAL parse (recipient-only, amount-only) rather than discarding incomplete input, so
        // the card can render in-progress state and its card<->pill echo guard can converge on it.
        // amountSats is populated only when the payment is fully valid (gating submit).
        if (normalizeActionTag(tag) === 'pay') {
            settings.pay = parsePayGroup(
                displayActionPrompt || promptContentValue || textInputValue,
            )
        }
        const referenceImagePayloads = (attrs.referenceImageInputs || []).map((image, index) => ({
            ...this.aiMediaInputPayload(image),
            label: `[Image${index + 1}]`,
            source_label: image.label || null,
        }))
        const genericImagePayload = attrs.hasImageInput
            ? this.aiMediaInputPayload(imageInput)
            : null
        const keyframes = videoKeyframeInputsFromFormState(
            tag,
            genericImagePayload,
            referenceImagePayloads,
            attrs.videoKeyframeMode === true,
        )
        const keyframePayload = keyframes.frameMode
            ? explicitVideoKeyframePayload(tag, keyframes.startImage, keyframes.endImage)
            : null
        const remainingReferenceImages = keyframePayload
            ? referenceImagePayloads.filter(
                  image => image !== keyframes.startImage && image !== keyframes.endImage,
              )
            : referenceImagePayloads
        const inputs = {
            ...(attrs.hasTextInput || textInputValue
                ? {
                      text: {
                          value: actionPrompt,
                      },
                  }
                : {}),
            ...(keyframePayload || {}),
            ...(!keyframePayload && attrs.hasImageInput
                ? {
                      image: genericImagePayload,
                  }
                : {}),
            ...(attrs.hasVideoInput
                ? {
                      video: this.aiMediaInputPayload(videoInput),
                  }
                : {}),
            ...(attrs.videoInputs?.length
                ? {
                      videos: attrs.videoInputs.map((video, index) => ({
                          ...this.aiMediaInputPayload(video),
                          label: video.label || `[Video${index + 1}]`,
                          source_label: video.label || null,
                      })),
                  }
                : {}),
            ...(attrs.hasAudioInput
                ? {
                      audio: this.aiMediaInputPayload(audioInput),
                  }
                : {}),
            ...(remainingReferenceImages.length ||
            attrs.referenceVideoInputs?.length ||
            attrs.referenceAudioInputs?.length
                ? {
                      references: {
                          ...(remainingReferenceImages.length
                              ? {
                                    images: remainingReferenceImages,
                                }
                              : {}),
                          ...(attrs.referenceVideoInputs?.length
                              ? {
                                    videos: attrs.referenceVideoInputs.map((video, index) => ({
                                        ...this.aiMediaInputPayload(video),
                                        label: `[Video${index + 1}]`,
                                        source_label: video.label || null,
                                    })),
                                }
                              : {}),
                          ...(attrs.referenceAudioInputs?.length
                              ? {
                                    audios: attrs.referenceAudioInputs.map((audio, index) => ({
                                        ...this.aiMediaInputPayload(audio),
                                        label: `[Audio${index + 1}]`,
                                        source_label: audio.label || null,
                                    })),
                                }
                              : {}),
                      },
                  }
                : {}),
        }

        const actionId = this.getAiActionRefId(attrs) || this.createAiActionClientId()
        const kind = attrs.kind || attrs.type || 'model'
        const actionDefinitionId = attrs.actionDefinitionId || attrs.action_definition_id || null
        const fallbackAiModelId =
            kind === 'model' &&
            actionDefinitionId &&
            !`${actionDefinitionId}`.startsWith('fallback-')
                ? actionDefinitionId
                : null
        const baseAiModelId = attrs.aiModelId || attrs.ai_model_id || fallbackAiModelId
        const effectiveAiModel = findEffectiveModelForAction(this.props.aiModels || [], tag, {
            ...attrs,
            aiModelId: baseAiModelId,
        })
        const aiModelId = effectiveAiModel?.id || baseAiModelId

        return {
            id: actionId,
            client_id: actionId,
            action_definition_id: actionDefinitionId,
            ai_run_id: aiRunIds[0],
            ai_run_ids: aiRunIds,
            remix_ai_run_id: attrs.remixAiRunId || attrs.remix_ai_run_id || null,
            root_remix_ai_run_id: attrs.rootRemixAiRunId || attrs.root_remix_ai_run_id || null,
            remix_kind: attrs.remixKind || attrs.remix_kind || null,
            kind,
            tag,
            action_tag: tag,
            actionTag: tag,
            type: kind,
            ai_model_id: aiModelId,
            tool_key: attrs.toolKey || attrs.tool_key || null,
            model_type: attrs.modelType || inferActionCommandModelType(tag) || null,
            analytics_source:
                attrs.analyticsSource ||
                attrs.analytics_source ||
                attrs.usageSource ||
                attrs.usage_source ||
                null,
            command_text: attrs.commandText || `!${tag}`,
            prompt: actionPrompt,
            prompt_placement: attrs.promptPlacement || attrs.prompt_placement || null,
            prompt_is_default: usesDefaultPrompt,
            sync_with_draft: false,
            ...displayMetadata,
            preview_title: attrs.previewTitle || attrs.preview_title || null,
            settings,
            inputs,
            input_order: attrs.inputOrder || [],
        }
    }

    nbComposerMediaAttrs = (deltaJson = null) =>
        this.currentAnswerInputAttrsForActionCommand('nb', null, deltaJson || this.getDeltaJson(), {
            includeThreadImageFallback: false,
        })

    nbComposerMediaFingerprint = composerAttrs => {
        const snapshot = this.actionInputSnapshotFromAttrs({
            ...composerAttrs,
            hasTextInput: false,
            textInputValue: null,
        })
        return JSON.stringify({
            image: snapshot.image,
            audio: snapshot.audio,
            referenceImages: snapshot.referenceImages,
            referenceVideos: snapshot.referenceVideos,
            referenceAudios: snapshot.referenceAudios,
        })
    }

    nbActionRequestsWithComposerMedia = (
        actionRequests,
        deltaJson = null,
        composerAttrs = null,
    ) => {
        if (!Array.isArray(actionRequests) || !actionRequests.length) return actionRequests || []

        const resolvedComposerAttrs = composerAttrs || this.nbComposerMediaAttrs(deltaJson)
        const hasComposerMedia =
            resolvedComposerAttrs.hasImageInput ||
            resolvedComposerAttrs.hasAudioInput ||
            resolvedComposerAttrs.referenceImageInputs?.length ||
            resolvedComposerAttrs.referenceVideoInputs?.length ||
            resolvedComposerAttrs.referenceAudioInputs?.length
        if (!hasComposerMedia) return actionRequests

        return actionRequests.map(action => {
            if (normalizeActionTag(action?.tag || action?.action_tag) !== 'nb') return action

            const existingAttrs = this.aiActionToActionTokenAttrs(action)
            if (!existingAttrs) return action
            const mergedAttrs = mergeNbComposerMediaInputAttrs(existingAttrs, resolvedComposerAttrs)
            if (
                isEqual(
                    this.actionInputSnapshotFromAttrs(existingAttrs),
                    this.actionInputSnapshotFromAttrs(mergedAttrs),
                )
            ) {
                return action
            }

            const rebuiltAttrs =
                existingAttrs.promptPlaceholder === true
                    ? {
                          ...mergedAttrs,
                          hasTextInput: false,
                          textInputValue: null,
                      }
                    : mergedAttrs
            const rebuiltAction = this.buildAiActionFromAttrs(
                {
                    ...rebuiltAttrs,
                    actionId: this.getAiActionRefId(action),
                    syncWithDraft: false,
                },
                deltaJson || this.getDeltaJson(),
                { syncDraftInputs: false, useComposerPromptFallback: false },
            )
            if (!rebuiltAction) return action

            return this.ensureAiActionClientId({
                ...action,
                ...rebuiltAction,
                id: action.id || action.client_id,
                client_id: action.client_id || action.id,
                ai_run_id: action.ai_run_id || rebuiltAction.ai_run_id,
                ai_run_ids: action.ai_run_ids || rebuiltAction.ai_run_ids,
            })
        })
    }

    syncNbActionRequestsWithComposerMedia = (
        deltaJson = null,
        { onlyIfComposerMediaChanged = false } = {},
    ) => {
        const composerAttrs = this.nbComposerMediaAttrs(deltaJson)
        const composerMediaFingerprint = this.nbComposerMediaFingerprint(composerAttrs)
        if (
            onlyIfComposerMediaChanged &&
            composerMediaFingerprint === this._nbComposerMediaFingerprint
        ) {
            return false
        }
        this._nbComposerMediaFingerprint = composerMediaFingerprint

        const currentActions = this.getCurrentDraftActionRequests()
        const nextActions = this.nbActionRequestsWithComposerMedia(
            currentActions,
            deltaJson,
            composerAttrs,
        )
        if (isEqual(currentActions, nextActions)) return false

        this.setDraftActionRequests(nextActions)
        return true
    }

    getActionRequestsForSubmit = (deltaJson = null) => {
        const currentActions = this.getCurrentDraftActionRequests()
        const deltaActionTokens = this.getDeltaActionTokenAttrs(deltaJson || this.getDeltaJson())
        if (!deltaActionTokens.length) return currentActions

        let changed = false
        const actionsById = new Map()
        currentActions.forEach(action => {
            const actionId = this.getAiActionRefId(action)
            if (actionId) actionsById.set(actionId, action)
        })
        const nextActionRequests = deltaActionTokens
            .map((rawAttrs, nodeIndex) => {
                const tag = normalizeActionTag(rawAttrs.tag)
                if (!tag) return null

                const existingActionId = this.getAiActionRefId(rawAttrs)
                const existingAction =
                    (existingActionId && actionsById.get(existingActionId)) ||
                    (normalizeActionTag(currentActions[nodeIndex]?.tag) === tag
                        ? currentActions[nodeIndex]
                        : null)
                if (existingAction) return existingAction

                const actionId = existingActionId || this.createAiActionClientId()
                const currentInputAttrs = {
                    ...this.currentAnswerInputAttrsForActionCommand(tag, null, deltaJson),
                    hasTextInput: false,
                    textInputValue: null,
                    promptContent: null,
                }
                const baseAttrs = {
                    ...currentInputAttrs,
                    ...rawAttrs,
                    tag,
                    ai_action_id: actionId,
                    actionId,
                    aiRunId: rawAttrs.aiRunId || this.createAiRunId(),
                    commandText: rawAttrs.commandText || `!${tag}`,
                    syncWithDraft: false,
                }
                const action = this.ensureAiActionClientId(
                    this.buildAiActionFromAttrs(
                        {
                            ...baseAttrs,
                            actionId,
                            syncWithDraft: false,
                        },
                        deltaJson,
                        { syncDraftInputs: false, useComposerPromptFallback: false },
                    ),
                )
                if (action) changed = true
                return action
            })
            .filter(Boolean)

        if (
            changed ||
            nextActionRequests.length !== currentActions.length ||
            nextActionRequests.some(
                (action, index) =>
                    this.getAiActionRefId(action) !== this.getAiActionRefId(currentActions[index]),
            )
        ) {
            this.rememberDraftActionRequests(nextActionRequests)
        }

        return nextActionRequests
    }

    runProgrammaticAiActionEditorUpdate = dispatchUpdate => {
        this._programmaticAiActionEditorSyncSkips =
            (this._programmaticAiActionEditorSyncSkips || 0) + 1
        dispatchUpdate()

        setTimeout(() => {
            if (this._programmaticAiActionEditorSyncSkips > 0) {
                this._programmaticAiActionEditorSyncSkips -= 1
            }
        }, 50)
    }

    getCurrentActionRequestsForPreview = () => {
        return this.getCurrentDraftActionRequests()
    }

    aiMediaInputHasPendingUpload = input => {
        if (!input || typeof input !== 'object') return false
        const hasUploadIdentity =
            input.source === 'upload' ||
            input.upload_id ||
            input.uploadId ||
            input.url?.startsWith?.('blob:')
        if (!hasUploadIdentity) return false

        const uploadStatus = this.aiInputUploadManager?.getStatusForInput?.(input)
        if (!uploadStatus) return false
        if (uploadStatus.status !== 'uploading') return false

        return !input.signed_id && !input.signedId && !this.isPersistentDraftPayloadUrl(input.url)
    }

    aiMediaInputHasFailedUpload = input => {
        if (!input || typeof input !== 'object') return false
        const uploadStatus = this.aiInputUploadManager?.getStatusForInput?.(input)
        return uploadStatus?.status === 'failed'
    }

    aiActionHasPendingUpload = action => {
        const inputs = action?.inputs || {}
        const references = inputs.references || {}
        return (
            ['image', 'start_image', 'end_image', 'video', 'audio', 'file'].some(kind =>
                this.aiMediaInputHasPendingUpload(inputs[kind]),
            ) ||
            (Array.isArray(inputs.videos) &&
                inputs.videos.some(this.aiMediaInputHasPendingUpload)) ||
            ['images', 'videos', 'audios'].some(referenceKey =>
                (Array.isArray(references?.[referenceKey]) ? references[referenceKey] : []).some(
                    this.aiMediaInputHasPendingUpload,
                ),
            )
        )
    }

    aiActionHasFailedUpload = action => {
        const inputs = action?.inputs || {}
        const references = inputs.references || {}
        return (
            ['image', 'start_image', 'end_image', 'video', 'audio', 'file'].some(kind =>
                this.aiMediaInputHasFailedUpload(inputs[kind]),
            ) ||
            (Array.isArray(inputs.videos) &&
                inputs.videos.some(this.aiMediaInputHasFailedUpload)) ||
            ['images', 'videos', 'audios'].some(referenceKey =>
                (Array.isArray(references?.[referenceKey]) ? references[referenceKey] : []).some(
                    this.aiMediaInputHasFailedUpload,
                ),
            )
        )
    }

    aiActionInputPreviewUrl = input => {
        if (!input || typeof input !== 'object') return ''
        const url = input.url || ''
        if (this.isPersistentDraftPayloadUrl(url)) return url
        if (url?.startsWith?.('blob:') && this.aiInputUploadManager?.getStatusForInput?.(input)) {
            return url
        }

        return (
            activeStorageBlobPreviewUrl(
                input.signed_id || input.signedId || null,
                input.label || input.source_label || input.sourceLabel || null,
            ) || ''
        )
    }

    aiActionToActionTokenAttrs = action => {
        const tag = normalizeActionTag(action?.tag)
        if (!tag) return null

        const model = findModelByActionTag(this.props.aiModels || [], tag)
        const config = actionPreviewConfig(tag, model)
        const defaultSettings = config.defaultSettings || {}
        const settings = action?.settings || {}
        const inputs = action?.inputs || {}
        const references = inputs?.references || {}
        const keyframes = videoKeyframeInputs(tag, inputs)
        const displayImageInput = keyframes.startImage || inputs.image
        const displayReferenceImages = keyframes.frameMode
            ? keyframes.endImage
                ? [keyframes.endImage]
                : []
            : references?.images || []
        const imageInputUrl = this.aiActionInputPreviewUrl(displayImageInput)
        const videoInputUrl = this.aiActionInputPreviewUrl(inputs.video)
        const audioInputUrl = this.aiActionInputPreviewUrl(inputs.audio)
        const serializedTextValue = inputs?.text?.value || action?.prompt || null
        const textValue = serializedTextValue
            ? deserializePromptReferenceMentions(serializedTextValue, {
                  tag,
                  settings,
                  inputs,
              })
            : null
        const defaultPrompt = this.defaultPromptForActionCommand(tag)
        const inferredModelType = inferActionCommandModelType(tag)
        const kind = action?.kind || action?.type || 'model'
        const displayName =
            tag === 'pay'
                ? 'pay'
                : action?.display_name ||
                  action?.displayName ||
                  aiActionTokenDisplayName(model, tag)
        const supportsPromptInput =
            action?.supports_prompt_input !== false && action?.supportsPromptInput !== false

        return {
            tag,
            ai_action_id: action?.id || action?.client_id || null,
            actionId: action?.id || action?.client_id || null,
            craftStatus: action?.craft_context?.craft_status || null,
            promptContent: textValue?.trim() || null,
            promptPlacement: action?.prompt_placement || action?.promptPlacement || null,
            promptPlaceholder:
                action?.prompt_is_default === true &&
                (action?.prompt_placement === 'before' || action?.promptPlacement === 'before'),
            supportsPromptInput,
            displayName,
            actionDefinitionId: action?.action_definition_id || action?.actionDefinitionId || null,
            kind,
            aiModelId: action?.ai_model_id || action?.aiModelId || null,
            toolKey: action?.tool_key || action?.toolKey || null,
            analyticsSource:
                action?.analytics_source ||
                action?.analyticsSource ||
                action?.usage_source ||
                action?.usageSource ||
                null,
            aiRunId: action?.ai_run_id || null,
            aiRunIds: Array.isArray(action?.ai_run_ids) ? action.ai_run_ids : null,
            remixAiRunId: action?.remix_ai_run_id || null,
            rootRemixAiRunId: action?.root_remix_ai_run_id || null,
            remixKind: action?.remix_kind || null,
            type: kind,
            modelType:
                action?.model_type ||
                model?.modelType ||
                inferredModelType ||
                config.modelType ||
                null,
            defaultDurationSeconds:
                action?.default_duration_seconds ??
                settings.default_duration_seconds ??
                model?.defaultDurationSeconds ??
                null,
            defaultAspectRatio:
                action?.default_aspect_ratio ??
                settings.default_aspect_ratio ??
                getDefaultAspectRatioForActionCommand(tag),
            durationSeconds: settings.duration_seconds ?? null,
            aspectRatio: settings.aspect_ratio ?? null,
            generationCount: settings.generation_count ?? 1,
            gifFrameRate: settings.gif_frame_rate ?? defaultSettings.gifFrameRate ?? null,
            gifMaxWidth: settings.gif_max_width ?? defaultSettings.gifMaxWidth ?? null,
            screenshotTimestamp:
                settings.screenshot_timestamp ?? defaultSettings.screenshotTimestamp ?? null,
            extendAiModelId: settings.extend_ai_model_id ?? settings.extendAiModelId ?? null,
            extendActionTag: settings.extend_action_tag ?? null,
            extendTimestamp: settings.extend_timestamp ?? null,
            smartStitch: settings.smart_stitch === true,
            previewTitle: action?.preview_title || action?.previewTitle || null,
            hasTextInput: !!textValue,
            textInputValue: textValue,
            textInputIsDefault:
                action?.prompt_is_default === true ||
                (!inputs?.text?.value && !!defaultPrompt && textValue === defaultPrompt),
            hasImageInput: !!(
                imageInputUrl ||
                displayImageInput?.id ||
                displayImageInput?.attachment_id ||
                displayImageInput?.blob_id ||
                displayImageInput?.signed_id
            ),
            imageInputUrl: imageInputUrl || null,
            imageInput: displayImageInput
                ? {
                      url: imageInputUrl,
                      id:
                          displayImageInput.id ||
                          displayImageInput.attachment_id ||
                          displayImageInput.blob_id ||
                          `image-input-${
                              displayImageInput.url || displayImageInput.signed_id || ''
                          }`,
                      label:
                          displayImageInput.label ||
                          (keyframes.frameMode ? 'Start frame' : 'Image input'),
                      source: displayImageInput.source || null,
                      uploadId: displayImageInput.upload_id || displayImageInput.uploadId || null,
                      signedId: displayImageInput.signed_id || displayImageInput.signedId || null,
                      contentType:
                          displayImageInput.content_type || displayImageInput.contentType || null,
                      attachmentId:
                          displayImageInput.attachment_id || displayImageInput.attachmentId || null,
                      blobId: displayImageInput.blob_id || displayImageInput.blobId || null,
                      answerId: displayImageInput.answer_id || displayImageInput.answerId || null,
                      runId: displayImageInput.run_id || displayImageInput.runId || null,
                      rootRemixAiRunId:
                          displayImageInput.root_remix_ai_run_id ||
                          displayImageInput.rootRemixAiRunId ||
                          null,
                      actionId: displayImageInput.action_id || displayImageInput.actionId || null,
                  }
                : null,
            referenceImageInputs: displayReferenceImages
                .map((image, index) => {
                    const signedId = image?.signed_id || image?.signedId || null
                    const url = this.aiActionInputPreviewUrl(image)
                    return {
                        id:
                            image?.id ||
                            image?.attachment_id ||
                            image?.blob_id ||
                            signedId ||
                            image?.upload_id ||
                            `reference-image-${index}-${url}`,
                        url,
                        label:
                            image?.label || (keyframes.frameMode ? 'End frame' : 'Reference image'),
                        source: image?.source || null,
                        uploadId: image?.upload_id || image?.uploadId || null,
                        signedId,
                        contentType: image?.content_type || image?.contentType || null,
                        attachmentId: image?.attachment_id || image?.attachmentId || null,
                        blobId: image?.blob_id || image?.blobId || null,
                        answerId: image?.answer_id || image?.answerId || null,
                        runId: image?.run_id || image?.runId || null,
                        rootRemixAiRunId:
                            image?.root_remix_ai_run_id || image?.rootRemixAiRunId || null,
                        actionId: image?.action_id || image?.actionId || null,
                        index: image?.index ?? null,
                    }
                })
                .filter(image => !!image.url),
            videoKeyframeMode: keyframes.frameMode,
            hasVideoInput: !!(inputs?.video && videoInputUrl),
            videoInputUrl: videoInputUrl || null,
            videoPreviewImageUrl: inputs?.video?.preview_image_url || null,
            videoInputActionTag: inputs?.video?.action_tag || null,
            videoInput: inputs?.video
                ? {
                      url: videoInputUrl,
                      id:
                          inputs.video.id ||
                          inputs.video.attachment_id ||
                          inputs.video.blob_id ||
                          `video-input-${inputs.video.url || ''}`,
                      label: inputs.video.label || 'Video input',
                      previewImageUrl: inputs.video.preview_image_url || null,
                      actionTag: inputs.video.action_tag || null,
                      durationSeconds:
                          inputs.video.duration_seconds ??
                          inputs.video.durationSeconds ??
                          inputs.video.duration ??
                          null,
                      source: inputs.video.source || null,
                      uploadId: inputs.video.upload_id || inputs.video.uploadId || null,
                      signedId: inputs.video.signed_id || inputs.video.signedId || null,
                      contentType: inputs.video.content_type || inputs.video.contentType || null,
                      attachmentId: inputs.video.attachment_id || inputs.video.attachmentId || null,
                      blobId: inputs.video.blob_id || inputs.video.blobId || null,
                      answerId: inputs.video.answer_id || inputs.video.answerId || null,
                      runId: inputs.video.run_id || inputs.video.runId || null,
                      rootRemixAiRunId:
                          inputs.video.root_remix_ai_run_id ||
                          inputs.video.rootRemixAiRunId ||
                          null,
                      actionId: inputs.video.action_id || inputs.video.actionId || null,
                  }
                : null,
            videoInputs: (inputs?.videos || (tag === 'stitch' ? references?.videos || [] : []))
                .map((video, index) => {
                    const url = this.aiActionInputPreviewUrl(video)
                    return {
                        id: video?.id || `video-input-${index}-${url || video?.signed_id || ''}`,
                        url,
                        label: video?.label || `Video ${index + 1}`,
                        previewImageUrl: video?.preview_image_url || null,
                        actionTag: video?.action_tag || null,
                        durationSeconds:
                            video?.duration_seconds ??
                            video?.durationSeconds ??
                            video?.duration ??
                            null,
                        source: video?.source || null,
                        uploadId: video?.upload_id || video?.uploadId || null,
                        signedId: video?.signed_id || video?.signedId || null,
                        contentType: video?.content_type || video?.contentType || null,
                    }
                })
                .filter(video => !!video.url),
            referenceVideoInputs: (keyframes.frameMode ? [] : references?.videos || [])
                .map((video, index) => {
                    const signedId = video?.signed_id || video?.signedId || null
                    const url = this.aiActionInputPreviewUrl(video)
                    return {
                        id:
                            video?.id ||
                            video?.attachment_id ||
                            video?.blob_id ||
                            signedId ||
                            video?.upload_id ||
                            `reference-video-${index}-${url}`,
                        url,
                        label: video?.label || 'Reference video',
                        previewImageUrl: video?.preview_image_url || null,
                        actionTag: video?.action_tag || null,
                        durationSeconds:
                            video?.duration_seconds ??
                            video?.durationSeconds ??
                            video?.duration ??
                            null,
                        source: video?.source || null,
                        uploadId: video?.upload_id || video?.uploadId || null,
                        signedId,
                        contentType: video?.content_type || video?.contentType || null,
                        attachmentId: video?.attachment_id || video?.attachmentId || null,
                        blobId: video?.blob_id || video?.blobId || null,
                        answerId: video?.answer_id || video?.answerId || null,
                        runId: video?.run_id || video?.runId || null,
                        rootRemixAiRunId:
                            video?.root_remix_ai_run_id || video?.rootRemixAiRunId || null,
                        actionId: video?.action_id || video?.actionId || null,
                        index: video?.index ?? null,
                    }
                })
                .filter(video => !!video.url),
            hasAudioInput: !!(inputs?.audio && audioInputUrl),
            audioInputUrl: audioInputUrl || null,
            audioInputSource: inputs?.audio?.source || null,
            audioInputUploadId: inputs?.audio?.upload_id || inputs?.audio?.uploadId || null,
            audioInputSignedId: inputs?.audio?.signed_id || inputs?.audio?.signedId || null,
            audioInputLabel: inputs?.audio?.label || null,
            audioInputContentType:
                inputs?.audio?.content_type || inputs?.audio?.contentType || null,
            audioInputFile: inputs?.audio?.file || null,
            audioInput: inputs?.audio
                ? {
                      url: audioInputUrl,
                      id:
                          inputs.audio.id ||
                          inputs.audio.attachment_id ||
                          inputs.audio.blob_id ||
                          `audio-input-${inputs.audio.url || inputs.audio.signed_id || ''}`,
                      label: inputs.audio.label || 'Audio input',
                      source: inputs.audio.source || null,
                      uploadId: inputs.audio.upload_id || inputs.audio.uploadId || null,
                      signedId: inputs.audio.signed_id || inputs.audio.signedId || null,
                      contentType: inputs.audio.content_type || inputs.audio.contentType || null,
                      attachmentId: inputs.audio.attachment_id || inputs.audio.attachmentId || null,
                      blobId: inputs.audio.blob_id || inputs.audio.blobId || null,
                      answerId: inputs.audio.answer_id || inputs.audio.answerId || null,
                      runId: inputs.audio.run_id || inputs.audio.runId || null,
                      rootRemixAiRunId:
                          inputs.audio.root_remix_ai_run_id ||
                          inputs.audio.rootRemixAiRunId ||
                          null,
                      actionId: inputs.audio.action_id || inputs.audio.actionId || null,
                  }
                : null,
            referenceAudioInputs: (keyframes.frameMode ? [] : references?.audios || [])
                .map((audio, index) => {
                    const signedId = audio?.signed_id || audio?.signedId || null
                    const url = this.aiActionInputPreviewUrl(audio)
                    return {
                        id:
                            audio?.id ||
                            audio?.attachment_id ||
                            audio?.blob_id ||
                            signedId ||
                            audio?.upload_id ||
                            `reference-audio-${index}-${url}`,
                        url,
                        label: audio?.label || 'Reference audio',
                        source: audio?.source || null,
                        uploadId: audio?.upload_id || audio?.uploadId || null,
                        signedId,
                        contentType: audio?.content_type || audio?.contentType || null,
                        attachmentId: audio?.attachment_id || audio?.attachmentId || null,
                        blobId: audio?.blob_id || audio?.blobId || null,
                        answerId: audio?.answer_id || audio?.answerId || null,
                        runId: audio?.run_id || audio?.runId || null,
                        rootRemixAiRunId:
                            audio?.root_remix_ai_run_id || audio?.rootRemixAiRunId || null,
                        actionId: audio?.action_id || audio?.actionId || null,
                        index: audio?.index ?? null,
                    }
                })
                .filter(audio => !!audio.url),
            hasLyrics: !!settings.lyrics,
            lyrics: settings.lyrics || null,
            inputOrder: Array.isArray(action?.input_order) ? action.input_order : null,
            syncWithDraft: false,
            hasPendingUpload: this.aiActionHasPendingUpload(action),
            hasFailedUpload: this.aiActionHasFailedUpload(action),
            commandText: `!${tag}`,
        }
    }

    getDefaultActionTokenAttrsForCurrentAnswer = (tag, actionDefinition = null) => {
        const normalizedTag = normalizeActionTag(tag)
        if (!normalizedTag) return null

        const actionDefinitionId =
            actionDefinition?.actionDefinitionId || actionDefinition?.id || null
        const kind = actionDefinition?.type || actionDefinition?.kind || null
        const explicitAiModelId =
            actionDefinition?.aiModelId || actionDefinition?.ai_model_id || null
        const fallbackAiModelId =
            kind === 'model' &&
            actionDefinitionId &&
            !`${actionDefinitionId}`.startsWith('fallback-')
                ? actionDefinitionId
                : null
        const aiModelId = explicitAiModelId || fallbackAiModelId
        const toolKey =
            actionDefinition?.toolKey ||
            actionDefinition?.tool_key ||
            (kind === 'tool' ? normalizedTag : null)
        const displayName =
            normalizedTag === 'pay'
                ? 'pay'
                : actionDefinition?.displayName ||
                  actionDefinition?.display_name ||
                  actionDefinition?.aiModel?.displayName ||
                  null
        const supportsPromptInput =
            typeof actionDefinition?.supportsPromptInput === 'boolean'
                ? actionDefinition.supportsPromptInput
                : this.supportedInputKindsForActionCommand(normalizedTag).has('text')
        const currentInputAttrs = this.currentAnswerInputAttrsForActionCommand(normalizedTag)

        return this.actionTokenReferenceAttrs(
            {
                ...currentInputAttrs,
                tag: normalizedTag,
                ai_action_id: this.createAiActionClientId(),
                displayName,
                supportsPromptInput,
                actionDefinitionId,
                kind,
                aiModelId,
                toolKey,
                analyticsSource: 'action_tag_dropdown',
            },
            { includeDisplayName: true },
        )
    }

    findActionTokenNodeAtIndex = targetIndex => {
        const instance = this.getEditorInstance()
        const state = instance?.state
        if (!state?.doc) return null

        let target = null
        let currentIndex = 0
        state.doc.descendants((node, pos) => {
            if (target) return false
            if (node.type?.name !== 'actionTag') return true
            if (currentIndex === targetIndex) {
                target = { node, pos, instance }
                return false
            }
            currentIndex += 1
            return false
        })
        return target
    }

    getDeltaActionTokenAttrs = deltaJson => {
        const ops = Array.isArray(deltaJson?.ops) ? deltaJson.ops : []
        return ops
            .map(op => {
                const rawAttrs = op?.attributes?.actionTagAttrs
                if (!rawAttrs || typeof rawAttrs !== 'object') return null

                const tag = normalizeActionTag(rawAttrs.tag)
                if (!tag) return null

                return {
                    ...rawAttrs,
                    tag,
                    commandText:
                        rawAttrs.commandText ||
                        (typeof op.insert === 'string' ? op.insert : `!${tag}`),
                }
            })
            .filter(Boolean)
    }

    ensureDraftActionRequestsForEditorTokens = deltaJson => {
        if (this._programmaticAiActionEditorSyncSkips > 0) return

        const deltaActionTokens = this.getDeltaActionTokenAttrs(deltaJson)
        if (!deltaActionTokens.length) {
            if (this.getCurrentDraftActionRequests().length) {
                this.setDraftActionRequests([])
            }
            return
        }

        let nextActionRequests = this.getCurrentDraftActionRequests()
        let changed = false
        const actionTokenIds = new Set(
            deltaActionTokens.map(attrs => this.getAiActionRefId(attrs)).filter(Boolean),
        )

        if (actionTokenIds.size) {
            const prunedActionRequests = nextActionRequests.filter(action =>
                this.getAiActionRefId(action)
                    ? actionTokenIds.has(this.getAiActionRefId(action))
                    : true,
            )
            if (prunedActionRequests.length !== nextActionRequests.length) {
                nextActionRequests = prunedActionRequests
                changed = true
            }
        }

        deltaActionTokens.forEach((rawAttrs, nodeIndex) => {
            const tag = rawAttrs.tag
            const existingActionId = this.getAiActionRefId(rawAttrs)
            const existingAction = existingActionId
                ? nextActionRequests.find(
                      action => this.getAiActionRefId(action) === existingActionId,
                  )
                : null
            if (existingAction) return

            const actionId = existingActionId || this.createAiActionClientId()
            const tokenPromptContent =
                typeof rawAttrs.promptContent === 'string'
                    ? rawAttrs.promptContent.trim()
                    : typeof rawAttrs.prompt_content === 'string'
                    ? rawAttrs.prompt_content.trim()
                    : ''
            const tokenPromptIsPlaceholder = rawAttrs.promptPlaceholder === true
            const currentInputAttrs = {
                ...this.currentAnswerInputAttrsForActionCommand(tag, null, deltaJson),
                hasTextInput: false,
                textInputValue: null,
                promptContent: null,
            }
            const baseAttrs = {
                ...currentInputAttrs,
                ...rawAttrs,
                tag,
                ai_action_id: actionId,
                actionId,
                aiRunId: rawAttrs.aiRunId || this.createAiRunId(),
                commandText: rawAttrs.commandText || `!${tag}`,
                syncWithDraft: false,
                ...(tokenPromptContent
                    ? {
                          hasTextInput: !tokenPromptIsPlaceholder,
                          textInputValue: tokenPromptIsPlaceholder ? null : tokenPromptContent,
                          promptContent: tokenPromptContent,
                          promptPlaceholder: tokenPromptIsPlaceholder,
                      }
                    : {}),
            }
            const action = this.ensureAiActionClientId(
                this.buildAiActionFromAttrs(
                    {
                        ...baseAttrs,
                        actionId,
                        syncWithDraft: false,
                    },
                    deltaJson,
                    { syncDraftInputs: false, useComposerPromptFallback: false },
                ),
            )
            if (!action) return

            this._pendingActionRequestsByClientId ||= {}
            this._pendingActionRequestsByClientId[this.getAiActionRefId(action)] = action
            const insertAt = Math.min(nodeIndex, nextActionRequests.length)
            nextActionRequests = [
                ...nextActionRequests.slice(0, insertAt),
                action,
                ...nextActionRequests.slice(insertAt),
            ]
            if (!existingActionId) {
                const target = this.findActionTokenNodeAtIndex(nodeIndex)
                if (target?.instance?.view) {
                    const nextAttrs = this.actionTokenReferenceAttrs(action, {
                        includePromptContent: true,
                        includeDisplayName: true,
                    })
                    this.runProgrammaticAiActionEditorUpdate(() => {
                        const tr = this.actionTokenAttrsTransaction(target, nextAttrs)
                        if (tr) target.instance.view.dispatch(tr)
                    })
                }
            }
            changed = true
        })

        if (!changed) return

        this.setDraftActionRequests(nextActionRequests)
    }

    findActionTokenNodeByActionId = actionId => {
        if (!actionId) return null
        const instance = this.getEditorInstance()
        const state = instance?.state
        if (!state?.doc) return null

        let target = null
        state.doc.descendants((node, pos) => {
            if (node.type?.name !== 'actionTag') return true
            const nodeActionId = this.getAiActionRefId(node.attrs)
            if (nodeActionId === actionId) {
                target = { node, pos, instance }
                return false
            }
            return true
        })
        return target
    }

    updateActionTokenAttrsAtIndex = (index, attrs) => {
        const currentActions =
            this._draftActionRequestsSnapshot || this.state.draftActionRequests || []
        const actionId =
            this.getAiActionRefId(attrs) || this.getAiActionRefId(currentActions?.[index])
        const target =
            this.findActionTokenNodeByActionId(actionId) || this.findActionTokenNodeAtIndex(index)
        const currentActionIndex = actionId
            ? currentActions.findIndex(action => this.getAiActionRefId(action) === actionId)
            : index
        const currentAction =
            (actionId ? this._pendingActionRequestsByClientId?.[actionId] : null) ||
            (currentActionIndex >= 0 ? currentActions?.[currentActionIndex] : null)
        if (!currentAction) return
        const currentAttrs = this.aiActionToActionTokenAttrs(currentAction) || {}
        const nextAttrs = { ...currentAttrs, ...(attrs || {}) }
        const attrKeys = Object.keys(attrs || {})
        const isOnlySyncToggle = attrKeys.length === 1 && attrKeys[0] === 'syncWithDraft'
        const nextAction =
            isOnlySyncToggle && currentAction
                ? {
                      ...currentAction,
                      sync_with_draft: attrs.syncWithDraft === true,
                  }
                : this.buildAiActionFromAttrs(nextAttrs, null, {
                      syncDraftInputs: false,
                      useComposerPromptFallback: false,
                  })
        const nextActionIndex = currentActionIndex >= 0 ? currentActionIndex : index
        const committedAction = this.commitDraftAiActionAtIndex(nextActionIndex, nextAction)
        if (committedAction && isOnlySyncToggle) {
            this._preserveAiActionInputsOnNextDraftSave = true
        }

        if (!target?.instance?.view || !committedAction) return

        const { pos, instance } = target
        const nextTokenAttrs = this.actionTokenReferenceAttrs(committedAction, {
            includePromptContent: true,
            includeDisplayName: true,
        })
        this.runProgrammaticAiActionEditorUpdate(() => {
            const tr = this.actionTokenAttrsTransaction(
                { ...target, pos, instance },
                nextTokenAttrs,
            )
            if (tr) instance.view.dispatch(tr)
        })
    }

    handleActionTokenPromptChange = payload => {
        const attrs = payload?.attrs || {}
        const promptContent =
            typeof payload?.promptContent === 'string' ? payload.promptContent.trim() : ''
        const currentActions = this.getCurrentDraftActionRequests()
        const actionId = this.getAiActionRefId(attrs)
        const payloadIndex = Number.isInteger(payload?.index) ? payload.index : -1
        const actionIndex = actionId
            ? currentActions.findIndex(action => this.getAiActionRefId(action) === actionId)
            : payloadIndex
        const nextActionIndex = actionIndex >= 0 ? actionIndex : payloadIndex
        if (nextActionIndex < 0) return

        if (payload?.removed) {
            this.deleteActionTokenNodeAtIndex(nextActionIndex)
            return
        }

        this.updateActionTokenAttrsAtIndex(nextActionIndex, {
            ...attrs,
            promptContent: promptContent || null,
            promptPlaceholder: false,
            textInputValue: promptContent || null,
            hasTextInput: !!promptContent,
            syncWithDraft: false,
        })
    }

    // Card -> request for pay. The action-request JSON is the source of truth: commit the
    // STRUCTURED settings.pay directly onto the action via the atomic path (updateAiActionAtIndex
    // commits the object verbatim, never re-parsing text), so no precision is lost to a
    // fields->text->reparse round-trip. The pill's visible text is derived from the same fields as
    // a one-way projection (inputs.text.value/prompt) so the glyph and the message content stay in
    // sync with settings.pay. (Pill TEXT edits still parse-once into settings.pay via
    // handleActionTokenPromptChange -> buildAiActionFromAttrs; that path is unchanged.)
    updatePayActionAtIndex = (index, { payments }) => {
        const currentActions =
            this._draftActionRequestsSnapshot || this.state.draftActionRequests || []
        const currentAction = currentActions[index]
        if (!currentAction) return
        const pay = { payments: payments.map(normalizePay) }
        const text = formatPayGroup(pay)
        const nextAction = {
            ...currentAction,
            prompt: text || null,
            settings: { ...(currentAction.settings || {}), pay },
            inputs: { ...(currentAction.inputs || {}), text: { value: text || '' } },
        }
        this.updateAiActionAtIndex(index, nextAction)
    }

    updateAiActionAtIndex = (index, action) => {
        const currentActions =
            this._draftActionRequestsSnapshot || this.state.draftActionRequests || []
        const actionId =
            this.getAiActionRefId(action) || this.getAiActionRefId(currentActions?.[index])
        const target =
            this.findActionTokenNodeByActionId(actionId) || this.findActionTokenNodeAtIndex(index)
        const currentActionIndex = actionId
            ? currentActions.findIndex(candidate => this.getAiActionRefId(candidate) === actionId)
            : index
        const normalizedAction = this.ensureAiActionClientId(action)
        if (!normalizedAction) return

        const nextActionIndex = currentActionIndex >= 0 ? currentActionIndex : index
        const committedAction = this.commitDraftAiActionAtIndex(nextActionIndex, normalizedAction)
        if (!target?.instance?.view || !committedAction) return

        const nextAttrs = this.actionTokenReferenceAttrs(committedAction, {
            includePromptContent: true,
            includeDisplayName: true,
        })
        const currentTag = normalizeActionTag(target.node?.attrs?.tag)
        const nextTag = normalizeActionTag(nextAttrs?.tag)
        const { instance } = target
        this.runProgrammaticAiActionEditorUpdate(() => {
            const tr =
                currentTag && nextTag && currentTag !== nextTag
                    ? this.replaceActionTokenNodeTransaction(target, nextAttrs)
                    : this.actionTokenAttrsTransaction(target, nextAttrs)
            if (tr) instance.view.dispatch(tr)
        })
    }

    toggleActionTokenSyncAtIndex = (index, attrs) => {
        const nextSyncWithDraft = !attrs.syncWithDraft
        if (!nextSyncWithDraft) {
            this.updateActionTokenAttrsAtIndex(index, {
                syncWithDraft: false,
            })
            return
        }

        if (this.wouldSyncAlterActionInputs(attrs)) {
            this.showNotice(
                'Syncing the action request will replace its contents with the message contents. Continue?',
                {
                    variant: 'info',
                    durationMs: 10000,
                    kind: 'ai-action-sync-confirm',
                    testId: 'toast-ai-action-sync-confirm',
                    actionLabel: 'Continue',
                    cancelLabel: 'Cancel',
                    onAction: () => {
                        this.updateActionTokenAttrsAtIndex(
                            index,
                            this.attrsWithSyncedDraftInputs({ ...attrs, syncWithDraft: true }),
                        )
                    },
                },
            )
            return
        }

        this.updateActionTokenAttrsAtIndex(
            index,
            this.attrsWithSyncedDraftInputs({ ...attrs, syncWithDraft: true }),
        )
    }

    deleteActionTokenNodeAtIndex = index => {
        const currentActions =
            this._draftActionRequestsSnapshot || this.state.draftActionRequests || []
        const currentAction = currentActions?.[index] || null
        const actionId = this.getAiActionRefId(currentAction)
        const target =
            this.findActionTokenNodeByActionId(actionId) || this.findActionTokenNodeAtIndex(index)
        const targetActionId = this.getAiActionRefId(target?.node?.attrs) || actionId
        const nextActionRequests = currentActions.filter((_, actionIndex) => {
            if (targetActionId) {
                return this.getAiActionRefId(currentActions?.[actionIndex]) !== targetActionId
            }
            return actionIndex !== index
        })

        if (!target?.instance?.view) {
            this.setDraftActionRequests(nextActionRequests)
            return
        }

        this.runProgrammaticAiActionEditorUpdate(() => {
            const tr = this.deleteActionTokenRangeTransaction(target)
            if (tr) {
                target.instance.view.dispatch(tr)
            } else {
                const { node, pos, instance } = target
                instance.view.dispatch(instance.state.tr.delete(pos, pos + node.nodeSize))
            }
        })
        this.setDraftActionRequests(nextActionRequests)
    }

    getActionTokenEventEditor = () => {
        return this.getEditorInstance()
    }

    getAiActionPreviewProps = action => {
        const attrs = this.actionTokenAttrsWithPreviewDefaults(
            this.aiActionToActionTokenAttrs(action),
        )
        if (!attrs) return null

        const model =
            findEffectiveModelForAction(this.props.aiModels || [], attrs.tag, attrs) ||
            findModelByActionTag(this.props.aiModels || [], attrs.tag)
        const config = actionPreviewConfig(attrs.tag, model)
        const extendAiModel =
            attrs.tag === 'extend' && attrs.extendAiModelId
                ? (this.props.aiModels || []).find(
                      candidate => candidate?.id === attrs.extendAiModelId,
                  )
                : null
        const pricedModel =
            extendAiModel ||
            (attrs.tag === 'extend' && attrs.extendActionTag
                ? findModelByActionTag(this.props.aiModels || [], attrs.extendActionTag) || model
                : model)
        const supportedInputs = this.supportedInputKindsForActionCommand(attrs.tag)
        const supportsVideoListInput = this.supportsVideoListInputForActionCommand(attrs.tag)
        const supportsReferenceInputs = this.supportsReferenceInputsForActionAttrs(attrs)
        const defaultAspectRatio =
            attrs.defaultAspectRatio ||
            getDefaultAspectRatioForActionCommand(
                pricedModel?.actionTagName || model?.actionTagName || attrs.tag,
            )
        const previewPriceLabel = formatAiActionPrice(
            Number(
                modelActionPrice(
                    pricedModel,
                    isPublicAiPricingContext(this.props.quest),
                    attrs.durationSeconds,
                    getActionPricingContext(pricedModel, attrs),
                ),
            ) * this.generationCountForAction(attrs),
        )

        return {
            attrs,
            model,
            actionPreviewProps: {
                tag: attrs.tag,
                previewTitle: attrs.previewTitle,
                displayName:
                    attrs.tag === 'extend'
                        ? 'Extend Video'
                        : action?.display_name ||
                          action?.displayName ||
                          attrs.displayName ||
                          aiActionModelDisplayName(model, attrs.tag),
                priceLabel: previewPriceLabel || action?.price_label || action?.priceLabel,
                type: model || config.modelType ? 'model' : attrs.type || 'model',
                modelType: model?.modelType || attrs.modelType || config.modelType,
                defaultDurationSeconds:
                    attrs.defaultDurationSeconds || pricedModel?.defaultDurationSeconds,
                defaultAspectRatio,
                durationSeconds: attrs.durationSeconds,
                aspectRatio: attrs.aspectRatio,
                generationCount: attrs.generationCount,
                remixKind: attrs.remixKind,
                gifFrameRate: attrs.gifFrameRate,
                gifMaxWidth: attrs.gifMaxWidth,
                screenshotTimestamp: attrs.screenshotTimestamp,
                extendActionTag: attrs.extendAiModelId ? null : attrs.extendActionTag,
                extendModelDisplayName:
                    attrs.tag === 'extend' && pricedModel
                        ? aiActionModelDisplayName(pricedModel, attrs.extendActionTag)
                        : null,
                extendTimestamp: attrs.extendTimestamp,
                smartStitch: attrs.smartStitch,
                hasTextInput: supportedInputs.has('text') && attrs.hasTextInput,
                textInputValue: supportedInputs.has('text') ? attrs.textInputValue : null,
                textInputIsDefault: attrs.textInputIsDefault,
                hasImageInput: supportedInputs.has('image') && attrs.hasImageInput,
                imageInputUrl: supportedInputs.has('image') ? attrs.imageInputUrl : null,
                referenceImageInputs: supportsReferenceInputs ? attrs.referenceImageInputs : [],
                videoKeyframeMode: attrs.videoKeyframeMode,
                hasVideoInput: supportedInputs.has('video') && attrs.hasVideoInput,
                videoInputUrl: supportedInputs.has('video') ? attrs.videoInputUrl : null,
                videoPreviewImageUrl: supportedInputs.has('video')
                    ? attrs.videoPreviewImageUrl
                    : null,
                videoInputs: supportsVideoListInput ? attrs.videoInputs : [],
                referenceVideoInputs: supportsReferenceInputs ? attrs.referenceVideoInputs : [],
                hasAudioInput: supportedInputs.has('audio') && attrs.hasAudioInput,
                audioInputUrl: supportedInputs.has('audio') ? attrs.audioInputUrl : null,
                audioInputSource: supportedInputs.has('audio') ? attrs.audioInputSource : null,
                audioInputUploadId: supportedInputs.has('audio') ? attrs.audioInputUploadId : null,
                audioInputSignedId: supportedInputs.has('audio') ? attrs.audioInputSignedId : null,
                audioInputLabel: supportedInputs.has('audio') ? attrs.audioInputLabel : null,
                audioInputContentType: supportedInputs.has('audio')
                    ? attrs.audioInputContentType
                    : null,
                audioInputFile: supportedInputs.has('audio') ? attrs.audioInputFile : null,
                referenceAudioInputs: supportsReferenceInputs ? attrs.referenceAudioInputs : [],
                hasLyrics: attrs.hasLyrics,
                lyrics: attrs.lyrics,
                inputOrder: attrs.inputOrder,
                supportsTextInput: supportedInputs.has('text'),
                supportsImageInput: supportedInputs.has('image'),
                supportsVideoInput: supportedInputs.has('video'),
                supportsAudioInput: supportedInputs.has('audio'),
                supportsReferenceInputs,
                simpleMode: isSimplePostMediaAction(action),
                simpleKind:
                    action?.craft_context?.kind || postMediaActionKindForTag(action?.tag) || null,
                sourcePostReference: postMediaSourceReference(action),
                syncWithDraft: attrs.syncWithDraft,
                hasPendingUpload: attrs.hasPendingUpload,
                hasFailedUpload: attrs.hasFailedUpload,
                fullWidth: true,
            },
        }
    }

    renderAiActionPreview = () => {
        const actions = this.getCurrentActionRequestsForPreview()
        if (!actions?.length) return null

        return actions
            .map((action, index) => {
                // Pay renders a dedicated card (recipient + amount), NOT the AI-centric preview,
                // so it never touches the model/fee path.
                if (normalizeActionTag(action?.tag) === 'pay') {
                    return (
                        <div className={styles.aiActionPreviewRow} key={`pay-${index}`}>
                            <PayActionCard
                                settingsPay={action.settings?.pay || null}
                                promptContent={
                                    this.aiActionToActionTokenAttrs(action)?.promptContent || ''
                                }
                                onChange={next => this.updatePayActionAtIndex(index, next)}
                                onRemove={() => this.deleteActionTokenNodeAtIndex(index)}
                            />
                        </div>
                    )
                }
                const preview = this.getAiActionPreviewProps(action)
                if (!preview) return null
                const { attrs, actionPreviewProps } = preview
                const validationKey = this.aiActionValidationKey(action, index)
                const missingInputKinds =
                    this.state.aiActionInputValidation?.missingByActionKey?.[validationKey] || null
                const postMediaSettingsKind = ['film', 'illustrate'].includes(
                    actionPreviewProps.simpleKind,
                )
                    ? actionPreviewProps.simpleKind
                    : null
                const openPostMediaSettings = postMediaSettingsKind
                    ? () =>
                          this.openPostMediaSettingsDialog(postMediaSettingsKind, 'card_edit', {
                              index,
                              action,
                          })
                    : null

                return (
                    <div
                        className={styles.aiActionPreviewRow}
                        data-ai-action-validation-key={validationKey}
                        data-testid="ai-action-preview-row"
                        key={`${attrs.tag}-${index}-${
                            this.state.aiActionInputValidation?.token || 'valid'
                        }`}
                    >
                        <AiActionPreview
                            {...actionPreviewProps}
                            missingInputKinds={missingInputKinds}
                            simpleSettingsSummary={
                                actionPreviewProps.simpleMode
                                    ? this.postMediaSettingsSummaryForAction(action)
                                    : null
                            }
                            onOpenSimpleSettings={
                                actionPreviewProps.simpleMode && openPostMediaSettings
                                    ? openPostMediaSettings
                                    : null
                            }
                            onOpenSourcePost={
                                actionPreviewProps.sourcePostReference
                                    ? () =>
                                          openSourceAnswerThread({
                                              answerId:
                                                  actionPreviewProps.sourcePostReference.answerId,
                                              openRight: this.props.openRight,
                                          })
                                    : null
                            }
                            onEdit={() => {
                                if (openPostMediaSettings) {
                                    openPostMediaSettings()
                                    return
                                }
                                window.dispatchEvent(
                                    new CustomEvent('knov:edit-action-request', {
                                        detail: {
                                            aiAction: action,
                                            directSettingsEdit: true,
                                            editor: this.getActionTokenEventEditor(),
                                            updateAiAction: nextAction =>
                                                this.updateAiActionAtIndex(index, nextAction),
                                        },
                                    }),
                                )
                            }}
                            onEditInput={(inputKind, inputSlotIndex) => {
                                window.dispatchEvent(
                                    new CustomEvent('knov:edit-action-request', {
                                        detail: {
                                            aiAction: action,
                                            focusedInputKind: inputKind,
                                            inputSlotIndex,
                                            directInputEdit: true,
                                            editor: this.getActionTokenEventEditor(),
                                            updateAiAction: nextAction =>
                                                this.updateAiActionAtIndex(index, nextAction),
                                        },
                                    }),
                                )
                            }}
                            onEditModel={() => {
                                if (openPostMediaSettings) {
                                    openPostMediaSettings()
                                    return
                                }
                                window.dispatchEvent(
                                    new CustomEvent('knov:edit-action-request', {
                                        detail: {
                                            aiAction: action,
                                            openModelPicker: true,
                                            modelPickerModelType: actionPreviewProps.modelType,
                                            editor: this.getActionTokenEventEditor(),
                                            updateAiAction: nextAction =>
                                                this.updateAiActionAtIndex(index, nextAction),
                                        },
                                    }),
                                )
                            }}
                            onEditExtendModel={() => {
                                window.dispatchEvent(
                                    new CustomEvent('knov:edit-action-request', {
                                        detail: {
                                            aiAction: action,
                                            openExtendModel: true,
                                            editor: this.getActionTokenEventEditor(),
                                            updateAiAction: nextAction =>
                                                this.updateAiActionAtIndex(index, nextAction),
                                        },
                                    }),
                                )
                            }}
                            onEditReferences={() => {
                                window.dispatchEvent(
                                    new CustomEvent('knov:edit-action-request', {
                                        detail: {
                                            aiAction: action,
                                            openReferences: true,
                                            editor: this.getActionTokenEventEditor(),
                                            updateAiAction: nextAction =>
                                                this.updateAiActionAtIndex(index, nextAction),
                                        },
                                    }),
                                )
                            }}
                            onRemove={() => this.deleteActionTokenNodeAtIndex(index)}
                        />
                    </div>
                )
            })
            .filter(Boolean)
    }

    hydrateDeltaJsonWithActionRequests = (deltaJson, actionRequests) => {
        if (!deltaJson?.ops || !Array.isArray(actionRequests) || actionRequests.length === 0) {
            return deltaJson
        }

        const actionAttrsById = new Map()
        const actionQueues = actionRequests.reduce((queues, action) => {
            const attrs = this.aiActionToActionTokenAttrs(action)
            if (!attrs) return queues
            const tag = normalizeActionTag(attrs.tag)
            const referenceAttrs = this.actionTokenReferenceAttrs(attrs, {
                includePromptContent: true,
                includeDisplayName: true,
            })
            const actionId = this.getAiActionRefId(referenceAttrs)
            if (actionId) actionAttrsById.set(actionId, referenceAttrs)
            queues[tag] ||= []
            queues[tag].push(referenceAttrs)
            return queues
        }, {})

        const hydratedOps = []
        const actionCommandRegex =
            /(^|[\s\n])!([A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*)(?=[\s\n.,;:!?]|$)/g

        deltaJson.ops.forEach(op => {
            if (typeof op.insert !== 'string') {
                hydratedOps.push(op)
                return
            }
            if (op.attributes?.actionTagAttrs) {
                const existingAttrs = op.attributes.actionTagAttrs
                const tag = normalizeActionTag(existingAttrs.tag)
                const existingActionId = this.getAiActionRefId(existingAttrs)
                const hydratedAttrs = existingActionId
                    ? actionAttrsById.get(existingActionId) ||
                      this.actionTokenReferenceAttrs(existingAttrs, {
                          includePromptContent: true,
                          includeDisplayName: true,
                      })
                    : actionQueues[tag]?.shift() ||
                      this.actionTokenReferenceAttrs(existingAttrs, {
                          includePromptContent: true,
                          includeDisplayName: true,
                      })
                hydratedOps.push({
                    ...op,
                    attributes: {
                        ...op.attributes,
                        actionTagAttrs: hydratedAttrs,
                    },
                })
                return
            }

            let lastIndex = 0
            let matched = false
            actionCommandRegex.lastIndex = 0
            let match
            while ((match = actionCommandRegex.exec(op.insert)) !== null) {
                const prefixLength = match[1]?.length || 0
                const tag = normalizeActionTag(match[2])
                const attrs = actionQueues[tag]?.shift()
                if (!attrs) {
                    continue
                }

                matched = true
                const tagStart = match.index + prefixLength
                const tagEnd = tagStart + tag.length + 1
                if (tagStart > lastIndex) {
                    hydratedOps.push({
                        ...op,
                        insert: op.insert.slice(lastIndex, tagStart),
                    })
                }
                hydratedOps.push({
                    insert: op.insert.slice(tagStart, tagEnd),
                    attributes: {
                        ...(op.attributes || {}),
                        actionTagAttrs: attrs,
                    },
                })
                lastIndex = tagEnd
            }

            if (!matched) {
                hydratedOps.push(op)
                return
            }

            if (lastIndex < op.insert.length) {
                hydratedOps.push({
                    ...op,
                    insert: op.insert.slice(lastIndex),
                })
            }
        })

        return { ...deltaJson, ops: hydratedOps }
    }

    getSavedDraftDeltaJson = savedDraft => {
        return this.hydrateDeltaJsonWithActionRequests(
            savedDraft?.delta_json || '',
            this.getActionRequestsFromSource(savedDraft),
        )
    }

    savedDraftCachePatch = savedDraft => ({
        action_requests: this.ensureAiActionClientIds(this.getActionRequestsFromSource(savedDraft)),
        ...this.draftPayloadCachePatch(savedDraft),
        suppressed_preview_urls: savedDraft?.suppressed_preview_urls || [],
        message_type: savedDraft?.message_type || null,
        poll_options: savedDraft?.poll_options || [],
        poll_duration_minutes: savedDraft?.poll_duration_minutes || null,
    })

    ensureCurrentUser = () => {
        if (gon.currentUser) {
            return true
        } else {
            this.showNotice('Please login first!', { variant: 'error' })
            // $('#auth-modal').modal('show')
            return false
        }
    }

    processMentions = async delta => {
        const mentions = delta.ops.reduce(
            (acc, cur) => (cur.insert?.mention?.value ? [...acc, cur.insert.mention?.value] : acc),
            [],
        )

        const teamId = this?.props?.quest?.team?.id
        let team
        if (teamId) team = await api.getTeam(teamId)

        if (team) {
            const toAdd = []
            mentions.forEach(mentionedName => {
                if (!team?.users?.find(u => u?.name === mentionedName)) toAdd.push(mentionedName)
            })

            if (toAdd?.length > 0) {
                let msg =
                    toAdd?.length == 1
                        ? `@${toAdd[0]} is not part of channel '${team?.name}'. Would you like to add them?\n`
                        : `The following users aren't part of channel '${
                              team?.name
                          }'. Would you like to add them?\n\n${toAdd.map(u => `- @${u}`).join('\n')}
                        `

                msg += "\nPress 'OK' to add, or 'Cancel' to message only."

                const inviteUsers = await confirmAsync(msg)
                if (inviteUsers) api.addToTeam({ names: toAdd }, teamId)
            }
        }

        return mentions
    }

    onPost = async () => {
        const replyDiagnostic = this._replySubmitDiagnostics.submitBegin({
            composer: this.diagnosticComposerKind(),
        })
        if (!this.ensureCurrentUser()) {
            replyDiagnostic.submitEnd('blocked', { reason: 'signed_out' })
            return
        }

        const diagnostic = this._composerDiagnosticId
            ? this.startComposerDiagnostic('composer_submit', {
                  media_kind: this.diagnosticMediaKind(),
              })
            : null
        diagnostic?.mark('submit_begin')
        let handedOff = false

        const hasUnresolvedStreamEmbeds = this.hasUnresolvedStreamEmbeds()
        if (
            this.state.waitingForEmbeds ||
            this.state.streamEmbedsLoading ||
            hasUnresolvedStreamEmbeds
        ) {
            if (hasUnresolvedStreamEmbeds) this.setStreamEmbedsLoading(true)
            diagnostic?.mark('blocked')
            replyDiagnostic.submitEnd('blocked', { reason: 'embeds_pending' })
            return
        }

        const postingDraftId =
            this.props.newAnswer?.id || this._currentTitleDraftId || this.state.currentDraftId
        if (this._postingDraftId) {
            diagnostic?.mark('blocked')
            replyDiagnostic.submitEnd('blocked', { reason: 'already_posting' })
            return
        }

        this._postingDraftId = postingDraftId || true
        this.setState({ isPosting: true })

        try {
            const pollValidationErrors = this.getPollValidationErrors()
            if (pollValidationErrors.length > 0) {
                this.showNotice(pollValidationErrors[0], { variant: 'error' })
                return
            }

            const hasAiGenerateDraft = this.hasCurrentDraftActionRequests()

            // Keep the mobile keyboard stable for immediate posts, but do not refocus the
            // editor before AI Generate confirmations. Focusing a contenteditable during a
            // touch-triggered tray open summons the software keyboard on mobile browsers.
            if (isMobile && !hasAiGenerateDraft && this.richEditor.current) {
                const editor = this.richEditor.current.getEditor()
                editor.focus()
            }

            if (this.state.isUploading) return // guard while uploading
            if (this.uploadManager?.hasActiveUpload?.()) return

            commitPendingExactMention(this.getEditorInstance())
            commitPendingAutolinks(this.getEditorInstance())
            // Snapshot the editor content (delta) BEFORE any modal interactions
            const initialDeltaJson = this.getDeltaJson()

            // use setTimeout to run it on next tick to prevent race condition where quill hasn't
            // finished updating text in time
            if (
                this?.props?.quest?.public &&
                window.gon?.currentUser?.features?.some?.(
                    f => f.name === 'confirm_before_posting_publicly',
                )
            ) {
                if (!(await confirmAsync('Post this message publicly?'))) {
                    return // Abort if user cancels public post confirmation
                }
            }

            // fetch content after the delay, using the snapshotted delta
            let delimitedContent = getText(initialDeltaJson || {}, { wrapUsernames: true }).trim()
            let preSubmitDeltaJson = initialDeltaJson
            const actionCommandPresentRegex = /!([A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*)/
            const normalizeActionCommandForSubmit = rawCommand => {
                const normalizedCommandTag = normalizeActionTag(rawCommand)
                if (!normalizedCommandTag || normalizedCommandTag === rawCommand) {
                    return normalizedCommandTag
                }

                const oldTag = `!${rawCommand}`
                const newTag = `!${normalizedCommandTag}`
                delimitedContent = delimitedContent.replace(oldTag, newTag)
                preSubmitDeltaJson = this.replaceTagInDeltaJson(preSubmitDeltaJson, oldTag, newTag)
                return normalizedCommandTag
            }

            const firstActionCommandMatch = delimitedContent.match(actionCommandPresentRegex)
            if (firstActionCommandMatch) {
                normalizeActionCommandForSubmit(firstActionCommandMatch[1])
            }

            // Check for !extend with a t2v tag that should be redirected to i2v
            const extendRedirectResult = await this.checkExtendT2vRedirect(
                delimitedContent,
                preSubmitDeltaJson,
            )
            if (extendRedirectResult === INTERRUPT_POSTING) {
                return // User cancelled the redirect
            }
            if (extendRedirectResult) {
                // User confirmed redirect, use the corrected content and delta
                delimitedContent = extendRedirectResult.content
                preSubmitDeltaJson = extendRedirectResult.deltaJson
            }

            // Detect a typed !command for legacy client-side checks.
            const match = delimitedContent.match(actionCommandPresentRegex)
            const actionCommand = match ? match[1] : null
            if (actionCommand === 'extend') {
                const nestedTagMatch = delimitedContent.match(
                    /!extend\s+!([A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*)/,
                )
                if (nestedTagMatch) {
                    normalizeActionCommandForSubmit(nestedTagMatch[1])
                }
            }

            // Check for invalid video duration and offer to fix it
            if (actionCommand) {
                const durationFixResult = await this.checkVideoDurationValidation(
                    actionCommand,
                    delimitedContent,
                    preSubmitDeltaJson,
                )
                if (durationFixResult === INTERRUPT_POSTING) {
                    return // User cancelled the duration fix
                }
                if (durationFixResult) {
                    // User accepted duration fix, use the corrected content and delta
                    delimitedContent = durationFixResult.content
                    preSubmitDeltaJson = durationFixResult.deltaJson
                }
            }

            if (!this.validateAiActionInputsForPost(preSubmitDeltaJson)) {
                return
            }

            let preSubmitActionRequests = this.getActionRequestsForSubmit(preSubmitDeltaJson)
            const aiFeeInterrupt = await this.handleAiActionFeeConfirmation(preSubmitActionRequests)
            if (aiFeeInterrupt === INTERRUPT_POSTING) {
                return
            }
            if (aiFeeInterrupt?.paymentMode) {
                preSubmitActionRequests = this.withPaymentModeForActionRequests(
                    preSubmitActionRequests,
                    aiFeeInterrupt.paymentMode,
                )
            }

            // Guard: only the FIRST action command in a message is processed. A !pay command
            // that isn't first would be silently ignored while the message still posts publicly
            // implying a payment that never happens. Stop and tell the user to reorder.
            const hasPayActionRequest = preSubmitActionRequests.some(
                action => normalizeActionTag(action?.tag || action?.action_tag) === 'pay',
            )
            if (
                (hasPayActionRequest || /!pay\s+@/i.test(delimitedContent)) &&
                normalizeActionTag(actionCommand) !== 'pay'
            ) {
                this.showNotice(
                    `!pay must be the first command in your message. Move the !pay command ahead of any other !command and try again.`,
                    { variant: 'error', durationMs: 9000 },
                )
                return
            }

            // Guard: do not post while upload is in progress or media not yet pre-uploaded
            const na = this.props.newAnswer || {}
            const completedMedia = this.uploadManager.getCompletedMedia(na.id)
            const hasPendingLocalMedia = hasPendingLocalMediaForSubmit(na, completedMedia)
            const hasActiveProgress = (() => {
                const recPct = this.state.uploadProgress?.recording
                if (recPct != null && recPct < 100) return true
                const files = this.state.uploadProgress?.files || {}
                return Object.values(files).some(p => p != null && p < 100)
            })()
            if (hasPendingLocalMedia || hasActiveProgress) {
                this.showNotice(
                    hasActiveProgress
                        ? 'Attachment upload is not finished. Your draft is still here. Please wait and try again.'
                        : 'Attachment upload failed. Your draft is still here. Remove it or attach it again.',
                    { variant: 'error', durationMs: 6500 },
                )
                return
            }

            // If a non-AI client-side command is present, handle it.
            if (normalizeActionTag(actionCommand) === 'pay') {
                // TODO this errors out if user is not a stream admin, need to fix.
                //this.processMentions(deltaJson)
                const shouldInterrupt = await this.handleClientSideActionCommands(
                    actionCommand,
                    delimitedContent,
                    preSubmitActionRequests,
                )
                if (shouldInterrupt === INTERRUPT_POSTING) {
                    return // Abort if the command handler interrupts
                }
            }

            // proceed to post (media already pre-uploaded if present)
            diagnostic?.mark('validation_end', {
                link_count: this.diagnosticLinkCount(preSubmitDeltaJson),
            })
            handedOff = true
            await this.postAnswer(
                false,
                preSubmitDeltaJson,
                null,
                preSubmitActionRequests,
                diagnostic,
                replyDiagnostic,
            )
        } catch (error) {
            replyDiagnostic.finish()
            diagnostic?.mark('failed')
            throw error
        } finally {
            if (!handedOff) diagnostic?.mark('blocked')
            replyDiagnostic.submitEnd(handedOff ? 'handed_off' : 'blocked')
            this._postingDraftId = null
            if (!this._isUnmounted) {
                this.setState({ isPosting: false })
            }
        }
    }

    /**
     * Check if the user is trying to use !extend with a text-to-video tag.
     * If so, prompt them to use the image-to-video equivalent.
     *
     * @returns null if no redirect needed, INTERRUPT_POSTING if user cancels,
     *          or { content, deltaJson } with the corrected values if user confirms
     */
    checkExtendT2vRedirect = async (content, deltaJson) => {
        // Match !extend followed by another action command
        const extendMatch = content.match(/!extend\s+!([A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*)/)
        if (!extendMatch) return null

        const nestedTag = extendMatch[1]
        const aiModels = this.props.aiModels || []

        // Find the model for the nested command
        const nestedModel = findModelByActionTag(aiModels, nestedTag)
        if (!nestedModel) return null // Unknown model, let backend handle it

        // Check if it's a text-to-video model (video type, can't remix image)
        const isT2vModel = nestedModel.modelType === 'video' && !nestedModel.canRemixImage
        if (!isT2vModel) return null // Already an i2v model or not a video model

        // Find the corresponding i2v model by matching humanName
        const i2vModel = aiModels.find(
            m =>
                m.modelType === 'video' &&
                m.canRemixImage === true &&
                m.humanName === nestedModel.humanName,
        )

        if (!i2vModel) return null // No i2v equivalent found

        // Show confirmation modal
        const confirmed = await confirmAsync(
            `The !extend command requires an image-to-video model to animate the extracted frame. ` +
                `You used !${nestedTag}, which is text-to-video. ` +
                `Use !${i2vModel.actionTagName} instead?`,
        )

        if (!confirmed) {
            return INTERRUPT_POSTING
        }

        // Replace the tag in content and deltaJson
        const oldTagPattern = `!${nestedTag}`
        const newTag = `!${i2vModel.actionTagName}`
        const newContent = content.replace(
            new RegExp(`(!extend\\s+)!${nestedTag}`, 'i'),
            `$1${newTag}`,
        )

        // Replace in deltaJson ops
        const newDeltaJson = this.replaceTagInDeltaJson(deltaJson, oldTagPattern, newTag)

        return { content: newContent, deltaJson: newDeltaJson }
    }

    /**
     * Replace a tag string in deltaJson ops.
     * Handles both plain text and text with attributes (like bold).
     */
    replaceTagInDeltaJson = (deltaJson, oldTag, newTag) => {
        if (!deltaJson?.ops) return deltaJson

        const newOps = deltaJson.ops.map(op => {
            if (typeof op.insert === 'string' && op.insert.includes(oldTag)) {
                return {
                    ...op,
                    insert: op.insert.replace(oldTag, newTag),
                }
            }
            return op
        })

        return { ...deltaJson, ops: newOps }
    }

    /**
     * Replace a duration string in deltaJson ops.
     * Matches patterns like "8s", "8sec", "8seconds" and replaces with new duration.
     */
    replaceDurationInDeltaJson = (deltaJson, oldDuration, newDuration) => {
        if (!deltaJson?.ops) return deltaJson

        const oldPattern = new RegExp(`\\b${oldDuration}(?:s|sec|secs|seconds?)\\b`, 'i')
        const newOps = deltaJson.ops.map(op => {
            if (typeof op.insert === 'string' && oldPattern.test(op.insert)) {
                return {
                    ...op,
                    insert: op.insert.replace(oldPattern, `${newDuration}s`),
                }
            }
            return op
        })

        return { ...deltaJson, ops: newOps }
    }

    extendModelCommandTagForAction = action => {
        const settings = action?.settings || {}
        const extendAiModelId = settings.extend_ai_model_id || settings.extendAiModelId
        if (extendAiModelId) {
            const model = (this.props.aiModels || []).find(
                candidate => candidate?.id === extendAiModelId,
            )
            if (model?.actionTagName) return canonicalActionTagFor(model.actionTagName)
        }
        return canonicalActionTagFor(
            settings.extend_action_tag ||
                settings.extendActionTag ||
                action?.extend_action_tag ||
                action?.extendActionTag,
        )
    }

    feeCommandTagForAction = action => {
        const tag = normalizeActionTag(action?.tag)
        const postMediaTargetTag = postMediaTargetActionTag(action)
        if (postMediaTargetTag) return postMediaTargetTag
        if (tag === 'extend') {
            return this.extendModelCommandTagForAction(action) || DEFAULT_VIDEO_MODEL.actionTag
        }
        return tag
    }

    durationForAiActionFee = action => {
        const duration = Number(action?.settings?.duration_seconds)
        return Number.isFinite(duration) && duration > 0 ? duration : null
    }

    // "H3 Max · 15s · $0.45" chip for the simple Film card, so the recorded
    // model and settings stay visible without opening the dialog.
    postMediaSettingsSummaryForAction = action => {
        const tag = this.feeCommandTagForAction(action)
        const model =
            findEffectiveModelForAction(this.props.aiModels || [], tag, action) ||
            findModelByActionTag(this.props.aiModels || [], tag)
        const settings = action?.settings || {}
        const durationSeconds = this.durationForAiActionFee(action)
        const price = modelActionPrice(
            model,
            isPublicAiPricingContext(this.props.quest),
            durationSeconds,
        )

        return [
            model?.humanName || model?.displayName || tag,
            durationSeconds ? `${durationSeconds}s` : null,
            settings.resolution || null,
            settings.aspect_ratio || null,
            formatAiActionPrice(price),
        ]
            .filter(Boolean)
            .join(' · ')
    }

    getAiActionFeeItems = actionRequests => {
        const isPublic = isPublicAiPricingContext(this.props.quest)

        return (actionRequests || [])
            .map((action, index) => {
                const tag = this.feeCommandTagForAction(action)
                const model =
                    findEffectiveModelForAction(this.props.aiModels || [], tag, action) ||
                    findModelByActionTag(this.props.aiModels || [], tag)
                const durationSeconds = this.durationForAiActionFee(action)
                const generationCount = this.generationCountForAction(action)
                const usdCost = modelActionPrice(
                    model,
                    isPublic,
                    durationSeconds,
                    getActionPricingContext(model, action),
                )
                const numericUsdCost = Number(usdCost) * generationCount

                if (!Number.isFinite(numericUsdCost) || numericUsdCost <= 0) return null

                return {
                    index,
                    tag,
                    model,
                    modelType: model?.modelType || action?.model_type || null,
                    modelName: aiActionModelDisplayName(model, tag),
                    durationSeconds,
                    generationCount,
                    usdCost: numericUsdCost,
                }
            })
            .filter(Boolean)
    }

    safeLogAiActionUsageEvent = (eventName, props) => {
        try {
            window?.logEv?.(eventName, props)
        } catch (error) {
            if (window?.gon?.env !== 'production') {
                console.warn(`Failed to log ${eventName}`, error)
            }
        }
    }

    aiActionUsageSource = action =>
        action?.analytics_source ||
        action?.analyticsSource ||
        action?.usage_source ||
        action?.usageSource ||
        AI_ACTION_USAGE_SOURCE_UNKNOWN

    aiActionPaymentMode = action => {
        const settings = action?.settings || {}
        return (
            action?.payment_mode ||
            action?.paymentMode ||
            settings.payment_mode ||
            settings.paymentMode ||
            null
        )
    }

    aiActionUsagePricing = action => {
        const isPublic = isPublicAiPricingContext(this.props.quest)
        const actionTag = normalizeActionTag(action?.tag)
        const feeTag = this.feeCommandTagForAction(action)
        const model =
            findEffectiveModelForAction(this.props.aiModels || [], feeTag || actionTag, action) ||
            (action?.ai_model_id &&
                (this.props.aiModels || []).find(model => model?.id === action.ai_model_id)) ||
            findModelByActionTag(this.props.aiModels || [], feeTag || actionTag)
        const durationSeconds = this.durationForAiActionFee(action)
        const generationCount = this.generationCountForAction(action)
        const usdCost = modelActionPrice(
            model,
            isPublic,
            durationSeconds,
            getActionPricingContext(model, action),
        )
        const totalUsdCost = Number(usdCost) * generationCount
        const isPaidModelUsage = Number.isFinite(totalUsdCost) && totalUsdCost > 0

        return {
            feeTag,
            model,
            durationSeconds,
            generationCount,
            isPaidModelUsage,
        }
    }

    aiActionUsageInputProps = action => {
        const inputs = action?.inputs || {}
        const references = inputs.references || {}
        return {
            has_text_input: !!inputs.text,
            has_image_input: !!(inputs.image || inputs.start_image || inputs.end_image),
            has_video_input: !!inputs.video,
            has_audio_input: !!inputs.audio,
            video_input_count: Array.isArray(inputs.videos) ? inputs.videos.length : 0,
            reference_image_count: Array.isArray(references.images) ? references.images.length : 0,
            reference_video_count: Array.isArray(references.videos) ? references.videos.length : 0,
            reference_audio_count: Array.isArray(references.audios) ? references.audios.length : 0,
        }
    }

    buildAiActionUsageProps = (action, index, answer) => {
        const actionTag = normalizeActionTag(action?.tag)
        const pricing = this.aiActionUsagePricing(action)
        const model = pricing.model
        const settings = action?.settings || {}

        return this.compactObject({
            answer_id: answer?.id || null,
            quest_id: answer?.quest_id || this.props.quest?.id || null,
            action_request_id: this.getAiActionRefId(action),
            ai_run_id: action?.ai_run_id || null,
            action_index: index,
            action_tag: actionTag,
            effective_action_tag: pricing.feeTag || actionTag,
            action_kind: action?.kind || action?.type || null,
            ai_model_id: action?.ai_model_id || model?.id || null,
            model_name: model?.name || null,
            model_type: action?.model_type || model?.modelType || null,
            provider: model?.provider || null,
            source: this.aiActionUsageSource(action),
            payment_mode: this.aiActionPaymentMode(action),
            is_paid_model_usage: pricing.isPaidModelUsage,
            is_public_context: isPublicAiPricingContext(this.props.quest),
            user_has_free_ai_feature: this.currentUserHasFreeAiFeature(),
            generation_count: pricing.generationCount,
            duration_seconds: pricing.durationSeconds,
            aspect_ratio: settings.aspect_ratio || null,
            remix_kind: action?.remix_kind || null,
            is_remix: !!(
                action?.remix_kind ||
                action?.remix_ai_run_id ||
                action?.root_remix_ai_run_id
            ),
            ...this.aiActionUsageInputProps(action),
        })
    }

    logAiActionUsageSubmitted = answer => {
        const actionRequests = this.getActionRequestsFromSource(answer)
        if (!actionRequests.length) return

        try {
            const actionProps = actionRequests.map((action, index) =>
                this.buildAiActionUsageProps(action, index, answer),
            )
            const unique = values => [...new Set(values.filter(Boolean))]
            const sourceCounts = actionProps.reduce((counts, props) => {
                const source = props.source || AI_ACTION_USAGE_SOURCE_UNKNOWN
                counts[source] = (counts[source] || 0) + 1
                return counts
            }, {})
            const paidModelActionCount = actionProps.filter(
                props => props.is_paid_model_usage === true,
            ).length

            this.safeLogAiActionUsageEvent('AI_ACTION_REQUESTS_SUBMITTED', {
                answer_id: answer?.id || null,
                quest_id: answer?.quest_id || this.props.quest?.id || null,
                action_count: actionProps.length,
                action_tags: actionProps.map(props => props.action_tag).filter(Boolean),
                effective_action_tags: actionProps
                    .map(props => props.effective_action_tag)
                    .filter(Boolean),
                model_types: unique(actionProps.map(props => props.model_type)),
                sources: unique(actionProps.map(props => props.source)),
                source_counts: sourceCounts,
                payment_modes: unique(actionProps.map(props => props.payment_mode)),
                paid_model_action_count: paidModelActionCount,
                free_model_action_count: actionProps.length - paidModelActionCount,
                has_multiple_actions: actionProps.length > 1,
                is_public_context: isPublicAiPricingContext(this.props.quest),
                user_has_free_ai_feature: this.currentUserHasFreeAiFeature(),
            })

            actionProps.forEach(props => {
                this.safeLogAiActionUsageEvent('AI_ACTION_REQUEST_SUBMITTED', props)
            })
        } catch (error) {
            if (window?.gon?.env !== 'production') {
                console.warn('Failed to build AI action usage analytics', error)
            }
        }
    }

    currentUserHasFreeAiFeature = () =>
        window?.gon?.currentUser?.features?.some?.(feature => feature.name === 'use_ai_for_free') ||
        false

    buildAiPaymentConfirmMessage = ({
        eyebrow,
        title,
        lineItems = [],
        effectiveModelFeeSatsStr,
        bsvStatus,
        usdStatus,
        selectedPaymentMode,
    }) => {
        const renderStatus = status => {
            if (status === 'loading') {
                return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <i className="fa fa-spin fa-spinner" />
                        Checking
                    </span>
                )
            }

            if (status === 'ready') return 'Ready'
            if (status === 'setup') return 'Setup needed'
            return 'Unavailable'
        }

        return (
            <div
                style={{
                    color: 'var(--color-text-primary)',
                    minWidth: 0,
                    textAlign: 'left',
                }}
            >
                <div
                    style={{
                        fontWeight: 600,
                        fontSize: '12px',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        opacity: 0.7,
                        marginBottom: '6px',
                    }}
                >
                    {eyebrow}
                </div>
                <div style={{ fontWeight: 700, fontSize: '18px' }}>{title}</div>
                {lineItems.length > 0 && (
                    <div style={{ marginTop: '8px', display: 'grid', gap: '4px' }}>
                        {lineItems.map(item => (
                            <div
                                key={item.key}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: '12px',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                }}
                            >
                                <span>{item.label}</span>
                                <span>{item.value}</span>
                            </div>
                        ))}
                    </div>
                )}
                {selectedPaymentMode === 'bsv' && effectiveModelFeeSatsStr && (
                    <div style={{ marginTop: '6px', fontSize: '12px', opacity: 0.7 }}>
                        BSV wallet required: {effectiveModelFeeSatsStr}
                    </div>
                )}
                <div
                    style={{
                        marginTop: '12px',
                        display: 'grid',
                        gap: '6px',
                        fontSize: '12px',
                    }}
                >
                    {selectedPaymentMode === 'bsv' ? (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: '12px',
                            }}
                        >
                            <span style={{ opacity: 0.7 }}>BSV wallet</span>
                            <span>{renderStatus(bsvStatus)}</span>
                        </div>
                    ) : (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: '12px',
                            }}
                        >
                            <span style={{ opacity: 0.7 }}>USD billing</span>
                            <span>{renderStatus(usdStatus)}</span>
                        </div>
                    )}
                </div>
            </div>
        )
    }

    confirmAiPaymentTray = async ({
        eyebrow,
        title,
        lineItems = [],
        usdPriceStr,
        iconType,
        effectiveModelFeeSats,
    }) => {
        const choice = await confirmSharedAiPaymentTray({
            eyebrow,
            title,
            lineItems,
            usdPriceStr,
            iconType,
            effectiveModelFeeSats,
            walletType: this.props.currentUserBsvWalletType,
            onError: message => this.showNotice(message, { variant: 'error' }),
            buildMessage: ({
                eyebrow,
                title,
                lineItems,
                effectiveModelFeeSatsStr,
                bsvStatus,
                usdStatus,
                selectedPaymentMode,
            }) =>
                this.buildAiPaymentConfirmMessage({
                    eyebrow,
                    title,
                    lineItems,
                    effectiveModelFeeSatsStr,
                    bsvStatus,
                    usdStatus,
                    selectedPaymentMode,
                }),
        })

        if (choice === 'bsv' || choice === 'stripe_metered') return choice
        if (choice === 'redirect') return 'redirect'
        if (choice === 'setup') return 'setup'
        return 'cancel'
    }

    handleAiActionFeeConfirmation = async actionRequests => {
        return new Promise(resolve => {
            defer(async () => {
                const feeItems = this.getAiActionFeeItems(actionRequests)
                const craftFeeItems = (actionRequests || [])
                    .map((action, index) => {
                        const usdCost = postMediaPromptCraftFeeForAction(action)
                        if (!usdCost) return null

                        const kind = action?.craft_context?.kind
                        return {
                            index,
                            usdCost,
                            modelType: action?.model_type || null,
                            label:
                                kind === 'illustrate'
                                    ? 'Creative direction · Illustrate'
                                    : 'Creative direction · Film',
                        }
                    })
                    .filter(Boolean)
                if (!feeItems.length && !craftFeeItems.length) {
                    resolve()
                    return
                }

                const totalUsdCost = [...feeItems, ...craftFeeItems].reduce(
                    (sum, item) => sum + item.usdCost,
                    0,
                )
                const totalSats = usdToSats(totalUsdCost, this.props.exchangeRate)
                const currentUserHasFreeAiFeature = this.currentUserHasFreeAiFeature()
                const allModelTypes = new Set(
                    [...feeItems, ...craftFeeItems].map(item => item.modelType).filter(Boolean),
                )
                const iconType =
                    allModelTypes.size === 1 && allModelTypes.has('image')
                        ? 'image-gen'
                        : allModelTypes.size === 1 && allModelTypes.has('video')
                        ? 'video-gen'
                        : allModelTypes.size === 1 && allModelTypes.has('audio')
                        ? 'audio-gen'
                        : null
                const usdPriceStr = `$${totalUsdCost.toFixed(2)}`
                const effectiveModelFeeSats = currentUserHasFreeAiFeature
                    ? feeItems.length + craftFeeItems.length
                    : totalSats
                const lineItems = (actionRequests || []).flatMap((_action, index) => {
                    const craftItem = craftFeeItems.find(item => item.index === index)
                    const generationItem = feeItems.find(item => item.index === index)
                    return [
                        craftItem
                            ? {
                                  key: `promptcraft-${index}`,
                                  label: craftItem.label,
                                  value: `$${craftItem.usdCost.toFixed(2)}`,
                              }
                            : null,
                        generationItem
                            ? {
                                  key: `${generationItem.tag}-${generationItem.index}`,
                                  label: `${generationItem.modelName}${
                                      generationItem.durationSeconds
                                          ? ` ${generationItem.durationSeconds}s`
                                          : ''
                                  }`,
                                  value: `$${generationItem.usdCost.toFixed(2)}`,
                              }
                            : null,
                    ].filter(Boolean)
                })
                const actionCount = new Set([...feeItems, ...craftFeeItems].map(item => item.index))
                    .size

                if (isMobile) {
                    this.blurEditor()
                }

                const choice = await this.confirmAiPaymentTray({
                    eyebrow: `Generate ${actionCount} AI ${
                        actionCount === 1 ? 'action' : 'actions'
                    }`,
                    title: `Total ${usdPriceStr}`,
                    lineItems,
                    usdPriceStr,
                    iconType,
                    effectiveModelFeeSats,
                })

                if (choice !== 'bsv' && choice !== 'stripe_metered') {
                    resolve(INTERRUPT_POSTING)
                    return
                }

                resolve({ paymentMode: choice })
            })
        })
    }

    handleClientSideActionCommands = async (
        actionCommand,
        delimitedContent,
        actionRequests = [],
    ) => {
        // common handler for image generation cost confirmations
        // (this handler doesn't need the content, only the tag)
        const doImgGenFeePromptIfNeeded = async (tagForPrompt, contentForDuration = null) => {
            // wrap the core logic in a promise resolved via defer to
            // help work around race condition where quill hasn't updated yet
            return new Promise(resolve => {
                defer(async () => {
                    const model = findModelByActionTag(this.props.aiModels, tagForPrompt)
                    const isPublic = isPublicAiPricingContext(this.props.quest)

                    // Parse duration from content for per-second pricing
                    const durationSeconds = contentForDuration
                        ? parseDurationFromContent(contentForDuration)
                        : null

                    // grab public and private costs from the model so that our messaging reflects reality
                    // Use duration-aware cost functions for per-second pricing
                    const pricingContext = {
                        textCharacterCount:
                            typeof contentForDuration === 'string'
                                ? contentForDuration.length
                                : null,
                    }
                    const publicCost = getPublicModelCostWithDuration(
                        model,
                        durationSeconds,
                        pricingContext,
                    )
                    const privateCost = getPrivateModelCostWithDuration(
                        model,
                        durationSeconds,
                        pricingContext,
                    )

                    // pick the cost that applies to the current posting context
                    const usdCost = isPublic ? publicCost : privateCost

                    // nothing to confirm if the cost for the current context is free
                    if (usdCost === 0) {
                        return resolve()
                    }

                    const sats = usdToSats(usdCost, this.props.exchangeRate)
                    const currentUserHasFreeAiFeature = this.currentUserHasFreeAiFeature()

                    let iconType = null
                    if (model && model.modelType === 'image') {
                        iconType = 'image-gen'
                    } else if (model && model.modelType === 'video') {
                        iconType = 'video-gen'
                    } else if (model && model.modelType === 'audio') {
                        iconType = 'audio-gen'
                    }

                    const modelName = model?.humanName || tagForPrompt
                    const usdPriceStr = `$${usdCost.toFixed(2)}`
                    const effectiveModelFeeSats =
                        currentUserHasFreeAiFeature && sats != null && sats > 0 ? 1 : sats
                    const generationLabel =
                        iconType === 'video-gen'
                            ? 'Generate Video'
                            : iconType === 'image-gen'
                            ? 'Generate Image'
                            : iconType === 'audio-gen'
                            ? 'Generate Song'
                            : 'Generating'

                    const choice = await this.confirmAiPaymentTray({
                        eyebrow: generationLabel,
                        title: modelName,
                        usdPriceStr,
                        iconType,
                        effectiveModelFeeSats,
                    })

                    if (choice !== 'bsv' && choice !== 'stripe_metered') {
                        return resolve(INTERRUPT_POSTING)
                    }

                    // resolve with no value (undefined) if we should proceed
                    resolve()
                })
            })
        }

        // Command handlers can return the INTERRUPT_POSTING const to interrupt the posting process.
        // Default i2v model for !extend when no nested command is specified
        const EXTEND_DEFAULT_I2V_TAG = DEFAULT_VIDEO_MODEL.actionTag

        // any other return value will allow posting to proceed
        const clientSideActionCommands = {
            extend: async content => {
                // !extend delegates to a nested i2v tag, so we need to extract that tag
                // and prompt for its fee. If no nested command, use the default.
                const nestedTagMatch = content.match(
                    /!extend\s+!([A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*)/,
                )
                const nestedTag = nestedTagMatch ? nestedTagMatch[1] : EXTEND_DEFAULT_I2V_TAG
                // Pass content for duration parsing to calculate per-second pricing
                return doImgGenFeePromptIfNeeded(nestedTag, content)
            },
            pay: async content => {
                return new Promise(resolveOuter => {
                    defer(async () => {
                        const payActionRequests = actionRequests.filter(
                            action =>
                                normalizeActionTag(action?.tag || action?.action_tag) === 'pay',
                        )
                        const { matches, error: payInputError } = paySubmissionInputs(
                            content,
                            payActionRequests.map(action => action?.settings?.pay),
                        )
                        if (payInputError || matches.length === 0) {
                            this.showNotice(
                                payInputError === 'mixed'
                                    ? 'Use Add recipient in the payment card, or create another payment card, for each additional payment. Remove the extra typed !pay command. No payment was sent and your message was not posted.'
                                    : 'Complete every payment recipient and amount. Use sats or BSV, with a period for decimals and no commas. No payment was sent and your message was not posted.',
                                { variant: 'error', durationMs: 9000 },
                            )
                            resolveOuter(INTERRUPT_POSTING)
                            return
                        }

                        // Parse + validate every payment before sending anything.
                        const payments = []
                        for (const {
                            recipient: mentionedUser,
                            rawAmount: strAmount,
                            unit,
                            amountSats: validatedAmountSats,
                        } of matches) {
                            // Reject comma amounts outright: '1,5' is locale-ambiguous and the old
                            // comma-stripping turned it into 15 (a 10x overpayment).
                            if (/,/.test(strAmount)) {
                                this.showNotice(
                                    `Amount "${strAmount}" is ambiguous. Use a period for decimals (e.g. 1.5 bsv) and no commas. No payment was sent.`,
                                    { variant: 'error', durationMs: 9000 },
                                )
                                resolveOuter(INTERRUPT_POSTING)
                                return
                            }

                            const lowerUnit = unit ? unit.toLowerCase() : 'sats'
                            const satoshiUnits = ['sat', 'sats', 'satoshi', 'satoshis']
                            const bitcoinUnits = ['btc', 'bitcoin', 'bsv', 'bitcoinsv']
                            if (
                                !satoshiUnits.includes(lowerUnit) &&
                                !bitcoinUnits.includes(lowerUnit)
                            ) {
                                this.showNotice(
                                    `The unit for @${mentionedUser} is invalid. Use sats or bsv. No payment was sent.`,
                                    { variant: 'error', durationMs: 9000 },
                                )
                                resolveOuter(INTERRUPT_POSTING)
                                return
                            }
                            const parsedAmountSats = bsvAmountToSats(
                                strAmount,
                                bitcoinUnits.includes(lowerUnit) ? 'bsv' : 'sats',
                            )
                            const amountInSats =
                                validatedAmountSats == null ? parsedAmountSats : validatedAmountSats

                            if (
                                amountInSats === '' ||
                                !Number.isSafeInteger(amountInSats) ||
                                amountInSats <= 0
                            ) {
                                this.showNotice(
                                    `The amount for @${mentionedUser} isn\'t a valid, positive number of sats. No payment was sent.`,
                                    { variant: 'error', durationMs: 9000 },
                                )
                                resolveOuter(INTERRUPT_POSTING)
                                return
                            }

                            const { address, error: lookupError } = await api.getBsvAddressForUser(
                                mentionedUser,
                            )
                            if (!address) {
                                this.showNotice(
                                    `Couldn\'t resolve @${mentionedUser}${
                                        lookupError ? `: ${lookupError}` : ''
                                    }. No payment was sent and your message was not posted.`,
                                    { variant: 'error', durationMs: 9000 },
                                )
                                resolveOuter(INTERRUPT_POSTING)
                                return
                            }

                            payments.push({ mentionedUser, address, amountInSats })
                        }

                        const draftId = this.getComposeAnswerId()
                        if (!this._composerPaymentIntents) {
                            this._composerPaymentIntents = restoreComposerPaymentIntents(
                                getCachedAnswer(draftId)?.composer_payment_intents ||
                                    this.props.newAnswer?.composer_payment_intents,
                            )
                        }
                        const intent = getComposerPaymentIntent(
                            this._composerPaymentIntents,
                            draftId,
                            payments,
                            () => crypto.randomUUID(),
                        )
                        cache.updateCachedAnswer(draftId, {
                            composer_payment_intents: [...this._composerPaymentIntents.entries()],
                        })
                        if (intent.receipt) {
                            this.rememberComposerPaymentReceipt(intent.receipt)
                            resolveOuter()
                            return
                        }

                        const shouldSendFunds = await confirmBsvPaymentTray(
                            payments.map(({ mentionedUser, address, amountInSats }) => ({
                                recipient: mentionedUser,
                                address,
                                amountSats: amountInSats,
                            })),
                        )
                        if (!shouldSendFunds) {
                            resolveOuter(INTERRUPT_POSTING)
                            return
                        }

                        // Declared before the try so the catch can resolve the same status toast.
                        const paymentId = intent.paymentId
                        const payKind = `payment-${paymentId}`
                        let payUnsubscribe = null
                        let payTrackTimeout = null
                        const stopPaymentTracking = () => {
                            if (payUnsubscribe) {
                                payUnsubscribe()
                                payUnsubscribe = null
                            }
                            if (payTrackTimeout) {
                                clearTimeout(payTrackTimeout)
                                payTrackTimeout = null
                            }
                        }

                        try {
                            const wallet = initWallet(this.props.currentUserBsvWalletType)
                            const outgoingPayments = intent.payments
                            const treechatUsers = outgoingPayments.map(p => p.mentionedUser)

                            // In-flight status toast (spinner, no auto-dismiss) resolved below.
                            toast.pending('Sending payment…', {
                                kind: payKind,
                                testId: 'toast-pay',
                            })

                            // Track the payment's terminal outcome for user feedback. Replaces the
                            // old subscription to the dead 'sent bsv transaction' channel + 2-minute
                            // timeout; keyed on this payment's id and driven by bsv_payment_update.
                            payUnsubscribe = subscribePaymentUpdate(paymentId, update => {
                                if (!isTerminalPaymentStatus(update.status)) return
                                if (isSuccessfulPaymentStatus(update.status)) {
                                    toast.update(
                                        payKind,
                                        update.txId
                                            ? `Payment sent · ${shortTxid(update.txId)}`
                                            : 'Payment sent',
                                        { variant: 'success' },
                                    )
                                } else if (update.status === 'failed') {
                                    toast.update(
                                        payKind,
                                        `Payment failed: ${update.lastError || 'unknown error'}`,
                                        { variant: 'error' },
                                    )
                                } else if (update.status === 'cancelled') {
                                    toast.update(payKind, 'Payment cancelled', { variant: 'info' })
                                }
                                stopPaymentTracking()
                            })
                            // Self-custody (client-signing) sends wait for the payer's device to
                            // sign (up to ~10 min server-side) before broadcasting, so give them a
                            // wider tracking window than the server-signing/legacy path.
                            const features = gon.currentUser?.features || []
                            const hasClientSigning = features.some(
                                f => (typeof f === 'string' ? f : f?.name) === 'use_client_signing',
                            )
                            const trackTimeoutMs = (hasClientSigning ? 15 : 5) * 60 * 1000
                            payTrackTimeout = setTimeout(() => {
                                stopPaymentTracking()
                                toast.update(
                                    payKind,
                                    'Still processing — check Payments history for status',
                                    { variant: 'info' },
                                )
                            }, trackTimeoutMs)

                            let sendResult
                            if (outgoingPayments.length === 1) {
                                sendResult = await wallet.sendBsv(
                                    outgoingPayments[0].address,
                                    outgoingPayments[0].amountInSats,
                                    undefined,
                                    treechatUsers,
                                    paymentId,
                                    true,
                                )
                            } else {
                                const [firstPayment, ...additionalPayments] = outgoingPayments
                                const finalAdditionalPayments = additionalPayments.map(p => ({
                                    amount: p.amountInSats,
                                    addr: p.address,
                                }))
                                sendResult = await wallet.sendBsv(
                                    firstPayment.address,
                                    firstPayment.amountInSats,
                                    finalAdditionalPayments,
                                    treechatUsers,
                                    paymentId,
                                    true,
                                )
                            }

                            const acceptedStatuses = [
                                'temporal_pending',
                                'in progress',
                                'ok',
                                'pending',
                                'completed',
                            ]
                            if (!sendResult || !acceptedStatuses.includes(sendResult.status)) {
                                // Initiation failed: do NOT publish a post that implies a payment
                                // happened when it never even started.
                                stopPaymentTracking()
                                const reason =
                                    sendResult?.status === 'conflict'
                                        ? 'this payment is already being processed'
                                        : sendResult?.error || 'unknown error'
                                toast.update(
                                    payKind,
                                    `Payment could not be sent: ${reason}. Your message was not posted.`,
                                    { variant: 'error' },
                                )
                                resolveOuter(INTERRUPT_POSTING)
                                return
                            }

                            if (!sendResult.payment_receipt) {
                                stopPaymentTracking()
                                toast.update(
                                    payKind,
                                    'Payment processing, but its receipt could not be loaded. Retry posting to recover the same payment.',
                                    { variant: 'error' },
                                )
                                resolveOuter(INTERRUPT_POSTING)
                                return
                            }
                            intent.receipt = sendResult.payment_receipt
                            this.rememberComposerPaymentReceipt(intent.receipt)

                            if (sendResult.status === 'completed') {
                                stopPaymentTracking()
                                toast.update(
                                    payKind,
                                    sendResult.tx_id
                                        ? `Payment sent · ${shortTxid(sendResult.tx_id)}`
                                        : 'Payment sent',
                                    { variant: 'success' },
                                )
                            }

                            resolveOuter() // Payment accepted for processing; allow the post.
                        } catch (error) {
                            stopPaymentTracking()
                            toast.update(
                                payKind,
                                `Payment could not be sent: ${error.message}. Your message was not posted.`,
                                { variant: 'error' },
                            )
                            resolveOuter(INTERRUPT_POSTING)
                        }
                    })
                })
            },
        }

        // dynamically add handlers for ai models that might have fees
        this.props.aiModels?.forEach(model => {
            const commandTagName = normalizeActionTag(model.actionTagName)
            if (!commandTagName) return

            // Register every known AI model command. The prompt-time cost check below still skips
            // truly free models, but duration-priced production rows must get a handler before
            // we know the user's requested duration.
            clientSideActionCommands[commandTagName] = async content =>
                await doImgGenFeePromptIfNeeded(commandTagName, content)
        })

        const normalizedActionCommand = normalizeActionTag(actionCommand)
        if (clientSideActionCommands[normalizedActionCommand]) {
            return clientSideActionCommands[normalizedActionCommand](delimitedContent)
        }
        // No handler for this command; proceed with normal posting process.
        return
    }

    /**
     * Check if the user is requesting an invalid duration for a video model.
     * If invalid, show a confirmation modal offering to fix the duration.
     *
     * @returns null if no validation needed (duration valid or not applicable),
     *          INTERRUPT_POSTING if user cancels,
     *          or { content, deltaJson } with the corrected values if user confirms
     */
    checkVideoDurationValidation = async (commandTag, content, deltaJson) => {
        const aiModels = this.props.aiModels || []

        // Handle !extend by extracting the nested model command.
        let modelTag = commandTag
        if (commandTag === 'extend') {
            const nestedTagMatch = content.match(/!extend\s+!([A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*)/)
            if (nestedTagMatch) {
                let nestedTag = nestedTagMatch[1]

                // Check for T2V->I2V redirect (same logic as checkExtendT2vRedirect)
                const nestedModel = findModelByActionTag(aiModels, nestedTag)
                if (nestedModel) {
                    const isT2vModel =
                        nestedModel.modelType === 'video' && !nestedModel.canRemixImage
                    if (isT2vModel) {
                        // Find the corresponding i2v model by matching humanName
                        const i2vModel = aiModels.find(
                            m =>
                                m.modelType === 'video' &&
                                m.canRemixImage === true &&
                                m.humanName === nestedModel.humanName,
                        )
                        if (i2vModel) {
                            modelTag = i2vModel.actionTagName
                        } else {
                            modelTag = nestedTag
                        }
                    } else {
                        modelTag = nestedTag
                    }
                } else {
                    modelTag = nestedTag
                }
            } else {
                // No nested command found, use default i2v model
                modelTag = DEFAULT_VIDEO_MODEL.actionTag
            }
        }

        // Find the model for this action command
        const model = findModelByActionTag(aiModels, modelTag)
        if (!model) return null // Unknown model, let backend handle it

        // Only check video models
        if (model.modelType !== 'video') return null

        // Parse duration from content
        const requestedDuration = parseDurationFromContent(content)
        if (requestedDuration === null) return null // No duration specified, uses default

        // Validate duration against model constraints
        const validationResult = validateDurationForModel(requestedDuration, model)
        if (validationResult.isValid) return null // Duration is valid

        // Duration is invalid - show confirmation modal
        const confirmResult = await confirmInvalidDuration(model, validationResult)
        if (!confirmResult.confirmed) {
            return INTERRUPT_POSTING
        }

        // User confirmed - replace duration in content and deltaJson
        const newDuration = confirmResult.selectedDuration
        const durationPattern = /\b(\d{1,3})(?:s|sec|secs|seconds?)\b/i
        const newContent = content.replace(durationPattern, `${newDuration}s`)
        const newDeltaJson = this.replaceDurationInDeltaJson(
            deltaJson,
            requestedDuration,
            newDuration,
        )

        return { content: newContent, deltaJson: newDeltaJson }
    }

    postOnVideo = async videoType => {
        let content, deltaJson
        if (this.richEditor.current) {
            content = this.getContent().trim()
            deltaJson = this.getDeltaJson()
        }
        if (!content) {
            content = `!${videoType}`
            deltaJson = { ops: [{ insert: content, attributes: { bold: true } }] }
        }
    }

    captureSetValue = setValue => {
        // We want to set value of Common Editor when post button is pressed so need to grab it from inside the functional comp.
        this.setEditorValue = setValue
    }

    onChange = (value, text) => {
        if (!gon.currentUser) {
            const guestHasComposerContent = answerModel.hasContent({
                deltaJson: value,
            })

            if (!guestHasComposerContent) {
                return
            }

            useStore.getState().actions.modalActions.openAuthModal('signup', 'composer_focus')
            return
        }

        this.ensureDraftActionRequestsForEditorTokens(value)
        this.syncNbActionRequestsWithComposerMedia(value, {
            onlyIfComposerMediaChanged: true,
        })

        // Check if answer has content using the same logic as button validation
        const hasContent = answerModel.hasContent(this.getNewAnswer())

        // Only update state if validity changed to minimize re-renders
        if (this.state.isValidContent !== hasContent) {
            this.setState({ isValidContent: hasContent })
        }

        this.reconcileSuppressedPreviewUrls(value)
        const streamEmbedsLoading = this.hasUnresolvedStreamEmbeds(value)
        if (this.state.streamEmbedsLoading !== streamEmbedsLoading) {
            this.setState({ streamEmbedsLoading })
        }

        if (this.isCompactComposer() && hasContent && !this.state.inlineComposerExpanded) {
            this.setState({ inlineComposerExpanded: true })
        }

        // Handle draft saving based on context
        if (this.props.isTitle) {
            // New quest - use title drafts
            // Save immediately on first character, then debounce
            if (
                !this.state.isDraftLoaded &&
                !TitleDraftManager.load().find(d => d.id === this.state.currentDraftId)
            ) {
                // First save - do it immediately
                this.saveToTitleDrafts()
            } else {
                // Subsequent saves - debounce
                this.debouncedSaveTitleDraft()
            }
        } else if (this.props.quest?.id) {
            // Cache content for unmount (editor may be gone by then)
            this._lastContent = this.getContent()
            this._lastDeltaJson = this.getDeltaJson()
            // Reply - save draft with debounce for performance
            // Unmount will cancel debounce and save immediately
            this.debouncedSaveDraft()
        }
    }

    tmpAnswerId = () => `tmp-id-new-answer-for-quest-${this.props.quest.id}`

    richEditor = React.createRef()
    containerRef = React.createRef()

    // Deliberately unset rather than null: the first report has to go out even
    // when it is null, so a composer that mounts with nothing to quote clears a
    // latch the thread may still hold from a composer that quoted before it.
    _reportedQuoteTargetId = undefined
    postMediaActionTooltipRef = React.createRef()

    // Focus the composer. A caret the reader is writing at is never moved: a
    // quote arriving, a menu choice or a refocus retry is not a reason to send
    // it to the end, and a caret placed before the editor lost focus (the
    // keyboard dismissed, a reply pressed on a message above) comes back where
    // it was. Only a draft freshly loaded, before the reader has focused this
    // editor, opens at its end — or any caret when the caller asks for the end.
    focusEditor = ({ defer = true, at = 'caret' } = {}) => {
        // Posting from the modal closes it, so the refocus retries below can
        // fire after this composer is gone. The ref identity check is not
        // enough on its own: React keeps the handle, and the editor inside it
        // is already destroyed.
        if (this._isUnmounted || !this.richEditor.current) return
        const editor = this.richEditor.current
        const isCurrentEditor = () => !this._isUnmounted && this.richEditor.current === editor

        const place = () => {
            if (!isCurrentEditor()) return
            const instance = this.getEditorInstance()
            const view = instance?.view
            let hadFocus = false
            try {
                hadFocus = !!view?.hasFocus?.()
            } catch (e) {}
            if (hadFocus && at !== 'end') return
            // Read before anything focuses: a DOM focus can move the DOM caret
            // to the start, and the editor then reads it back.
            let caret = null
            if (at !== 'end') {
                try {
                    const { from, to } = instance.state.selection
                    if (from > 1 || to > 1 || this._lastFocusedEditor === instance) {
                        caret = { from, to }
                    }
                } catch (e) {}
            }
            // bottom_editor_dock on iOS: TipTap focuses without preventScroll,
            // and WebKit then reveals the field as the keyboard comes — the
            // whole page scrolled by the keyboard's height even with the pill
            // placed clear of it, and snapped back when the resize landed. A
            // focus that prevents scrolling goes first, whatever asked for it
            // (a tap on the pill, a post's reply button quoting into it);
            // TipTap's then only places the caret.
            if (isIOS && this.props.panelContext?.bottomEditorDocked) {
                try {
                    view?.dom?.focus({ preventScroll: true })
                } catch (e) {}
            }
            if (caret) {
                try {
                    instance
                        .chain()
                        .setTextSelection(caret)
                        .focus(undefined, { scrollIntoView: false })
                        .run()
                    return
                } catch (e) {}
            }
            focusEditorAtEndSafely(editor, isCurrentEditor)
        }

        if (defer) {
            setTimeout(place)
        } else {
            place()
        }
    }

    // The reply modal's field is taller than its text: a press in the blank
    // under the text puts the caret at the end, as a press under a textarea's
    // last line does.
    onModalFieldAreaMouseDown = ev => {
        const target = ev?.target
        if (!(target instanceof Element)) return
        if (
            target.closest('[contenteditable="true"], button, a, input, textarea, [role="button"]')
        ) {
            return
        }
        ev.preventDefault()
        this.focusEditor({ defer: false, at: 'end' })
    }

    // The folded pill's tap whose native focus was cancelled (iOS with the
    // keyboard down, shouldTakeComposerTapFocus): the caret goes where the
    // finger landed, as the native tap would have put it — not to the end.
    focusEditorAtPoint = (clientX, clientY) => {
        const instance = this.getEditorInstance()
        const view = instance?.view
        let pos = null
        try {
            pos = view?.posAtCoords?.({ left: clientX, top: clientY })?.pos ?? null
        } catch (e) {}
        if (pos == null || typeof instance?.chain !== 'function') {
            this.focusEditor({ defer: false })
            return
        }
        if (isIOS && this.props.panelContext?.bottomEditorDocked) {
            try {
                view.dom.focus({ preventScroll: true })
            } catch (e) {}
        }
        try {
            instance.chain().setTextSelection(pos).focus(undefined, { scrollIntoView: false }).run()
        } catch (e) {
            this.focusEditor({ defer: false })
        }
    }

    scrollComposerIntoView = () => {
        const panel = this.props.panelContext?.panel
        const targetEl = this.containerRef?.current

        const scroll = () => {
            try {
                panel?.scrollToBottom?.()
            } catch (e) {}

            try {
                targetEl?.scrollIntoView?.({
                    block: 'end',
                    inline: 'nearest',
                    behavior: 'smooth',
                })
            } catch (e) {}
        }

        requestAnimationFrame(scroll)
        setTimeout(scroll, 80)
        setTimeout(scroll, 220)
    }

    // Expose a focus method that Quest.jsx can call
    focus = () => {
        this.focusEditor()
    }

    refocusComposerAfterPost = () => {
        // Posting can remount the composer (new draft id) and briefly steal focus.
        // Re-assert focus over a couple of frames so the caret lands in the next message box.
        const attemptFocus = () => this.focusEditor({ defer: false })
        setTimeout(attemptFocus, 0)
        setTimeout(attemptFocus, 80)
        setTimeout(attemptFocus, 180)
    }

    refocusInlineComposerAfterExpand = () => {
        const attemptFocus = () => this.focusEditor({ defer: false })
        requestAnimationFrame(attemptFocus)
        setTimeout(attemptFocus, 0)
        setTimeout(attemptFocus, 80)
        setTimeout(attemptFocus, 180)
    }

    editorHasFocus = () => {
        try {
            return !!this.getEditorInstance()?.view?.hasFocus?.()
        } catch (e) {
            return false
        }
    }

    blurEditor = () => {
        const editor = this.richEditor.current
        if (!editor) return
        try {
            if (typeof editor.blur === 'function') {
                editor.blur()
                return
            }
            const actualEditor = editor.getEditor ? editor.getEditor() : editor
            if (actualEditor?.commands?.blur) {
                actualEditor.commands.blur()
                return
            }
            const dom = actualEditor?.view?.dom || actualEditor?.root
            if (dom && typeof dom.blur === 'function') {
                dom.blur()
            }
        } catch (e) {}
    }

    insertActionRequestIntoComposer = actionRequest => {
        const editor = this.getEditor()
        if (!editor || !actionRequest) return
        // An insert leaves a phone's keyboard as it found it: inserting focuses the
        // editor, so one started with the keyboard down (a remix from a post's media)
        // lets it go again, and one made while writing keeps it up.
        const keyboardWasUp = isMobile && this.editorHasFocus()
        // A real choice from a portaled menu intentionally opens the compact
        // composer. Its close notification must not immediately fold it again.
        if (this.isCompactComposer()) this.armInlineCollapseSkip()
        const actionRequests = Array.isArray(actionRequest) ? actionRequest : [actionRequest]
        const insertionItems = []
        let nextActionRequests = this.getCurrentDraftActionRequests()
        let hasNewActionRequests = false

        actionRequests.forEach(rawItem => {
            if (!rawItem) return
            let item = rawItem
            const rawTag =
                typeof rawItem === 'string'
                    ? rawItem
                    : rawItem?.tag || rawItem?.aiAction?.tag || rawItem?.attrs?.tag
            const postMediaKind = postMediaActionKindForTag(rawTag)
            const existingPostMediaContext =
                typeof rawItem === 'string' ? null : rawItem?.aiAction?.craft_context
            if (postMediaKind && existingPostMediaContext?.mode !== 'simple') {
                this.openPostMediaSettingsDialog(postMediaKind, 'magic_wand_modal')
                return
            }
            const commandText = typeof item === 'string' ? item : item.commandText || `!${item.tag}`
            const providedAiAction = typeof item === 'string' ? null : item.aiAction
            const fallbackAttrs = typeof item === 'string' ? null : item.attrs
            const buildOptions = typeof item === 'string' ? {} : item.buildOptions || {}
            const focusPromptOnInsert = buildOptions.focusPromptOnInsert === true
            let actionTokenReferenceAttrs = null

            if (providedAiAction || fallbackAttrs) {
                const action = this.ensureAiActionClientId(
                    providedAiAction
                        ? providedAiAction
                        : this.buildAiActionFromAttrs(
                              fallbackAttrs,
                              this.getDeltaJson(),
                              buildOptions,
                          ),
                )
                if (action) {
                    const actionId = this.getAiActionRefId(action)
                    this._pendingActionRequestsByClientId ||= {}
                    this._pendingActionRequestsByClientId[actionId] = action
                    nextActionRequests = [...nextActionRequests, action]
                    hasNewActionRequests = true
                    actionTokenReferenceAttrs = this.actionTokenReferenceAttrs(
                        {
                            ...(this.aiActionToActionTokenAttrs(action) || {}),
                            ...(fallbackAttrs || {}),
                            tag: action.tag,
                            actionId,
                            commandText,
                        },
                        { includePromptContent: true, includeDisplayName: true },
                    )
                }
            }

            insertionItems.push({
                commandText,
                actionTokenReferenceAttrs,
                focusPromptOnInsert,
            })
        })

        if (!insertionItems.length) return
        if (hasNewActionRequests) {
            this.setDraftActionRequests(nextActionRequests)
        }

        let shouldKeepMobileFocus = false
        if (editor.commands) {
            const instance = editor.getEditor ? editor.getEditor() : editor
            const shouldFocusPromptOnInsert = insertionItems.some(
                item =>
                    item.focusPromptOnInsert &&
                    item.actionTokenReferenceAttrs &&
                    this.actionTokenSupportsPromptInput(item.actionTokenReferenceAttrs),
            )
            if (shouldFocusPromptOnInsert && typeof instance?.commands?.focus === 'function') {
                instance.commands.focus('end', { scrollIntoView: false })
            } else if (isMobile) {
                try {
                    const endPos = instance?.state?.doc?.content?.size
                    if (typeof endPos === 'number') {
                        instance?.commands?.setTextSelection?.({ from: endPos, to: endPos })
                    }
                } catch (e) {}
            } else {
                instance?.commands?.focus?.('end', { scrollIntoView: false })
            }
            const { $from } = instance?.state?.selection || {}
            const pos = $from?.pos || 0
            const textBefore =
                pos > 0 ? instance.state.doc.textBetween(Math.max(0, pos - 1), pos) : ''
            const contentToInsert = []
            if (textBefore?.trim()) contentToInsert.push({ type: 'text', text: ' ' })
            insertionItems.forEach(({ commandText, actionTokenReferenceAttrs }) => {
                if (actionTokenReferenceAttrs) {
                    contentToInsert.push(
                        ...this.actionTokenInlineContentForAttrs(actionTokenReferenceAttrs),
                    )
                } else {
                    contentToInsert.push({
                        type: 'text',
                        text: commandText,
                        marks: [{ type: 'bold' }],
                    })
                }
                contentToInsert.push({ type: 'text', text: ' ' })
            })
            if (shouldFocusPromptOnInsert && typeof instance?.chain === 'function') {
                instance
                    .chain()
                    .focus(undefined, { scrollIntoView: false })
                    .insertContent(contentToInsert)
                    .run()
            } else {
                instance?.commands?.insertContent?.(contentToInsert)
            }
            const focusPromptItem = insertionItems.find(
                item =>
                    item.focusPromptOnInsert &&
                    item.actionTokenReferenceAttrs &&
                    this.actionTokenSupportsPromptInput(item.actionTokenReferenceAttrs),
            )
            if (focusPromptItem?.actionTokenReferenceAttrs) {
                shouldKeepMobileFocus = true
                this.focusActionTokenPrompt(focusPromptItem.actionTokenReferenceAttrs)
            }
        } else {
            const cursorPosition =
                editor?.selection?.savedRange?.index ?? editor?.getLength?.() ?? 0
            try {
                const textBefore =
                    cursorPosition > 0 && typeof editor.getText === 'function'
                        ? editor.getText(cursorPosition - 1, 1)
                        : ''
                const prefix = textBefore?.trim() ? ' ' : ''
                const commandText = insertionItems.map(item => item.commandText).join(' ')
                editor.insertText(cursorPosition, `${prefix}${commandText} `, 'bold', true, 'user')
                if (!isMobile) {
                    editor.setSelection(cursorPosition + prefix.length + commandText.length + 1)
                }
            } catch (e) {}
        }

        if (hasNewActionRequests) {
            setTimeout(() => {
                this.setDraftActionRequests(nextActionRequests)
            }, 0)
        }

        if (this.isCompactComposer()) {
            this.setState({ inlineComposerExpanded: true, isValidContent: true })
        }

        if (isMobile && !shouldKeepMobileFocus && !keyboardWasUp) {
            this.blurEditor()
        }
    }

    getCurrentPostMediaActionInputs = () => {
        const sourceAnswer =
            getCachedAnswer(this.getComposeAnswerId()) ||
            this.props.newAnswer ||
            this.props.quest?.parent ||
            {}
        const mediaCandidateOptions = {
            includeAiActionInputs: false,
            includeAiRunOutputs: false,
        }

        return {
            images: this.getAnswerAiImageCandidates(
                sourceAnswer,
                'Attached image',
                mediaCandidateOptions,
            ),
            videos: this.getAnswerAiVideoCandidates(
                sourceAnswer,
                'Attached video',
                mediaCandidateOptions,
            ),
            audios: this.getAnswerAiAudioCandidates(
                sourceAnswer,
                'Attached audio',
                mediaCandidateOptions,
            ),
        }
    }

    preparePostMediaActionInput = (input, kind) => {
        if (!(input?.file instanceof File) || this.isPersistentDraftPayloadUrl(input.url)) {
            return input
        }

        const existingUpload = this.aiInputUploadManager.getRefForBlobUrl(input.url)
        const upload =
            existingUpload ||
            this.aiInputUploadManager.addFile(input.file, kind, {
                blobUrl: input.url?.startsWith?.('blob:') ? input.url : null,
            })
        return {
            ...input,
            url: upload.url,
            source: upload.source,
            uploadId: upload.upload_id,
            signedId: upload.signed_id || null,
            label: input.label || upload.label,
            contentType: input.contentType || upload.content_type,
            file: null,
        }
    }

    preparePostMediaAiAction = action => {
        if (!action) return action
        const inputs = action.inputs || {}
        const references = inputs.references || null

        return {
            ...action,
            inputs: {
                ...inputs,
                ...(inputs.start_image
                    ? {
                          start_image: this.preparePostMediaActionInput(
                              inputs.start_image,
                              'image',
                          ),
                      }
                    : {}),
                ...(inputs.end_image
                    ? {
                          end_image: this.preparePostMediaActionInput(inputs.end_image, 'image'),
                      }
                    : {}),
                ...(inputs.image
                    ? { image: this.preparePostMediaActionInput(inputs.image, 'image') }
                    : {}),
                ...(inputs.video
                    ? { video: this.preparePostMediaActionInput(inputs.video, 'video') }
                    : {}),
                ...(Array.isArray(inputs.videos)
                    ? {
                          videos: inputs.videos.map(input =>
                              this.preparePostMediaActionInput(input, 'video'),
                          ),
                      }
                    : {}),
                ...(inputs.audio
                    ? { audio: this.preparePostMediaActionInput(inputs.audio, 'audio') }
                    : {}),
                ...(references
                    ? {
                          references: {
                              ...references,
                              ...(Array.isArray(references.images)
                                  ? {
                                        images: references.images.map(input =>
                                            this.preparePostMediaActionInput(input, 'image'),
                                        ),
                                    }
                                  : {}),
                              ...(Array.isArray(references.videos)
                                  ? {
                                        videos: references.videos.map(input =>
                                            this.preparePostMediaActionInput(input, 'video'),
                                        ),
                                    }
                                  : {}),
                              ...(Array.isArray(references.audios)
                                  ? {
                                        audios: references.audios.map(input =>
                                            this.preparePostMediaActionInput(input, 'audio'),
                                        ),
                                    }
                                  : {}),
                          },
                      }
                    : {}),
            },
        }
    }

    capturePostMediaActionInputs = () => {
        this._postMediaActionInputsSnapshot = this.getCurrentPostMediaActionInputs()
    }

    onPostMediaActionMenuStateChange = isOpen => {
        if (isOpen) {
            this.postMediaActionTooltipRef.current?.hideTooltip()
            this.capturePostMediaActionInputs()
        } else {
            setTimeout(() => {
                this._postMediaActionInputsSnapshot = null
            }, 0)
        }
        this.onAgentMenuStateChange(isOpen)
    }

    buildPostMediaActionForComposer = (kind, source, overrides = null) => {
        // Empty text is allowed: the server crafts a Treechat house piece from the member's
        // recent public posts instead.
        const postText = this.getCurrentAiTextInput().trim()

        // Simple mode keeps the fallback prompt in the payload but hides it from the composer.
        // The action worker replaces it with LLM creative direction after payment confirmation.
        const aiAction = this.ensureAiActionClientId(
            this.preparePostMediaAiAction(
                buildPostMediaAiAction({
                    kind,
                    postText,
                    upvalueSats: 0,
                    aiModels: this.props.aiModels,
                    mediaInputs:
                        (source === 'new_answer_controls' && this._postMediaActionInputsSnapshot) ||
                        this.getCurrentPostMediaActionInputs(),
                    overrides,
                }),
            ),
        )
        if (source === 'new_answer_controls') this._postMediaActionInputsSnapshot = null
        if (!aiAction) return null

        const usageAnalytics = buildPostMediaActionUsageAnalytics({
            kind,
            source,
            answerId: this.props.newAnswer?.id,
            questId: this.props.quest?.id,
            aiModelId: aiAction.ai_model_id,
            actionTag: aiAction.tag,
        })
        window.logEv?.(usageAnalytics.event, usageAnalytics.properties)

        return aiAction
    }

    insertPostMediaAction = kind => {
        const aiAction = this.buildPostMediaActionForComposer(kind, 'new_answer_controls')
        if (!aiAction) return

        this.insertActionRequestIntoComposer({
            tag: aiAction.tag,
            commandText: aiAction.command_text,
            aiAction,
            buildOptions: { syncDraftInputs: false, focusPromptOnInsert: false },
        })
        this.settleComposerAfterAiInsert()
    }

    openPostMediaSettingsDialog = (kind, source, edit = null) => {
        this.setState({
            postMediaSettingsDialog: {
                kind,
                source,
                edit,
                // The palette-menu snapshot is nulled when the menu closes, so carry it into
                // the dialog for the deferred insert.
                mediaInputsSnapshot:
                    !edit && source === 'new_answer_controls'
                        ? this._postMediaActionInputsSnapshot ||
                          this.getCurrentPostMediaActionInputs()
                        : null,
            },
        })
    }

    closePostMediaSettingsDialog = () => this.setState({ postMediaSettingsDialog: null })

    confirmPostMediaSettingsDialog = selection => {
        const dialog = this.state.postMediaSettingsDialog
        if (!dialog) return
        this.closePostMediaSettingsDialog()

        if (dialog.edit) {
            const updated = applyPostMediaOverridesToAction(
                dialog.edit.action,
                this.props.aiModels,
                selection,
            )
            if (updated) this.updateAiActionAtIndex(dialog.edit.index, updated)
            return
        }

        if (dialog.source === 'new_answer_controls' && dialog.mediaInputsSnapshot) {
            this._postMediaActionInputsSnapshot = dialog.mediaInputsSnapshot
        }
        const aiAction = this.buildPostMediaActionForComposer(dialog.kind, dialog.source, selection)
        if (!aiAction) return

        this.insertActionRequestIntoComposer({
            tag: aiAction.tag,
            commandText: aiAction.command_text,
            aiAction,
            buildOptions: { syncDraftInputs: false, focusPromptOnInsert: false },
        })
        this.settleComposerAfterAiInsert()
    }

    renderPostMediaSettingsDialog = () => {
        const dialog = this.state.postMediaSettingsDialog
        if (!dialog) return null

        const action = dialog.edit?.action
        const settings = action?.settings || {}
        return (
            <PostMediaSettingsDialog
                kind={dialog.kind}
                aiModels={this.props.aiModels || []}
                isPublic={isPublicAiPricingContext(this.props.quest)}
                initial={
                    action
                        ? {
                              aiModelId: action.ai_model_id || null,
                              durationSeconds: settings.duration_seconds || null,
                              resolution: settings.resolution || null,
                              aspectRatio: settings.aspect_ratio || null,
                          }
                        : null
                }
                confirmLabel={
                    dialog.edit ? 'Save' : dialog.kind === 'film' ? 'Add Film' : 'Add Illustration'
                }
                onConfirm={this.confirmPostMediaSettingsDialog}
                onClose={this.closePostMediaSettingsDialog}
            />
        )
    }

    getCraftedPromptInputs = () => []

    settleComposerAfterAiInsert = () => {
        this.scrollComposerIntoView()
        // On a phone the insert already left the keyboard as it found it.
        if (isMobile) return
        this.props.focusNewAnswer?.(0)
        this.focusEditor({ defer: false })
    }

    // Answer-controls menu Illustrate/Film: builds the simple post-media action from the
    // referenced post's contents (text, attached/generated media, earned upvalue) and drops
    // it into this composer. craft_context carries the source answer reference so every
    // card surface can show which post the generation is based on.
    insertPostMediaActionFromSource = source => {
        const kind =
            source?.kind === 'film' ? 'film' : source?.kind === 'illustrate' ? 'illustrate' : null
        const sourceAnswer = source?.answerId ? getCachedAnswer(source.answerId) : null
        if (!kind || !canLaunchExistingPostMediaAction(sourceAnswer, window.gon?.currentUser?.id)) {
            return
        }

        const postText = getAnswerDisplayText(sourceAnswer)?.trim()
        if (!postText) return

        const authorName = (sourceAnswer.virtual_user_name || sourceAnswer.user?.name || '').trim()
        const upvalueSats = Number(sourceAnswer.bsv_upvalue_amt || 0)
        // Attached media plus visible AI-generated outputs are the post's contents; the
        // author's private action inputs are metadata and stay out.
        const mediaCandidateOptions = { includeAiActionInputs: false }
        const mediaInputs = sourceAnswer
            ? {
                  images: this.getAnswerAiImageCandidates(
                      sourceAnswer,
                      'Source post image',
                      mediaCandidateOptions,
                  ),
                  videos: this.getAnswerAiVideoCandidates(
                      sourceAnswer,
                      'Source post video',
                      mediaCandidateOptions,
                  ),
                  audios: this.getAnswerAiAudioCandidates(
                      sourceAnswer,
                      'Source post audio',
                      mediaCandidateOptions,
                  ),
              }
            : null

        const aiAction = this.ensureAiActionClientId(
            this.preparePostMediaAiAction(
                buildPostMediaAiAction({
                    kind,
                    postText,
                    upvalueSats,
                    aiModels: this.props.aiModels,
                    mediaInputs,
                    sourceAnswer: { id: sourceAnswer.id, authorName: authorName || null },
                }),
            ),
        )
        if (!aiAction) return

        const usageAnalytics = buildPostMediaActionUsageAnalytics({
            kind,
            source: 'answer_controls_menu',
            answerId: sourceAnswer.id,
            questId: this.props.quest?.id,
            aiModelId: aiAction.ai_model_id,
            actionTag: aiAction.tag,
            upvalueSats,
        })
        window.logEv?.(usageAnalytics.event, usageAnalytics.properties)

        this.insertActionRequestIntoComposer({
            tag: aiAction.tag,
            commandText: aiAction.command_text,
            aiAction,
            buildOptions: { syncDraftInputs: false, focusPromptOnInsert: false },
        })
        this.settleComposerAfterAiInsert()
    }

    insertAiActionFromMedia = event => {
        const detail = event?.detail || {}
        if (detail.questId && detail.questId !== this.props.quest?.id) return
        if (this.props.isTitle) return
        if (this.props.isActive === false && !this.props.isTitle) return

        if (detail.postMediaSource && typeof detail.postMediaSource === 'object') {
            this.insertPostMediaActionFromSource(detail.postMediaSource)
            return
        }

        const sourceAiAction = detail.aiAction || detail.ai_action
        if (sourceAiAction && typeof sourceAiAction === 'object') {
            const aiAction = this.cloneAiActionForRemix(sourceAiAction)
            const remixTag = this.canonicalRemixCommandTag(
                detail.tag ||
                    detail.actionTag ||
                    detail.action_tag ||
                    sourceAiAction.tag ||
                    sourceAiAction.action_tag ||
                    sourceAiAction.actionTag,
            )
            if (aiAction && remixTag) {
                aiAction.tag = remixTag
                aiAction.command_text = `!${remixTag}`
            }
            const tag = normalizeActionTag(aiAction?.tag)
            if (!tag) return
            if (tag === 'extend') {
                const settings =
                    aiAction.settings && typeof aiAction.settings === 'object'
                        ? { ...aiAction.settings }
                        : {}
                const existingExtendTag =
                    settings.extend_action_tag || settings.extendActionTag || null
                const existingExtendModelId =
                    settings.extend_ai_model_id || settings.extendAiModelId || null
                const extendAiModel = existingExtendModelId
                    ? (this.props.aiModels || []).find(
                          candidate => candidate?.id === existingExtendModelId,
                      )
                    : findModelByActionTag(this.props.aiModels || [], existingExtendTag)
                const resolvedExtendActionTag =
                    existingExtendTag ||
                    extendAiModel?.actionTagName ||
                    DEFAULT_VIDEO_MODEL.actionTag

                if (resolvedExtendActionTag) {
                    settings.extend_action_tag = canonicalActionTagFor(resolvedExtendActionTag)
                    delete settings.extendActionTag
                    delete settings.extend_ai_model_id
                    delete settings.extendAiModelId
                    aiAction.settings = settings
                }
            }

            this.insertActionRequestIntoComposer({
                tag,
                commandText: aiAction.command_text || `!${tag}`,
                aiAction: {
                    ...aiAction,
                    analytics_source:
                        detail.analyticsSource ||
                        detail.analytics_source ||
                        (detail.remix
                            ? 'media_remix_menu'
                            : aiAction.analytics_source ||
                              aiAction.analyticsSource ||
                              'media_ai_action'),
                },
                buildOptions: { syncDraftInputs: false, focusPromptOnInsert: true },
            })
            this.settleComposerAfterAiInsert()
            return
        }

        const tag = normalizeActionTag(detail.tag)
        const previewTitle = detail.previewTitle || detail.preview_title || null
        const imageUrl = detail.image?.url
        const videoUrl = detail.video?.url
        if (!tag || (!imageUrl && !videoUrl)) return
        const imageInput = detail.image
            ? {
                  id:
                      detail.image.attachmentId ||
                      detail.image.blobId ||
                      detail.image.id ||
                      imageUrl,
                  url: imageUrl,
                  label: detail.image.label || 'Image input',
                  answerId: detail.image.answerId || null,
                  attachmentId: detail.image.attachmentId || null,
                  blobId: detail.image.blobId || null,
                  runId: detail.image.runId || null,
                  rootRemixAiRunId:
                      detail.image.rootRemixAiRunId || detail.image.root_remix_ai_run_id || null,
                  actionId: detail.image.actionId || null,
              }
            : null
        const videoInput = detail.video
            ? {
                  id:
                      detail.video.attachmentId ||
                      detail.video.blobId ||
                      detail.video.id ||
                      videoUrl,
                  url: videoUrl,
                  label: detail.video.label || 'Video input',
                  previewImageUrl: detail.video.previewImageUrl || null,
                  actionTag: detail.video.actionTag || null,
                  answerId: detail.video.answerId || null,
                  attachmentId: detail.video.attachmentId || null,
                  blobId: detail.video.blobId || null,
                  runId: detail.video.runId || null,
                  rootRemixAiRunId:
                      detail.video.rootRemixAiRunId || detail.video.root_remix_ai_run_id || null,
                  actionId: detail.video.actionId || null,
              }
            : null

        const model = findModelByActionTag(this.props.aiModels || [], tag)
        const actionDefinitions =
            Array.isArray(this.props.actionDefinitions) && this.props.actionDefinitions.length > 0
                ? this.props.actionDefinitions
                : buildActionDefinitions(this.props.aiModels || [])
        const toolDispatchMetadata = toolActionDispatchMetadataForCommandTag(actionDefinitions, tag)
        const extendActionTag =
            tag === 'extend'
                ? canonicalActionTagFor(detail.extendActionTag) || DEFAULT_VIDEO_MODEL.actionTag
                : null
        const extendModel = extendActionTag
            ? findModelByActionTag(this.props.aiModels || [], extendActionTag)
            : null
        const extendModelDisplayName =
            tag === 'extend'
                ? extendModel?.displayName || extendModel?.humanName || extendActionTag || null
                : null
        const defaultAspectRatio =
            detail.aspectRatio ||
            getDefaultAspectRatioForActionCommand(
                extendModel?.actionTagName || model?.actionTagName || extendActionTag || tag,
            )
        const durationSeconds =
            detail.durationSeconds ??
            extendModel?.defaultDurationSeconds ??
            model?.defaultDurationSeconds ??
            null
        const commandText = `!${tag}`
        const sourceRunId = imageInput?.runId || videoInput?.runId || null
        const rootRemixAiRunId =
            this.rootRemixAiRunIdFromMediaInput(imageInput) ||
            this.rootRemixAiRunIdFromMediaInput(videoInput) ||
            null
        const remixKind = this.derivationKindForMediaCommandTag(tag, { previewTitle })
        const attrs =
            tag === 'extend'
                ? {
                      tag,
                      type: toolDispatchMetadata?.kind || 'model',
                      ...(toolDispatchMetadata || {}),
                      modelType: 'video',
                      defaultDurationSeconds: durationSeconds,
                      defaultAspectRatio,
                      durationSeconds,
                      aspectRatio: defaultAspectRatio || null,
                      extendAiModelId: null,
                      extendActionTag: extendActionTag || DEFAULT_VIDEO_MODEL.actionTag,
                      extendModelDisplayName,
                      smartStitch: detail.smartStitch !== false,
                      hasTextInput: false,
                      textInputValue: null,
                      hasImageInput: false,
                      hasVideoInput: true,
                      videoInputUrl: videoUrl,
                      videoInputLabel: detail.video?.label || 'Video input',
                      videoPreviewImageUrl: detail.video?.previewImageUrl || null,
                      videoInputActionTag: detail.video?.actionTag || null,
                      videoInput,
                      syncWithDraft: false,
                      commandText,
                      previewTitle,
                      remixAiRunId: sourceRunId,
                      rootRemixAiRunId,
                      remixKind,
                      analyticsSource: 'media_remix_menu',
                  }
                : {
                      tag,
                      type: toolDispatchMetadata?.kind || 'model',
                      ...(toolDispatchMetadata || {}),
                      modelType: model?.modelType || inferActionCommandModelType(tag) || null,
                      defaultDurationSeconds: model?.defaultDurationSeconds || null,
                      defaultAspectRatio,
                      durationSeconds: model?.defaultDurationSeconds || null,
                      aspectRatio: defaultAspectRatio || null,
                      hasTextInput: false,
                      textInputValue: null,
                      hasImageInput: !!imageUrl,
                      imageInputUrl: imageUrl || null,
                      imageInputLabel: detail.image?.label || 'Image input',
                      imageInput,
                      hasVideoInput: !!videoUrl,
                      videoInputUrl: videoUrl || null,
                      videoInputLabel: detail.video?.label || 'Video input',
                      videoPreviewImageUrl: detail.video?.previewImageUrl || null,
                      videoInputActionTag: detail.video?.actionTag || null,
                      videoInput,
                      syncWithDraft: false,
                      commandText,
                      previewTitle,
                      remixAiRunId: sourceRunId,
                      rootRemixAiRunId,
                      remixKind,
                      analyticsSource: 'media_remix_menu',
                  }

        const existingDuplicateAction = this.getCurrentDraftActionRequests().find(action => {
            const actionCommandTag = normalizeActionTag(action?.tag)
            if (actionCommandTag !== tag) return false

            if (tag === 'extend') {
                const actionExtendModelId = action?.settings?.extend_ai_model_id || null
                const actionExtendModel = actionExtendModelId
                    ? (this.props.aiModels || []).find(
                          candidate => candidate?.id === actionExtendModelId,
                      )
                    : null
                const actionExtendTag =
                    normalizeActionTag(action?.settings?.extend_action_tag) ||
                    normalizeActionTag(actionExtendModel?.actionTagName)

                return (
                    action?.inputs?.video?.url === videoUrl &&
                    actionExtendTag === extendActionTag &&
                    Number(action?.settings?.duration_seconds || 0) === Number(durationSeconds || 0)
                )
            }

            if (imageUrl) return action?.inputs?.image?.url === imageUrl
            return action?.inputs?.video?.url === videoUrl
        })
        if (existingDuplicateAction) {
            this.settleComposerAfterAiInsert()
            return
        }

        this.insertActionRequestIntoComposer({
            tag,
            commandText,
            attrs,
            buildOptions: { syncDraftInputs: false, focusPromptOnInsert: true },
        })
        this.settleComposerAfterAiInsert()
    }

    getCurrentAiTextInput = () => this.getContent({ excludeActionTokens: true }) || ''

    getAnswerAiTextCandidate = (answer, labelPrefix) => {
        if (!answer) return null

        const text = this.getAnswerAiTextCandidates(answer, labelPrefix)[0]?.text || ''
        if (!text) return null

        return {
            id: `${answer.id || labelPrefix}-text`,
            text,
            label: labelPrefix || answer.user?.name || answer.user_name || null,
        }
    }

    getAnswerAiTextCandidates = (answer, labelPrefix) => {
        if (!answer) return []

        const candidates = []
        const answerLabel = labelPrefix || answer.user?.name || answer.user_name || null
        const answerText =
            getText(answer.delta_json || answer.deltaJson || {}, {
                excludeActionTokens: true,
            }).trim() ||
            (answer.content || '')
                .replace(/(^|[\s\n])![A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*(?=[\s\n.,;:!?]|$)/g, '$1')
                .trim()

        if (answerText) {
            candidates.push({
                id: `${answer.id || labelPrefix}-text`,
                text: answerText,
                label: answerLabel,
            })
        }

        this.getActionRequestsFromSource(answer).forEach((action, index) => {
            const prompt = (action?.inputs?.text?.value || action?.prompt || '').trim()
            if (!prompt) return

            candidates.push({
                id: `${answer.id || labelPrefix}-ai-action-${index}-text`,
                text: prompt,
                label:
                    action?.display_name ||
                    (action?.tag ? `!${normalizeActionTag(action.tag)}` : answerLabel),
            })
        })

        return candidates
    }

    aiActionLabel = (action, fallbackLabel = null) =>
        action?.display_name || (action?.tag ? `!${normalizeActionTag(action.tag)}` : fallbackLabel)

    aiActionMediaInputs = (action, kind) => {
        const inputs = action?.inputs || {}
        const references = inputs.references || {}
        if (kind === 'image') {
            return [
                inputs.image,
                inputs.start_image,
                inputs.end_image,
                ...(references.images || []),
            ].filter(input => !!input?.url)
        }
        if (kind === 'video') {
            return [inputs.video, ...(inputs.videos || []), ...(references.videos || [])].filter(
                input => !!input?.url,
            )
        }
        if (kind === 'audio') {
            return [inputs.audio, ...(references.audios || [])].filter(input => !!input?.url)
        }
        return []
    }

    getThreadAiTextInputs = () => {
        const seen = new Set()
        const draftActionRequests = this.getCurrentDraftActionRequests()
        const draftAiActionTextInputs = draftActionRequests
            .map((action, index) => {
                const prompt = (action?.inputs?.text?.value || action?.prompt || '').trim()
                if (!prompt) return null

                return {
                    id: `draft-ai-action-${action?.client_id || index}-text`,
                    text: prompt,
                    label: this.aiActionLabel(action, 'action request'),
                }
            })
            .filter(Boolean)
        const answers = [
            this.props.quest?.parent
                ? {
                      answer:
                          getCachedAnswer(this.props.quest.parent?.id) || this.props.quest.parent,
                      label: 'Title',
                  }
                : null,
            ...(this.props.quest?.sorted_answers || []).map((answer, index) => ({
                answer: getCachedAnswer(answer?.id) || answer,
                label: `Reply ${index + 1}`,
            })),
        ].filter(Boolean)

        return [
            ...draftAiActionTextInputs,
            ...answers.flatMap(({ answer, label }) =>
                this.getAnswerAiTextCandidates(answer, label),
            ),
        ].filter(input => {
            const key = input.text
            if (!key || seen.has(key)) return false
            seen.add(key)
            return true
        })
    }

    setComposerTextInput = text => {
        const nextText = (text || '').trim()
        const nextDeltaJson = nextText ? { ops: [{ insert: nextText }] } : { ops: [] }

        try {
            if (this.richEditor.current?.setContents) {
                this.richEditor.current.setContents(nextDeltaJson, Quill.sources.SILENT)
            } else {
                const editor = this.getEditor()
                editor?.setContents?.(nextDeltaJson, Quill.sources.SILENT)
            }
        } catch (error) {
            console.warn('Failed to set AI text input:', error)
        }

        this._lastContent = nextText
        this._lastDeltaJson = nextDeltaJson
        this.onChange(nextDeltaJson, nextText)
        if (this.isCompactComposer() && nextText) {
            this.setState({ inlineComposerExpanded: true, isValidContent: true })
        }
    }

    getAnswerAiImageCandidates = (answer, labelPrefix, options = {}) => {
        if (!answer) return []
        const includeAiActionInputs = options.includeAiActionInputs !== false
        const includeAiRunOutputs = options.includeAiRunOutputs !== false

        /** @type {Array<Record<string, any>>} */
        const images = []
        if (answer.local_answer_image_url || answer.answer_image_url) {
            images.push({
                id: answer?.image_urls?.[0]?.attachment_id || answer?.images_v2?.[0]?.attachment_id,
                url: answer.local_answer_image_url || answer.answer_image_url,
                file: answer.answer_image || null,
                // Keep the upload origin when this image becomes a video start frame.
                source: answer.answer_image ? 'answer_image' : null,
                signedId: typeof answer.answer_image === 'string' ? answer.answer_image : null,
                attachmentId:
                    answer?.image_urls?.[0]?.attachment_id || answer?.images_v2?.[0]?.attachment_id,
                blobId: answer?.images_v2?.[0]?.blob_id || null,
                answerId: answer?.id || null,
            })
        }

        const suppressGeneratedAnswerImages = this.hasCompletedAiRunImageOutput(answer)

        if (Array.isArray(answer.image_urls)) {
            answer.image_urls.forEach(image => {
                if (suppressGeneratedAnswerImages && this.isGeneratedAnswerImage(answer, image)) {
                    return
                }
                const url = image?.image_url || image?.url
                if (url) {
                    images.push({
                        id: image?.attachment_id || image?.blob_id || url,
                        url,
                        file: null,
                        attachmentId: image?.attachment_id || null,
                        blobId: image?.blob_id || null,
                        answerId: answer?.id || null,
                    })
                }
            })
        }

        if (Array.isArray(answer.images_v2)) {
            answer.images_v2.forEach(image => {
                if (suppressGeneratedAnswerImages && this.isGeneratedAnswerImage(answer, image)) {
                    return
                }
                const imageUrls = image?.urls || {}
                const url =
                    imageUrls.medium || imageUrls.small || imageUrls.original || imageUrls.large
                if (url) {
                    images.push({
                        id: image?.attachment_id || image?.blob_id || url,
                        url,
                        file: null,
                        attachmentId: image?.attachment_id || null,
                        blobId: image?.blob_id || null,
                        answerId: answer?.id || null,
                    })
                }
            })
        }

        const deltaImages = getImages(answer.delta_json || answer.deltaJson || {}) || []
        deltaImages.forEach(url => images.push({ url, file: null }))
        if (includeAiActionInputs) {
            this.getActionRequestsFromSource(answer).forEach((action, actionIndex) => {
                this.aiActionMediaInputs(action, 'image').forEach((input, inputIndex) => {
                    images.push({
                        url: input.url,
                        file: null,
                        label:
                            input.label ||
                            input.source_label ||
                            this.aiActionLabel(action, labelPrefix) ||
                            null,
                        contentType: input.content_type || input.contentType || null,
                        source: input.source || null,
                        uploadId: input.upload_id || input.uploadId || null,
                        signedId: input.signed_id || input.signedId || null,
                        attachmentId: input.attachment_id || input.attachmentId || null,
                        blobId: input.blob_id || input.blobId || null,
                        answerId: input.answer_id || input.answerId || null,
                        runId: input.run_id || input.runId || null,
                        actionId: input.action_id || input.actionId || null,
                        aiActionInputId: `${action?.client_id || actionIndex}-image-${inputIndex}`,
                    })
                })
            })
        }

        if (includeAiRunOutputs) {
            this.getAnswerAiRunOutputCandidates(answer, 'image', labelPrefix).forEach(image =>
                images.push(image),
            )
        }

        return images
            .filter(image => image.url)
            .map((image, index) => ({
                id:
                    image.aiActionInputId ||
                    image.id ||
                    `${answer.id || labelPrefix}-${index}-${image.url}`,
                url: image.url,
                label: image.label || (labelPrefix === 'Attached image' ? labelPrefix : null),
                file: image.file,
                contentType: image.contentType || null,
                source: image.source || null,
                uploadId: image.uploadId || null,
                signedId: image.signedId || null,
                attachmentId: image.attachmentId || image.attachment_id || null,
                blobId: image.blobId || image.blob_id || null,
                answerId: image.answerId || image.answer_id || null,
                runId: image.aiRunId || image.runId || image.run_id || null,
                actionId: image.actionId || image.action_id || null,
            }))
    }

    getThreadAiImageInputs = () => {
        const seen = new Set()
        const draftActionRequests = this.getCurrentDraftActionRequests()
        const draftAnswer = {
            id: this.props.newAnswer?.id || 'draft-ai-actions',
            action_requests: draftActionRequests,
        }
        const answers = [
            this.props.newAnswer ? { answer: this.props.newAnswer, label: 'Attached image' } : null,
            draftActionRequests.length ? { answer: draftAnswer, label: null } : null,
            this.props.quest?.parent
                ? {
                      answer:
                          getCachedAnswer(this.props.quest.parent?.id) || this.props.quest.parent,
                      label: null,
                  }
                : null,
            ...(this.props.quest?.sorted_answers || []).map((answer, index) => ({
                answer: getCachedAnswer(answer?.id) || answer,
                label: null,
            })),
        ].filter(
            item =>
                item?.answer &&
                (item.answer === this.props.newAnswer ||
                    !this.isArchivedAiInputSource(item.answer)),
        )

        return answers
            .flatMap(({ answer, label }) => this.getAnswerAiImageCandidates(answer, label))
            .filter(image => {
                if (!image.url || seen.has(image.url)) return false
                seen.add(image.url)
                return true
            })
    }

    getCurrentDraftAiImageInput = () => {
        const draftAnswer =
            getCachedAnswer(this.props.newAnswer?.id) || this.props.newAnswer || null
        return (
            this.getAnswerAiImageCandidates(draftAnswer, 'Attached image', {
                includeAiActionInputs: false,
                includeAiRunOutputs: false,
            })?.[0] || null
        )
    }

    getAnswerAiVideoCandidates = (answer, labelPrefix, options = {}) => {
        if (!answer) return []
        const includeAiActionInputs = options.includeAiActionInputs !== false
        const includeAiRunOutputs = options.includeAiRunOutputs !== false

        /** @type {Array<Record<string, any>>} */
        const videos = []
        if (answer.recording_url || answer.mp4_recording_url) {
            videos.push({
                url: answer.recording_url || answer.mp4_recording_url,
                file: answer.recording || answer.mp4_recording || null,
                contentType: answer.recording_content_type || 'video/mp4',
                previewImageUrl: answer.recording_thumbnail_url || null,
                actionTag: null,
                durationSeconds:
                    answer?.recording_info?.duration ??
                    answer?.recording_duration ??
                    answer?.duration ??
                    null,
                attachmentId: answer?.recording_info?.attachment_id || null,
                answerId: answer?.id || null,
            })
        }

        if (Array.isArray(answer.file_urls)) {
            answer.file_urls.forEach(file => {
                const contentType = file?.content_type || file?.contentType || ''
                const url = file?.url || file?.file_url
                const previewImageUrl =
                    file?.thumbnail_url ||
                    file?.thumbnailUrl ||
                    file?.preview_image_url ||
                    file?.previewImageUrl ||
                    null
                if (url && contentType.startsWith('video/')) {
                    videos.push({
                        id: file?.id || url,
                        url,
                        file: null,
                        contentType,
                        previewImageUrl,
                        actionTag: null,
                        durationSeconds:
                            file?.duration_seconds ??
                            file?.durationSeconds ??
                            file?.duration ??
                            null,
                        attachmentId: file?.id || null,
                        blobId: file?.blob_id || file?.blobId || null,
                        answerId: answer?.id || null,
                    })
                }
            })
        }

        if (includeAiRunOutputs) {
            this.getAnswerAiRunOutputCandidates(answer, 'video', labelPrefix).forEach(video =>
                videos.push(video),
            )
        }
        if (includeAiActionInputs) {
            this.getActionRequestsFromSource(answer).forEach((action, actionIndex) => {
                this.aiActionMediaInputs(action, 'video').forEach((input, inputIndex) => {
                    videos.push({
                        url: input.url,
                        file: null,
                        contentType: input.content_type || input.contentType || 'video/mp4',
                        previewImageUrl:
                            input.preview_image_url ||
                            input.previewImageUrl ||
                            input.thumbnail_url ||
                            input.thumbnailUrl ||
                            null,
                        actionTag: input.action_tag || input.actionTag || action?.tag || null,
                        durationSeconds:
                            input.duration_seconds ??
                            input.durationSeconds ??
                            input.duration ??
                            null,
                        label:
                            input.label ||
                            input.source_label ||
                            this.aiActionLabel(action, labelPrefix) ||
                            null,
                        source: input.source || null,
                        uploadId: input.upload_id || input.uploadId || null,
                        signedId: input.signed_id || input.signedId || null,
                        attachmentId: input.attachment_id || input.attachmentId || null,
                        blobId: input.blob_id || input.blobId || null,
                        answerId: input.answer_id || input.answerId || null,
                        runId: input.run_id || input.runId || null,
                        actionId: input.action_id || input.actionId || null,
                        aiActionInputId: `${action?.client_id || actionIndex}-video-${inputIndex}`,
                    })
                })
            })
        }

        return videos
            .filter(video => video.url)
            .map((video, index) => ({
                id:
                    video.aiActionInputId ||
                    video.id ||
                    `${answer.id || labelPrefix}-video-${index}-${video.url}`,
                url: video.url,
                label: video.label || (labelPrefix === 'Attached video' ? labelPrefix : null),
                file: video.file,
                contentType: video.contentType,
                previewImageUrl: video.previewImageUrl || null,
                actionTag: video.actionTag || null,
                durationSeconds:
                    video.durationSeconds ?? video.duration_seconds ?? video.duration ?? null,
                source: video.source || null,
                uploadId: video.uploadId || null,
                signedId: video.signedId || null,
                attachmentId: video.attachmentId || video.attachment_id || null,
                blobId: video.blobId || video.blob_id || null,
                answerId: video.answerId || video.answer_id || null,
                runId: video.aiRunId || video.runId || video.run_id || null,
                actionId: video.actionId || video.action_id || null,
            }))
    }

    getThreadAiVideoInputs = () => {
        const seen = new Set()
        const draftActionRequests = this.getCurrentDraftActionRequests()
        const draftAnswer = {
            id: this.props.newAnswer?.id || 'draft-ai-actions',
            action_requests: draftActionRequests,
        }
        const answers = [
            this.props.newAnswer ? { answer: this.props.newAnswer, label: 'Attached video' } : null,
            draftActionRequests.length ? { answer: draftAnswer, label: null } : null,
            this.props.quest?.parent ? { answer: this.props.quest.parent, label: null } : null,
            ...(this.props.quest?.sorted_answers || []).map((answer, index) => ({
                answer: getCachedAnswer(answer?.id) || answer,
                label: null,
            })),
        ].filter(
            item =>
                item?.answer &&
                (item.answer === this.props.newAnswer ||
                    !this.isArchivedAiInputSource(item.answer)),
        )

        return answers
            .flatMap(({ answer, label }) => this.getAnswerAiVideoCandidates(answer, label))
            .filter(video => {
                if (!video.url || seen.has(video.url)) return false
                seen.add(video.url)
                return true
            })
    }

    getCurrentDraftAiVideoInput = () => {
        const draftAnswer =
            getCachedAnswer(this.props.newAnswer?.id) || this.props.newAnswer || null
        return (
            this.getAnswerAiVideoCandidates(draftAnswer, 'Attached video', {
                includeAiActionInputs: false,
                includeAiRunOutputs: false,
            })?.[0] || null
        )
    }

    getAnswerAiAudioCandidates = (answer, labelPrefix, options = {}) => {
        if (!answer) return []
        const includeAiActionInputs = options.includeAiActionInputs !== false
        const includeAiRunOutputs = options.includeAiRunOutputs !== false

        /** @type {Array<Record<string, any>>} */
        const audios = []
        const answerTimestamp = Date.parse(answer.updated_at || answer.created_at || '') || 0
        const answerAgeLabel = answer.created_at
            ? window.moment?.(answer.created_at)?.fromNow?.()
            : null
        const answerOriginLabel =
            labelPrefix ||
            [answer.user?.name || answer.user_name || null, answerAgeLabel]
                .filter(Boolean)
                .join(' ')
        if (Array.isArray(answer.file_urls)) {
            answer.file_urls.forEach(file => {
                const contentType = file?.content_type || file?.contentType || ''
                const url = file?.url || file?.file_url
                if (url && contentType.startsWith('audio/')) {
                    const uploadRef = this.aiInputUploadManager?.getRefForBlobUrl(url)
                    audios.push({
                        url,
                        file: null,
                        contentType,
                        label: file?.name || labelPrefix || null,
                        metaLabel: answerOriginLabel || null,
                        sortTimestamp: answerTimestamp,
                        source: uploadRef?.source || null,
                        uploadId: uploadRef?.upload_id || null,
                    })
                }
            })
        }

        if (includeAiRunOutputs) {
            this.getAnswerAiRunOutputCandidates(answer, 'audio', labelPrefix).forEach(audio =>
                audios.push(audio),
            )
        }
        if (includeAiActionInputs) {
            this.getActionRequestsFromSource(answer).forEach((action, actionIndex) => {
                this.aiActionMediaInputs(action, 'audio').forEach((input, inputIndex) => {
                    audios.push({
                        url: input.url,
                        file: null,
                        contentType: input.content_type || input.contentType || 'audio/mpeg',
                        label:
                            input.label ||
                            input.source_label ||
                            this.aiActionLabel(action, labelPrefix) ||
                            null,
                        metaLabel: answerOriginLabel || null,
                        sortTimestamp: answerTimestamp,
                        source: input.source || null,
                        uploadId: input.upload_id || input.uploadId || null,
                        signedId: input.signed_id || input.signedId || null,
                        actionTag: action?.tag || null,
                        aiActionInputId: `${action?.client_id || actionIndex}-audio-${inputIndex}`,
                    })
                })
            })
        }

        return audios
            .filter(audio => audio.url)
            .map((audio, index) => ({
                id:
                    audio.aiActionInputId ||
                    `${answer.id || labelPrefix}-audio-${index}-${audio.url}`,
                url: audio.url,
                label: audio.label,
                metaLabel: audio.metaLabel || answerOriginLabel || null,
                sortTimestamp: audio.sortTimestamp || answerTimestamp || null,
                file: audio.file,
                contentType: audio.contentType,
                source: audio.source || null,
                uploadId: audio.uploadId || null,
                signedId: audio.signedId || null,
                aiRunId: audio.aiRunId || null,
                actionTag: audio.actionTag || null,
            }))
    }

    getThreadAiAudioInputs = () => {
        const seen = new Set()
        const draftActionRequests = this.getCurrentDraftActionRequests()
        const draftAnswer = {
            id: this.props.newAnswer?.id || 'draft-ai-actions',
            action_requests: draftActionRequests,
        }
        const answers = [
            this.props.newAnswer ? { answer: this.props.newAnswer, label: 'Attached audio' } : null,
            draftActionRequests.length ? { answer: draftAnswer, label: null } : null,
            this.props.quest?.parent ? { answer: this.props.quest.parent, label: null } : null,
            ...(this.props.quest?.sorted_answers || []).map((answer, index) => ({
                answer: getCachedAnswer(answer?.id) || answer,
                label: null,
            })),
        ].filter(
            item =>
                item?.answer &&
                (item.answer === this.props.newAnswer ||
                    !this.isArchivedAiInputSource(item.answer)),
        )

        return answers
            .flatMap(({ answer, label }) => this.getAnswerAiAudioCandidates(answer, label))
            .filter(audio => {
                if (!audio.url || seen.has(audio.url)) return false
                seen.add(audio.url)
                return true
            })
            .sort((left, right) => (right.sortTimestamp || 0) - (left.sortTimestamp || 0))
    }

    getCurrentDraftAiAudioInput = () => {
        const draftAnswer =
            getCachedAnswer(this.props.newAnswer?.id) || this.props.newAnswer || null
        return (
            this.getAnswerAiAudioCandidates(draftAnswer, 'Attached audio', {
                includeAiActionInputs: false,
                includeAiRunOutputs: false,
            })?.[0] || null
        )
    }

    getThreadTreeAiMediaInputs = async () => {
        const questId = this.props.quest?.id
        if (!questId) return { images: [], videos: [], audios: [] }

        const cached = this.state.aiInputMediaTree
        if (cached?.questId === questId) {
            return {
                texts: cached.texts || [],
                images: cached.images || [],
                videos: cached.videos || [],
                audios: cached.audios || [],
            }
        }

        this.setState({ aiInputMediaTreeLoading: true })
        try {
            const response = await api.getQuestAiInputMediaTree(questId)
            const answers = Array.isArray(response?.answers) ? response.answers : []
            const currentThreadAnswerIds = new Set(
                [
                    this.props.newAnswer?.id,
                    this.props.quest?.parent?.id,
                    ...(this.props.quest?.sorted_answers || []).map(answer => answer?.id),
                ]
                    .filter(Boolean)
                    .map(String),
            )
            const images = []
            const videos = []
            const audios = []
            const texts = []

            answers.forEach(answer => {
                if (!answer?.id || currentThreadAnswerIds.has(String(answer.id))) return
                if (this.isArchivedAiInputSource(answer)) return
                texts.push(...this.getAnswerAiTextCandidates(answer, null))
                images.push(...this.getAnswerAiImageCandidates(answer, null))
                videos.push(...this.getAnswerAiVideoCandidates(answer, null))
                audios.push(...this.getAnswerAiAudioCandidates(answer, null))
            })

            const seenTexts = new Set()
            const nextTree = {
                questId,
                texts: texts.filter(input => {
                    const key = input.text
                    if (!key || seenTexts.has(key)) return false
                    seenTexts.add(key)
                    return true
                }),
                images,
                videos,
                audios,
            }
            this.setState({ aiInputMediaTree: nextTree, aiInputMediaTreeLoading: false })
            return nextTree
        } catch (error) {
            console.warn('Failed to load AI input media tree:', error)
            this.setState({ aiInputMediaTreeLoading: false })
            return { texts: [], images: [], videos: [], audios: [] }
        }
    }

    attachAiImageInput = event => {
        const file = event?.target?.files?.[0] || null
        if (!file) return null

        const url = URL.createObjectURL(file)
        const uploadRef = this.aiInputUploadManager.addFile(file, 'image', { blobUrl: url })
        if (event?.target?.value != null) {
            try {
                event.target.value = ''
            } catch (e) {}
        }

        return {
            id: `attached-${uploadRef.upload_id}`,
            url: uploadRef.url,
            label: uploadRef.label || file.name || 'Attached image',
            file: null,
            contentType: uploadRef.content_type || file.type || 'image/png',
            source: uploadRef.source,
            uploadId: uploadRef.upload_id,
            signedId: uploadRef.signed_id || null,
        }
    }

    attachAiVideoInput = event => {
        const file = event?.target?.files?.[0] || null
        if (!file) return null

        const url = URL.createObjectURL(file)
        const uploadRef = this.aiInputUploadManager.addFile(file, 'video', { blobUrl: url })
        if (event?.target?.value != null) {
            try {
                event.target.value = ''
            } catch (e) {}
        }

        return {
            id: `attached-video-${uploadRef.upload_id}`,
            url: uploadRef.url,
            label: uploadRef.label || file.name || 'Attached video',
            file: null,
            contentType: uploadRef.content_type || file.type || 'video/mp4',
            source: uploadRef.source,
            uploadId: uploadRef.upload_id,
            signedId: uploadRef.signed_id || null,
            previewImageUrl: null,
        }
    }

    attachAiAudioInput = event => {
        const file = event?.target?.files?.[0] || null
        if (!file) return null

        const uploadRef = this.aiInputUploadManager.addFile(file, 'audio')
        if (event?.target?.value != null) {
            try {
                event.target.value = ''
            } catch (e) {}
        }

        return {
            id: `attached-audio-${uploadRef.upload_id}`,
            url: uploadRef.url,
            label: uploadRef.label || 'Attached audio',
            file: null,
            contentType: uploadRef.content_type || 'audio/mpeg',
            source: uploadRef.source,
            uploadId: uploadRef.upload_id,
            signedId: uploadRef.signed_id || null,
        }
    }

    setComposerImageInput = image => {
        if (!this.props.newAnswer?.id) return

        if (!image) {
            const nextAnswer = {
                ...(this.props.newAnswer || {}),
                answer_image: null,
                answer_image_url: null,
                local_answer_image_url: null,
            }
            cache.updateCachedAnswer(this.props.newAnswer.id, {
                answer_image: null,
                answer_image_url: null,
                local_answer_image_url: null,
            })
            this.setState(
                {
                    isValidContent: answerModel.hasContent(this.getNewAnswer(null, nextAnswer)),
                },
                this.scheduleDraftPersistence,
            )
            return
        }

        const nextAnswer = {
            ...(this.props.newAnswer || {}),
            answer_image: image.file || null,
            answer_image_url: image.url,
            local_answer_image_url: image.url,
        }
        cache.updateCachedAnswer(this.props.newAnswer.id, {
            answer_image: image.file || null,
            answer_image_url: image.url,
            local_answer_image_url: image.url,
        })
        this.setState(
            {
                isValidContent: answerModel.hasContent(this.getNewAnswer(null, nextAnswer)),
            },
            this.scheduleDraftPersistence,
        )
    }

    setComposerAudioInput = audio => {
        if (!this.props.newAnswer?.id) return

        if (!audio) {
            const nextAnswer = {
                ...(this.props.newAnswer || {}),
                files: [],
                file_urls: [],
            }
            cache.updateCachedAnswer(this.props.newAnswer.id, {
                files: [],
                file_urls: [],
            })
            this.setState(
                {
                    isValidContent: answerModel.hasContent(this.getNewAnswer(null, nextAnswer)),
                },
                this.scheduleDraftPersistence,
            )
            return
        }

        const fileUrl = {
            url: audio.url,
            file_url: audio.url,
            name: audio.label || 'Selected audio',
            content_type: audio.contentType || 'audio/mpeg',
        }
        cache.updateCachedAnswer(this.props.newAnswer.id, {
            files: audio.file ? [audio.file] : [],
            file_urls: [fileUrl],
        })
        const nextAnswer = {
            ...(this.props.newAnswer || {}),
            files: audio.file ? [audio.file] : [],
            file_urls: [fileUrl],
        }
        this.setState(
            {
                isValidContent: answerModel.hasContent(this.getNewAnswer(null, nextAnswer)),
            },
            this.scheduleDraftPersistence,
        )
    }

    setComposerVideoInput = video => {
        if (!this.props.newAnswer?.id) return

        if (!video) {
            const nextAnswer = {
                ...(this.props.newAnswer || {}),
                recording: null,
                recording_url: null,
                mp4_recording: null,
                mp4_recording_url: null,
            }
            cache.updateCachedAnswer(this.props.newAnswer.id, {
                recording: null,
                recording_url: null,
                mp4_recording: null,
                mp4_recording_url: null,
            })
            this.setState(
                {
                    isValidContent: answerModel.hasContent(this.getNewAnswer(null, nextAnswer)),
                },
                this.scheduleDraftPersistence,
            )
            return
        }

        const nextAnswer = {
            ...(this.props.newAnswer || {}),
            recording: video.file || this.props.newAnswer?.recording || null,
            recording_url: video.url,
            recording_content_type: video.contentType || 'video/mp4',
        }
        cache.updateCachedAnswer(this.props.newAnswer.id, {
            recording: nextAnswer.recording,
            recording_url: video.url,
            recording_content_type: nextAnswer.recording_content_type,
        })
        this.setState(
            {
                isValidContent: answerModel.hasContent(this.getNewAnswer(null, nextAnswer)),
            },
            this.scheduleDraftPersistence,
        )
    }

    clearInlineTitleComposerImmediately = () => {
        try {
            if (this.richEditor.current?.clearContentProgrammatically) {
                this.richEditor.current.clearContentProgrammatically()
            }
        } catch (e) {}

        this.rememberDraftActionRequests([])
        this.setState({
            isValidContent: false,
            waitingForEmbeds: false,
            linkPreviews: [],
            streamEmbeds: [],
            streamEmbedsLoading: false,
            hiddenPreviewUrls: [],
            suppressedPreviewUrls: [],
            draftActionRequests: [],
        })
    }

    onMouseEnter = () => {
        this.setState({ hover: true })
    }

    onMouseLeave = () => {
        this.setState({ hover: false })
    }

    // A press on one of the composer's own controls is about to move focus;
    // the blur it causes must not fold the composer. Armed with a deadline
    // rather than a latch: a control whose menu keeps focus where it was (the
    // model picker cancels its mousedown) would otherwise leave the arm set
    // for whatever blur came next, and the composer would stay open through
    // the reader's first tap away.
    _skipInlineCollapseOnBlurUntil = 0

    // The phone pill opening out of its folded line, or folding back, as a
    // motion: the box runs from the height it had to the height it has, the
    // rows inside clipped to it. The pill's host is anchored to the panel foot,
    // so the box grows upward, the field rising with its top edge and the
    // toolbar unveiling from the bottom; folding is the reverse. The row stays
    // pinned to the moving top edge through the fold (align-items) rather than
    // re-centring in the shrinking box.
    compactComposerResizeAnimation = null
    // The root's own inline overflow and alignment, from before any resize
    // animation borrowed them; restored when the last one settles.
    compactComposerResizeRestore = null

    animateCompactComposerResize = fromHeight => {
        const root = this.containerRef?.current
        if (!root || typeof root.animate !== 'function') return
        if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return
        // A resize still running holds the box at its own height. It has to
        // let go before the box is measured, or the new motion aims for
        // wherever the old one happened to be — a fold cut short by a reply
        // press opened to a size the pill then snapped away from. The
        // snapshot's height was taken with it still applied, which is right:
        // that is where the box is, and where the new motion starts.
        if (this.compactComposerResizeAnimation) {
            this.compactComposerResizeAnimation.oncancel = null
            this.compactComposerResizeAnimation.cancel()
            this.compactComposerResizeAnimation = null
        }
        const toHeight = root.getBoundingClientRect().height
        if (!Number.isFinite(fromHeight) || Math.abs(toHeight - fromHeight) < 1) {
            this.restoreCompactComposerResizeStyles(root)
            return
        }

        if (!this.compactComposerResizeRestore) {
            this.compactComposerResizeRestore = {
                overflow: root.style.overflow,
                alignItems: root.style.alignItems,
            }
        }
        root.style.overflow = 'hidden'
        root.style.alignItems = 'flex-start'
        const animation = root.animate(
            [{ height: `${fromHeight}px` }, { height: `${toHeight}px` }],
            { duration: COMPACT_COMPOSER_RESIZE_MS, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
        )
        this.compactComposerResizeAnimation = animation
        const settle = () => {
            if (this.compactComposerResizeAnimation !== animation) return
            this.compactComposerResizeAnimation = null
            this.restoreCompactComposerResizeStyles(root)
            if (this._reseatCaretOnSettle) {
                this._reseatCaretOnSettle = false
                requestAnimationFrame(this.reseatCaret)
            }
        }
        animation.onfinish = settle
        animation.oncancel = settle
    }

    restoreCompactComposerResizeStyles = root => {
        const restore = this.compactComposerResizeRestore
        if (!restore) return
        this.compactComposerResizeRestore = null
        root.style.overflow = restore.overflow
        root.style.alignItems = restore.alignItems
    }

    // The keyboard being dismissed is the reader's "done" with the pill: it
    // folds back to its line, as it does when focus and the pointer leave it.
    // The blur check alone could not fold it here — the last press was inside
    // the pill (the tap that opened it), which that check reads as the reader
    // staying — so this is the pill stuck open with the keyboard gone, whose
    // next tap on the field lands wherever the field is not. A control press
    // just before (attach, the palette) keeps its exemption: the picker or menu
    // it opens is what took the keyboard, and the choice made there decides.
    foldCompactComposerForKeyboardDismiss = () => {
        const skipRemainingMs = this._skipInlineCollapseOnBlurUntil - Date.now()
        const verdict = resolveKeyboardDismissFold({
            compact: this.isCompactComposer(),
            expanded: this.state.inlineComposerExpanded,
            menuOpen: this.state.menuOpen,
            hasAttachment: this.hasPickedComposerMedia(),
            skipRemainingMs,
        })
        if (verdict === 'retry') {
            // The exemption is a window, not a verdict: ask again once it has
            // closed, if the keyboard is still gone.
            if (this.keyboardDismissFoldRetryTimer) clearTimeout(this.keyboardDismissFoldRetryTimer)
            this.keyboardDismissFoldRetryTimer = setTimeout(() => {
                this.keyboardDismissFoldRetryTimer = null
                if (this._isUnmounted || this.props.panelContext?.keyboardOpen) return
                this.foldCompactComposerForKeyboardDismiss()
            }, skipRemainingMs + 16)
            return
        }
        if (verdict !== 'fold') return
        this._replySubmitDiagnostics.crumb('composer_fold')
        this._replySubmitDiagnostics.closed({
            how: 'fold',
            composer: this.diagnosticComposerKind(),
            has_draft: !!this.state.isValidContent,
        })
        if (this.inlineComposerCollapseCheckTimer) {
            clearTimeout(this.inlineComposerCollapseCheckTimer)
            this.inlineComposerCollapseCheckTimer = null
        }
        this._lastInlineBoundaryPointerDownInside = false
        this._inlineCollapsePendingAfterMenuClose = false
        this.setState({ inlineComposerExpanded: false })
    }

    keyboardDismissFoldRetryTimer = null

    armInlineCollapseSkip = () => {
        this._skipInlineCollapseOnBlurUntil = Date.now() + INLINE_COLLAPSE_SKIP_WINDOW_MS
    }

    consumeInlineCollapseSkip = () => {
        const armed = this._skipInlineCollapseOnBlurUntil > Date.now()
        this._skipInlineCollapseOnBlurUntil = 0
        return armed
    }

    _inlineCollapsePendingAfterMenuClose = false
    _inlineComposerMenuTransferPending = false
    inlineComposerCollapseCheckTimer = null
    inlineComposerMenuOpenSkipClearTimer = null

    scheduleInlineComposerCollapseCheck = ({ menuDismissal = false } = {}) => {
        if (this.inlineComposerCollapseCheckTimer) {
            clearTimeout(this.inlineComposerCollapseCheckTimer)
        }
        this.inlineComposerCollapseCheckTimer = setTimeout(() => {
            this.inlineComposerCollapseCheckTimer = null
            if (!this.isCompactComposer() || this.state.menuOpen) return

            this._inlineCollapsePendingAfterMenuClose = false
            if (this.consumeInlineCollapseSkip()) {
                this._lastInlineBoundaryPointerDownInside = false
                return
            }

            const root = this.getInlineCollapseBoundaryRoot()
            const activeEl = document.activeElement
            const focusStillInside = !!(root && activeEl && root.contains(activeEl))
            const pointerStayedInsideBoundary = this._lastInlineBoundaryPointerDownInside
            this._lastInlineBoundaryPointerDownInside = false
            // A portaled menu can close from a non-focusable backdrop while the
            // editor still owns focus. Its outside pointer is enough to fold;
            // ordinary blur still requires both focus and pointer to leave.
            const interactionLeftBoundary = menuDismissal
                ? !focusStillInside || !pointerStayedInsideBoundary
                : !focusStillInside && !pointerStayedInsideBoundary
            if (interactionLeftBoundary) {
                if (this.shouldKeepInlineComposerExpandedAfterBlur()) return
                // A phone's pill with the keyboard up folds when the keyboard
                // goes (foldCompactComposerForKeyboardDismiss), not on the
                // blur that sends it: the blur comes first, and the foot only
                // drops once the keyboard's own state follows — folding here
                // left a one-line pill hanging where the keyboard's edge had
                // been until the drop. The two are one motion now.
                if (this.props.collapsibleComposer && this.props.panelContext?.keyboardOpen) return
                this.setState({ inlineComposerExpanded: false })
            }
        }, 0)
    }

    onComposerControlPointerDown = event => {
        // Portaled menus bubble through their React owner even though their DOM
        // target is outside the composer. Their selection handlers decide when
        // a choice should keep the composer open.
        const root = this.containerRef?.current
        if (event?.target instanceof Element && root && !root.contains(event.target)) return
        this.armInlineCollapseSkip()

        if (
            shouldPreventComposerControlFocusTransfer({
                isAndroid,
                isIOS,
                eventType: event?.type,
            })
        ) {
            event.preventDefault()
        }
    }

    onPostPointerDown = event => {
        this.onComposerControlPointerDown(event)
        this._replySubmitDiagnostics.press(this.replySubmitPressSnapshot(event))
    }

    diagnosticComposerKind = () => (this.props.closeModal ? 'modal' : 'inline')

    // Any touch inside the composer, so a close right after it can be told
    // apart from a deliberate dismissal (see replySubmitDiagnostics.closed).
    onReplyDiagnosticTouch = event => {
        if (event?.pointerType && event.pointerType !== 'touch') return
        const root = this.containerRef?.current
        const active = typeof document === 'undefined' ? null : document.activeElement
        this._replySubmitDiagnostics.touch({
            target: describeTarget(event?.target),
            composer: this.diagnosticComposerKind(),
            editor_focused: !!(root && active && root.contains(active)),
            keyboard_open: !!this.props.panelContext?.keyboardOpen,
        })
    }

    // What decides whether this tap can post, without any draft content.
    replySubmitPressSnapshot = event => {
        const root = this.containerRef?.current
        const active = typeof document === 'undefined' ? null : document.activeElement
        return {
            input: event?.type,
            can_submit: !event?.currentTarget?.disabled,
            composer: this.diagnosticComposerKind(),
            compact: this.isCompactComposer(),
            expanded: !!this.state.inlineComposerExpanded,
            valid: !!this.state.isValidContent,
            posting: !!this.state.isPosting,
            waiting_embeds: !!this.state.waitingForEmbeds,
            embeds_loading: !!this.state.streamEmbedsLoading,
            uploading: !!this.state.isUploading || !!this.uploadManager?.hasActiveUpload?.(),
            editor_focused: !!(root && active && root.contains(active)),
            keyboard_open: !!this.props.panelContext?.keyboardOpen,
            docked: !!this.props.panelContext?.bottomEditorDocked,
        }
    }

    onAiControlPointerDown = event => {
        this.onComposerControlPointerDown(event)
    }

    onAttachmentPointerDown = event => {
        this.onComposerControlPointerDown(event)
    }

    // The models menu (the magic wand) is opened from the palette's "Models"
    // row rather than from a trigger of its own.
    aiWandRef = React.createRef()

    openAiModelsMenu = () => {
        const menu = this.aiWandRef.current
        if (!menu?.open) return
        // The palette closes as the Models dialog opens. Keep the pending
        // outside-focus check for the dialog's eventual close instead of
        // treating this handoff as a dismissal.
        this._inlineComposerMenuTransferPending = true
        this._aiModelsKeyboardWasUp = isMobile && this.editorHasFocus()
        menu.open()
    }

    _aiModelsMenuOpen = false
    _aiModelsKeyboardWasUp = false

    // Closing the Models dialog returns to writing. If the keyboard was up when it
    // opened and the dialog took the focus (a field or card inside it), the editor
    // gets it back, so the keyboard and the open pill return instead of the pill
    // folding under a dropped keyboard.
    onAiModelsMenuStateChange = isOpen => {
        const closing = !isOpen && this._aiModelsMenuOpen
        this._aiModelsMenuOpen = isOpen
        this.onAgentMenuStateChange(isOpen)
        if (!closing) return
        const restore = this._aiModelsKeyboardWasUp
        this._aiModelsKeyboardWasUp = false
        if (!restore || this._isUnmounted || this.editorHasFocus()) return
        if (this.isCompactComposer()) this.armInlineCollapseSkip()
        this.focusEditor({ defer: false })
    }

    imgInputRef = React.createRef()
    imgPreviewRef = React.createRef()
    videoInputRef = React.createRef()
    videoPreviewRef = React.createRef()
    fileInputRef = React.createRef()

    fileHandler = evOrFile => {
        const composeAnswerId = this.getComposeAnswerId()
        if (!composeAnswerId) return
        evOrFile?.preventDefault?.()
        evOrFile?.stopPropagation?.()
        const pickedFile = evOrFile?.target?.files?.[0] || evOrFile
        const result = this.uploadManager.handleFileSelection(evOrFile)
        result?.acceptedImages?.forEach(file => this.persistLocalDraftImageFile(file))
        if (result?.rejectedImageCount) {
            this.showNotice(`You can attach up to ${MAX_COMPOSER_IMAGES} images.`, {
                variant: 'error',
                durationMs: 4000,
            })
        }
        if (pickedFile && (pickedFile.name || pickedFile.type)) {
            const updatedAnswer = getCachedAnswer(composeAnswerId) || this.props.newAnswer
            if (pickedFile.type?.startsWith?.('audio/')) {
                const localAudio = (updatedAnswer?.file_urls || []).find(file => {
                    const contentType = file?.content_type || file?.contentType || ''
                    return (
                        file?.url?.startsWith?.('blob:') &&
                        contentType.startsWith('audio/') &&
                        (!pickedFile.name || file?.name === pickedFile.name)
                    )
                })
                if (localAudio?.url) {
                    this.aiInputUploadManager.addFile(pickedFile, 'audio', {
                        blobUrl: localAudio.url,
                        signedIdPromise: result?.fileUploadPromise,
                        reportError: false,
                    })
                }
            }
            this.syncNbActionRequestsWithComposerMedia()
            // The picker took the keyboard, and with it the pill may have
            // folded; the file it hands back opens the pill out so it shows.
            this.setState({
                isValidContent: true,
                ...(this.isCompactComposer() && { inlineComposerExpanded: true }),
            })
            this.scheduleDraftPersistence()
        }
        if (evOrFile?.target?.value != null) {
            try {
                evOrFile.target.value = ''
            } catch (e) {}
        }
    }

    removeComposerImage = index => {
        const composeAnswerId = this.getComposeAnswerId()
        if (!composeAnswerId) return

        const currentAnswer = getCachedAnswer(composeAnswerId) || this.props.newAnswer || {}
        const imageUrl = composerImagePreviewUrls(currentAnswer)[index]
        if (imageUrl?.startsWith?.('blob:')) {
            try {
                URL.revokeObjectURL(imageUrl)
            } catch (e) {}
        }
        const patch = removeComposerImage(currentAnswer, index)
        cache.updateCachedAnswer(composeAnswerId, patch)
        this.setState(
            {
                isValidContent: answerModel.hasContent(
                    this.getNewAnswer(null, { ...currentAnswer, ...patch }),
                ),
            },
            this.scheduleDraftPersistence,
        )
    }

    startVideoUploadIfPending = async () => {
        await this.uploadManager.startPendingVideoUpload(this.getComposeAnswerId())
        this.scheduleDraftPersistence()
    }

    removeFile = () => {
        if (this.fileInputRef && this.fileInputRef.current) this.fileInputRef.current.value = ''
        cache.updateCachedAnswer(this.props.newAnswer?.id, { files: [], file_urls: [] })
    }

    setEmbeds = (embeds, diagnostic) => {
        const nextEmbeds = this.filterEmbedsForCurrentVisibility(embeds)
        const embedIds = (nextEmbeds || []).map(e => e.id)
        const currentEmbedIds = (this.props.newAnswer?.embeds || []).map(e => e.id)
        diagnostic?.mark('cache_update_begin', {
            target_relation: this.props.quest?.parent?.id
                ? embedIds.includes(this.props.quest.parent.id)
                    ? 'thread_parent'
                    : 'other'
                : 'unknown',
        })
        if (!isEqual(currentEmbedIds, embedIds)) {
            cache.updateCachedAnswer(this.props.newAnswer?.id, { embeds: nextEmbeds })
        }
        diagnostic?.mark('cache_update_end')
        // Clear loading state when embeds are set
        this.setState({ embedsLoading: false, embedLoadingPromise: null }, () => {
            // This confirms React committed the composer with these embeds, not browser paint.
            if (
                diagnostic &&
                embedIds.length &&
                isEqual(
                    (this.props.newAnswer?.embeds || []).map(e => e.id),
                    embedIds,
                )
            ) {
                diagnostic.mark('embed_commit')
            }
        })
    }

    setEmbedsLoading = (loading, promise = null) => {
        this.setState({ embedsLoading: loading, embedLoadingPromise: promise })
    }

    ensureEmbedsForLinks = async knovEmbedLinks => {
        return answerModel.ensureEmbedsForLinks({
            knovEmbedLinks,
            filterEmbeds: embeds =>
                filterEmbedsForKnovEmbedLinks(
                    embeds,
                    knovEmbedLinks,
                    embed => getCachedAnswer(embed?.id) || embed,
                ),
            onEmbedsResolved: nextEmbeds => {
                cache.updateCachedAnswer(this.props.newAnswer?.id, { embeds: nextEmbeds })
            },
            onError: () => {
                this.showNotice('Unable to create embed. Please try again once it loads.', {
                    variant: 'error',
                })
            },
            setWaitingForEmbeds: waiting => this.setState({ waitingForEmbeds: waiting }),
        })
    }

    getVisibleStreamEmbedConfigs = (deltaJson = this.getDeltaJson() || {}) => {
        const extracted = extractUrls(deltaJson || {})
        const hiddenPreviewUrls = answerModel.mergeHiddenPreviewUrls(
            this.getHiddenPreviewUrls(),
            this.getPersistedSuppressedPreviewUrls(),
        )
        const visibleStreamLinks = filterSuppressedPreviewUrls(
            extracted.streamEmbedLinks || [],
            hiddenPreviewUrls,
        )
        return extractStreamEmbedConfigs(visibleStreamLinks)
    }

    hasUnresolvedStreamEmbeds = (deltaJson = this.getDeltaJson() || {}) => {
        const visibleStreamEmbeds = this.getVisibleStreamEmbedConfigs(deltaJson)
        if (!visibleStreamEmbeds.length) return false

        const currentStreamEmbedIds = new Set(
            (this.state.streamEmbeds || []).map(embed => String(embed?.id || '')),
        )
        return visibleStreamEmbeds.some(embed => !currentStreamEmbedIds.has(String(embed.id)))
    }

    setStreamEmbeds = streamEmbeds => {
        this.setState({ streamEmbeds: streamEmbeds || [], streamEmbedsLoading: false })
    }

    setStreamEmbedsLoading = loading => {
        const nextLoading = !!loading
        if (this.state.streamEmbedsLoading !== nextLoading) {
            this.setState({ streamEmbedsLoading: nextLoading })
        }
    }

    setLinkPreviews = linkPreviews => {
        const lp = linkPreviews || []
        try {
            // If any ordinal link is present and no preview yet, proactively disable Post
            const hasOrdinal = lp.some(href => {
                try {
                    const u = new URL(href)
                    const host = u.hostname.replace(/^www\./, '')
                    if (host === 'ordfs.network' || host === 'ordinals.gorillapool.io') return true
                    if (host === '3dordi.io') return !!extractOrdinalOutpointFromUrl(href)
                    return false
                } catch (e) {
                    return false
                }
            })
            if (hasOrdinal && !this.state.waitingForEmbeds) {
                this.setState({ waitingForEmbeds: true })
                // Safety timeout to re-enable if worker doesn't respond
                try {
                    if (this._urlPreviewTimeout) clearTimeout(this._urlPreviewTimeout)
                    this._urlPreviewTimeout = setTimeout(() => {
                        if (this.state.waitingForEmbeds) this.setState({ waitingForEmbeds: false })
                    }, 20000)
                } catch (e) {}
            }
        } catch (e) {}
        this.setState({ linkPreviews: lp })
        this.reconcileSuppressedPreviewUrls()
    }

    getHiddenPreviewUrls = () => this.state.hiddenPreviewUrls || []

    getPersistedSuppressedPreviewUrls = () => {
        if (this.props.isTitle) return this.state.suppressedPreviewUrls || []
        return this.props.newAnswer?.suppressed_preview_urls || []
    }

    getPreviewUrls = (deltaJson = this.getDeltaJson() || {}) => {
        const extracted = extractUrls(deltaJson || {})
        return [
            ...(extracted.nonKnovLinks || []),
            ...(extracted.knovEmbedLinks || []),
            ...(extracted.streamEmbedLinks || []),
        ].filter(Boolean)
    }

    getVisibleKnovEmbedTargets = (deltaJson = this.getDeltaJson() || {}) => {
        const extracted = extractUrls(deltaJson || {})
        return getKnovEmbedTargets(
            filterSuppressedPreviewUrls(
                extracted.knovEmbedLinks || [],
                this.getHiddenPreviewUrls(),
            ),
        )
    }

    filterEmbedsForCurrentVisibility = (embeds = [], deltaJson = this.getDeltaJson() || {}) => {
        const visibleTargets = this.getVisibleKnovEmbedTargets(deltaJson)
        return (embeds || []).filter(embed => {
            const resolved = getCachedAnswer(embed?.id) || embed
            return visibleTargets.some(target => answerMatchesKnovEmbedTarget(resolved, target))
        })
    }

    setHiddenPreviewUrls = nextUrls => {
        this.setState({ hiddenPreviewUrls: [...new Set((nextUrls || []).filter(Boolean))] })
    }

    setPersistedSuppressedPreviewUrls = nextUrls => {
        const normalized = [...new Set((nextUrls || []).filter(Boolean))]
        if (this.props.isTitle) {
            this.setState({ suppressedPreviewUrls: normalized })
        } else if (this.props.newAnswer?.id) {
            cache.updateCachedAnswer(this.props.newAnswer.id, {
                suppressed_preview_urls: normalized,
            })
        }
    }

    reconcileSuppressedPreviewUrls = (deltaJson = this.getDeltaJson() || {}) => {
        const currentUrls = this.getPreviewUrls(deltaJson)
        const previousUrls = this._lastPreviewUrls || []
        const pendingProgrammaticUrls = this._pendingProgrammaticSuppressedPreviewUrls || []
        const reAddedUrls = currentUrls.filter(
            url => !previousUrls.some(previousUrl => areSamePreviewUrl(previousUrl, url)),
        )
        const preservedReAddedUrls = pendingProgrammaticUrls.filter(programmaticUrl =>
            reAddedUrls.some(url => areSamePreviewUrl(url, programmaticUrl)),
        )
        const effectiveReAddedUrls = reAddedUrls.filter(
            url =>
                !preservedReAddedUrls.some(programmaticUrl =>
                    areSamePreviewUrl(programmaticUrl, url),
                ),
        )
        const currentHiddenUrls = this.getHiddenPreviewUrls()
        const nextHiddenUrls = currentHiddenUrls.filter(
            url => !effectiveReAddedUrls.some(reAddedUrl => areSamePreviewUrl(reAddedUrl, url)),
        )
        if (nextHiddenUrls.length !== currentHiddenUrls.length) {
            this.setHiddenPreviewUrls(nextHiddenUrls)
        }
        const currentPersistedUrls = this.getPersistedSuppressedPreviewUrls()
        const nextPersistedUrls = currentPersistedUrls.filter(
            url => !effectiveReAddedUrls.some(reAddedUrl => areSamePreviewUrl(reAddedUrl, url)),
        )
        if (nextPersistedUrls.length !== currentPersistedUrls.length) {
            this.setPersistedSuppressedPreviewUrls(nextPersistedUrls)
        }
        this._pendingProgrammaticSuppressedPreviewUrls = pendingProgrammaticUrls.filter(
            url =>
                !preservedReAddedUrls.some(programmaticUrl =>
                    areSamePreviewUrl(programmaticUrl, url),
                ),
        )
        this._lastPreviewUrls = currentUrls
        return nextPersistedUrls
    }

    hidePreviewUrl = targetUrl => {
        if (!targetUrl) return
        const hiddenEmbedTarget = extractKnovEmbedTarget(targetUrl)
        const hiddenStreamId = extractStreamEmbedConfig(targetUrl)?.id
        const nextHiddenUrls = addSuppressedPreviewUrl(this.getHiddenPreviewUrls(), targetUrl)
        const nextPersistedUrls = addSuppressedPreviewUrl(
            this.getPersistedSuppressedPreviewUrls(),
            targetUrl,
        )
        this.setHiddenPreviewUrls(nextHiddenUrls)
        this.setPersistedSuppressedPreviewUrls(nextPersistedUrls)
        if (hiddenEmbedTarget && this.props.newAnswer?.id) {
            const currentEmbeds = this.props.newAnswer?.embeds || []
            const nextEmbeds = currentEmbeds.filter(embed => {
                const resolved = getCachedAnswer(embed?.id) || embed
                return !answerMatchesKnovEmbedTarget(resolved, hiddenEmbedTarget)
            })
            if (nextEmbeds.length !== currentEmbeds.length) {
                cache.updateCachedAnswer(this.props.newAnswer.id, { embeds: nextEmbeds })
            }
        }
        if (hiddenStreamId) {
            this.setState(state => ({
                streamEmbeds: (state.streamEmbeds || []).filter(
                    embed => String(embed?.id) !== String(hiddenStreamId),
                ),
            }))
        }
        this.setState({
            embedsLoading: false,
            embedLoadingPromise: null,
            waitingForEmbeds: false,
            streamEmbedsLoading: false,
        })
        if (this.props.isTitle) this.saveToTitleDrafts()
        else this.saveDraftToLocalStorage()
    }

    clearAttachedUrl = targetUrl => {
        if (!targetUrl || !this.props.newAnswer?.id) return
        const didAppend = answerModel.appendUrlToEditorText(this.getEditor(), targetUrl)
        if (didAppend) {
            this._pendingProgrammaticSuppressedPreviewUrls = addSuppressedPreviewUrl(
                this._pendingProgrammaticSuppressedPreviewUrls || [],
                targetUrl,
            )
        }

        const nextHiddenUrls = addSuppressedPreviewUrl(this.getHiddenPreviewUrls(), targetUrl)
        const nextPersistedUrls = addSuppressedPreviewUrl(
            this.getPersistedSuppressedPreviewUrls(),
            targetUrl,
        )

        this.setState(
            {
                hiddenPreviewUrls: nextHiddenUrls,
                embedsLoading: false,
                embedLoadingPromise: null,
                waitingForEmbeds: false,
                streamEmbedsLoading: false,
            },
            () => {
                this.setPersistedSuppressedPreviewUrls(nextPersistedUrls)
                cache.updateCachedAnswer(this.props.newAnswer.id, {
                    url: null,
                    url_id: null,
                })

                if (this.props.isTitle) this.saveToTitleDrafts()
                else this.saveDraftToLocalStorage()
            },
        )
    }

    handleAttachedUrlPreviewClick = targetUrl => {
        if (!targetUrl || answerModel.answerContainsUrlText(this.getDeltaJson() || {}, targetUrl)) {
            return
        }

        const didAppend = answerModel.appendUrlToEditorText(this.getEditor(), targetUrl)
        if (didAppend) {
            return false
        }
    }

    onFocus = editor => {
        // Position 1 is both a fresh draft's default selection and a caret the
        // reader deliberately placed at the start. Once this editor has been
        // focused, a later refocus must preserve that position too.
        this._lastFocusedEditor = editor
        // Once the caret is placed (focusEditor places it straight after the
        // focus), it shows in the bottom editor's field.
        if (this.props.bottomEditor) requestAnimationFrame(this.revealCaretInField)
        if (this.props.panelContext?.setExternalHideBottomChip) {
            this.props.panelContext.setExternalHideBottomChip(true)
        }

        // iOS cursor positioning fix: minimally ensure the editor is visible
        // Avoid forcing scroll to container bottom, which can push the editor off-screen.
        // Not for the bottom-editor pill, which is pinned at the foot and moved
        // to the keyboard's edge by the panel (lib/bottomEditorGlide).
        if (isIOS && !this.props.bottomEditor) {
            const targetEl = this.containerRef?.current
            if (targetEl && targetEl.scrollIntoView) {
                setTimeout(() => {
                    try {
                        targetEl.scrollIntoView({
                            block: 'nearest',
                            inline: 'nearest',
                            behavior: 'smooth',
                        })
                    } catch (e) {}
                }, 50)
            }
        }

        // Android Chrome in QuestContainer: request bottom scroll on next keyboard-open
        // so the NewAnswer isn't obscured once the keyboard appears. Not for the
        // bottom-editor pill, which is pinned outside the thread's flow
        // (shouldRequestScrollToBottomOnKeyboardOpen).
        if (this.requestsScrollToBottomOnKeyboardOpen()) {
            const panel = this.props.panelContext?.panel
            if (panel?.requestScrollToBottomOnKeyboardOpen) {
                panel.requestScrollToBottomOnKeyboardOpen()
            }

            // If the keyboard is already open, perform a direct bottom scroll (short delay)
            try {
                const vv = window.visualViewport
                const kbOpen = vv ? window.innerHeight - vv.height > 100 : false
                if (kbOpen && panel?.scrollToBottom) {
                    setTimeout(() => {
                        try {
                            panel.scrollToBottom()
                        } catch (e) {}
                    }, 80)
                }
            } catch (e) {}
        }
    }

    // The bottom editor's field scrolls once a long draft reaches its cap
    // (PanelContainer), and a focus never scrolls: WebKit on iOS would pan the
    // page, and the dock snaps it back. The writer's caret is brought into the
    // field's view instead, scrolling only the field: as the editor takes
    // focus, and as the field changes size under it (the keyboard coming up
    // takes room from the cap).
    revealCaretInField = () => {
        const field = this._fieldElement
        if (this._isUnmounted || !field || !this.props.bottomEditor) return
        const view = this.getEditorInstance()?.view
        if (!view?.hasFocus?.()) return
        let caret = null
        try {
            caret = view.coordsAtPos(view.state.selection.head)
        } catch (e) {
            return
        }
        revealBottomEditorCaret(field, caret)
    }

    _fieldElement = null
    _fieldResizeObserver = null

    setFieldElement = element => {
        if (this._fieldElement === element) return
        this._fieldResizeObserver?.disconnect()
        this._fieldResizeObserver = null
        this._fieldElement = element
        if (!element || !this.props.bottomEditor || typeof ResizeObserver === 'undefined') return
        this._fieldResizeObserver = new ResizeObserver(this.revealCaretInField)
        this._fieldResizeObserver.observe(element)
    }

    // iOS: when the quote pill arrives or leaves above the field, and the
    // composer's box resizes around it, WebKit can go on drawing the caret
    // where the field was — above the pill, or where the pill used to be.
    // Re-seating the same DOM selection once the box has settled makes it draw
    // the caret where the field is. The selection itself is unchanged, so
    // ProseMirror sees no transaction; nothing is touched mid-composition.
    _reseatCaretOnSettle = false

    reseatCaretAfterLayout = () => {
        if (!isIOS) return
        if (this.compactComposerResizeAnimation) {
            this._reseatCaretOnSettle = true
            return
        }
        // The bottom editor is anchored at its foot: a quote row arriving
        // above the open field, or leaving, moves nothing under the caret.
        // Re-seating the selection there anyway was a programmatic selection
        // change right after the reply press, which iOS answered with its
        // copy/paste menu over a caret the reader never touched.
        if (this.props.bottomEditor) return
        requestAnimationFrame(() => requestAnimationFrame(this.reseatCaret))
    }

    reseatCaret = () => {
        if (this._isUnmounted || !this.composerHasFocus()) return
        // The TipTap editor under CommonEditor's and TiptapEditor's handles.
        const view = this.getEditorInstance()?.view
        if (view?.composing) return
        const selection = typeof window === 'undefined' ? null : window.getSelection?.()
        if (!selection || selection.rangeCount === 0) return
        const range = selection.getRangeAt(0).cloneRange()
        selection.removeAllRanges()
        selection.addRange(range)
    }

    // Whether the keyboard is up for this composer: focus inside its root.
    composerHasFocus = () => {
        const root = this.containerRef?.current
        const activeEl = typeof document === 'undefined' ? null : document.activeElement
        return !!(root && activeEl instanceof Node && root.contains(activeEl))
    }

    holdKeyboardForComposerHandoff = () => {
        if (
            !shouldHoldKeyboardAcrossHandoff({
                isMobile,
                composerFocused: this.composerHasFocus(),
            })
        ) {
            return
        }
        holdKeyboardAcrossHandoff()
    }

    requestsScrollToBottomOnKeyboardOpen = () =>
        shouldRequestScrollToBottomOnKeyboardOpen({
            isAndroid,
            isTitle: this.props.isTitle,
            closeModal: this.props.closeModal,
            bottomEditor: this.props.bottomEditor,
        })

    // Ensure re-tapping the NewAnswer while keyboard is open still scrolls to bottom
    _onPointerDown = () => {
        if (!this.requestsScrollToBottomOnKeyboardOpen()) return
        const panel = this.props.panelContext?.panel
        // Always request a bottom scroll for the upcoming keyboard-open
        try {
            panel?.requestScrollToBottomOnKeyboardOpen?.()
        } catch (e) {}

        // If the keyboard is already open, also perform an immediate bottom scroll
        try {
            const vv = window.visualViewport
            const kbOpen = vv ? window.innerHeight - vv.height > 100 : false
            if (kbOpen && panel?.scrollToBottom) {
                setTimeout(() => {
                    try {
                        panel.scrollToBottom()
                    } catch (e) {}
                }, 60)
            }
        } catch (e) {}
    }

    findScrollableParent = () => {
        // Prefer the component's root container (works with Tiptap and Quill)
        let element = this.containerRef?.current
        // Fallback to Quill container if available
        if (!element) element = this.richEditor.current?.getEditor?.()?.container
        if (!element) return null

        while (element && element !== document.body) {
            const style = window.getComputedStyle(element)
            if (
                style.overflow === 'auto' ||
                style.overflow === 'scroll' ||
                style.overflowY === 'auto' ||
                style.overflowY === 'scroll'
            ) {
                return element
            }
            element = element.parentElement
        }
        return null
    }

    onBlur = () => {
        if (this.props.panelContext?.setExternalHideBottomChip) {
            this.props.panelContext.setExternalHideBottomChip(false)
        }
    }

    onInlineComposerFocusCapture = ev => {
        if (!this.isCompactComposer() || this.state.inlineComposerExpanded) return
        const target = ev?.target
        if (target instanceof Element) {
            // Focus inside a portaled menu (the palette's, the model picker's,
            // the models menu) bubbles here through the React tree, and focus
            // in a folded pill's own palette or picker control is a reach for
            // that menu: neither is a request to open the composer.
            const root = this.containerRef?.current
            if (root && !root.contains(target)) return
            if (this.isInlineComposerActionTarget(target)) return
        }
        this.setState({ inlineComposerExpanded: true })
    }

    // Two composers fold to a single line and open on focus: the stream's
    // inline title composer, and the thread's bottom-editor pill on a phone,
    // where the folded line leaves the thread its room and the navbar its
    // place. Everything the fold touches asks here rather than the props.
    isCompactComposer = () =>
        !!(this.props.inlineCompactComposer && this.props.isTitle) ||
        !!this.props.collapsibleComposer

    getInlineCollapseBoundaryRoot = () => {
        return this.props.inlineCollapseBoundaryRef?.current || this.containerRef?.current
    }

    onDocumentInlineBoundaryPointerDown = ev => {
        if (!this.isCompactComposer()) return
        const root = this.getInlineCollapseBoundaryRoot()
        const target = ev?.target
        this._lastInlineBoundaryPointerDownInside = !!(
            root &&
            target instanceof Node &&
            root.contains(target)
        )
    }

    shouldAutoFocusInlineEditorFromTarget = target => {
        if (!target || !(target instanceof Element)) return false
        return !!target.closest('[data-inline-editor-container="true"], [contenteditable="true"]')
    }

    isContentEditableInlineEditorTarget = target => {
        if (!target || !(target instanceof Element)) return false
        return !!target.closest('[contenteditable="true"]')
    }

    shouldFocusInlineComposerFromTarget = target => {
        return shouldProgrammaticallyFocusInlineComposer({
            isMobile,
            isAndroid,
            isEditorSurfaceTarget: this.shouldAutoFocusInlineEditorFromTarget(target),
            isContentEditableTarget: this.isContentEditableInlineEditorTarget(target),
        })
    }

    isInlineComposerActionTarget = target => {
        if (!target || !(target instanceof Element)) return false
        return !!target.closest('[data-inline-compose-action="true"]')
    }

    onInlineComposerPointerDownCapture = ev => {
        if (!this.isCompactComposer()) return
        // A press inside one of this composer's portaled menus (the palette's
        // rows, the model picker, the models menu) reaches here through the
        // React tree. It is a choice in that menu, not a tap on the composer:
        // the menu decides whether the pill opens (see setAgentModel and
        // insertActionRequestIntoComposer).
        const root = this.containerRef?.current
        if (ev?.target instanceof Element && root && !root.contains(ev.target)) return
        const shouldFocusEditor = this.shouldFocusInlineComposerFromTarget(ev?.target)

        // Keep action taps stable while collapsed. Expanding here can remount/reposition
        // the button before mobile synthesizes the click, which makes the first tap no-op.
        if (this.isInlineComposerActionTarget(ev?.target)) {
            this.armInlineCollapseSkip()
            return
        }

        // On iOS the tap on the field itself must not focus natively: WebKit
        // would scroll the field into view for the keyboard, and the foot
        // moves the pill to the keyboard's edge itself. The landed tap
        // focuses the editor by hand (onInlineComposerClickCapture).
        if (
            shouldTakeComposerTapFocus({
                isIOS,
                compact: true,
                keyboardOpen: Boolean(this.props.panelContext?.keyboardOpen),
                eventType: ev?.type,
                targetIsContentEditable: this.isContentEditableInlineEditorTarget(ev?.target),
            })
        ) {
            ev.preventDefault()
            this._composerTapTakesFocus = true
        }

        // A phone's tap is settled where the finger lifts, and the fold opening
        // on the press would carry the field up and put the toolbar under the
        // finger before then: the tap landed on the camera, or on the thread,
        // and never focused the field. The press only records itself; the tap
        // opens the fold once it has landed (onInlineComposerClickCapture), or
        // the focus a direct tap gives the field does.
        if (!shouldExpandCompactComposerOnPointerDown({ isMobile })) return

        if (this.isCompactComposer() && !this.state.inlineComposerExpanded) {
            this.setState({ inlineComposerExpanded: true })
        }

        // Composer chrome still opens the keyboard, while direct editable-text taps
        // are left to native caret placement.
        if (shouldFocusEditor) {
            this.focusEditor({ defer: !isMobile })
        }
    }

    // The phone's tap on a folded pill, landed: open the fold now, and give the
    // field the focus a tap on the pill's chrome asks for — here, inside the
    // tap's own gesture, which is what lets the phone raise the keyboard for a
    // programmatic focus. A direct tap in the field is focused by the tap
    // itself, with the caret where the finger was.
    onInlineComposerClickCapture = ev => {
        if (!isMobile || !this.isCompactComposer()) return
        const root = this.containerRef?.current
        const target = ev?.target
        if (target instanceof Element && root && !root.contains(target)) return
        if (this.isInlineComposerActionTarget(target)) return

        if (!this.state.inlineComposerExpanded) {
            this.setState({ inlineComposerExpanded: true })
        }
        // A tap on the field whose native focus was cancelled at the press
        // (shouldTakeComposerTapFocus) takes it here, at the editor's end.
        const tapTakesFocus = this._composerTapTakesFocus
        this._composerTapTakesFocus = false
        if (tapTakesFocus) {
            this.focusEditorAtPoint(ev.clientX, ev.clientY)
        } else if (
            this.shouldFocusInlineComposerFromTarget(target) &&
            !this.isContentEditableInlineEditorTarget(target)
        ) {
            this.focusEditor({ defer: false })
        }
    }

    _composerTapTakesFocus = false

    // The folded bottom editor's door to the full editor: the reply modal, on
    // the same draft (one per thread, draftAnswerStore) with the same quote.
    // What the pill holds is saved first so the modal opens on it; closing the
    // modal hands its edits back (draft_modal_closed), and a post from it lands
    // as the pill's own would: the thread counts it and drops the quote, and
    // the panel shows the reply the way it shows the pill's.
    openComposerInModal = () => {
        const { quest, panelContext, addAnswer } = this.props
        if (!quest?.id) return
        this.debouncedSaveDraft?.cancel?.()
        this.saveDraftToLocalStorage()
        const draft = this.ensureReplyDraftForQuest(quest)
        const savedDraft = this.loadDraftFromLocalStorage()
        if (draft?.id && savedDraft) {
            cache.updateCachedAnswer(draft.id, this.savedDraftCachePatch(savedDraft))
        }
        const quote = this.autoQuoteAnswer()
        useStore.setState({
            isModalOpen: true,
            modalParams: {
                type: 'NewAnswer',
                noHeader: true,
                quest,
                newAnswer: (draft?.id && getCachedAnswer(draft.id)) || draft,
                ...(quote?.id
                    ? { parentAnswer: quote, showReplyTo: true, replyToAnswerId: quote.id }
                    : null),
                user: currentUserSnapshot(),
                showUser: true,
                showUsername: false,
                placeholder: this.props.placeholder,
                onAnswerPosted: posted => {
                    useStore.setState({ isModalOpen: false })
                    if (!posted?.id) return
                    addAnswer?.(posted)
                    panelContext?.offerAnswerJump?.(posted.id, quest.id, {
                        fromBottomEditor: true,
                        viaModal: true,
                    })
                },
                // The reply modal's frame (Answer.openQuoteMainComposer).
                customStyles: {
                    width: '50dvw',
                    minWidth: '600px',
                    maxWidth: '1000px',
                    maxHeight: '80dvh',
                    left: '50%',
                    transform: 'translate(-50%, 0%)',
                },
                mobileCustomStyles: {
                    width: '95dvw',
                    maxWidth: '95dvw',
                    minWidth: 'unset',
                    left: '50%',
                    transform: 'translate(-50%, 0%)',
                    top: '2%',
                },
            },
        })
    }

    // The door itself, the same control wherever the pill keeps it: in the
    // folded row where the open pill's post button stands, and in the open
    // pill's top-right corner, right of the quote's X when there is one. The
    // press keeps the editor's focus (and a phone its keyboard) so the modal's
    // editor takes it over rather than the keyboard going down and back up.
    renderOpenFullEditorButton = () => (
        <WithTooltip tip="Open the full editor">
            <ReliableButton
                type="button"
                data-testid="reply-open-full-editor-button"
                data-inline-compose-action="true"
                className={styles.openFullEditorButton}
                aria-label="Open the full editor"
                onMouseDown={ev => ev.preventDefault()}
                preserveFocusOnTouch={isMobile}
                onClick={this.openComposerInModal}
            >
                <Maximize2 aria-hidden="true" />
            </ReliableButton>
        </WithTooltip>
    )

    hasPickedComposerMedia = () =>
        !!this.containerRef?.current?.querySelector?.(PICKED_MEDIA_SELECTORS.join(','))

    hasRenderedComposerAttachment = () =>
        !!this.containerRef?.current?.querySelector?.(
            [
                ...PICKED_MEDIA_SELECTORS,
                '[data-testid="new-answer-embed-remove-button"]',
                '[data-testid="new-answer-stream-preview-remove-button"]',
                '[data-testid="new-answer-leaderboard-preview-remove-button"]',
                '[data-testid="new-answer-tdp-preview-remove-button"]',
                '[data-testid="new-answer-link-preview-remove-button"]',
            ].join(','),
        )

    shouldKeepInlineComposerExpandedAfterBlur = () => {
        // The bottom editor folds when it is left, draft or not, on a desktop
        // as on a phone (where the keyboard going folds it): its one row keeps
        // the draft's first line. Picked media has no line to keep — folded
        // away, the picture just picked reads as lost — so a draft carrying
        // it stays open. The stream's title composer stays open over a draft
        // on a desktop.
        if (isMobile || this.props.collapsibleComposer) return this.hasPickedComposerMedia()

        try {
            const deltaJson = this.getDeltaJson()
            const textContent = getText(deltaJson || {}, {}).trim()
            if (textContent) return true
            if (this.getDeltaActionTokenAttrs(deltaJson).length) return true

            if (this.hasRenderedComposerAttachment() || getImages(deltaJson)) return true

            const answer = this.props.newAnswer || {}
            const hasPollInput =
                this.isPollMode(answer) &&
                this.getPollOptions(answer).some(option => option?.toString?.().trim())

            return !!answer.dice || hasPollInput
        } catch (e) {
            return false
        }
    }

    onInlineComposerBlurCapture = () => {
        if (!this.isCompactComposer()) return
        if (this.state.menuOpen) {
            this._inlineCollapsePendingAfterMenuClose =
                this._inlineCollapsePendingAfterMenuClose || this.state.inlineComposerExpanded
            return
        }
        if (this.consumeInlineCollapseSkip()) return
        // Use next tick so document.activeElement is up to date.
        this.scheduleInlineComposerCollapseCheck()
    }

    defaultValue = () => {
        let value = this.props.newAnswer?.delta_json || ''

        // delta_json is usually a Quill delta object, not a string.
        const hasEmbed = value?.ops?.some?.(op => op?.insert && typeof op.insert === 'object')
        if (!(getText(value) || '').trim() && !hasEmbed) {
            const savedDraft = this.loadDraftFromLocalStorage()
            if (savedDraft && savedDraft.delta_json) {
                value = this.getSavedDraftDeltaJson(savedDraft)
            }
        }

        return value
    }

    // generate a per-quest localstorage key
    buildLocalstorageDraftKey = () => {
        const questId = this.props.quest?.id
        return questId ? `draft-answer-${questId}` : null
    }

    // save the current editor contents to local storage
    saveDraftToLocalStorage = () => {
        // Try to get content from editor, fall back to cached values (for unmount)
        const content = this.getContent() ?? this._lastContent ?? ''
        const deltaJson = this.getDeltaJson() ?? this._lastDeltaJson
        const persistedDeltaJson = this.sanitizeDeltaJsonForTransport(deltaJson)
        const persistedSuppressedPreviewUrls = this.reconcileSuppressedPreviewUrls(deltaJson)
        const storageKey = this.buildLocalstorageDraftKey()
        const draftAnswer = this.getNewAnswer(deltaJson, this.getCurrentDraftAnswerForPersistence())
        this._preserveAiActionInputsOnNextDraftSave = false
        const payloads = this.getSerializableDraftPayloads(draftAnswer)
        const actionRequests = this.sanitizeActionRequestsForDraftPersistence(
            this.getActionRequestsFromSource(draftAnswer),
            payloads,
        )
        const hasDraftState =
            answerModel.hasContent(draftAnswer) ||
            actionRequests.length > 0 ||
            Object.keys(payloads || {}).length > 0

        if (!storageKey) return

        // If there's content or AI draft state, save it; otherwise clear the draft.
        if (hasDraftState) {
            const draftData = {
                content,
                delta_json: persistedDeltaJson,
                action_requests: actionRequests,
                payloads,
                suppressed_preview_urls: persistedSuppressedPreviewUrls,
                message_type: draftAnswer?.message_type || null,
                poll_options: draftAnswer?.poll_options || [],
                poll_duration_minutes: draftAnswer?.poll_duration_minutes || null,
                timestamp: new Date().getTime(),
            }
            try {
                localStorage.setItem(storageKey, JSON.stringify(draftData))
            } catch (error) {
                console.error('Error saving draft to local storage:', error)
            }
        } else {
            // Clear the draft when content is empty
            this.clearDraftFromLocalStorage()
        }
    }

    // load a draft from local storage into the editor if it exists
    // The composer quotes what it is answering. The modal is handed its target
    // explicitly; the inline composer at the foot of a thread quotes the message
    // the reader pressed reply on (Quest.composerQuoteAnswer) — nothing is
    // offered unasked — and that quote stays refusable, since minds change.
    autoQuoteAnswer = () => {
        if (!quoteMainEnabled()) return null
        if (this.props.showReplyTo || this.props.replyToAnswerId) return null
        if (this.props.isTitle) return null
        const offered = this.props.hydratedAutoQuoteAnswer || this.props.autoQuoteAnswer
        if (!offered?.id) return null
        // Dismissal is keyed to the answer, so a later press on a different
        // message is not refused by an earlier one's.
        return this.state.dismissedAutoQuoteId === String(offered.id) ? null : offered
    }

    effectiveReplyToAnswerId = () => {
        if (this.props.replyToAnswerId) return this.props.replyToAnswerId
        const offered = this.autoQuoteAnswer()
        return offered?.id || null
    }

    // Refusing hides the pill here at once and drops the pin at its source, the
    // thread. A refusal held only in this composer's state would come undone
    // on remount — the bottom_editor portal moves the composer between hosts —
    // and ship a reply edge the reader had refused.
    dismissAutoQuote = () => {
        const offered = this.props.autoQuoteAnswer
        if (!offered?.id) return
        this.setState({ dismissedAutoQuoteId: String(offered.id) })
        this.props.onQuoteDismiss?.()
    }

    // Only a visible suggestion belonging to this editor owns Escape. Other
    // panels can retain their popups, and Tippy keeps closing popups mounted.
    suggestionOwnsEscape = () => {
        const editorId = this.getEditorInstance()?.instanceId
        if (!editorId) return false
        return Array.from(document.querySelectorAll('[data-suggestion-editor]')).some(popup => {
            const popper = /** @type {import('tippy.js').PopperElement | null} */ (
                popup.closest('[data-tippy-root]')
            )
            return (
                popup.getAttribute('data-suggestion-editor') === editorId &&
                popper?._tippy?.state.isVisible
            )
        })
    }

    // Escape refuses the offered quote — the reach for the X without the reach.
    //
    // Capture phase. ProseMirror calls preventDefault on Escape unconditionally,
    // whether or not anything handled it, so a bubble-phase listener always sees
    // defaultPrevented and never fires: measured doc-capture false, then
    // editor-bubble true. Suggestions still go first, by asking whether one is
    // open rather than by yielding a phase.
    //
    // Gated on focus being inside THIS composer, so it is the quote you are
    // looking at that goes, and on a quote being offered at all, so a composer
    // with nothing to refuse never claims the key.
    onQuoteEscape = ev => {
        if (ev.key !== 'Escape' || ev.defaultPrevented) return
        if (!this.autoQuoteAnswer()) return
        const container = this.containerRef?.current
        if (!container || !container.contains(document.activeElement)) return
        if (this.suggestionOwnsEscape()) return
        ev.preventDefault()
        ev.stopPropagation()
        this.dismissAutoQuote()
    }

    // The composer owns what it is quoting — the requested quote, minus any
    // refusal — so the thread has to be told rather than guess. It needs the answer to
    // decide whether this composer is the next link in the reply chain, and
    // both ends of that line have to agree.
    reportQuoteTarget = () => {
        if (!this.props.onQuoteTargetChange) return
        const offeredId = this.autoQuoteAnswer()?.id
        const next = offeredId ? String(offeredId) : null
        if (next === this._reportedQuoteTargetId) return
        this._reportedQuoteTargetId = next
        this.props.onQuoteTargetChange(next)
    }

    loadDraftFromLocalStorage = () => {
        const storageKey = this.buildLocalstorageDraftKey()
        if (storageKey) {
            try {
                const savedDraft = localStorage.getItem(storageKey)
                if (savedDraft) {
                    const draftData = JSON.parse(savedDraft)
                    return draftData
                }
            } catch (error) {
                console.error('Error loading draft from local storage:', error)
            }
        }
        return null
    }

    // clear the draft from local storage (e.g., after posting)
    clearDraftFromLocalStorage = () => {
        const storageKey = this.buildLocalstorageDraftKey()
        if (storageKey) {
            try {
                localStorage.removeItem(storageKey)
            } catch (error) {
                console.error('Error clearing draft from local storage:', error)
            }
        }
    }

    // reload draft content from localStorage into the editor
    // called when the reply modal closes to sync in-thread editor with modal edits
    reloadDraftFromLocalStorage = () => {
        const savedDraft = this.loadDraftFromLocalStorage()
        const deltaJson = this.getSavedDraftDeltaJson(savedDraft)
        const actionRequests = this.ensureAiActionClientIds(
            this.getActionRequestsFromSource(savedDraft),
        )

        if (this.richEditor.current) {
            const editor = this.richEditor.current
            if (editor.setContents) {
                editor.setContents(deltaJson, 'api')
            } else if (editor.getEditor) {
                // Fallback for Quill-based editor
                const quill = editor.getEditor()
                if (quill?.setContents) {
                    quill.setContents(deltaJson, 'api')
                }
            }
            // Update validity state
            const nextActionRequests = this.rememberDraftActionRequests(actionRequests)
            const hasContent = this.hasCurrentDraftContent(nextActionRequests)
            this.setState({
                isValidContent: hasContent,
                draftActionRequests: nextActionRequests,
            })
            if (this.props.newAnswer?.id) {
                this.setHiddenPreviewUrls(savedDraft?.suppressed_preview_urls || [])
                cache.updateCachedAnswer(
                    this.props.newAnswer.id,
                    this.savedDraftCachePatch(savedDraft),
                )
            }
        } else {
            const nextActionRequests = this.rememberDraftActionRequests(actionRequests)
            this.setState({
                draftActionRequests: nextActionRequests,
                isValidContent: this.hasCurrentDraftContent(nextActionRequests),
            })
        }
    }

    setAgentModel = model => {
        const questId = this.props.quest?.id
        const normalizedModel = resolveAgentModelSelectionId(
            model,
            useStore.getState().agentModelConfig,
            this.props.aiModels || useStore.getState().aiModels,
        )

        // Choosing an agent is a decision to write to it: a folded composer
        // opens out with the caret in the field. Any explicit picker choice,
        // including removing the agent, keeps an open composer open.
        if (this.isCompactComposer()) {
            if (normalizedModel || this.state.inlineComposerExpanded) {
                this.armInlineCollapseSkip()
            }
            if (!this.state.inlineComposerExpanded) {
                if (normalizedModel) {
                    if (isMobile) this._pendingInlineFocusAfterExpand = true
                    this.setState({ inlineComposerExpanded: true })
                    if (!isMobile) this.focusEditor({ defer: true })
                }
            }
        }

        // Update the store first - this will trigger the useEffect in CommonEditor
        useStore.getState().set(state => {
            // If turning off AI, remove from questAgentModels
            // If turning on AI, add to questAgentModels with default model
            if (normalizedModel) {
                state.questAgentModels[questId] = normalizedModel
            } else {
                //console.log('toggleAIModel: removing from questAgentModels', questId)
                delete state.questAgentModels[questId]
            }
        })
        // Then update the API, from the committed state rather than the draft
        api.updateUserSpaceOptions({
            quest_agent_models: useStore.getState().questAgentModels,
        })
    }

    // Initialize draft system for new quests (title drafts)
    initializeTitleDrafts = () => {
        if (this.props.isTitle) {
            TitleDraftManager.cleanupOld(30)
            const draftId = this._currentTitleDraftId || this.state.currentDraftId || uuid()
            this._currentTitleDraftId = draftId

            this.setState({
                currentDraftId: draftId,
                isDraftLoaded: false,
                titleDraftCount: TitleDraftManager.getCount(),
            })
        }
    }

    ensureTitleDraftId = () => {
        if (!this.props.isTitle) return null
        if (this._currentTitleDraftId) return this._currentTitleDraftId

        const draftId = this.state.currentDraftId || uuid()
        this._currentTitleDraftId = draftId
        if (this.state.currentDraftId !== draftId) {
            this.setState({
                currentDraftId: draftId,
                isDraftLoaded: false,
            })
        }
        return draftId
    }

    // Save to title drafts (for new quests only)
    saveToTitleDrafts = () => {
        const draftId = this.ensureTitleDraftId()
        if (!draftId) return

        const content = this.getContent()
        const deltaJson = this.getDeltaJson()
        const persistedDeltaJson = this.sanitizeDeltaJsonForTransport(deltaJson)
        const persistedSuppressedPreviewUrls = this.reconcileSuppressedPreviewUrls(deltaJson)
        const draftAnswer = this.getCurrentDraftAnswerForPersistence()
        const payloads = this.getSerializableDraftPayloads(draftAnswer)
        const actionRequests = this.sanitizeActionRequestsForDraftPersistence(
            this.getActionRequestsForSubmit(deltaJson),
            payloads,
        )
        this._preserveAiActionInputsOnNextDraftSave = false
        // Always save, even if empty - this ensures the draft appears in the list
        // The TitleDraftManager will handle removing it if it's too short
        TitleDraftManager.saveWithId(draftId, content || '', persistedDeltaJson, {
            action_requests: actionRequests,
            payloads,
            suppressed_preview_urls: persistedSuppressedPreviewUrls,
            message_type: draftAnswer?.message_type || null,
            poll_options: draftAnswer?.poll_options || [],
            poll_duration_minutes: draftAnswer?.poll_duration_minutes || null,
        })

        // Update draft count - this will make the drafts link appear
        const newCount = TitleDraftManager.getCount()
        if (newCount !== this.state.titleDraftCount) {
            this.setState({
                titleDraftCount: newCount,
            })
        }
    }

    // Handle draft selection from modal
    handleDraftSelection = draft => {
        this._currentTitleDraftId = draft.id

        // Load the draft content into editor
        if (this.richEditor.current) {
            this.richEditor.current.setContents(
                this.getSavedDraftDeltaJson(draft),
                Quill.sources.SILENT, // Don't trigger onChange
            )
        }

        // Adopt the draft's ID so future edits update this draft
        const draftActionRequests = this.rememberDraftActionRequests(
            this.getActionRequestsFromSource(draft),
        )
        this.setState({
            currentDraftId: draft.id,
            isDraftLoaded: true,
            isValidContent: true,
            draftActionRequests,
            hiddenPreviewUrls: draft.suppressed_preview_urls || [],
            suppressedPreviewUrls: draft.suppressed_preview_urls || [],
            titleDraftCount: TitleDraftManager.getCount(),
        })
        if (this.props.newAnswer?.id) {
            cache.updateCachedAnswer(this.props.newAnswer.id, {
                action_requests: this.getActionRequestsFromSource(draft),
                ...this.draftPayloadCachePatch(draft),
                message_type: draft.message_type || null,
                poll_options: draft.poll_options || [],
                poll_duration_minutes: draft.poll_duration_minutes || null,
            })
        }
    }

    // Open drafts modal
    openDraftsModal = () => {
        const { set } = useStore.getState()

        // Track drafts selector opening
        window.logEv('OPEN_DRAFTS_SELECTOR', {
            draft_count: this.state.titleDraftCount,
            current_draft_id: this.state.currentDraftId,
            from_location: 'new_answer_controls',
        })

        set({
            isModalOpen: true,
            modalParams: {
                type: 'DraftsSelector',
                title: 'Select a Draft',
                icon: 'FilePen',
                callback: this.handleDraftSelection,
                currentDraftId: this.state.currentDraftId,
            },
        })
    }

    // Clear draft after successful post
    handleSuccessfulPost = () => {
        const postedDraftId = this._currentTitleDraftId || this.state.currentDraftId
        if (this.props.isTitle && postedDraftId) {
            // Remove the posted draft
            TitleDraftManager.remove(postedDraftId)

            // Reset for next draft
            const nextDraftId = uuid()
            this._currentTitleDraftId = nextDraftId
            this.setState({
                currentDraftId: nextDraftId,
                isDraftLoaded: false,
                titleDraftCount: TitleDraftManager.getCount(),
            })
        }
    }

    componentDidMount = () => {
        this.reportQuoteTarget()
        this.subscribeUrlPreviewChannel()
        document.addEventListener('mousedown', this.onDocumentInlineBoundaryPointerDown, true)
        document.addEventListener('touchstart', this.onDocumentInlineBoundaryPointerDown, true)
        document.addEventListener('keydown', this.onQuoteEscape, true)
        // Check if panelContext exists (it won't in modal context)
        if (this.props.panelContext?.panel) {
            const { panel } = this.props.panelContext
            const panelId = panel.panelId
            const isOnScreen = isMobile
                ? isPanelCenter(panelId)
                : isPanelLeft(panelId) || isPanelCenter(panelId) || isPanelRight(panelId)
        }

        // Listen for draft modal close to reload content from localStorage
        // This syncs the in-thread editor with changes made in the modal
        this._onDraftModalClosed = ev => {
            if (ev?.detail?.questId === this.props.quest?.id) {
                this.reloadDraftFromLocalStorage()
            }
        }
        window.addEventListener('draft_modal_closed', this._onDraftModalClosed)
        window.addEventListener('knov:insert-ai-action-from-media', this.insertAiActionFromMedia)
        window.addEventListener('beforeunload', this.flushDraftPersistence)

        // Initialize title drafts for new quests
        this.initializeTitleDrafts()
        let initialActionRequests = this.state.draftActionRequests || []
        if (!this.props.isTitle) {
            const savedDraft = this.loadDraftFromLocalStorage()
            if (savedDraft) {
                initialActionRequests = this.ensureAiActionClientIds(
                    this.getActionRequestsFromSource(savedDraft),
                )
                this.rememberDraftActionRequests(initialActionRequests)
                this.setState({
                    draftActionRequests: initialActionRequests,
                })
                this.setHiddenPreviewUrls(savedDraft?.suppressed_preview_urls || [])
                if (this.props.newAnswer?.id) {
                    cache.updateCachedAnswer(
                        this.props.newAnswer.id,
                        this.savedDraftCachePatch(savedDraft),
                    )
                }
            }
        }
        this._lastPreviewUrls = this.getPreviewUrls(this.defaultValue())

        // Check initial content validity
        const hasInitialContent = this.hasCurrentDraftContent(initialActionRequests)
        if (hasInitialContent) {
            this.setState({ isValidContent: true })
        }

        // Share/clip flow: ingest any shared content stashed for this fresh draft —
        // text through the editor, files through the normal attachment pipeline.
        if (this.props.isTitle && this.props.quest?.is_draft) {
            const seed = takePendingComposerSeed(this.props.quest?.id)
            if (seed) this.applyComposerSeed(seed)
        }
    }

    // Load a share/clip seed once the editor ref is wired up. The editor can
    // mount slightly after us, so retry briefly instead of dropping the seed.
    applyComposerSeed = (seed, attempt = 0) => {
        const MAX_ATTEMPTS = 20
        const editor = this.richEditor.current
        if (!editor && attempt < MAX_ATTEMPTS) {
            setTimeout(() => this.applyComposerSeed(seed, attempt + 1), 100)
            return
        }

        if (seed.deltaJson && editor) {
            try {
                if (editor.setContents) {
                    editor.setContents(seed.deltaJson, 'api')
                } else if (editor.getEditor) {
                    editor.getEditor()?.setContents?.(seed.deltaJson, 'api')
                }
                this.setState({ isValidContent: true })
            } catch (error) {
                console.error('Failed to seed shared content into composer', error)
            }
        }

        if (seed.files?.length) {
            seed.files.forEach(file => this.fileHandler(file))
        }
        if (seed.uploadedFiles?.length) {
            seed.uploadedFiles.forEach(file => this.uploadManager.handleUploadedFile(file))
        }
    }

    titleHasUpvalues = () => {
        const parent = this.props.quest?.parent
        if (!parent || !parent.bsv_upvalues || !Array.isArray(parent.bsv_upvalues)) {
            return false
        }

        // Check if there are upvalues from users other than the parent's author
        const upvaluesFromOthers = parent.bsv_upvalues.filter(upvalue => {
            // Check both user_id and user.id in case of different data structures
            const upvalueUserId = upvalue.from_user_id
            const parentUserId = parent.user_id
            return upvalueUserId && parentUserId && upvalueUserId !== parentUserId
        })

        // Need at least one upvalue from someone other than the author
        return upvaluesFromOthers.length > 1
    }

    onAgentMenuStateChange = isOpen => {
        if (isOpen) {
            const completedMenuTransfer = this._inlineComposerMenuTransferPending
            this._inlineComposerMenuTransferPending = false
            // Let the opening control's synchronous blur consume its exemption,
            // then clear any remainder (Android preserves focus while opening).
            // Clear a palette-to-Models handoff immediately and again after the
            // opening click finishes bubbling, since that click can re-arm it.
            if (completedMenuTransfer) {
                this._skipInlineCollapseOnBlurUntil = 0
            }
            if (this.inlineComposerMenuOpenSkipClearTimer) {
                clearTimeout(this.inlineComposerMenuOpenSkipClearTimer)
            }
            this.inlineComposerMenuOpenSkipClearTimer = setTimeout(() => {
                this.inlineComposerMenuOpenSkipClearTimer = null
                if (this.state.menuOpen) this._skipInlineCollapseOnBlurUntil = 0
            }, 0)
            if (this.inlineComposerCollapseCheckTimer) {
                clearTimeout(this.inlineComposerCollapseCheckTimer)
                this.inlineComposerCollapseCheckTimer = null
            }
            if (this.isCompactComposer() && this.state.inlineComposerExpanded) {
                this._inlineCollapsePendingAfterMenuClose = true
            }
        }

        this.setState({ menuOpen: isOpen }, () => {
            if (
                !isOpen &&
                !this.state.menuOpen &&
                !this._inlineComposerMenuTransferPending &&
                this._inlineCollapsePendingAfterMenuClose
            ) {
                this.scheduleInlineComposerCollapseCheck({ menuDismissal: true })
            }
        })

        // Update global store to communicate with MobilePullToRefresh
        // Delay updating the global state slightly for iOS to initialize scrolling
        setTimeout(
            () => {
                useStore.getState().set(state => {
                    state.anyMenuOpen = isOpen
                })
            },
            isOpen ? 100 : 0,
        ) // Only delay when opening, not when closing
    }

    render() {
        const debug = this.props.DEBUG
        const quest = this.props.quest
        const newAnswer = this.props.newAnswer
        // TODO need to handle postAnswer with content param when clicking button!!!! So need access to editor value.
        const hoverClass = this.state.hover ? 'is-hover' : ''
        const mobileClass = isMobile ? 'is-mobile' : ''

        const getShortcutText = () => {
            if (isMobile) return ''
            if (
                gon.currentUser?.features?.some(
                    feature => feature.name === 'enter_key_posts_immediately',
                )
            ) {
                return '↵'
            }
            return navigator.platform.toLowerCase().includes('mac') ? '⌘↵' : '⌃↵'
        }

        const extractedUrls = extractUrls(this.getDeltaJson() || {})
        const hiddenPreviewUrls = answerModel.mergeHiddenPreviewUrls(
            this.getHiddenPreviewUrls(),
            this.getPersistedSuppressedPreviewUrls(),
        )
        const visibleStreamEmbedLinks = filterSuppressedPreviewUrls(
            extractedUrls.streamEmbedLinks || [],
            hiddenPreviewUrls,
        )
        const visibleStreamEmbedConfigs = extractStreamEmbedConfigs(visibleStreamEmbedLinks)
        const visibleStreamIds = new Set(visibleStreamEmbedConfigs.map(embed => String(embed.id)))
        const isVisibleStreamEmbed = embed =>
            visibleStreamIds.has(String(embed?.id)) ||
            visibleStreamEmbedLinks.some(href => areSamePreviewUrl(href, embed?.url))
        const visibleLeaderboardEmbedLinks = filterSuppressedPreviewUrls(
            extractedUrls.leaderboardEmbedLinks || [],
            hiddenPreviewUrls,
        )
        const visibleTdpEmbedLinks = filterSuppressedPreviewUrls(
            extractedUrls.tdpEmbedLinks || [],
            hiddenPreviewUrls,
        )
        const attachmentState = answerModel.getComposeAttachmentState({
            answer: newAnswer,
            isEditing: true,
            extractedUrls,
            hiddenPreviewUrls,
            resolveEmbedAnswer: embed => getCachedAnswer(embed?.id) || embed,
        })
        const embeds = attachmentState.embeds
        const visibleKnovEmbedTargets = getKnovEmbedTargets(
            filterSuppressedPreviewUrls(extractedUrls.knovEmbedLinks || [], hiddenPreviewUrls),
        )
        const embedsFingerprint = generateFingerprint(
            getMatchedKnovEmbedTargetKeys(
                embeds || [],
                visibleKnovEmbedTargets,
                embed => getCachedAnswer(embed?.id) || embed,
            ),
        )

        // Use state for button validity to avoid recalculating on every render
        const isValid = this.state.isValidContent
        const isPollMode = this.isPollMode(newAnswer)
        const pollOptions = this.getPollOptions(newAnswer)
        const pollDurationMinutes = this.getPollDurationMinutes(newAnswer)
        const pollValidationErrors = this.getPollValidationErrors(newAnswer)
        const isPosting = !!this.state.isPosting
        const isWaitingForPreview =
            !!this.state.waitingForEmbeds || !!this.state.streamEmbedsLoading
        const hasIncompleteUploadProgress = (() => {
            const recPct = this.state.uploadProgress?.recording
            if (recPct != null && recPct < 100) return true
            const files = this.state.uploadProgress?.files || {}
            return Object.values(files).some(progress => progress != null && progress < 100)
        })()
        const isUploadInProgress =
            !!this.uploadManager?.hasActiveUpload?.() || hasIncompleteUploadProgress
        const canSubmit =
            isValid &&
            pollValidationErrors.length === 0 &&
            !isPosting &&
            !isWaitingForPreview &&
            !isUploadInProgress
        const validStyles = canSubmit ? styles.validBtn : null

        const isPublic = questModel.isPublic(quest)
        const mainThreadReplyPolicy = this.mainThreadReplyPolicy(quest)
        // The reply-policy row renders in two composers: the title composer while
        // the thread is a draft (policy travels with createQuest), and the
        // thread-bottom composer of a published thread for its author (policy
        // persists through quests#update). The author always sees the current
        // state and can flip it either way.
        const showDraftReplyPolicyRow =
            this.props.isTitle && quest?.is_draft && !quest?.destination_quest_id
        const showThreadReplyPolicyRow =
            !!this.props.showThreadReplyPolicy &&
            !quest?.is_draft &&
            !!quest?.user_id &&
            !!gon.currentUser?.id &&
            String(quest.user_id) === String(gon.currentUser.id)
        const isQuestEmpty = questModel.showUnarchivedAnswers(quest).length === 0
        const inlineCompactEnabled = this.isCompactComposer()
        const isInlineCompactCollapsed = inlineCompactEnabled && !this.state.inlineComposerExpanded
        // The bottom editor's door to the full editor (openComposerInModal).
        const showOpenFullEditor = !!this.props.collapsibleComposer && !this.props.isTitle
        // The folded phone pill keeps the palette and the model picker beside
        // its post button, so the reader can go straight to them; choosing
        // there opens the pill out (insertActionRequestIntoComposer,
        // setAgentModel). The stream's folded title composer shows only its
        // post button, as before.
        const showAiControls = !isInlineCompactCollapsed || !!this.props.collapsibleComposer
        const replyToAnswer = this.props.hydratedParentAnswer || this.props.parentAnswer
        // The reply modal names the thread the reply is going to by its head
        // post: the thread it was started from (a branch reply's opener passes
        // it), else the one it posts into.
        const crumbAnswer =
            this.props.closeModal && !this.props.isTitle
                ? threadHeadPost(this.props.crumbQuest || quest)
                : null
        const showModalCrumb = !!crumbAnswer
        // Display only: a reply to the head post the crumb names shows no quote
        // (what it replies to is carried by replyToAnswerId either way).
        const showReplyTo =
            !!this.props.showReplyTo &&
            !!replyToAnswer &&
            !quoteRepeatsCrumb(crumbAnswer, replyToAnswer)
        // The inline composer's requested quote: shown inside the byline, under
        // the username, rather than above the composer like the modal's.
        const autoQuote = this.autoQuoteAnswer()
        // Open, it rides the quote row when there is one, and otherwise the
        // pill's own top-right corner. The field keeps its column clear either
        // way, so a quote coming or going never re-wraps the text under the
        // caret.
        const openFullEditorOpen = showOpenFullEditor && !isInlineCompactCollapsed
        const openFullEditorInCorner = openFullEditorOpen && !autoQuote

        const imagePreviewUrls = composerImagePreviewUrls(newAnswer)
        const videoUrl = attachmentState.videoUrl
        const videoPreviewSource = videoUrl
            ? resolveComposerVideoPreviewSource(videoUrl, newAnswer?.recording_content_type)
            : null

        const imagePayloadItems = composerImagePayloadItems(newAnswer).map((item, index) => ({
            ...item,
            controls: (
                <PreviewCancelButton
                    title={`Remove image ${index + 1}`}
                    ariaLabel={`Remove image ${index + 1}`}
                    testId={`new-answer-image-remove-button-${index}`}
                    onClick={() => this.removeComposerImage(index)}
                />
            ),
        }))

        const linkPreviews = attachmentState.linkPreviews
        const attachedUrl = attachmentState.attachedUrl
        const attachedUrlAddress = attachmentState.attachedUrlAddress
        const isAttachedLeaderboardEmbed = !!extractLeaderboardEmbedConfig(attachedUrlAddress)
        const isAttachedTdpEmbed = !!extractTdpEmbedConfig(attachedUrlAddress)
        // Keep reply composer instance stable across draft-id swaps after posting.
        // A remount here drops selection/focus and sends activeElement back to <body>.
        const inlineDraftInstanceKey = this.props.isTitle
            ? `${this.props.quest?.id || 'no-quest'}-${this.props.newAnswer?.id || 'no-answer'}`
            : `${this.props.quest?.id || 'no-quest'}-reply`

        const titleClass = this.props.isTitle ? 'new-title' : ''
        const titleEditorContainerStyles = this.props.isTitle ? styles.titleEditorContainer : null
        const composerTestId = this.props.isTitle ? 'title-composer' : 'reply-composer'
        const postButtonTestId = this.props.isTitle ? 'title-post-button' : 'reply-post-button'
        const postButtonLabel = this.hasCurrentDraftActionRequests()
            ? 'Create'
            : this.props.isTitle
            ? 'Post'
            : 'Reply'
        const aiWandTriggerTestId = this.props.isTitle
            ? 'title-ai-wand-button'
            : 'reply-ai-wand-button'
        const aiWandMenuTestId = this.props.isTitle ? 'title-ai-wand-menu' : 'reply-ai-wand-menu'
        const postMediaMenuButtonTestId = this.props.isTitle
            ? 'title-post-media-menu-button'
            : 'reply-post-media-menu-button'
        const postMediaMenuTestId = this.props.isTitle
            ? 'title-post-media-menu'
            : 'reply-post-media-menu'
        const modelPickerButtonTestId = this.props.isTitle
            ? 'title-model-picker-button'
            : 'reply-model-picker-button'
        const editorTestId = this.props.isTitle ? 'title-editor' : 'reply-editor'

        const fileUrl = attachmentState.fileUrl
        const composeAnswerId = newAnswer?.id || quest?.parent?.id || quest?.parent_id

        const agentModel = this.props.globalAgentModel || getDefaultAgentModel()
        const agentStatus = this.props.agentReplyPendingStatus || null
        const postMediaActionsEnabled = canUsePostMediaActions()

        return (
            <div
                ref={this.containerRef}
                data-testid={composerTestId}
                data-inline-composer={inlineCompactEnabled ? 'true' : 'false'}
                data-inline-composer-folded={isInlineCompactCollapsed ? 'true' : undefined}
                className={cn(
                    styles.newAnswerComp,
                    `new-answer-comp ${hoverClass} ${mobileClass}`,
                    titleClass,
                    this.props.closeModal && styles.inModal,
                    isQuestEmpty && styles.emptyQuest,
                    !this.props.isTitle && styles.replyComposer,
                    inlineCompactEnabled && styles.inlineCompactComposer,
                    isInlineCompactCollapsed && styles.inlineCompactCollapsed,
                    openFullEditorOpen && styles.reservesOpenFullEditor,
                    // The reply modal with nothing it replies to shown above the
                    // byline keeps the top margin the reply-to block gives it.
                    this.props.closeModal && !showReplyTo && styles.inModalWithoutReplyTo,
                    showModalCrumb && styles.inModalWithCrumb,
                    // The lower half of the avatar-gutter line running down
                    // from the message this composer answers.
                    autoQuote && this.props.autoQuoteAdjacent && styles.connectedComposer,
                )}
                onMouseDown={this._onPointerDown}
                onTouchStart={this._onPointerDown}
                onMouseEnter={this.onMouseEnter}
                onMouseLeave={this.onMouseLeave}
                onFocusCapture={this.onInlineComposerFocusCapture}
                onBlurCapture={this.onInlineComposerBlurCapture}
                onMouseDownCapture={this.onInlineComposerPointerDownCapture}
                onTouchStartCapture={this.onInlineComposerPointerDownCapture}
                onPointerDownCapture={this.onReplyDiagnosticTouch}
                onClickCapture={this.onInlineComposerClickCapture}
            >
                {/* URL screenshot socket integrated directly in NewAnswer */}
                {debug && (
                    <div
                        style={{
                            background: '#eaf0ff',
                            marginTop: -5,
                            marginBottom: 15,
                            marginLeft: 50,
                        }}
                    >
                        <strong style={{ marginLeft: 5 }}>Q:</strong>
                        <span onClick={() => debugCopy(quest?.id)}>{quest?.id}</span>
                        <strong style={{ marginLeft: 10 }}>D:</strong>
                        <span onClick={() => debugCopy(newAnswer?.id)}>{newAnswer?.id}</span>
                        <strong style={{ marginLeft: 10 }}>CQ:</strong>
                        <span onClick={() => debugCopy(newAnswer?.child_quests?.[0]?.id)}>
                            {newAnswer?.child_quests?.[0]?.id}
                        </span>
                    </div>
                )}

                {showModalCrumb && (
                    <ReplyModalCrumb
                        answer={crumbAnswer}
                        className={showReplyTo ? undefined : styles.modalCrumbOverByline}
                    />
                )}

                {openFullEditorInCorner && (
                    <div className={styles.openFullEditorCorner}>
                        {this.renderOpenFullEditorButton()}
                    </div>
                )}

                {/* Show all possible statuses for testing */}
                {agentStatus && <NewAnswerStatus status={agentStatus} agentModel={agentModel} />}

                {/* The message being replied to, in the quote chrome the sent
                    reply will wear — not a second rendered post. */}
                {showReplyTo && (
                    <>
                        <div className={styles.replyingToContainer}>
                            <ReplyTargetQuote answer={replyToAnswer} />
                        </div>
                        <div className={styles.replyTargetDivider} aria-hidden="true" />
                    </>
                )}

                {!isInlineCompactCollapsed && (
                    <div
                        className={cn(
                            styles.questHeader,
                            autoQuote && styles.quotingQuestHeader,
                            this.props.isTitle && styles.titleQuestHeader,
                            showReplyTo && styles.replyQuestHeader,
                        )}
                    >
                        <div
                            className={cn(
                                styles.userContainer,
                                this.props.isTitle && styles.titleUserContainer,
                            )}
                        >
                            <div
                                className={cn(
                                    styles.userIconContainer,
                                    this.props.isTitle && styles.titleUserIconContainer,
                                )}
                            >
                                <UserIcon
                                    user={gon.currentUser}
                                    showKnovigator={false}
                                    contextStyles={cn(
                                        styles.userIcon,
                                        this.props.isTitle && styles.titleUserIcon,
                                    )}
                                    size={'medium'}
                                />
                            </div>

                            <div
                                className={cn(
                                    styles.userNameContainer,
                                    autoQuote && styles.quotingUserNameContainer,
                                )}
                            >
                                <UserName
                                    user={gon.currentUser}
                                    answerCreatedAt={'now'}
                                    contextStyles={cn(
                                        styles.userName,
                                        // The quote needs the line below the name, so
                                        // the timestamp moves up beside it.
                                        autoQuote && styles.quotingUserName,
                                        this.props.isTitle && styles.titleUserName,
                                    )}
                                />
                                {autoQuote && (
                                    <div className={styles.autoQuoteContainer}>
                                        <ComposerAutoQuote
                                            answer={autoQuote}
                                            onRemove={this.dismissAutoQuote}
                                            attentionKey={this.state.quoteAttention.key}
                                            attentionKind={this.state.quoteAttention.kind}
                                            trailing={
                                                showOpenFullEditor
                                                    ? this.renderOpenFullEditorButton()
                                                    : undefined
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div
                    className={cn(
                        styles.userPostContainer,
                        // Under the byline's text column, the way a reply's body sits
                        // under its own byline rather than under its avatar. The
                        // modal keeps its own indent; the title composer has no
                        // byline column to sit under.
                        !this.props.isTitle && !showReplyTo && styles.threadReplyUserPostContainer,
                        this.props.isTitle && styles.titleUserPostContainer,
                        embeds?.length > 0 && styles.hasEmbedUserPostContainer,
                        showReplyTo && styles.replyUserPostContainer,
                        inlineCompactEnabled && styles.inlineCompactTransition,
                    )}
                    onMouseDown={this.props.closeModal ? this.onModalFieldAreaMouseDown : undefined}
                >
                    <WithSelection
                        isTitle={isMobile}
                        swipeWithFocus={false}
                        noSwipeRef={this.containerRef}
                    >
                        <div
                            ref={this.setFieldElement}
                            className={cn(styles.editorContainer, titleEditorContainerStyles)}
                            data-inline-editor-container="true"
                        >
                            <CommonEditor
                                key={`editor-${inlineDraftInstanceKey}`}
                                ref={this.richEditor}
                                editorTestId={editorTestId}
                                type={'new-answer'}
                                quest={quest}
                                defaultValue={(() => {
                                    if (this.props.isTitle) {
                                        return ''
                                    }
                                    // For replies, load saved draft if it exists
                                    const savedDraft = this.loadDraftFromLocalStorage()
                                    return this.getSavedDraftDeltaJson(savedDraft)
                                })()}
                                setAgentModel={this.setAgentModel}
                                globalAgentModel={this.props.globalAgentModel}
                                readOnly={false}
                                placeholder={[
                                    this.props.placeholder || 'Post reply...',
                                    getShortcutText(),
                                ]
                                    .filter(Boolean)
                                    .join(' ')}
                                setEmbeds={this.setEmbeds}
                                diagnosticComposer={this.props.closeModal ? 'modal' : 'inline'}
                                diagnosticComposerId={this._composerDiagnosticId}
                                setEmbedsLoading={this.setEmbedsLoading}
                                setStreamEmbeds={this.setStreamEmbeds}
                                setStreamEmbedsLoading={this.setStreamEmbedsLoading}
                                linkPreviewsFingerprint={generateFingerprint(linkPreviews)}
                                embedsFingerprint={embedsFingerprint}
                                streamEmbedsFingerprint={generateFingerprint(
                                    this.state.streamEmbeds,
                                    ',',
                                    'id',
                                )}
                                generateFingerprint={generateFingerprint}
                                setLinkPreviews={this.setLinkPreviews}
                                imgHandler={this.fileHandler}
                                postHandler={this.onPost}
                                onChange={this.onChange}
                                captureSetValue={this.captureSetValue}
                                getDefaultActionTokenAttrs={
                                    this.getDefaultActionTokenAttrsForCurrentAnswer
                                }
                                getActionTokenReferenceMentions={
                                    this.getActionTokenReferenceMentions
                                }
                                onActionTokenPromptChange={this.handleActionTokenPromptChange}
                                active={this.props.active}
                                onFocus={this.onFocus}
                                onBlur={this.onBlur}
                                onEditorLoad={() => {}}
                            />
                            {this.renderAiActionPreview()}
                            {this.renderPostMediaSettingsDialog()}
                        </div>
                    </WithSelection>
                </div>

                {isPollMode && (
                    <div className={styles.pollComposer} data-testid="new-answer-poll-composer">
                        <PreviewCancelButton
                            title="Remove poll"
                            ariaLabel="Remove poll"
                            testId="new-answer-poll-remove-button"
                            onClick={this.togglePollMode}
                        />
                        <div className={styles.pollComposerHeader}>
                            <div className={styles.pollComposerTitle}>Poll Options</div>
                            <div className={styles.pollComposerMeta}>
                                {pollOptions.length}/{MAX_POLL_OPTIONS}
                            </div>
                        </div>

                        <div className={styles.pollOptionsList}>
                            {pollOptions.map((option, index) => (
                                <div className={styles.pollOptionRow} key={`poll-option-${index}`}>
                                    <input
                                        className={styles.pollOptionInput}
                                        type="text"
                                        value={option}
                                        maxLength={MAX_POLL_OPTION_LENGTH}
                                        placeholder={`Option ${index + 1}`}
                                        onChange={event =>
                                            this.updatePollOption(index, event.target.value)
                                        }
                                    />
                                    <div className={styles.pollOptionCount}>
                                        {option.length}/{MAX_POLL_OPTION_LENGTH}
                                    </div>
                                    <ReliableButton
                                        type="button"
                                        className={styles.pollOptionRemove}
                                        onClick={() => this.removePollOption(index)}
                                        disabled={pollOptions.length <= MIN_POLL_OPTIONS}
                                    >
                                        Remove
                                    </ReliableButton>
                                </div>
                            ))}
                        </div>

                        <div className={styles.pollComposerFooter}>
                            <ReliableButton
                                type="button"
                                className={styles.pollAddOption}
                                onClick={this.addPollOption}
                                disabled={pollOptions.length >= MAX_POLL_OPTIONS}
                            >
                                Add Option
                            </ReliableButton>

                            <div className={styles.pollDurationGroup}>
                                {POLL_DURATION_PRESETS.map(preset => (
                                    <ReliableButton
                                        key={preset.minutes}
                                        type="button"
                                        className={cn(
                                            styles.pollDurationButton,
                                            pollDurationMinutes === preset.minutes &&
                                                styles.pollDurationButtonActive,
                                        )}
                                        onClick={() => this.updatePollDuration(preset.minutes)}
                                    >
                                        {preset.label}
                                    </ReliableButton>
                                ))}
                            </div>
                        </div>

                        {pollValidationErrors.length > 0 && (
                            <div className={styles.pollValidationList}>
                                {pollValidationErrors.map(error => (
                                    <div key={error} className={styles.pollValidationItem}>
                                        {error}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {imagePreviewUrls.length > 0 && (
                    <div
                        className={cn(
                            styles.payloadContainer,
                            imagePayloadItems.length > 1 && styles.multiImagePayloadContainer,
                        )}
                        data-testid="new-answer-image-previews"
                        data-image-count={imagePreviewUrls.length}
                    >
                        <PayloadContainer hideSingleModelLabel items={imagePayloadItems} />
                        <div className={styles.imagePreviewCount}>
                            {imagePreviewUrls.length}/{MAX_COMPOSER_IMAGES}
                        </div>
                    </div>
                )}

                {videoUrl && (
                    <>
                        <div
                            className={cn(
                                styles.videoContainer,
                                isMobile && styles.mobileVideoContainer,
                            )}
                            onClick={ev => ev.stopPropagation()}
                        >
                            <PreviewCancelButton
                                title="Cancel video upload"
                                ariaLabel="Cancel video upload"
                                testId="new-answer-video-remove-button"
                                onClick={() => {
                                    this.uploadManager.cancelRecordingUpload()
                                    const recId = this.props.newAnswer?.recording
                                    if (typeof recId === 'string' && recId) {
                                        try {
                                            api.purgePreuploadedBlob(recId)
                                        } catch (e) {}
                                    }
                                    try {
                                        if (this.videoInputRef?.current)
                                            this.videoInputRef.current.value = ''
                                    } catch (e) {}
                                    try {
                                        const url = this.props.newAnswer?.recording_url
                                        if (
                                            url &&
                                            typeof url === 'string' &&
                                            url.startsWith('blob:')
                                        ) {
                                            try {
                                                URL.revokeObjectURL(url)
                                            } catch (e) {}
                                        }
                                        cache.updateCachedAnswer(this.props.newAnswer?.id, {
                                            recording: null,
                                            recording_url: null,
                                        })
                                    } catch (e) {}
                                    this.setState(state => ({
                                        uploadProgress: {
                                            ...state.uploadProgress,
                                            recording: null,
                                        },
                                    }))
                                }}
                            />
                            <video
                                ref={this.videoPreviewRef}
                                src={videoPreviewSource?.videoSrc}
                                controls
                                playsInline
                                preload="metadata"
                                key={videoUrl}
                                onLoadedMetadata={this.startVideoUploadIfPending}
                            >
                                {/* Chromium rejects video/quicktime before sniffing; direct src lets the
                                platform decoder inspect MOV previews while typed sources stay explicit. */}
                                {videoPreviewSource?.typedSourceSrc && (
                                    <source
                                        src={videoPreviewSource.typedSourceSrc}
                                        type={videoPreviewSource.contentType}
                                    />
                                )}
                            </video>
                        </div>
                    </>
                )}

                {(() => {
                    const pct = this.state.uploadProgress?.recording
                    return (
                        pct != null && (
                            <div className={styles.uploadProgressContainer}>
                                <UploadProgress
                                    inline={true}
                                    percent={pct || 0}
                                    message={pct >= 100 ? 'Processing complete' : 'Processing…'}
                                    onCancel={() => {
                                        this.uploadManager.cancelRecordingUpload()
                                        this.setState(state => ({
                                            uploadProgress: {
                                                ...state.uploadProgress,
                                                recording: null,
                                            },
                                        }))
                                    }}
                                />
                            </div>
                        )
                    )
                })()}

                {fileUrl && (
                    <div
                        key={`new-answer-files-${this.props.quest.id}`}
                        className={styles.filesContainer}
                    >
                        <PreviewCancelButton
                            title="Cancel file upload"
                            ariaLabel="Cancel file upload"
                            testId="new-answer-file-remove-button"
                            onClick={() => {
                                const firstName =
                                    this.props.newAnswer?.file_urls?.[0]?.name ||
                                    this.props.newAnswer?.files?.[0]?.name ||
                                    null
                                this.uploadManager.cancelFileUpload(firstName || undefined)
                                const signedIds = Array.isArray(this.props.newAnswer?.files)
                                    ? this.props.newAnswer.files.filter(
                                          id => typeof id === 'string',
                                      )
                                    : []
                                try {
                                    signedIds.forEach(id => {
                                        try {
                                            api.purgePreuploadedBlob(id)
                                        } catch (e) {}
                                    })
                                } catch (e) {}
                                try {
                                    if (this.fileInputRef?.current)
                                        this.fileInputRef.current.value = ''
                                } catch (e) {}
                                try {
                                    // Revoke local object URLs
                                    const urls = (this.props.newAnswer?.file_urls || [])
                                        .map(u => u?.url)
                                        .filter(Boolean)
                                    urls.forEach(url => {
                                        if (typeof url === 'string' && url.startsWith('blob:')) {
                                            try {
                                                URL.revokeObjectURL(url)
                                            } catch (e) {}
                                        }
                                    })
                                    cache.updateCachedAnswer(composeAnswerId, {
                                        files: [],
                                        file_urls: [],
                                        file_previews: [],
                                    })
                                } catch (e) {}
                                this.setState(state => {
                                    const next = { ...(state.uploadProgress?.files || {}) }
                                    if (firstName) delete next[firstName]
                                    return {
                                        uploadProgress: { ...state.uploadProgress, files: next },
                                        waitingForEmbeds: false,
                                    }
                                })
                            }}
                        />
                        <FileEmbed
                            file={fileUrl}
                            canDelete={false}
                            showDelete={false}
                            processingPercent={this.state.modelPreviewProgress}
                            onModelPreviewProgress={pct =>
                                this.setState({ modelPreviewProgress: Math.round(pct || 0) })
                            }
                            onModelPreviewCaptureStart={() =>
                                this.setState({
                                    waitingForEmbeds: true,
                                    modelPreviewProgress: 0,
                                })
                            }
                            onModelPreviewCaptureEnd={() =>
                                this.setState({
                                    waitingForEmbeds: false,
                                    modelPreviewProgress: 100,
                                })
                            }
                            answerId={composeAnswerId}
                            autoCaptureModelPreview={true}
                            onModelPreviewGenerated={(blob, dataUrl, key = '1x1') => {
                                try {
                                    const cur = getCachedAnswer(composeAnswerId)
                                    const curUrls = Array.isArray(cur?.file_urls)
                                        ? cur.file_urls
                                        : []
                                    const newUrls = curUrls.map((u, idx) => {
                                        if (idx !== 0) return u
                                        const nextPreviewUrls = { ...(u.preview_urls || {}) }
                                        nextPreviewUrls[key] = dataUrl
                                        const fallback = nextPreviewUrls['1x1'] || dataUrl
                                        return {
                                            ...u,
                                            preview_urls: nextPreviewUrls,
                                            preview_url: fallback,
                                        }
                                    })
                                    // Only attach the 1x1 blob to the draft for uploading with the post
                                    const previews =
                                        key === '1x1' ? [blob] : cur?.file_previews || []
                                    cache.updateCachedAnswer(composeAnswerId, {
                                        file_urls: newUrls,
                                        file_previews: previews,
                                    })
                                } catch (e) {
                                    console.warn('Failed to update file preview in cache:', e)
                                }
                                this.setState({
                                    waitingForEmbeds: false,
                                    modelPreviewProgress: 100,
                                })
                            }}
                        />
                    </div>
                )}

                {(() => {
                    const firstName =
                        this.props.newAnswer?.file_urls?.[0]?.name ||
                        this.props.newAnswer?.files?.[0]?.name ||
                        null
                    const pct = firstName
                        ? (this.state.uploadProgress?.files || {})[firstName]
                        : null
                    return (
                        pct != null && (
                            <div className={styles.uploadProgressContainer}>
                                <UploadProgress
                                    inline={true}
                                    percent={pct || 0}
                                    message={pct >= 100 ? 'Processing complete' : 'Processing…'}
                                    onCancel={() => {
                                        this.uploadManager.cancelFileUpload(firstName || undefined)
                                        this.setState(state => {
                                            const next = { ...(state.uploadProgress?.files || {}) }
                                            if (firstName) delete next[firstName]
                                            return {
                                                uploadProgress: {
                                                    ...state.uploadProgress,
                                                    files: next,
                                                },
                                            }
                                        })
                                    }}
                                />
                            </div>
                        )
                    )
                })()}

                {this.props.newAnswer?.dice && (
                    <div className={styles.raffleContainer}>
                        <Raffle
                            key={`raffle-${this.props.newAnswer?.id || 'new'}`}
                            mode={this.props.newAnswer.dice.status || 'preview'}
                            upvalues={this.props.newAnswer.dice.upvalues || []}
                            titleUserId={this.props.newAnswer.dice.title_user_id}
                            onRemove={this.removeRaffle}
                            answerId={undefined}
                            diceData={this.props.newAnswer.dice}
                        />
                    </div>
                )}

                {!!embeds?.length && (
                    <div className={styles.embedsContainer}>
                        {embeds.map((embed, idx) => {
                            // Default PostEmbed for answer embeds
                            return (
                                <div key={idx} style={{ position: 'relative' }}>
                                    <PreviewCancelButton
                                        title="Remove embed"
                                        ariaLabel="Remove embed"
                                        testId="new-answer-embed-remove-button"
                                        onClick={() => {
                                            const targetUrl = (
                                                extractedUrls.knovEmbedLinks || []
                                            ).find(href =>
                                                answerMatchesKnovEmbedTarget(
                                                    getCachedAnswer(embed?.id) || embed,
                                                    extractKnovEmbedTarget(href),
                                                ),
                                            )
                                            this.hidePreviewUrl(targetUrl)
                                        }}
                                    />
                                    <PostEmbed
                                        label="QUOTE"
                                        isQuote={true}
                                        panel={this.props.panel}
                                        parentAnswer={this.props.newAnswer}
                                        containerAnswer={this.props.newAnswer}
                                        answer={embed}
                                        containerQuest={this.props.quest}
                                        delete={() => {}}
                                    />
                                </div>
                            )
                        })}
                    </div>
                )}

                {!!this.state.streamEmbeds?.filter(isVisibleStreamEmbed).length && (
                    <div className={styles.embedsContainer}>
                        {this.state.streamEmbeds
                            .filter(isVisibleStreamEmbed)
                            .map((streamEmbed, idx) => {
                                return (
                                    <div
                                        key={`stream-${streamEmbed.id}-${streamEmbed.url || idx}`}
                                        style={{
                                            marginTop: idx > 0 ? '10px' : '0',
                                            position: 'relative',
                                        }}
                                    >
                                        <PreviewCancelButton
                                            title="Remove channel preview"
                                            ariaLabel="Remove channel preview"
                                            testId="new-answer-stream-preview-remove-button"
                                            onClick={() => {
                                                const targetUrl = (
                                                    extractedUrls.streamEmbedLinks || []
                                                ).find(href => {
                                                    const config = extractStreamEmbedConfigs([
                                                        href,
                                                    ])[0]
                                                    return (
                                                        areSamePreviewUrl(href, streamEmbed?.url) ||
                                                        String(config?.id || '') ===
                                                            String(streamEmbed?.id)
                                                    )
                                                })
                                                this.hidePreviewUrl(targetUrl)
                                            }}
                                        />
                                        <StreamEmbed
                                            streamId={streamEmbed.id}
                                            filter={streamEmbed.filter}
                                            className={styles.streamEmbed}
                                        />
                                    </div>
                                )
                            })}
                    </div>
                )}

                {!!visibleLeaderboardEmbedLinks.length && (
                    <div className={styles.embedsContainer}>
                        {visibleLeaderboardEmbedLinks.map((leaderboardEmbed, i) => {
                            const previewKey =
                                typeof leaderboardEmbed === 'string'
                                    ? leaderboardEmbed
                                    : leaderboardEmbed.previewUrl ||
                                      getLeaderboardEmbedPreviewKey(leaderboardEmbed)
                            return (
                                <div
                                    key={previewKey || `leaderboard-${i}`}
                                    style={{ marginTop: '15px', position: 'relative' }}
                                >
                                    <PreviewCancelButton
                                        title="Remove leaderboard preview"
                                        ariaLabel="Remove leaderboard preview"
                                        testId="new-answer-leaderboard-preview-remove-button"
                                        onClick={() => this.hidePreviewUrl(previewKey)}
                                    />
                                    <LeaderboardMessageEmbed
                                        {...(typeof leaderboardEmbed === 'string'
                                            ? { url: leaderboardEmbed }
                                            : { config: leaderboardEmbed })}
                                    />
                                </div>
                            )
                        })}
                    </div>
                )}

                {!!visibleTdpEmbedLinks.length && (
                    <div className={styles.embedsContainer}>
                        {visibleTdpEmbedLinks.map((tdpEmbed, i) => {
                            const previewKey =
                                typeof tdpEmbed === 'string'
                                    ? tdpEmbed
                                    : tdpEmbed.previewUrl || getTdpEmbedPreviewKey(tdpEmbed)
                            return (
                                <div
                                    key={previewKey || `tdp-${i}`}
                                    style={{ marginTop: '15px', position: 'relative' }}
                                >
                                    <PreviewCancelButton
                                        title="Remove TDP preview"
                                        ariaLabel="Remove TDP preview"
                                        testId="new-answer-tdp-preview-remove-button"
                                        onClick={() => this.hidePreviewUrl(previewKey)}
                                    />
                                    <TdpMessageEmbed
                                        {...(typeof tdpEmbed === 'string'
                                            ? { url: tdpEmbed }
                                            : { config: tdpEmbed })}
                                    />
                                </div>
                            )
                        })}
                    </div>
                )}

                {attachedUrlAddress &&
                    !getKnovLinkRegexp().test(attachedUrlAddress) &&
                    !isAttachedLeaderboardEmbed &&
                    !isAttachedTdpEmbed &&
                    imagePreviewUrls.length === 0 && (
                        <div className={styles.linkPreviewsContainer}>
                            <div style={{ marginTop: '15px', position: 'relative' }}>
                                <PreviewCancelButton
                                    title="Remove link preview"
                                    ariaLabel="Remove link preview"
                                    testId="new-answer-link-preview-remove-button"
                                    onClick={() => this.clearAttachedUrl(attachedUrlAddress)}
                                />
                                <LinkPreview
                                    url={attachedUrlAddress}
                                    urlModel={attachedUrl}
                                    onPreviewClick={() =>
                                        this.handleAttachedUrlPreviewClick(attachedUrlAddress)
                                    }
                                    answerId={this.props.newAnswer?.id}
                                    setPostDisabled={disabled =>
                                        this.setState({ waitingForEmbeds: !!disabled })
                                    }
                                />
                            </div>
                        </div>
                    )}

                {linkPreviews && imagePreviewUrls.length === 0 && (
                    <div className={styles.linkPreviewsContainer}>
                        {linkPreviews
                            .filter(
                                href =>
                                    !areSamePreviewUrl(href, attachedUrlAddress) &&
                                    !extractLeaderboardEmbedConfig(href) &&
                                    !extractTdpEmbedConfig(href),
                            )
                            .map(href => (
                                <div key={href} style={{ marginTop: '15px', position: 'relative' }}>
                                    <PreviewCancelButton
                                        title="Remove link preview"
                                        ariaLabel="Remove link preview"
                                        testId="new-answer-link-preview-remove-button"
                                        onClick={() => this.hidePreviewUrl(href)}
                                    />
                                    <LinkPreview
                                        url={href}
                                        answerId={this.props.newAnswer?.id}
                                        setPostDisabled={disabled =>
                                            this.setState({ waitingForEmbeds: !!disabled })
                                        }
                                    />
                                </div>
                            ))}
                    </div>
                )}

                {(showDraftReplyPolicyRow || showThreadReplyPolicyRow) &&
                    !isInlineCompactCollapsed && (
                        <div
                            className={cn(
                                styles.replyPolicyRow,
                                inlineCompactEnabled && styles.inlineCompactTransition,
                            )}
                            data-testid="thread-reply-controls"
                            data-inline-compose-action="true"
                            onMouseDown={this.onAttachmentPointerDown}
                            onTouchStart={this.onAttachmentPointerDown}
                        >
                            <WithTooltip
                                tip={
                                    showThreadReplyPolicyRow
                                        ? 'Who can reply in the main thread. Anyone can still branch. Click to change.'
                                        : 'Who can reply in the main thread. Anyone can still branch.'
                                }
                            >
                                <ReliableButton
                                    type="button"
                                    data-testid="thread-reply-policy-toggle"
                                    className={styles.replyPolicyToggle}
                                    disabled={this.state.isUpdatingThreadReplyPolicy}
                                    aria-busy={this.state.isUpdatingThreadReplyPolicy}
                                    onClick={() =>
                                        this.setMainThreadReplyPolicy(
                                            this.nextMainThreadReplyPolicy(quest),
                                        )
                                    }
                                >
                                    {mainThreadReplyPolicy === 'author' ? (
                                        <Lock aria-hidden="true" />
                                    ) : mainThreadReplyPolicy === 'members' ? (
                                        <Users aria-hidden="true" />
                                    ) : (
                                        <Globe aria-hidden="true" />
                                    )}
                                    <span>
                                        {mainThreadReplyPolicy === 'author'
                                            ? 'Only you can reply'
                                            : mainThreadReplyPolicy === 'members'
                                            ? 'Members can reply'
                                            : 'Everyone can reply'}
                                    </span>
                                </ReliableButton>
                            </WithTooltip>
                        </div>
                    )}

                <div
                    className={cn(
                        styles.btnContainer,
                        inlineCompactEnabled && styles.inlineCompactTransition,
                        isInlineCompactCollapsed && styles.inlineCompactBtnOverlay,
                    )}
                    onMouseDown={this.onComposerControlPointerDown}
                    onTouchStart={this.onComposerControlPointerDown}
                >
                    <div className={styles.buttons}>
                        {/* Folded, the attach controls are hidden, not unmounted:
                            the gallery or camera they open takes the keyboard,
                            which folds the pill, and an unmounted file input
                            drops the picture the picker hands back. */}
                        <div
                            className={cn(
                                styles.mediaLeftGroup,
                                isInlineCompactCollapsed && styles.mediaLeftGroupFolded,
                                'composer-media-ghost',
                            )}
                        >
                            <div
                                className={styles.newAnswerBtnContainer}
                                data-inline-compose-action="true"
                                onMouseDown={this.onAttachmentPointerDown}
                                onTouchStart={this.onAttachmentPointerDown}
                            >
                                <AddFileButton
                                    btnId={`${this.props.quest.id}-${this.props.panel}`}
                                    ref={this.fileInputRef}
                                    answer={newAnswer}
                                    fileHandler={this.fileHandler}
                                />
                            </div>

                            {isMobile && (
                                <div
                                    className={styles.newAnswerBtnContainer}
                                    data-inline-compose-action="true"
                                    onMouseDown={this.onAttachmentPointerDown}
                                    onTouchStart={this.onAttachmentPointerDown}
                                >
                                    <AddImageButton
                                        btnId={`${this.props.quest.id}-${this.props.panel}`}
                                        ref={this.imgInputRef}
                                        videoRef={this.videoInputRef}
                                        imgHandler={this.fileHandler}
                                        videoHandler={this.fileHandler}
                                    />
                                </div>
                            )}

                            <div className={styles.newAnswerBtnContainer}>
                                <WithTooltip tip={isPollMode ? 'Remove poll' : 'Add poll'}>
                                    <ReliableButton
                                        className={cn(
                                            styles.pollToggleButton,
                                            isPollMode && styles.pollToggleButtonActive,
                                        )}
                                        onClick={this.togglePollMode}
                                        aria-label={isPollMode ? 'Remove poll' : 'Add poll'}
                                    >
                                        <BarChart3 aria-hidden="true" />
                                    </ReliableButton>
                                </WithTooltip>
                            </div>
                        </div>
                        {!isInlineCompactCollapsed &&
                            userHasFeature('raffle') &&
                            !this.props.isTitle &&
                            this.titleHasUpvalues() &&
                            gon.currentUser?.id === this.props.quest?.parent?.user_id && (
                                <div className={styles.agentButtonContainer}>
                                    <DiceButton
                                        contextStyles={styles.knovAgent}
                                        imageStyles={styles.knovAgentImage}
                                        onRaffleClick={this.addRaffleEmbed}
                                    />
                                </div>
                            )}
                        {showAiControls && postMediaActionsEnabled && (
                            <div
                                className={styles.postMediaActions}
                                data-inline-compose-action="true"
                                onMouseDown={this.onAiControlPointerDown}
                                onTouchStart={this.onAiControlPointerDown}
                            >
                                <WithTooltip
                                    ref={this.postMediaActionTooltipRef}
                                    tip={POST_MEDIA_ACTION_TOOLTIP}
                                >
                                    <PostMediaActionMenuButton
                                        menuTestId={postMediaMenuTestId}
                                        onMenuStateChange={this.onPostMediaActionMenuStateChange}
                                        onOpenModels={this.openAiModelsMenu}
                                        onSelect={this.insertPostMediaAction}
                                        triggerTestId={postMediaMenuButtonTestId}
                                    />
                                </WithTooltip>
                            </div>
                        )}
                        {/* The models menu (the magic wand) opens from the
                            palette's "Models" row and draws no trigger of its
                            own, so it takes no room in the row. */}
                        {showAiControls && (
                            <div
                                className={styles.aiModelsMenuHost}
                                data-inline-compose-action="true"
                            >
                                <AiMagicWandMenuButton
                                    key={`wand-${inlineDraftInstanceKey}`}
                                    ref={this.aiWandRef}
                                    hideTrigger
                                    imageStyles={styles.knovAgentImage}
                                    triggerTestId={aiWandTriggerTestId}
                                    menuTestId={aiWandMenuTestId}
                                    getEditor={this.getEditor}
                                    getCurrentTextInput={this.getCurrentAiTextInput}
                                    getCurrentImageInput={this.getCurrentDraftAiImageInput}
                                    getCurrentVideoInput={this.getCurrentDraftAiVideoInput}
                                    getCurrentAudioInput={this.getCurrentDraftAiAudioInput}
                                    getThreadTextInputs={this.getThreadAiTextInputs}
                                    getThreadImageInputs={this.getThreadAiImageInputs}
                                    getThreadVideoInputs={this.getThreadAiVideoInputs}
                                    getThreadAudioInputs={this.getThreadAiAudioInputs}
                                    getThreadTreeMediaInputs={this.getThreadTreeAiMediaInputs}
                                    getCraftedPromptInputs={this.getCraftedPromptInputs}
                                    onImageInputAttached={this.attachAiImageInput}
                                    onVideoInputAttached={this.attachAiVideoInput}
                                    onAudioInputAttached={this.attachAiAudioInput}
                                    questIsPublic={isPublicAiPricingContext(this.props.quest)}
                                    onMenuStateChange={this.onAiModelsMenuStateChange}
                                    onActionRequestSelected={this.insertActionRequestIntoComposer}
                                />
                            </div>
                        )}
                        {showAiControls && (
                            <div
                                className={styles.agentButtonContainer}
                                data-testid={modelPickerButtonTestId}
                                data-inline-compose-action="true"
                                onMouseDown={this.onAiControlPointerDown}
                                onTouchStart={this.onAiControlPointerDown}
                            >
                                <KnovAgentButtonUIMain
                                    key={`agent-${inlineDraftInstanceKey}`}
                                    contextStyles={styles.knovAgentButtonContainer}
                                    imageStyles={styles.knovAgentImage}
                                    setAgentModel={this.setAgentModel}
                                    active={this.props.globalAgentModel}
                                    editorRef={this.richEditor}
                                    onMenuStateChange={this.onAgentMenuStateChange}
                                />
                            </div>
                        )}

                        {isInlineCompactCollapsed && showOpenFullEditor ? (
                            this.renderOpenFullEditorButton()
                        ) : (
                            <ReliableButton
                                id="post-btn"
                                data-testid={postButtonTestId}
                                data-inline-post-button={inlineCompactEnabled ? 'true' : 'false'}
                                data-inline-compose-action="true"
                                className={cn(
                                    styles.postAnswerBtn,
                                    validStyles,
                                    (isWaitingForPreview || isPosting || isUploadInProgress) &&
                                        styles.loadingBtn,
                                )}
                                onClick={canSubmit ? this.onPost : undefined}
                                disabled={!canSubmit}
                                aria-disabled={canSubmit ? 'false' : 'true'}
                                onMouseDown={this.onPostPointerDown}
                                onTouchStart={this.onPostPointerDown}
                                preserveFocusOnTouch={isMobile}
                                tabIndex={-1}
                            >
                                {isWaitingForPreview ? (
                                    <div
                                        className={cn(
                                            styles.spinner,
                                            isValid && styles.spinnerActive,
                                        )}
                                    />
                                ) : (
                                    <StreamIcon
                                        quest={this.props.quest}
                                        isRoot={false}
                                        contextStyles={cn(
                                            styles.streamIcon,
                                            isValid && styles.validStreamIcon,
                                            isPublic && styles.publicIcon,
                                        )}
                                    />
                                )}
                                <div className={styles.label}>{postButtonLabel}</div>
                            </ReliableButton>
                        )}
                    </div>
                </div>
            </div>
        )
    }
}

/** @type {React.FC<{status: string, agentModel?: string}>} */
const NewAnswerStatus = React.memo(props => {
    const status = /** @type {{status: string, agentModel?: string}} */ (props).status

    // Don't show anything when status is awaiting
    if (!status || status === 'awaiting') return null

    return (
        <div
            className={cn(
                styles.newMessageStatus,
                status === 'pending' && styles.newMessageStatusPending,
                status === 'error' && styles.newMessageStatusError,
            )}
        >
            {status === 'pending' && (
                <div className={styles.loaderContainer}>
                    <div className={styles.loader}></div>
                </div>
            )}

            {status === 'error' && (
                <div className={styles.errorContainer}>
                    <span className={styles.errorSmiley}>
                        <i className={'fa fa-meh-o'} />
                    </span>
                </div>
            )}
        </div>
    )
})

export default Connector(stateSelector, props => {
    return {
        newAnswer: useCachedAnswer(props.newAnswer?.id),
        // The reply-to preview hydrates the quest head — except in the
        // quote_main lane, where the target is a specific room message, not
        // the head (legacy branch modals had quest.parent == the clicked
        // answer, so the two were the same thing).
        hydratedParentAnswer: useGetAnswer(props.replyToAnswerId || props.quest?.parent?.id).answer,
        // sorted_answers rows are lean — no user, no payload — so the offered
        // quote is hydrated before it can render an author and a snippet.
        hydratedAutoQuoteAnswer: useGetAnswer(props.autoQuoteAnswer?.id).answer,
        panelContext: usePanelContext(),
        openRight: useOpenRight().openRight,
        set: useStore.getState().set,
    }
})(
    React.forwardRef(
        /** @returns {React.ReactElement} */
        (/** @type {Record<string, any>} */ props, ref) => (
            <ErrorBoundary label={`NewAnswer ${props.answer?.id}`}>
                <NewAnswer ref={ref} {...props} />
            </ErrorBoundary>
        ),
    ),
)
